// 单机沙盘:整座城在浏览器里的 WebAssembly 引擎里跑(engine/web),画面用原来游戏的 CityMap。
// 照看板游戏(维多利亚 3 一类)的路子:顶上一排关键数(涨跌、小曲线),左边一列看板,地图有上色模式,
// 右边是点开的人、公司、地块、工程、市场、银行、政府——头上是要紧的数,下面分页,每页自己滚;名字都点得开;空格暂停、数字键调速。见 14-经济系统.md。
import type { City, Plot } from './api.ts';
import { CityMap, lineColor } from './city.ts';
import {
  areaChart, bars, CHARTS, dateOf, dbar, donut, drawHover, esc, histogram, hoverChart, installChartRange, lineChart, restoreChartRange, money, monthOf, newness, num, PALETTE, pct, resetCharts, sankey, spark, table, tile, treemap, trendNote, waterfall, yearOf, type Line,
} from './sbchart.ts';
import { lifeText, lifeTextSteps } from './sbexport.ts';
import { createExportDownloads } from './sbentity-export.ts';
import { investmentCompanyText, investmentHTML } from './sbinvestment.ts';
import { createDownloadPanel } from './sbdownload.ts';
import { installGameMonitor } from './sbmonitor.ts';
import { REPLAY_BUILD_ID, replayBits, type ReplayReceipt } from './sbcheckpoint.ts';
import { secondaryCompletionActions, secondaryCompletionHTML } from './sbeducation.ts';
import { schoolRosterHTML } from './sbschool-roster.ts';
import { hospitalActivityHTML, hospitalHistoryLinkHTML, medicalArchiveHTML, medicalArchiveKey, medicalConditionHTML, medicalRecordHTML, patientMedicalHTML } from './sbmedical.ts';
import { medicalCaseFilesHTML } from './sbmedical-cases.ts';
import { createDeathDirectory, recentDeathsHTML } from './sbmedical-deaths.ts';
import { clinicianCareerHTML, clinicianCareerLinks } from './sbclinical-career.ts';
import { employmentHTML, employmentRecordText, wageRecoveryHTML } from './sbemployment.ts';
import { taxCard, taxKinds, taxOverviewHTML, tenderReserveLabel } from './sbtax.ts';
import { historySourceLabel, historySourceNote } from './sbhistory.ts';
import { historyRawHTML } from './sbjournal-details.ts';
import { archiveWriteHTML, bootTimingText, engineTimingHTML, recentDayTimingCard, recentDayTimingHTML } from './sbengine-timing.ts';
import { deathHTML, deathHistoryHTML, type DeathLinks } from './sbdeath.ts';
import { runtimeFailureHTML } from './sbruntime.ts';
import { applyPanelReadView, createPanelReadState, type PanelReadContext } from './sbpanel-state.ts';
import { avatar, badge, icon, KIND_COLOR, kindPill, placeTip, plotTip, toast } from './sbui.ts';
import { buildNameIndex, linkedText, withCaseNames, type NameIndex } from './sbnames.ts';
import { stocksHTML, tradesHTML, valuationHTML, type StockState } from './sbstocks.ts';
import { installKline, loadRedUp, saveRedUp } from './sbkline.ts';
import { installDepth } from './sbdepth.ts';
import { ipoForm, syncIpo } from './sbipo.ts';
import { siteDefaultFloor, siteHTML, siteNote } from './sbsite.ts';
import { renderProjectSettlement } from './sbproject-settlement.ts';
import { projectProgressPercent, projectProgressStage, projectProgressText, renderProjectDelivery } from './sbproject-progress.ts';
import { companyOriginHTML } from './sbcompany-origin.ts';
import { acceptEquityHistory, acceptEquityOwners, acceptEquityRank, advanceEquityTimeline, EQUITY_HISTORY_PAGE, EQUITY_METRICS, EQUITY_OWNER_PAGE, EQUITY_RANK_CURRENT, EQUITY_RANK_PAGE, equityHistoryHTML, followEquityLeaders, newEquityHistoryState, rememberEquityHistory, rememberEquityOwners, rememberEquityRank, setEquityOwnersOpen, setEquityTimelineDay, stepEquityTimeline, toggleEquityOwner, toggleEquityPlayback, type EquityHistoryPage, type EquityHistoryState } from './sbequity-history.ts';
import { bizHTML } from './sbbiz.ts';
import { landHTML } from './sbland.ts';
import { creditHTML } from './sbcredit.ts';
import { govHTML, govState, type GovState } from './sbgov.ts';
import { makeWin, placed, raise } from './sbwin.ts';
import { Section } from './section.ts';
import type { Project } from './api.ts';
import { mobileDevice } from './sbthreads.ts';
import { ANY_PLOT, assetColors, assetMarks, assetsOf, type AssetSpot, backupSave, ENGINE_VERSION, newRulesHTML, prevSaves, RULES, rulesLine, SAME_ENGINE, saveBits, saveRules, prevSavesHTML, pushPrevSave, takePrevSave, forceLoadHTML, hereCardHTML, keepOldSave, oldSaveHTML, huntColors, huntHTML, loadSave, sideHTML, singlesHTML, startPageHTML, stopHTML, whoHTML, writeSave, type DateState, type HuntState, type PlayerCmd, type PlayerSave } from './sbplayer.ts';

/** 引擎的导出(在 sbworker.ts 的线程里):这边每个函数只给出「要哪份数据」的键,用 read 从缓存里取。 */
interface Engine {
  city(): string;
  plot(id: number): string;
  person(id: number): string;
  firm(id: number): string;
  plot2(id: number): string;
  person2(id: number): string;
  firm2(id: number): string;
  ownership_owners?(firm: number, offset: number, limit: number): string;
  ownership?(firm: number, kind: number, owner: number, offset: number, limit: number): string;
  ownership_rank?(firm: number, day: number, metric: number, offset: number, limit: number): string;
  project2(id: number): string;
  unit2(id: number): string;
  hospital2?(id: number): string;
  school2?(id: number): string;
  schoolstudents?(id: number, start: number): string;
  life2?(id: number): string;
  context2?(id: number): string;
  market2(k: number): string;
  series(): string;
  markets(): string;
  firms(): string;
  people(): string;
  bank(k: number): string;
  insure?(): string;
  gov(): string;
  estate(): string;
  complexes(): string;
  deals(): string;
  news(): string;
  media?(): string;
  society?(): string;
  medical_deaths?(beforeDay: number, beforeId: number): string;
  case2?(id: number): string;
  cases?(): string;
  names?(): string;
  court?(): string;
  construction?(): string;
  regions(): string;
  mapmode(k: number): string;
  flows(): string;
  plotlog?(id: number): string;
  plotrec?(arg: number): string;
  bldrec?(arg: number): string;
  gdp?(): string;
  econ?(): string;
  spend?(): string;
  cbd?(): string;
  me?(): string;
  mejobs?(): string;
  mehomes?(): string;
  melines?(): string;
  mehere?(plot: number): string;
  mesale?(): string;
  merent?(): string;
  meinvest?(): string;
  mecandidates?(): string;
  metenders?(): string;
  meshares?(): string;
  peek?(kind: number, id: number): string;
  mewho?(id: number): string;
  mesingles?(): string;
  mesite?(id: number): string;
  mestocks?(): string;
  mestockmarket?(): string;
  mestock?(id: number): string;
  meholdings?(): string;
  meipos?(): string;
  meipo_on?(): string;
  mecases?(): string;
  mecivic?(): string;
  mepremises?(): string;
  meceos?(): string;
  mebiz?(): string;
  meland?(): string;
  mecredit?(): string;
  megov?(): string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type J = any;
type Stats = Record<string, number>;
interface Who { kind: string; id?: number; name: string }
type Kind = 'plot' | 'person' | 'firm' | 'project' | 'unit' | 'hospital' | 'school' | 'market' | 'bank' | 'gov' | 'region' | 'case';
type Sel = { kind: Kind; id: number };

const TABS: [string, string][] = [
  ['overview', '概况'], ['people', '人口'], ['society', '社会'], ['medical', '医疗'], ['education', '教育'], ['govt', '政府'], ['justice', '司法'], ['market', '市场'], ['estate', '房地产'], ['build', '建设'],
  ['firms', '企业'], ['finance', '金融'], ['regions', '外地'], ['news', '快讯'], ['follow', '关注'], ['engine', '引擎与存档'],
];
const KIND_CN: Record<string, string> = {
  shop: '日用店', fun: '餐饮娱乐', goods: '用品店', plant: '建材厂', builder: '建筑公司', developer: '开发商', factory: '工厂', maker: '日用品、食品、用品厂', haul: '物流', service: '服务业', media: '报社', hotel: '酒店', fund: '投资公司', insurer: '保险公司', bank: '银行', public: '公共部门', hospital: '医院',
};
/** 地图模式:编号和引擎的 mapmode 一样;kind 决定上色的方式(数值从浅到深、分类各一种颜色、反着的数值越低越好)。 */
const MODES: { k: number; name: string; group: string; fmt: (v: number) => string; cat?: boolean; bad?: boolean }[] = [
  { k: -1, name: '平常', group: '', fmt: String },
  { k: 0, name: '房价', group: '价格', fmt: money },
  { k: 1, name: '房租', group: '价格', fmt: (v) => `${v.toFixed(0)} 元/天` },
  { k: 2, name: '地价', group: '价格', fmt: money },
  { k: 3, name: '写字楼租金', group: '价格', fmt: (v) => `${v.toFixed(0)} 元/天` },
  { k: 4, name: '生活成本', group: '价格', fmt: (v) => `${v.toFixed(2)} 天工钱`, bad: true },
  { k: 5, name: '人口', group: '人', fmt: (v) => `${v} 人` },
  { k: 6, name: '收入', group: '人', fmt: (v) => `${v.toFixed(0)} 元/天` },
  { k: 7, name: '身家', group: '人', fmt: money },
  { k: 8, name: '失业', group: '人', fmt: (v) => pct(v), bad: true },
  { k: 22, name: '通勤', group: '人', fmt: (v) => `来回 ${v.toFixed(0)} 分钟`, bad: true },
  { k: 24, name: '堵车', group: '人', fmt: (v) => `一天 ${v.toFixed(0)} 辆车`, bad: true },
  { k: 25, name: '人流', group: '人', fmt: (v) => `一天 ${num(v)} 人` },
  { k: 27, name: '建筑估值', group: '楼', fmt: money },
  { k: 30, name: '用途', group: '楼', fmt: String, cat: true },
  { k: 10, name: '层数', group: '楼', fmt: (v) => `${v} 层` },
  { k: 11, name: '楼龄', group: '楼', fmt: (v) => `${v.toFixed(0)} 年`, bad: true },
  { k: 12, name: '裂缝', group: '楼', fmt: (v) => `${v} 处`, bad: true },
  { k: 13, name: '空置', group: '楼', fmt: (v) => pct(v), bad: true },
  { k: 9, name: '空房', group: '楼', fmt: (v) => `${v} 套` },
  { k: 14, name: '施工', group: '楼', fmt: String, cat: true },
  { k: 15, name: '期房', group: '楼', fmt: (v) => `${v} 套` },
  { k: 16, name: '业主', group: '谁的', fmt: String, cat: true },
  { k: 17, name: '开发商', group: '谁的', fmt: String, cat: true },
  { k: 18, name: '岗位', group: '经济', fmt: (v) => `${v} 人` },
  { k: 19, name: '店', group: '经济', fmt: (v) => `${v} 家` },
  { k: 20, name: '写字楼', group: '经济', fmt: (v) => `${v} 套` },
  { k: 28, name: '地块 GDP', group: '经济', fmt: money },
  { k: 29, name: '人均 GDP', group: '经济', fmt: (v) => `${money(v)}/人` },
  { k: 32, name: '居民收入', group: '经济', fmt: money },
  { k: 33, name: '人均居民收入', group: '经济', fmt: (v) => `${money(v)}/人` },
  { k: 31, name: '商务区', group: '经济', fmt: (v) => `扎堆,一人多做 ${pct(v)}` },
  { k: 34, name: '酒店', group: '经济', fmt: (v) => `${v} 间客房` },
  { k: 35, name: '外地客人', group: '经济', fmt: (v) => `一天 ${v.toFixed(1)} 人` },
  { k: 21, name: '片区', group: '位置', fmt: String, cat: true },
  { k: 23, name: '离岗位远近', group: '位置', fmt: (v) => v.toFixed(1) },
  { k: 26, name: '招人方便', group: '位置', fmt: (v) => `全城平均的 ${v.toFixed(2)} 倍` },
];
/** 数值的色阶(多色相、浅到深,连续取色):一般的数黄绿到深蓝;越高越差的(bad)浅黄到深红;有正有负的,负的红、正的蓝。 */
const SEQ_VAL = ['#fbf8d0', '#e3f2b4', '#bfe3b5', '#86ccbb', '#4fb3c3', '#2b8fbe', '#2466a6', '#243f8f', '#13245c'];
const SEQ_BAD = ['#fdf6c8', '#fde6a0', '#fcd27a', '#f9b056', '#f48a42', '#e8602f', '#cf3a26', '#a8211f', '#6e1418'];
const DIV = ['#a3262a', '#cf5a47', '#ee9a7c', '#f8d2bf', '#f2efe9', '#c9dfec', '#8fbfdc', '#4a8fc2', '#1f5a99'];

/** 色阶上 t(0 到 1)处的颜色:相邻两个点按 RGB 插。 */
function rampAt(stops: string[], t: number): string {
  const x = Math.min(1, Math.max(0, t)) * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(x)), f = x - i;
  const a = parseInt(stops[i].slice(1), 16), b = parseInt(stops[i + 1].slice(1), 16);
  const ch = (sh: number) => Math.round(((a >> sh) & 255) * (1 - f) + ((b >> sh) & 255) * f);
  return `rgb(${ch(16)},${ch(8)},${ch(0)})`;
}

/**
 * 把一组数(已从小到大排好)换成 0 到 1 的位置,和色条上标的刻度。
 * - 按排名:第几小就放在第几成,刻度标几个分位数;
 * - 按数值:全是正数且最大是最小的二十倍以上的,按对数(差十倍隔一样远:一百万和一亿颜色差得开);
 *   有正有负、负的不止零星几个的,零在正中,两边各按对数往外;其余按数值均匀铺。
 */
function mapScale(vals: number[], byRank: boolean): { at: (x: number) => number; ticks: number[]; note: string; div: boolean } {
  const n = vals.length, lo = vals[0], hi = vals[n - 1];
  if (byRank || lo === hi) {
    const at = (x: number) => {
      if (n < 2) return 0.5;
      let a = 0, b = n; // 第一个不小于 x 的
      while (a < b) { const m = (a + b) >> 1; if (vals[m] < x) a = m + 1; else b = m; }
      let c = a, d = n; // 第一个大于 x 的
      while (c < d) { const m = (c + d) >> 1; if (vals[m] <= x) c = m + 1; else d = m; }
      return ((a + c - 1) / 2) / (n - 1);
    };
    return { at, ticks: [0, 0.25, 0.5, 0.75, 0.9, 1].map((q) => vals[Math.round(q * (n - 1))]), note: '按排名', div: false };
  }
  const neg = vals.filter((x) => x < 0).length;
  if (lo < 0 && hi > 0 && neg >= Math.max(2, n * 0.02)) {
    const mags = vals.map(Math.abs).filter((x) => x > 0).sort((a, b) => a - b);
    const c = Math.max(mags[Math.floor(mags.length * 0.1)] ?? 1, 1e-9);
    const L = (x: number) => Math.log1p(Math.abs(x) / c);
    const [ln, lp] = [L(lo), L(hi)];
    const at = (x: number) => (x < 0 ? 0.5 - (0.5 * L(x)) / ln : 0.5 + (0.5 * L(x)) / lp);
    return { at, ticks: [lo, lo / 10, 0, hi / 10, hi].filter((t, i, a) => a.indexOf(t) === i), note: '零在正中,两边按对数', div: true };
  }
  const pos = vals.filter((x) => x > 0);
  if (pos.length && pos[0] > 0 && hi / pos[0] >= 20) {
    const [a, b] = [Math.log10(pos[0]), Math.log10(hi)];
    const at = (x: number) => (x <= 0 ? 0 : (Math.log10(x) - a) / (b - a));
    // 刻度:每个十倍一个;不到三个十倍的再加 2、5 倍
    const ticks: number[] = [pos[0]];
    const steps = b - a < 3 ? [1, 2, 5] : [1];
    for (let e = Math.floor(a); e <= Math.ceil(b); e++) {
      for (const k of steps) {
        const t = k * 10 ** e;
        if (t > pos[0] * 1.3 && t < hi / 1.3) ticks.push(t);
      }
    }
    ticks.push(hi);
    const keep = ticks.length > 7 ? ticks.filter((_, i) => i === 0 || i === ticks.length - 1 || i % Math.ceil(ticks.length / 6) === 0) : ticks;
    return { at, ticks: keep, note: '对数刻度', div: false };
  }
  const at = (x: number) => (x - lo) / (hi - lo);
  return { at, ticks: [0, 0.25, 0.5, 0.75, 1].map((q) => lo + q * (hi - lo)), note: '', div: false };
}

/** 流水分到三块(和现金流量表一样):经营、投资(买卖资产)、筹资;k 是 eco::Tag 的编号。 */
/** 把 to 的样子套到 from 上:只改不一样的节点,滚动位置、焦点、展开都留着(整块重画会跳)。 */
function morph(from: Node, to: Node) {
  if (from.nodeType !== to.nodeType || from.nodeName !== to.nodeName) {
    from.parentNode!.replaceChild(to, from);
    return;
  }
  if (from.nodeType !== 1) {
    if (from.nodeValue !== to.nodeValue) from.nodeValue = to.nodeValue;
    return;
  }
  const a = from as Element, b = to as Element;
  // 图上看数的竖线和数值框由鼠标管;画布的大小由它自己管(去掉 width、height 会清掉画面)
  if (a.classList.contains('hov') || a.classList.contains('sb-tip') || a.nodeName === 'CANVAS') return;
  for (const at of [...a.attributes]) if (!b.hasAttribute(at.name)) a.removeAttribute(at.name);
  for (const at of [...b.attributes]) if (a.getAttribute(at.name) !== at.value) a.setAttribute(at.name, at.value);
  if (a instanceof HTMLInputElement) {
    // 填金额的格子(data-amt):新画的没带值,别把正在填、填了还没点的数刷掉
    const typed = a.hasAttribute('data-amt') && !b.hasAttribute('value');
    // 时间轴保留焦点后仍须跟随当前日；按住拖动时保留手上的位置。
    const timeline = a.hasAttribute('data-equity-time') && !a.matches(':active');
    if (!typed && (a !== document.activeElement || timeline) && a.value !== (b as HTMLInputElement).value) a.value = (b as HTMLInputElement).value;
    return;
  }
  morphChildren(a, b);
}
function morphChildren(a: Node, b: Node) {
  const fc = [...a.childNodes], tc = [...b.childNodes];
  if (a.nodeType === 1 && (a as Element).hasAttribute('data-equity-rank-rows')) {
    const ownerKey = (n: Node) => n.nodeType === 1 ? (n as Element).getAttribute('data-equity-rank-owner') : null;
    const byOwner = new Map(fc.flatMap(n => { const key = ownerKey(n); return key ? [[key, n] as const] : []; }));
    const kept = new Set<Node>();
    for (const n of tc) {
      const key = ownerKey(n), old = key ? byOwner.get(key) : null;
      const entryNode = old && old.nodeName === n.nodeName ? old : n;
      if (entryNode === old) morph(entryNode, n);
      a.appendChild(entryNode); kept.add(entryNode);
    }
    for (const n of fc) if (!kept.has(n)) a.removeChild(n);
    return;
  }
  tc.forEach((n, i) => (i < fc.length ? morph(fc[i], n) : a.appendChild(n)));
  for (let i = tc.length; i < fc.length; i++) a.removeChild(fc[i]);
}
function patch(el: HTMLElement, html: string) {
  const t = document.createElement(el.tagName);
  t.innerHTML = html;
  morphChildren(el, t);
}

async function main() {
  // 这几句放在最前面:下面 PENDING 到开场画面那一段会被测试原样切出来跑(sbquery-performance、sbcity-push 等),那里没有 Section、window
  Section.tweenMs = 900; // 单机版一天过得快:新的一天的样子一秒内长出来
  keepOldSave(); // 别的版本存的玩家存档先原样另存,之后怎么开局都丢不了
  // 读档自检的结果(无头浏览器验收读,loadCheck 见下)
  (window as unknown as { sbLoadCheck: () => unknown }).sbLoadCheck = () => loadCheck;
  // 无头浏览器试玩:一块地在画面上的哪(按它点地图)、眼下城里的地块
  (window as unknown as { sbScreenOf: (id: number) => unknown }).sbScreenOf = (id: number) => {
    const r = canvas.getBoundingClientRect();
    const at = map.screenOf(id);
    return at ? [Math.round(at[0] + r.left), Math.round(at[1] + r.top)] : null;
  };
  (window as unknown as { sbPlots: () => unknown }).sbPlots = () => city?.plots ?? [];
  (window as unknown as { sbFocus: (id: number) => void }).sbFocus = (id: number) => map.focus(id);
  const splash = document.getElementById('splash');
  const splashBox = splash?.querySelector('.sb-splash-in') ?? splash;
  const bootMessage = splash?.querySelector<HTMLElement>('span') ?? document.createElement('span');
  const bootDetail = document.createElement('p');
  bootDetail.setAttribute('role', 'status');
  bootDetail.setAttribute('aria-live', 'polite');
  const bootProgress = document.createElement('progress');
  bootProgress.max = 1;
  bootProgress.value = 0;
  bootProgress.hidden = true;
  bootProgress.setAttribute('aria-label', '城市预热进度');
  const stopSkip = document.createElement('button');
  stopSkip.type = 'button';
  stopSkip.className = 'btn primary';
  stopSkip.textContent = '先玩到这里';
  stopSkip.hidden = true;
  const bootNote = document.createElement('p');
  const recovery = document.createElement('a');
  recovery.className = 'btn small';
  recovery.href = `${location.origin}${location.pathname}?seed=1&years=0&speed=0`;
  recovery.textContent = '从开局重新进入（不预热）';
  if (!bootMessage.parentNode) splashBox?.appendChild(bootMessage);
  splashBox?.append(bootDetail, bootProgress, stopSkip, bootNote, recovery);
  let bootFinished = false;
  let bootFailed = false;
  let stopRequested = false;
  let bootDone = 0;
  let bootTarget = 0;
  let splashTimer: ReturnType<typeof setTimeout> | null = null;
  let clearCityPushes: () => void = () => {}; // Worker构造失败时队列尚未初始化。
  const bootFailure = (reason: unknown) => {
    clearCityPushes();
    const message = reason instanceof Error ? reason.message : String(reason ?? '引擎未能加载');
    if (bootFinished) {
      runtimeFailed = true;
      speed = realSpeed = 0;
      actionPending = null;
      clearQueryRedraws(); asking.clear(); inflight.clear();
      const notice = document.getElementById('engineAlert');
      if (notice) {
        notice.hidden = false;
        notice.innerHTML = runtimeFailureHTML(seed, cache.get('city')?.day ?? engineDay, message);
      }
      top();
      return;
    }
    bootFailed = true;
    bootMessage.textContent = '城市暂时没能启动';
    bootDetail.textContent = `模拟引擎加载或运行失败：${message.slice(0, 180)}`;
    bootNote.textContent = '可用下方入口不预热、从开局重新生成城市；这不是读取已保存的进度。';
    bootProgress.hidden = stopSkip.hidden = true;
    splash?.querySelector<HTMLElement>('.sb-load')?.setAttribute('hidden', '');
    splash?.classList.remove('gone');
  };
  const bootUpdate = (done: number, total: number, timing?: J, rate = 0) => {
    if (bootFinished || bootFailed || !Number.isFinite(done) || !Number.isFinite(total)) return;
    bootDone = Math.max(0, Math.floor(done));
    if (!stopRequested) bootTarget = Math.max(0, Math.floor(total));
    bootProgress.max = Math.max(1, bootTarget);
    bootProgress.value = Math.min(bootDone, bootTarget);
    bootProgress.hidden = bootTarget <= 0;
    stopSkip.hidden = bootTarget <= 0 || !!playMode; // 读档要重放到存档那天,中途停下来指令就对不上了
    bootMessage.textContent = stopRequested ? '正在进入当前城市' : bootTarget > 0 ? '正在预热城市' : '正在生成城市';
    // 还剩几天、照眼下一秒推几天估还要几秒
    const left = Math.max(0, bootTarget - bootDone);
    const eta = rate > 0 && left > 0 ? ` · 还剩 ${left.toLocaleString()} 天,约 ${Math.max(1, Math.ceil(left / rate))} 秒` : '';
    bootDetail.textContent = bootTarget > 0
      ? `已推演 ${bootDone.toLocaleString()} 天 / 目标 ${bootTarget.toLocaleString()} 天（${Math.min(100, bootDone / bootTarget * 100).toFixed(1)}%）· ${dateOf(bootDone)}${eta}`
      : '正在加载引擎并准备开局';
    bootProgress.setAttribute('aria-valuetext', bootDetail.textContent);
    const measured = bootTimingText(timing);
    if (measured) bootDetail.textContent += `\n${measured}`;
  };
  // ---------- 引擎:在另一个线程里跑(sbworker.ts),这边按键要数据、放在缓存里 ----------
  // 画面照旧用 read(E.xxx()) 取数据:缓存里有就用;没有就抛 PENDING,这一块先不画,
  // 向引擎要,到了再画一遍(见 guard)。倍速跑着的时候每秒把用到的数据整批刷新一次(见下面的 frame)。
  let worker: Worker;
  // 网址里的 mt(1 强制试多线程版引擎、0 强制单线程版;不给的桌面默认多线程、手机单线程)传给模拟线程,见 sbworker.ts
  const mt = new URLSearchParams(location.search).get('mt');
  // 手机告诉引擎线程(多线程版默认不在手机上开,见 sbworker.ts 的 mtWanted)
  const workerQuery = new URLSearchParams();
  if (mt === '0' || mt === '1') workerQuery.set('mt', mt);
  if (mobileDevice(navigator as Parameters<typeof mobileDevice>[0])) workerQuery.set('mobile', '1');
  // 发布时 index.html 给 app.js 带上版本号(?v=,见 scripts/ops/release.sh):往下传给引擎线程,它再传给帮手线程和计算助手。
  // 地址带版本号,浏览器和 CDN 就不会拿旧的脚本配新的引擎(Cloudflare 会把 .js 缓存四个钟头,不管源站的 no-cache)
  const build = new URL(import.meta.url).searchParams.get('v');
  if (build) workerQuery.set('v', build);
  try { worker = new Worker(workerQuery.size ? `worker.js?${workerQuery}` : 'worker.js'); }
  catch (e) { bootFailure(e); return; }
  worker.onerror = (e) => { if (!bootFinished) e.preventDefault(); bootFailure(e.message || '模拟线程发生异常'); };
  worker.onmessageerror = () => bootFailure('无法读取模拟线程的数据');
  const PENDING = new Error('等引擎的数据');
  let runtimeFailed = false;
  const cache = new Map<string, { day: number; raw: string }>();
  const cacheCity = (day: number, raw: string) => {
    // 查询与主动推送共用city键:迟到的旧日查询不能把待显示的最新地图倒退。
    if (day >= (cache.get('city')?.day ?? -1)) cache.set('city', { day, raw });
  };
  let engineDay = 0;
  let gen = 0; // 第几座城:换了城,上一座城的回信不要
  // 地图推送raw始终保留最新值,仅合并运行中尚未显示的refresh,不合并模拟/归档。
  let cityPushEpoch = 0;
  let cityPushFrame: number | null = null;
  let pendingCityPush: { gen: number } | null = null;
  let cityNeedsImmediate = true;
  let measureCityPushes = false;
  const cityPushPerf = { received: 0, applied: 0, coalesced: 0, cancelled: 0, parsed: 0, parseMs: 0, maxParseMs: 0 };
  const takeCityPush = () => {
    cityPushEpoch++;
    if (cityPushFrame != null) cancelAnimationFrame(cityPushFrame);
    cityPushFrame = null;
    const pending = pendingCityPush; pendingCityPush = null;
    return pending;
  };
  clearCityPushes = () => { if (takeCityPush()) cityPushPerf.cancelled++; };
  const flushCityPush = () => {
    cityNeedsImmediate = true;
    const pending = takeCityPush();
    if (!pending) return;
    if (pending.gen !== gen || runtimeFailed || bootFailed) { cityPushPerf.cancelled++; return; }
    cityPushPerf.applied++; onCity();
  };
  const refreshCityPush = () => {
    if (!bootFinished || speed <= 0 || actionPending != null || cityNeedsImmediate || archiveState.state === 'blocked') {
      clearCityPushes(); cityNeedsImmediate = false;
      cityPushPerf.applied++; onCity();
      return;
    }
    if (pendingCityPush) { cityPushPerf.coalesced++; return; }
    pendingCityPush = { gen };
    const epoch = cityPushEpoch, cityGen = gen;
    cityPushFrame = requestAnimationFrame(() => {
      if (epoch !== cityPushEpoch || cityGen !== gen || runtimeFailed || bootFailed) return;
      cityPushFrame = null; pendingCityPush = null;
      // cache可已被同城更新的查询刷新;此处不拿旧push覆盖它。
      cityPushPerf.applied++; onCity();
    });
  };
  let queryEpoch = 0;
  let queryFrame: number | null = null;
  const queryDraws = new Set<() => void>();
  const queryPerf = { ms: 0, draws: 0, maxMs: 0 };
  const clearQueryRedraws = () => {
    queryEpoch++;
    queryDraws.clear();
    if (queryFrame != null) cancelAnimationFrame(queryFrame);
    queryFrame = null;
  };
  // 同帧的回信只重画一次当前面板;地图等独立任务仍各自执行一次。
  const queueQueryRedraw = (draw: () => void) => {
    queryDraws.add(draw);
    if (queryFrame != null) return;
    const epoch = queryEpoch, cityGen = gen;
    queryFrame = requestAnimationFrame(() => {
      if (epoch !== queryEpoch || cityGen !== gen) return;
      queryFrame = null;
      const jobs = [...queryDraws]; queryDraws.clear();
      for (const job of jobs) {
        const t0 = performance.now();
        try { job(); } finally {
          const ms = performance.now() - t0;
          queryPerf.ms += ms; queryPerf.draws++; queryPerf.maxMs = Math.max(queryPerf.maxMs, ms);
        }
      }
    });
  };
  let drawQueryPanels: () => void = () => {};
  const requestQueryPanels = () => queueQueryRedraw(drawQueryPanels);
  const used = new Set<string>(); // 上次刷新以后画面用到的键
  let missing = new Set<string>(); // 这一块要的、缓存里没有的
  let stale = new Set<string>(); // 暂停的时候:缓存里的是前几天的,要新的
  let collectingReads: Set<string> | null = null;
  const queryObservers = new Set<(keys: Set<string>) => void>();
  const keyFn = (name: string) => (...args: number[]) => args.length ? `${name}:${args.join(':')}` : name;
  const ENGINE_FNS = ['city', 'plot', 'person', 'firm', 'plot2', 'person2', 'firm2', 'ownership_owners', 'ownership', 'ownership_rank', 'project2', 'unit2', 'hospital2', 'school2', 'schoolstudents', 'life2', 'context2', 'market2', 'series', 'markets', 'firms', 'people', 'bank', 'gov', 'estate', 'complexes', 'deals', 'news',
    'media', 'society', 'medical_deaths', 'case2', 'cases', 'names', 'court', 'construction', 'regions', 'mapmode', 'flows', 'insure', 'plotlog', 'plotrec', 'bldrec', 'gdp', 'econ', 'spend', 'cbd', 'me', 'mejobs', 'mehomes', 'melines', 'mehere', 'mesale', 'merent', 'meinvest', 'mecandidates', 'metenders', 'meshares', 'peek', 'mewho', 'mesingles', 'mesite', 'meceos', 'mestocks', 'mestockmarket', 'mestock', 'meholdings', 'mecases', 'mecivic', 'mepremises', 'mebiz', 'meland', 'mecredit', 'megov', 'meipos', 'meipo_on'];
  const makeE = (names: string[]) => Object.fromEntries(names.filter((n) => ENGINE_FNS.includes(n)).map((n) => [n, keyFn(n)])) as unknown as Engine;
  // 引擎还没好的时候先当什么都有(要了也是等着)
  let E = makeE(ENGINE_FNS);
  const read = (k: string): J => {
    used.add(k);
    collectingReads?.add(k);
    const e = cache.get(k);
    if (!e) {
      missing.add(k);
      throw PENDING;
    }
    if (speed === 0 && e.day !== engineDay) stale.add(k);
    const measured = k === 'city' && measureCityPushes;
    const started = measured ? performance.now() : 0;
    try {
      const value = JSON.parse(e.raw);
      if (k === 'city') cityPushPerf.parsed++;
      return value;
    } finally {
      if (measured) { const ms = performance.now() - started; cityPushPerf.parseMs += ms; cityPushPerf.maxParseMs = Math.max(cityPushPerf.maxParseMs, ms); }
    }
  };
  // 要数据:同一个键已经在要的不再要;到了以后跑 then
  let qid = 0;
  const asking = new Map<number, Set<() => void>>();
  const inflight = new Map<string, number>();
  const ask = (keys: string[], then?: () => void) => {
    if (runtimeFailed) return;
    const requested = new Set(keys);
    for (const notify of queryObservers) notify(requested);
    const ids = new Set<number>();
    const fresh: string[] = [];
    for (const k of new Set(keys)) {
      const id = inflight.get(k);
      if (id != null) ids.add(id);
      else fresh.push(k);
    }
    if (fresh.length) {
      const id = ++qid;
      asking.set(id, new Set());
      for (const k of fresh) inflight.set(k, id);
      worker.postMessage({ t: 'q', gen, id, keys: fresh });
      ids.add(id);
    }
    if (then) for (const id of ids) asking.get(id)?.add(then);
  };
  /** 画一块(顶栏、看板、查看器……):缺数据就先不画,要来了再画;暂停时用的是前几天的,也要新的再画。 */
  type PanelScope = () => { host: HTMLElement; context: PanelReadContext } | null;
  const guard = <A extends unknown[]>(draw: (...a: A) => void, panel?: PanelScope, isolated = false) => {
    let dependencies = new Set<string>();
    let latestArgs: A;
    const drawAgain = () => again(...latestArgs);
    const retry = () => queueQueryRedraw(drawAgain);
    let lastHost: HTMLElement | null = null;
    const state = panel ? createPanelReadState(view => {
      const scope = panel();
      if (view.context && scope?.context.key !== view.context.key) return;
      const host = scope?.host ?? lastHost;
      if (host) { lastHost = host; applyPanelReadView(host, view); }
    }) : null;
    if (panel && state) queryObservers.add(keys => {
      const scope = panel();
      if (scope && [...dependencies].some(key => keys.has(key))) state.pending(scope.context);
    });
    const again = (...a: A) => {
      latestArgs = a;
      const [m0, s0] = [missing, stale];
      const previousReads = collectingReads;
      const reads = new Set<string>(); collectingReads = reads;
      missing = new Set();
      stale = new Set();
      let waiting = false;
      state?.select(panel?.()?.context ?? null);
      try {
        draw(...a);
      } catch (e) {
        if (e !== PENDING) {
          [missing, stale] = [m0, s0];
          throw e;
        }
        waiting = true;
      } finally {
        collectingReads = previousReads;
        for (const key of reads) previousReads?.add(key);
      }
      const need = [...missing, ...stale];
      [missing, stale] = [m0, s0];
      if (state) {
        dependencies = reads;
        const scope = panel!();
        state.select(scope?.context ?? null); // 地图可能在本次读取中从地块解析为医院/楼栋。
        if (scope) {
          if (waiting || need.length) state.pending(scope.context);
          else state.ready(scope.context, [...reads].some(key => inflight.has(key)));
        }
      }
      if (need.length) ask(need, isolated ? retry : requestQueryPanels);
    };
    return again;
  };
  let onCity: () => void = () => {};
  let onReady: () => void = () => {};
  // Assigned after UI setup. Pure protocol fixtures keep a no-op observer.
  let monitorMessage: (message: J) => void = () => {};
  let monitorStart: (input: { gen: number; seed: number; mode: 'city' | 'player'; playerKind?: number; enterDay?: number; replay?: boolean }) => void = () => {};
  let pendingReplay: ReplayReceipt | null = null;
  let archiveState: J = { state: 'opening', stored: 0, message: '正在打开本城历史存档' };
  let actionPending: number | null = null;
  let actionSeq = 0;
  // 玩家模式(见 sbplayer.ts):开局身份、指令记录;null 是看城市
  // rules、bits:这局开局时的规则版本、要置上的开关(读档照那套重放,见 sbplayer.ts 的 RULES)
  let playMode: { kind: number; log: PlayerCmd[]; until?: number; name: string; enter?: number; rules: number; bits: number[] } | null = null;
  let enterYears = 4; // 开始页选的进城时间(默认第 5 年)
  let meTab = 'work'; // 侧栏开着的那一页
  let meList: string | null = null; // 展开着的单子:jobs 岗位、homes 空房、lines 能开的公司
  let herePlot: number | null = null; // 地图上点的那块地(「在这里能做什么」)
  let hereAt: { x: number; y: number } | null = null; // 点在屏幕上的哪(小卡就地弹)
  let hereFull = false; // 小卡展开了详情
  let hereType: string | null = null; // 小卡里展开了哪一种楼型的盖楼方案
  // 要拍板的面板打开就暂停,关掉接着走(找房、地图小卡)
  let heldSpeed = 0;
  const hold = (on: boolean) => {
    if (on && speed > 0) { heldSpeed = speed; speed = 0; flushCityPush(); top(); }
    else if (!on && heldSpeed > 0 && herePlot == null && !hunting && whoId == null && siteId == null && !stocksOpen && !bizOpen && !landOpen && !creditOpen && !govOpen) { speed = heldSpeed; heldSpeed = 0; top(); }
  };
  let whoId: number | null = null; // 「对这个人做什么」开着的是谁
  let assetLayer = false; // 「我的资产」图层开着
  let siteId: number | null = null; // 开着的工地页
  let siteFloor: number | null = null; // 工地页选中的那一层(没选的按最低的还没开工的)
  let siteSec: Section | null = null; // 工地剖面(画在工地页的画布上)
  let siteCanvas: HTMLCanvasElement | null = null;
  let stocksOpen = false; // 股市页开着
  const stocksScroll = new Map<string, number>(); // 股市页每一页滚到哪(行情、每家公司的详情各记各的)
  let bizOpen = false; // 生意页开着(自己管着的那家公司)
  let landOpen = false; // 房东页开着
  let creditOpen = false; // 失信页开着
  let govOpen = false; // 官职页开着
  const stk: StockState = { tab: 'market', firm: null, sort: 'cap', dir: -1, line: '', district: '', mineOnly: false, range: 'day', kz: 0, ko: 0, redUp: loadRedUp(), ipo: { sort: 'closed', dir: -1, firm: null } };
  let cmdQueue: [number, number, number, string][] = [];
  let resumeAfter = false; // 暂停卡上选的指令办成了以后接着走 // 一下要办几条的(挂单的股数、价),办完一条接着下一条
  let assetSpots: Map<number, AssetSpot> = new Map(); // 名下的东西按地块收拢(鼠标停上去的一行字用)
  const dates: DateState = { sort: 'score', only: 'all' }; // 找对象的筛选和排序
  let hunting = false; // 找房面板开着
  const hunt: HuntState = { show: 'all', sort: 'rent', max: 0, layer: false };
  let huntLayer: (string | null)[] | null = null; // 找房图层的颜色(按地块)
  let firmPlace = 0xffff_ffff; // 开公司开在哪(0xffff_ffff 让引擎挑)
  let verifyLoad: { day: number; print: string } | null = null; // 读档:重放到存档那天,和存档时的指纹比
  let loadT0 = performance.now(); // 读档、开局从哪一刻起算(量读档要多久)
  let forceLoad: { from: string; print?: string; day: number } | null = null; // 旧版本的存档按新版本读(读完弹卡说执行了几条、驳回几条)
  let loadCheck: { day: number; print: string; same: boolean } | null = null; // 上一次读档自检的结果(无头浏览器验收读)
  let pcmdPending: number | null = null;
  let pcmdLabel = ''; // 正在办或排着的指令(按钮上的字)
  let pcmdAt = 0; // 点下去的时刻(量多快看到结果)
  const sendCmd = (code: number, target: number, value: number, label: string) => {
    if (runtimeFailed || pcmdPending != null || !playMode) return;
    if (actionSeq >= 0xffff_ffff) { toast(document.getElementById('toasts')!, '<p>本城操作编号已用尽，请先导出进度。</p>', ''); return; }
    const id = ++actionSeq; pcmdPending = id; pcmdLabel = label; pcmdAt = performance.now();
    worker.postMessage({ t: 'pcmd', gen, id, code, target, value });
    mePanel();
  };
  /** 暂停卡看过了(点了「知道了」或者在卡上选了):记下看到哪,接着走。 */
  const resumeFromStop = () => {
    ackSeq = Math.max(stopSeq, lastAlertSeq);
    worker.postMessage({ t: 'pack', gen, seq: stopSeq });
    speed = lastSpeed || 1; top();
  };
  let stopSeq = 0; // 引擎报的最近一件要停下来的事
  let ackSeq = 0; // 玩家看过的
  let saveDay = -1;
  let lastPrint: { day: number; print: string } | null = null; // 引擎线程推地图时带的这一天的城的指纹
  const savePlay = () => {
    if (!playMode) return;
    const day = cache.get('city')?.day ?? engineDay;
    // 同一天的指纹才记;没有就不记(读档照样重放,只是不自检)
    const print = lastPrint?.day === day ? lastPrint.print : undefined;
    const ok = writeSave({ v: 1, engine: ENGINE_VERSION, seed, kind: playMode.kind, log: playMode.log, day, name: playMode.name, savedAt: Date.now(), print, enter: playMode.enter ?? 0, rules: playMode.rules, bits: playMode.bits });
    if (ok) saveDay = day; // A refused storage write must remain eligible for retry.
    monitorMessage({ t: 'legacySave', gen, day, ok });
  };
  let exportDownloads: ReturnType<typeof createExportDownloads> | null = null;
  type Cursor = { hi: number; lo: number };
  type HistoryState = { before?: Cursor; stack: (Cursor | undefined)[]; page: J | null; pending: number | null };
  const histories = new Map<string, HistoryState>();
  const historyRequests = new Map<number, string>();
  let updateArchive: () => void = () => {};
  const wk = { stepMs: 0, stepDays: 0, parallel: null as J, timing: null as J };
  worker.onmessage = (ev: MessageEvent<J>) => {
    const m = ev.data;
    if (m.gen !== gen) return;
    try { monitorMessage(m); } catch { /* Monitoring must never stop the game protocol. */ }
    if (m.t === 'archive') {
      // 中途打开完整历史:旧记录写完以后,已显示的「未开启」「正在保存」页换成真的历史页
      if ((archiveState.state === 'off' || archiveState.backfill > 0) && m.state !== 'off' && !(m.backfill > 0)) { histories.clear(); requestQueryPanels(); }
      archiveState = m;
      if (m.state === 'blocked') { speed = 0; flushCityPush(); worker.postMessage({ t: 'speed', gen, v: 0 }); }
      updateArchive();
      if (bootFinished && tab === 'engine') dashboard();
    } else if (m.t === 'acted') {
      if (actionPending !== m.id) return;
      clearCityPushes(); cityNeedsImmediate = true;
      actionPending = null;
      const result = JSON.parse(m.raw);
      cache.clear();
      // 已在行动前发出的旧查询不能覆盖行动后的新状态。
      inflight.clear(); asking.clear();
      clearQueryRedraws();
      toast(document.getElementById('toasts')!, `<p>居民行动：${esc(result.reason ?? '已处理')}</p>`, '');
      panels();
    } else if (m.t === 'pcmded') {
      if (pcmdPending !== m.id) return;
      pcmdPending = null; pcmdLabel = '';
      clearCityPushes(); cityNeedsImmediate = true;
      const result = JSON.parse(m.raw);
      // 没办成的也记进存档:有的「没办成」是对方真想过了(出价租房、还价,对方心里的价抽了一个),读档重放要一样
      if (playMode) { playMode.log.push(m.cmd); lastPrint = null; savePlay(); }
      // 只丢掉「我」的那几样和地图(别的看板第二天再刷);「我」的新数据随回信一起到,不再等一来一回
      for (const k of [...cache.keys()]) if (k === 'city' || /^me[a-z]*($|:)/.test(k)) cache.delete(k);
      // 投票、报名、挑人、市长拨杆:政府页(市长、官员、选举)当场换成新的
      if (m.cmd && m.cmd.code >= 293 && m.cmd.code <= 299) cache.delete('gov');
      inflight.clear(); asking.clear();
      clearQueryRedraws();
      if (m.me) cache.set('me', { day: m.day, raw: m.me });
      // 因为失信没办成的:带一个按钮去失信页(为什么、怎么出来)
      const why = !result.ok && /失信/.test(result.reason ?? '') ? ' <button class="sb-sd-btn" data-credit="open">失信页</button>' : '';
      toast(document.getElementById('toasts')!, `<p>${result.ok ? '' : '没办成:'}${esc(result.reason ?? '')}${why}</p>`, '');
      mePanel();
      panels();
      // 量一下:点下去到看见结果
      requestAnimationFrame(() => { (window as unknown as { sbCmdMs: number }).sbCmdMs = Math.round(performance.now() - pcmdAt); });
      // 暂停卡上选的办成了、卡上没有别的要选的:接着走;没办成的停着,能换一个选
      if (resumeAfter) {
        resumeAfter = false;
        if (result.ok) resumeFromStop();
      }
      // 一下要办几条的,接着办下一条(前一条没办成就不办了)
      if (cmdQueue.length) {
        if (result.ok) { const [c, t, v, l] = cmdQueue.shift()!; sendCmd(c, t, v, l); } else cmdQueue = [];
      }
    } else if (m.t === 'pstop') {
      stopSeq = Math.max(stopSeq, m.seq);
      if (speed) lastSpeed = speed;
      speed = 0;
      flushCityPush();
      cache.delete('me');
      panels();
    } else if (m.t === 'exported') {
      wk.timing = m.timing ?? wk.timing;
      void exportDownloads?.receive(m);
    } else if (m.t === 'historyResult') {
      wk.timing = m.timing ?? wk.timing;
      const key = historyRequests.get(m.id); historyRequests.delete(m.id);
      const h = key ? histories.get(key) : null;
      if (!h || h.pending !== m.id) return;
      h.pending = null; h.page = m; requestQueryPanels();
    } else if (m.t === 'progress') {
      wk.timing = m.timing ?? wk.timing;
      bootUpdate(m.done, m.total, m.timing, m.rate);
      (window as unknown as { sbBootTiming: J }).sbBootTiming = m.timing;
    } else if (m.t === 'error') {
      wk.timing = m.timing ?? wk.timing;
      bootFailure(m.message);
    } else if (m.t === 'ready') {
      if (runtimeFailed || bootFailed) return;
      clearCityPushes(); cityNeedsImmediate = true;
      E = makeE(m.exports);
      clearQueryRedraws();
      cache.clear();
      engineDay = m.day;
      if (!bootFailed) { bootMessage.textContent = '城市已生成，正在准备画面'; stopSkip.disabled = true; }
      (window as unknown as { sbLoadMs: number }).sbLoadMs = Math.round(performance.now() - loadT0);
      ackSeq = stopSeq = m.seq ?? 0;
      if (forceLoad) {
        // 旧版本的存档按新版本读完:城不一样是意料之中,指纹只写一句;弹卡说执行了几条、驳回几条
        const me = m.me ? JSON.parse(m.me) : null;
        const same = forceLoad.print && me ? me.day === forceLoad.day && me.print === forceLoad.print : null;
        const card = document.createElement('div');
        card.className = 'sb-load-wrap';
        card.innerHTML = forceLoadHTML(forceLoad.from, m.replay ?? null, same);
        document.body.appendChild(card);
        if (card.firstElementChild) makeWin(card.firstElementChild as HTMLElement, { kind: 'load', handle: '.sb-load-card', top: true });
        forceLoad = null;
        verifyLoad = null;
      }
      if (verifyLoad && m.me) {
        const me = JSON.parse(m.me);
        const same = me.day === verifyLoad.day && me.print === verifyLoad.print;
        loadCheck = { day: me.day, print: me.print, same };
        toast(document.getElementById('toasts')!, `<p>${same ? `读档完成:${esc(dateOf(me.day))},和存档时的城一致` : '读档完成,但和存档时的城不一致'}</p>`, '');
        verifyLoad = null;
      }
      if (playMode && m.player) {
        const started = JSON.parse(m.player);
        if (started.ok) { playMode.name = started.name; savePlay(); }
        else toast(document.getElementById('toasts')!, `<p>${esc(started.reason)}</p>`, '');
      }
      onReady();
    } else if (m.t === 'city') {
      if (runtimeFailed || bootFailed) return;
      cityPushPerf.received++;
      engineDay = Math.max(engineDay, m.day);
      cacheCity(m.day, m.raw);
      if (m.print) lastPrint = { day: m.day, print: m.print };
      realSpeed = m.real;
      wk.stepMs = m.stepMs;
      wk.stepDays = m.stepDays;
      wk.parallel = m.parallel ?? wk.parallel;
      wk.timing = m.timing ?? wk.timing;
      archiveState = m.archive ?? archiveState;
      if (archiveState.state === 'blocked') { speed = 0; worker.postMessage({ t: 'speed', gen, v: 0 }); }
      updateArchive();
      refreshCityPush();
      if (playMode && m.day - saveDay >= 10) savePlay();
    } else if (m.t === 'r') {
      if (!asking.has(m.id)) return;
      wk.timing = m.timing ?? wk.timing;
      engineDay = Math.max(engineDay, m.day);
      for (const [k, raw] of m.data as [string, string][]) {
        if (k === 'city') cacheCity(m.day, raw);
        else cache.set(k, { day: m.day, raw });
        if (inflight.get(k) === m.id) inflight.delete(k);
      }
      const then = asking.get(m.id);
      asking.delete(m.id);
      for (const f of then ?? []) f();
    }
  };

  // 开场画面(index.html 里)先留着,城市生成完再淡出
  document.body.insertAdjacentHTML('afterbegin', `
    <div class="map"><canvas id="city"></canvas></div>
    <header class="sb-top" id="top"></header>
    <nav class="sb-rail" id="rail">${TABS.map(([k, t]) => `<button data-tab="${k}" title="${t}">${icon(k, 20)}<span>${t}</span></button>${k === 'finance' ? `<button data-stk="toggle" title="股市">${icon('stocks', 20)}<span>股市</span></button>` : ''}`).join('')}<hr><button class="mapbtn" data-maps title="地图模式(M)">${icon('maps', 20)}<span>地图</span></button></nav>
    <section class="sb-dash" id="dash" hidden></section>
    <aside class="sb-insp" id="insp" hidden></aside>
    <aside class="sb-side" id="me" hidden></aside>
    <section class="sb-here" id="here" hidden></section>
    <section class="sb-here sb-pa" id="who" hidden></section>
    <section class="sb-here sb-pa" id="site" hidden></section>
    <section class="sb-here sb-pa" id="biz" hidden></section>
    <section class="sb-here sb-pa" id="land" hidden></section>
    <section class="sb-here sb-pa" id="credit" hidden></section>
    <section class="sb-here sb-pa" id="govp" hidden></section>
    <section class="sb-hunt sb-stocks" id="stocks" hidden></section>
    <section class="sb-hunt" id="hunt" hidden></section>
    <div class="sb-stop" id="stop" hidden></div>
    <div class="sb-modes" id="modes"></div>
    <div class="sb-maptip" id="maptip" hidden></div>
    <div class="sb-toasts" id="toasts"></div>
    <div class="sb-archive-alert" id="archiveAlert" role="status" hidden></div>
    <div class="sb-archive-alert" id="engineAlert" role="alert" hidden></div>
    <section class="sb-download-ready" id="downloadReady" role="status" aria-live="polite" aria-label="已生成的下载文件" hidden></section>
    <div class="sb-help" id="help" hidden><i class="sb-help-bg" data-help></i><div class="sb-help-card">
      <div class="sb-head"><h3>快捷键</h3><button class="sb-x" data-help title="关闭(Esc)">${icon('close', 16)}</button></div>
      <dl><dt><kbd>空格</kbd></dt><dd>暂停、继续</dd><dt><kbd>1</kbd> <kbd>2</kbd> <kbd>3</kbd> <kbd>4</kbd></dt><dd>一秒 1、5、30、120 天</dd>
        <dt><kbd>M</kbd></dt><dd>地图模式</dd><dt><kbd>T</kbd></dt><dd>地铁图层</dd><dt><kbd>H</kbd></dt><dd>楼高</dd><dt><kbd>Esc</kbd></dt><dd>关掉面板</dd><dt><kbd>?</kbd></dt><dd>这张表</dd>
        <dt>拖动 · 滚轮</dt><dd>移动 · 缩放地图</dd><dt>点一块地</dt><dd>这块地上的楼、住户、公司</dd><dt>窗口</dt><dd>拖标题栏挪窗口 · 拖右下角改大小 · 双击标题栏回原位</dd></dl></div></div>`);
  const downloadPanel = createDownloadPanel(document.getElementById('downloadReady')!);
  exportDownloads = createExportDownloads({ currentGen: () => gen, specialText: x => readableExport(x), personSteps: x => lifeTextSteps(x),
    filename: (m, raw, format) => `${String(m.name || m.kind).replace(/[\\/:*?"<>|]/g, '_')}-种子${seed}-第${raw.day ?? engineDay}天.${format}`,
    ready: (blob, filename, format) => {
      const a = downloadPanel.show(blob, filename, format);
      // 原生链接保留；生成成功不冒充已经写入下载目录。
      try { a.click(); } catch { /* 可继续手动点击持久链接。 */ }
    },
    failed: reason => toast(document.getElementById('toasts')!, `<p>下载失败：${esc(reason)}</p>`, ''),
  });
  // 浮出来的窗口都能按住标题栏拖、右下角拉大小,位置和大小按种类记着(见 sbwin.ts)
  {
    const w = (id: string) => document.getElementById(id)!;
    makeWin(w('dash'), { kind: 'dash', handle: '.sb-ph', resize: true, fill: true });
    makeWin(w('insp'), { kind: 'insp', handle: '.sb-ph', resize: true, fill: true });
    makeWin(w('me'), { kind: 'me', handle: '.sb-sd-head', resize: true, fill: true });
    makeWin(w('here'), { kind: 'here', handle: '.sb-here-h', resize: true, onReset: () => mePanel() });
    for (const id of ['who', 'site', 'biz', 'land', 'credit', 'govp']) makeWin(w(id), { kind: id, handle: '.sb-here-h', resize: true });
    for (const id of ['stocks', 'hunt']) makeWin(w(id), { kind: id, handle: '.sb-hunt-h', resize: true, fill: true, headVar: true });
    makeWin(w('modes'), { kind: 'modes', handle: '.sb-head' });
    makeWin(w('stop'), { kind: 'stop', handle: '.sb-stop-card', top: true });
    makeWin(w('help').querySelector<HTMLElement>('.sb-help-card')!, { kind: 'help', handle: '.sb-head', top: true, toggle: w('help') });
    makeWin(w('downloadReady'), { kind: 'download', handle: '.sb-download-ready', top: true });
  }
  window.addEventListener('pagehide', () => savePlay());
  window.addEventListener('pagehide', () => { exportDownloads?.reset(); downloadPanel.clear(); });
  const canvas = document.getElementById('city') as HTMLCanvasElement;
  const q = new URLSearchParams(location.search);
  // 完整历史默认不开:开着每天要多记、多写一批库(见 sbarchive.ts)。网址 history=1/0 优先,否则记住上次的选择
  const HISTORY_KEY = 'sb:history';
  let historyPref = q.has('history') ? q.get('history') === '1' : (() => { try { return localStorage.getItem(HISTORY_KEY) === '1'; } catch { return false; } })();
  const setHistoryPref = (on: boolean) => { historyPref = on; try { localStorage.setItem(HISTORY_KEY, on ? '1' : '0'); } catch { /* 存不了就只管这一座城 */ } };
  const requestedSeed = q.get('seed');
  let seed = requestedSeed != null && requestedSeed.trim() !== '' && Number.isFinite(Number(requestedSeed))
    ? Number(requestedSeed) >>> 0 : 1 + Math.floor(Math.random() * 1e6);
  const requestedYears = Number(q.get('years') ?? 0);
  const validYears = Number.isFinite(requestedYears) && requestedYears >= 0 && Number.isSafeInteger(Math.floor(requestedYears) * 360);
  const skip = validYears ? Math.floor(requestedYears) : 0;
  stopSkip.addEventListener('click', () => {
    if (stopRequested || bootFailed || bootFinished) return;
    stopRequested = true;
    stopSkip.disabled = true;
    stopSkip.textContent = '正在进入当前城市…';
    speed = 0;
    flushCityPush();
    worker.postMessage({ t: 'speed', gen, v: 0 });
    worker.postMessage({ t: 'stopSkip', gen });
    bootUpdate(bootDone, bootTarget);
    bootNote.textContent = '正在结束预热，保留本页已计算的城市；进入后暂停。当前进度没有因此保存为存档。';
  });
  const fixHour = q.has('hour') ? Number(q.get('hour')) : null;
  let tab: string | null = q.get('tab');
  let city: City & { day: number; stats: Stats; ok: boolean; statsDay?: number; statsSource?: 'initial_snapshot' | 'completed_day' };
  let sel: Sel | null = null;
  const trail: Sel[] = [];
  let mapIntent: number | null = null;
  let mode = Number(q.get('mode') ?? -1);
  // 地铁图层:0 普通、1 地铁图、2 不显示(见 city.ts 的 transitMap)
  const TRANSIT = ['普通', '地铁图', '不显示'];
  let transit = Number(q.get('transit') ?? 0) % 3;
  // 楼高:0 立体、1 矮楼(高楼压低)、2 平面(只画占地);见 city.ts 的 setRelief
  const RELIEF = ['立体', '矮楼', '平面'];
  let relief = (Number(q.get('relief') ?? 0) || 0) % 3;
  // 地图的色阶按排名(否则按数值)
  let byRank = q.get('scale') === 'rank';
  // 地图上的站:按格找(鼠标停上去、点开的时候用;城市数据到了重建)
  let stationIdx = new Map<string, { name: string; ks: number[] }>();
  let modesOpen = mode >= 0;
  let modeData: J = null;
  // 公司表:行业、搜索、筛选、排序
  const ft = { kinds: new Set<string>(), q: '', profit: 'all', debt: false, trouble: false, minStaff: 1, col: 4, dir: -1 };
  let peopleQ = '';
  // 别的可排序的表:按表记着排哪一列、正反
  const sorts: Record<string, { col: number; dir: number }> = { people: { col: 2, dir: -1 }, live: { col: 8, dir: 1 }, builders: { col: 1, dir: -1 }, cx: { col: 7, dir: -1 } };
  let newsKind = ''; // 快讯按类筛(空的是全部)
  // 案子查询:种类(刑事、民事、讨薪)、了结没有、搜名字、显示几条;全部案子按天缓存(十天读一次)
  const cb: Record<string, string> = { cat: '', open: '', q: '' };
  let cbShow = 200;
  let caseCache: { day: number; rows: J[] } | null = null;
  /** 按列排序:取值函数返回数或字符串。 */
  const sortRows = <T,>(key: string, list: T[], vals: ((x: T) => number | string)[]) => {
    const st = sorts[key] ?? (sorts[key] = { col: 0, dir: -1 });
    const f = vals[st.col] ?? vals[0];
    return [...list].sort((a, b) => {
      const va = f(a), vb = f(b);
      return (typeof va === 'string' ? String(va).localeCompare(String(vb), 'zh') : (va as number) - (vb as number)) * st.dir;
    });
  };
  const opened = new Set<string>(); // 展开的明细(刷新以后还展开着)
  let hover: { id: string; i: number } | null = null;

  canvas.addEventListener('pointerdown', (e) => { hereAt = { x: e.clientX, y: e.clientY }; });
  const map = new CityMap(canvas, (p: Plot | null) => {
    // 玩家模式:点地图就地弹小卡(在这里能做什么),不打开右边的地块页(小卡里有「地块页」)
    // 小卡开着再点同一块(双击也是):收起小卡,打开这块地的地块页;点别的地,小卡换过去
    if (playMode) {
      if (p && herePlot === p.id) {
        herePlot = null; hereFull = false; hold(false);
        trail.length = 0;
        sel = { kind: 'plot', id: p.id };
        map.select(p.id);
        inspector();
        mePanel();
        return;
      }
      herePlot = p ? p.id : null;
      hereFull = false;
      hold(herePlot != null);
      map.select(herePlot);
      mePanel();
      return;
    }
    // 开着地铁图点到站:直接看站那一页
    if (p && transit === 1 && stationIdx.has(`${p.x},${p.y}`)) sub.set('i:plot', 'station');
    trail.length = 0;
    sel = p ? { kind: 'plot', id: p.id } : null;
    mapIntent = p && !(transit === 1 && stationIdx.has(`${p.x},${p.y}`)) ? p.id : null;
    herePlot = playMode && p ? p.id : null;
    hereFull = false;
    if (playMode) hold(herePlot != null);
    inspector();
    if (playMode) mePanel();
  });

  // ---------- 共用 ----------
  const who = (w: Who | null | undefined) => {
    if (!w) return '—';
    if (w.kind === 'person') return `<a data-go="person:${w.id}">${esc(w.name)}</a>`;
    if (w.kind === 'firm') return `<a data-go="firm:${w.id}">${esc(w.name)}</a>`;
    if (w.kind === 'plot') return `<a data-go="plot:${w.id}">${esc(w.name)}</a>`;
    if (w.kind === 'hospital' || w.kind === 'school') return `<a data-go="${w.kind}:${w.id}">${esc(w.name)}</a>`;
    if (w.kind === 'bank') return `<a data-go="bank:${w.id ?? 0}">${esc(w.name)}</a>`;
    if (w.kind === 'gov') return `<a data-go="gov:0">${esc(w.name)}</a>`;
    if (w.kind === 'region') return `<a data-go="region:${w.id}">${esc(w.name)}</a>`;
    return esc(w.name);
  };
  const plotLink = (plot: number | null | undefined, text: string) => (plot == null ? esc(text) : `<a data-go="plot:${plot}">${esc(text || '(这块地)')}</a>`);
  const projLink = (id: number, text: string) => `<a data-go="project:${id}">${esc(text)}</a>`;
  const unitLink = (id: number, text: string) => `<a data-go="unit:${id}">${esc(text || '(这套房)')}</a>`;
  /** 地块、整栋楼和单套房的入口留在固定头部;只用当前面板已经取到的真实编号。 */
  const propertyNav = (items: { label: string; go?: string; view?: string; page?: string; current?: boolean; title?: string }[]) =>
    E.context2 ? '' : `<nav class="sb-property-nav" aria-label="地块、楼栋和房屋">${items.map((x) => x.current
      ? `<span class="on" aria-current="page">${esc(x.label)}</span>`
      : x.go != null || x.page ? `<a${x.go != null ? ` data-go="${esc(x.go)}"` : ` data-sub="${esc(x.page!)}"`}${x.view ? ` data-view="${esc(x.view)}"` : ''}${x.title ? ` title="${esc(x.title)}"` : ''}>${esc(x.label)}</a>`
      : `<span class="disabled" aria-disabled="true"${x.title ? ` title="${esc(x.title)}"` : ''}>${esc(x.label)}</span>`).join('')}</nav>`;
  const caseLink = (id: number, text: string) => `<a data-go="case:${id}">${esc(text)}</a>`;
  /** 案子到了哪一步的小标签(在逃、拘留、服刑的标红)。 */
  const stageTag = (s: string) => (s ? `<span class="tag${['在逃', '拘留', '服刑', '没讨全', '败诉', '执行中', '执行终结', '终结'].includes(s) ? ' bad' : ''}">${esc(s)}</span>` : '');
  const custodyName = (c: number) => ['', '拘留', '服刑', '取保候审'][c] ?? '';
  const row = (k: string, v: string, cls = '') => `<div class="sb-row"><span>${k}</span><b class="${cls}">${v}</b></div>`;
  const sec = (title: string, body: string, note = '', id = '') => `<section class="sb-sec"${id ? ` id="${id}"` : ''}><h4>${esc(title)}${note ? `<small>${esc(note)}</small>` : ''}</h4>${body}</section>`;
  const grid = (...cells: string[]) => `<div class="sb-grid">${cells.join('')}</div>`;
  const signed = (v: number) => `<span class="${v < 0 ? 'bad' : v > 0 ? 'good' : ''}">${v > 0 ? '+' : ''}${money(v)}</span>`;
  /** 保单一览(见 dash.rs 的 policy_json):保的什么、险种、哪家、保到哪天、这一期、一共交、赔到、出险、条款。 */
  const coverTable = (ps: J[]) => table(['保的', '险种', '保险公司', '保到', '这一期', '一共交', '赔到', '出险', '条款'], ps.map((p) => [
    typeof p.what === 'string' ? esc(p.what) : p.what.project != null ? projLink(p.what.project, p.what.name) : unitLink(p.what.unit, p.what.name),
    esc(p.line) + (p.paying ? ' <span class="tag">在赔</span>' : ''),
    p.by ? who(p.by) : '<span class="meta">没保</span>',
    p.live ? dateOf(p.until) : '<span class="meta">不保了</span>',
    p.prem ? money(p.prem) : '', p.paid ? money(p.paid) : '', p.got ? money(p.got) : '',
    p.claims || p.denied ? `${p.claims} 次${p.denied ? ` · <span class="bad">拒赔 ${p.denied}</span>` : ''}` : '', p.by ? esc(p.terms) : '']));
  /** 一家保险公司的产品(见 dash.rs 的 carrier_json):价、条款、拒保线、保单、收的赔的。 */
  const carrierLines = (c: J) => table(['险种', '费率', '手续费(月)', '条款', '拒保线', '保单', '一天收', '一天赔', '赔付率', '理赔', '拒赔', '拒保'],
    (c.lines as J[]).filter((l) => l.on || l.n || l.took).map((l) => [esc(l.name) + (l.on ? '' : ' <span class="tag">停卖</span>'), esc(l.rate), money(l.fee), esc(l.terms), `风险 ${l.strict.toFixed(1)} 倍`,
      String(l.n), money(l.prem + l.feeDay), money(l.claim), l.lr == null ? '—' : `<span class="${l.lr > 100 ? 'bad' : ''}">${l.lr.toFixed(0)}%</span>`,
      String(l.claims), l.denied ? `${l.denied}${l.frauds ? `(骗保 ${l.frauds})` : ''}` : '', l.refused ? String(l.refused) : '']));
  /** 理赔记录:哪天、哪家、险种、谁、什么事、损失、赔了。 */
  const claimTable = (rows: J[]) => table(['哪天', '保险公司', '险种', '谁', '事', '损失', '赔了'],
    rows.map((x) => [dateOf(x.day), who(x.by), esc(x.line), who(x.who), esc(x.what), money(x.loss), x.paid ? money(x.paid) : '—']));
  /** 一个面板:头(名字、要紧的数)不动,下面分页,每页自己滚。停在哪一页按面板的种类记着。 */
  type Tab = [string, string, () => string];
  const sub = new Map<string, string>();
  const pageLabels = new Map<string, string>();
  for (const x of q.getAll('sub')) {
    const [k, v] = x.split('|');
    sub.set(k, v); // 网址里带 sub=面板|页,打开就停在那一页(截图、分享用)
  }
  const scrolls = new Map<string, number>();
  const paged = (key: string, head: string, list: Tab[]) => {
    const on = list.find(([id]) => id === sub.get(key)) ?? list[0];
    for (const [id, label] of list) pageLabels.set(`${key}|${id}`, label.replace(/\s+\d+$/, ''));
    if (on) pageLabels.set(`${key}|`, on[1].replace(/\s+\d+$/, ''));
    const bar = list.length > 1 ? `<div class="sb-tabs">${list.map(([id, t]) => `<button class="${id === on[0] ? 'on' : ''}" data-sub="${key}|${id}">${esc(t)}</button>`).join('')}</div>` : '';
    // 一页出错只坏这一页,别的页、地图照常动
    let body = '';
    try {
      body = on ? on[2]() : '';
    } catch (e) {
      if (e === PENDING) throw e; // 数据还没到:整块先不画(见 guard)
      body = `<div class="sb-empty">这一页出错:${esc((e as Error)?.message ?? e)}</div>`;
    }
    return `<div class="sb-ph">${head}</div>${bar}<div class="sb-body" data-page="${key}|${on?.[0] ?? ''}">${body}</div>`;
  };
  const toTop = (panel: string) => {
    const b = document.querySelector(`#${panel} .sb-body`) as HTMLElement | null;
    if (b) b.scrollTop = 0;
  };
  // 正文只链接身份唯一的名字;索引随现有批量查询刷新,避免新重名仍沿用旧身份。
  let nameIdx: { gen: number; raw: string; index: NameIndex } | null = null;
  const noNames = buildNameIndex([]);
  const textNames = (): NameIndex => {
    if (!E.names) return noNames;
    const key = E.names();
    const entry = cache.get(key);
    used.add(key);
    if (speed === 0 && entry && entry.day !== engineDay) stale.add(key);
    if (!entry || !nameIdx || nameIdx.gen !== gen || nameIdx.raw !== entry.raw) {
      const list = read(key);
      nameIdx = { gen, raw: entry!.raw, index: buildNameIndex(list) };
    }
    return nameIdx.index;
  };
  const linkify = (text: string, context?: NameIndex): string => text ? linkedText(text, context ?? textNames()) : esc(text);
  /** 玩家看的字:人名、公司、楼能点(linkify),坐标「(横,竖)」点了开地块页,旁边一个小图标让地图飞过去。 */
  const plink = (text: string): string => {
    const w = city?.w ?? 0;
    return linkify(text).replace(/\((\d{1,3}),(\d{1,3})\)/g, (m, x, y) => {
      if (!w) return m;
      const id = Number(y) * w + Number(x);
      return `<a data-go="plot:${id}">${m}</a><button class="sb-loc-i" data-locate="${id}" title="在地图上找到">⌖</button>`;
    });
  };
  const mind = (list: J[]) =>
    list?.length ? `<ol class="sb-mind">${list.map((m: J) => `<li><time>${esc(dateOf(m.day))}</time>${linkify(m.text)}</li>`).join('')}</ol>` : '<div class="sb-empty">最近没做什么决定</div>';

  // 月度曲线(引擎给列名 + 一行一行的数)
  let seriesCache: { xs: number[]; col: (n: string) => number[] } | null = null;
  const series = () => {
    if (seriesCache) return seriesCache;
    const s = read(E.series());
    const col = (name: string): number[] => {
      const k = s.cols.indexOf(name);
      return s.rows.map((r: number[]) => r[k]);
    };
    seriesCache = { xs: col('day'), col };
    return seriesCache;
  };
  /** 月末历史(身家、账上的钱、人数、监管账户):按存了几个月往前推日子。 */
  // end:序列最后一个月底是哪天(去世、搬走的人和关了门的公司的账停在那天,不是今天)
  const monthsX = (n: number, end = Math.floor(city.day / 30) * 30) => {
    return Array.from({ length: n }, (_, i) => end - (n - 1 - i) * 30);
  };
  const history = (vals: number[], name: string, fmt = money, key?: string, end?: number) =>
    vals && vals.length >= 2 ? lineChart(monthsX(vals.length, end), [{ name, vals, fmt }], { h: 130, fmt, key, area: true }) : '<div class="sb-empty">还不到两个月</div>';

  /**
   * 三张表里的流水:经营、投资、筹资、其他分开;每一类一行(上月、本月至今、近两年的小曲线),点开看跟谁来往了多少。
   */
  /**
   * 一本账(人、公司、银行、政府、工程一样):先对账——月初手上的钱 + 进 − 出 = 月末(对不上的差额单列);
   * 再按组(人:收入、花销、买卖和借还、搬家和汇款;公司:经营、投资、筹资;政府:收入、支出、借还)一行行列出来,
   * 每行上个月、这个月到今天、近两年的曲线,点开看对方。分组和对账都是引擎给的(见 dash.rs 的 group_of)。
   */
  const statement = (book: J, owner: string) => {
    const sides = (inflow: boolean) => {
      const last: J[] = book[inflow ? 'lastIn' : 'lastOut'];
      const cur: J[] = book[inflow ? 'curIn' : 'curOut'];
      const months: number[][] = book[inflow ? 'monthsIn' : 'monthsOut'] ?? [];
      const ks = new Set<number>([...last.map((x) => x.k), ...cur.map((x) => x.k)]);
      return [...ks].map((k) => {
        const l = last.find((x) => x.k === k), c = cur.find((x) => x.k === k);
        const any = l ?? c;
        return { k, tag: any.tag as string, group: any.group as string, last: l?.amt ?? 0, cur: c?.amt ?? 0, who: (l?.who ?? c?.who ?? []) as J[], n: l?.n ?? 0, rest: l?.rest ?? 0, hist: months.map((m) => m[k] ?? 0), inflow };
      });
    };
    const all = [...sides(true), ...sides(false)];
    const line = (x: J) => {
      const key = `st:${owner}:${x.inflow ? 'i' : 'o'}:${x.k}`;
      const cps = x.who.length
        ? `<div class="sb-cps">${x.who.map((c: J) => `<div>${who(c.who)}<b>${money(c.amt)}</b></div>`).join('')}${x.n > 8 ? `<div class="meta">另外 ${x.n - 8} 个 · ${money(x.rest)}</div>` : ''}</div>`
        : '<div class="sb-cps meta">上个月没有</div>';
      return `<details data-key="${esc(key)}"${opened.has(key) ? ' open' : ''}><summary><span class="t">${esc(x.tag)}</span><span class="sp">${spark(x.hist, x.inflow ? 'var(--good)' : 'var(--bad)', 56, 14)}</span><b class="${x.inflow ? 'good' : 'bad'}">${x.inflow ? '' : '−'}${money(x.last)}</b><em>${money(x.cur)}</em></summary>${cps}</details>`;
    };
    const sum = (g: string, inflow: boolean, f: 'last' | 'cur') => all.filter((x) => x.group === g && x.inflow === inflow).reduce((a, x) => a + x[f], 0);
    const groups: J[] = book.groups ?? [];
    const block = (gr: J, i: number) => {
      const g = gr.key;
      const ins = all.filter((x) => x.group === g && x.inflow).sort((a, b) => b.last - a.last);
      const outs = all.filter((x) => x.group === g && !x.inflow).sort((a, b) => b.last - a.last);
      if (!ins.length && !outs.length) return '';
      // 有净额的组算自己的净额;人的「花销」、政府的「支出」、工程的「出」和前一组(收入)合起来算结余
      let net = '';
      if (gr.net) {
        net = `<div class="sb-net"><span>${esc(gr.net)}</span><b>${signed(sum(g, true, 'last') - sum(g, false, 'last'))}</b><em>${signed(sum(g, true, 'cur') - sum(g, false, 'cur'))}</em></div>`;
      } else if (i > 0 && !groups[i - 1].net) {
        const prev = groups[i - 1].key;
        const label = g === '花销' ? '结余(收入 − 花销)' : g === '支出' ? '收支差' : '净进出';
        net = `<div class="sb-net"><span>${label}</span><b>${signed(sum(prev, true, 'last') - sum(g, false, 'last'))}</b><em>${signed(sum(prev, true, 'cur') - sum(g, false, 'cur'))}</em></div>`;
      }
      return `<div class="sb-stmt"><h5>${esc(gr.title)}<span>上月</span><span>本月至今</span></h5>${ins.map(line).join('')}${outs.map(line).join('')}${net}</div>`;
    };
    // 对账:月初 + 进 − 出 = 月末
    const recon = (r: J | null, label: string) => r
      ? `<div class="sb-row"><span>${label}</span><b>${money(r.start)} + ${money(r.in)} − ${money(r.out)}${Math.abs(r.other) >= 1 ? ` <span class="meta">${r.other > 0 ? '+' : '−'} 没走账的 ${money(Math.abs(r.other))}</span>` : ''} = ${money(r.end)}</b></div>`
      : '';
    const head = book.reconLast || book.reconCur
      ? sec('对账:月初手上的钱 + 进 − 出 = 月末', recon(book.reconLast, '上个月') + recon(book.reconCur, '这个月到今天'))
      : '';
    const flow: [number, number][] = book.flow ?? [];
    const fx = monthsX(flow.length);
    const chart = flow.length >= 2 ? lineChart(fx, [{ name: '每月进账', vals: flow.map((f) => f[0]), color: 'var(--good)', fmt: money }, { name: '每月出账', vals: flow.map((f) => f[1]), color: 'var(--bad)', fmt: money }], { h: 120, fmt: money, key: `flow:${owner}` }) : '';
    const body = groups.map(block).join('');
    return body ? head + chart + body : '<div class="sb-empty">这两个月没有流水</div>';
  };
  /** 一组的构成(上个月,按类):人是收入、花销,公司是经营的进和出。 */
  const groupDonut = (book: J, group: string, inflow: boolean, label: string) => {
    const list: J[] = book[inflow ? 'lastIn' : 'lastOut'].filter((x: J) => x.group === group);
    return donut(list.map((x: J) => ({ label: x.tag, value: x.amt })), money, label);
  };
  /** 一组上个月一共多少。 */
  const groupSum = (book: J, group: string, inflow: boolean) => book[inflow ? 'lastIn' : 'lastOut'].filter((x: J) => x.group === group).reduce((s: number, x: J) => s + x.amt, 0);

  // ---------- 顶栏 ----------
  const speeds = [0, 1, 5, 30, 120];
  // 实际一秒走了几天(模拟跟不上倍速时顶栏写出来;顶栏开场就要用,放在前面)
  let realSpeed = 0;
  let speed = q.has('speed') ? Number(q.get('speed')) : 1;
  let lastSpeed = speed || 1;
  // 天色不跟着钟点转(一秒一天的时候一秒转一圈,晃眼睛);自己挑一种,换的时候慢慢过渡。网址里的 hour 优先
  // 天色:「自动」按现实时间自己转(六分钟一个昼夜,和倍速、游戏的日子无关——跟着游戏的日子走,倍速下一秒几十天会一闪一闪);
  // 也可以定在白天、傍晚、夜里、清晨。网址带 hour= 的(截图)定在那个钟点
  const SKIES: [string, string, number][] = [['auto', '自动', -1], ['day', '白天', 13], ['dusk', '傍晚', 18.6], ['night', '夜里', 23], ['dawn', '清晨', 7.3]];
  const CYCLE = 6 * 60 * 1000;
  /** 自动的天色眼下几点:一圈里白天(9 到 17 点)占一半,傍晚、清晨各一成多,夜里四分之一。 */
  const autoHour = (ms: number) => {
    const k = (ms % CYCLE) / CYCLE;
    const seg: [number, number, number][] = [[0.5, 9, 17], [0.12, 17, 20], [0.26, 20, 30], [0.12, 30, 33]]; // (占一圈的几成, 从几点, 到几点)
    let at = 0;
    for (const [len, h0, h1] of seg) {
      if (k < at + len) return (h0 + ((k - at) / len) * (h1 - h0)) % 24;
      at += len;
    }
    return 9;
  };
  let skyIdx = 0;
  let skyHour = fixHour ?? autoHour(Date.now());
  const top = guard(() => {
    const s = city.stats;
    let col: (name: string) => number[] = () => [];
    let trendStatus = '';
    try {
      const history = series();
      if (!Array.isArray(history.xs) || history.xs.some(value => !Number.isFinite(value))) throw new Error('趋势日期不完整');
      const trends = new Map<string, number[]>();
      for (const name of ['pop', 'idle', 'homeless', 'price', 'wage', 'house', 'rent', 'building', 'firms', 'loanRate', 'treasury']) {
        const values = history.col(name);
        if (!Array.isArray(values) || values.length !== history.xs.length || values.some(value => typeof value !== 'number' || !Number.isFinite(value))) throw new Error('趋势数据不完整');
        trends.set(name, values);
      }
      col = name => trends.get(name) ?? [];
    }
    catch (error) {
      trendStatus = error === PENDING || inflight.has(E.series()) ? '趋势加载中' : '历史趋势暂不可用';
    }
    const trendNotice = trendStatus ? `<span class="meta" role="status">${esc(trendStatus)} <button class="chip" data-trend-retry>重试趋势</button></span>` : '';
    const jobless = s.adults ? s.idle / s.adults : 0;
    const initialSnapshot = city.statsSource === 'initial_snapshot';
    // 涨跌和一年前比;worse 为真的指标涨了算坏事(标红)
    const ind = (label: string, v: string, t: string, hist: number[], cls = '', worse = false, tip = '') => {
      const n = hist.length;
      const d = n >= 13 && hist[n - 13] ? hist[n - 1] / hist[n - 13] - 1 : 0;
      const arrow = Math.abs(d) >= 0.01 ? `<em class="${(d > 0) !== worse ? 'up' : 'down'}">${d > 0 ? '▲' : '▼'}${Math.abs(d * 100).toFixed(0)}%</em>` : '';
      const source = initialSnapshot ? '开局登记快照，尚未结算首日；价格为初始参考，工资为当前合同报价。' : '';
      return `<button class="sb-ind ${cls}" data-tab="${t}"${tip || source ? ` title="${esc(source + tip)}"` : ''}><span class="k">${label}${arrow}</span><b>${v}</b>${spark(hist.slice(-24), cls === 'bad' ? 'var(--bad)' : 'var(--ink-3)', 64, 14)}</button>`;
    };
    const [skyKey, skyName] = SKIES[skyIdx];
    patch(document.getElementById('top')!, `
      <div class="sb-brand"><i class="sb-logo">${icon('crane', 20)}</i><div><b>城市模拟器<small class="sb-seed" title="当前城市种子；同一版本、种子和推演天数可复现城市">种子 ${seed}</small></b><span>${dateOf(city.day)}${initialSnapshot ? ' · 开局登记' : ''}</span></div></div>
      <div class="sb-ctl">
        <div class="sb-seg">${speeds.map((v, i) => `<button class="${v === speed ? 'on' : ''}" data-speed="${v}" title="${i === 0 ? '暂停(空格)' : `一秒 ${v} 天(${i})`}">${v === 0 ? icon('pause', 14) : v}</button>`).join('')}<span class="unit">${speed > 1 && realSpeed < speed * 0.8 ? `实际 ${realSpeed.toFixed(0)} ` : ''}天/秒</span></div>
        <button class="sb-iconbtn" data-sky title="天色:${skyName}">${icon(skyKey, 18)}</button>
        <button class="sb-iconbtn" data-reseed title="换一座城">${icon('reseed', 17)}</button>
        <button class="sb-iconbtn" data-help title="快捷键(?)">${icon('help', 17)}</button>${trendNotice}
      </div>
      <div class="sb-inds">
        ${ind('人口', num(s.pop), 'people', col('pop'), '', false, '城里住着的人,连孩子;箭头是和一年前比')}
        ${ind('没活干', pct(jobless, 1), 'people', col('idle'), jobless > 0.1 ? 'bad' : '', true, '成年人里没有工作、也没当老板的')}
        ${ind('露宿', num(s.homeless), 'people', col('homeless'), s.homeless > s.adults * 0.05 ? 'bad' : '', true, '没有住处的人')}
        ${ind('日用', `${s.price.toFixed(1)} 元`, 'market', col('price'), '', true, '一份日用品(一个人一天吃用的)的市价')}
        ${ind('日工钱', `${Math.round(s.wage)} 元`, 'market|labor', col('wage'), '', false, '在岗的人平均一天的工钱')}
        ${ind('房价', money(s.house), 'estate', col('house'), '', false, '全城成交价,每一百平方米:各片区近三个月的成交(折到一般地段)取中位数,按各片区的住房套数配。只看真成交,一年里买卖少、集中在几栋楼的时候跳得厉害;一套房值多少看它的估值(同楼、附近的成交),比这个稳')}
        ${ind('房租', `${s.rent.toFixed(0)} 元/天`, 'estate', col('rent'), '', true, '全城每一百平方米一天的房租')}
        ${ind('在盖', `${s.building} 栋`, 'build|live', col('building'), '', false, '正在施工的楼')}
        ${ind('公司', num(s.firms), 'firms', col('firms'), '', false, '开着的公司')}
        ${ind('贷款年息', `${(s.loanRate * 100).toFixed(1)}%`, 'finance', col('loanRate'), s.loanRate > 0.2 ? 'bad' : '', true, '各家银行按贷出额加权')}
        ${ind('国库', money(s.treasury), 'govt|fin', col('treasury'), '', false, '市政府账上的钱')}
      </div>
      <button class="sb-ok${city.ok ? '' : ' bad'}" data-tab="govt|fin" title="全城的钱对账">${'<i></i>'}${city.ok ? '账平' : '账不平'}</button>`);
  });

  // ---------- 几块共用的页(看板的页签、市政府的面板都用) ----------
  /** 每十万成年人一年几例。 */
  const per100k = (n: number, adults: number) => (adults ? (n / adults) * 1e5 : 0);
  /** 心理健康:抑郁的人、近一年自杀、压力分布、处境、方式、传播、每一例。 */
  const mentalPage = (m: J) => {
    if (!m) return '<div class="sb-empty">这个版本的引擎还没有</div>';
    const { xs, col } = series();
    const step: number = m.stressStep ?? 0.1;
    const line: number = m.line ?? 0.4;
    return grid(tile('抑郁的人', num(m.depressed), `成年人的 ${pct(m.adults ? m.depressed / m.adults : 0, 1)}`, m.adults && m.depressed / m.adults > 0.08 ? 'bad' : ''),
        tile('近一年自杀', `${m.year} 人`, `每十万成年人 ${per100k(m.year, m.adults).toFixed(1)}`, m.year ? 'bad' : ''), tile('有记录的', `${m.total} 例`),
        ...(m.amp != null ? [tile('消息传多远', `× ${m.amp.toFixed(2)}`, '', m.amp > 1.2 ? 'bad' : '')] : [])) +
      sec('抑郁的人', lineChart(xs, [{ name: '抑郁的人', vals: col('depressed') }], { zero: true, key: 'so:dep', area: true, h: 120 })) +
      sec('每月自杀', lineChart(xs, [{ name: '自杀', vals: col('suicides') }], { zero: true, key: 'so:sui', h: 100 })) +
      sec('压力分布', bars((m.stress as number[]).map((v, i) => ({ label: `${(i * step).toFixed(2)} 起`, value: v, color: (i + 0.5) * step >= line ? 'var(--bad)' : undefined })), (v) => `${v} 人`),
        `成年人按压力(三个月的平均)分档;${line} 以上抑郁`) +
      sec('这些人当时的处境', bars((m.reasons as J[]).filter((r) => r.n > 0).map((r) => ({ label: r.name, value: r.n })), (v) => `${v} 例`), '一例常有几样') +
      (m.methods ? sec('方式', bars((m.methods as J[]).filter((r) => r.n > 0).map((r) => ({ label: r.name, value: r.n })), (v) => `${v} 例`), '公共卫生统计的粗分类') : '') +
      sec('每一例', (m.cases as J[]).length ? (m.cases as J[]).map((c) => {
        const key = `case:${c.who?.id}:${c.day}`;
        const notes = (c.notes as J[] ?? []).map((n) => `<div>${esc(monthOf(n.day))} · ${esc(n.text)}</div>`).join('');
        return `<details class="sb-case" data-key="${esc(key)}"${opened.has(key) ? ' open' : ''}><summary><span>${esc(monthOf(c.day))}</span><b>${who(c.who)}</b><span>${c.age} 岁 · ${esc(c.job ?? '')}</span></summary>` +
          `<div class="sb-cps">${row('当时的处境', esc(c.reasons.join('、') || '看不出'))}${row('方式', esc(c.method ?? '') + (c.place != null ? ` · <a data-go="plot:${c.place}">在哪</a>` : ''))}${c.access?.length ? row('手段的可及性', esc(c.access.join('、'))) : ''}${row('身家 · 欠银行', `${money(c.worth)} · ${c.debt ? money(c.debt) : '不欠'}`)}${row('一天收入', `${Math.round(c.income)} 元`)}` +
          (notes ? `<div class="meta" style="margin-top:6px">去世前最后几件事</div>${notes}` : '') + `</div></details>`;
      }).join('') : '<div class="sb-empty">没有</div>');
  };
  /** 治安:破案率、记着仇的、有前科的;近一年各种案子和抓到的、起因;每月曲线;最近的案子。 */
  const crimePage = (j: J, adults: number, stolen = 0) => {
    if (!j) return '<div class="sb-empty">这个版本的引擎还没有</div>';
    const { xs, col } = series();
    const kinds = j.kinds as J[];
    const all = kinds.filter((k) => k.kind !== '交通肇事').reduce((a, k) => a + k.n, 0);
    const kill = kinds.find((k) => k.kind === '故意杀人')?.n ?? 0;
    return grid(tile('近一年案子', `${num(all)} 件`, `每十万成年人 ${per100k(all, adults).toFixed(0)}`), tile('近一年命案', `${kill} 件`, `每十万成年人 ${per100k(kill, adults).toFixed(1)}`, kill ? 'bad' : ''),
        tile('破案率', pct(j.clearance ?? 0), '近半年'), tile('记着仇的', `${j.grudges ?? 0} 人`, '欠薪、被赶出住处、离婚、辞退'), tile('有前科的', `${j.record ?? 0} 人`),
        ...(j.wrongArrests != null ? [tile('抓错人', `${j.wrongArrests} 件`, `判了 ${j.wrongful} 件 · 三年平反 ${j.exonerated} 件`, j.wrongful ? 'bad' : '')] : []),
        ...(stolen > 0 ? [tile('近一个月店里被偷', `${stolen.toFixed(0)} 份`)] : [])) +
      sec('近一年的案子', table(['案子', '件数', '每十万成年人', '抓到人'], kinds.map((k) => [esc(k.kind), String(k.n), per100k(k.n, adults).toFixed(1), k.n ? `${k.caught}(${pct(k.caught / k.n)})` : '']))) +
      (j.motives ? sec('起因(近一年)', bars((j.motives as J[]).filter((m) => m.n > 0).map((m) => ({ label: m.motive, value: m.n })), (v) => `${v} 件`)) : '') +
      sec('每月的案子', lineChart(xs, [{ name: '案子', vals: col('crimes') }, { name: '命案', vals: col('homicides') }], { zero: true, key: 'so:crime', h: 130 })) +
      sec('每月的车祸', lineChart(xs, [{ name: '车祸', vals: col('accidents') }, { name: '车祸身亡', vals: col('roadDeaths') }], { zero: true, key: 'so:road', h: 110 })) +
      sec('最近的案子', table(['哪天', '案子', '谁', '对谁', '损失', '起因', '到哪一步', '律师', '结果'], (j.recent as J[]).map((c) => [monthOf(c.day), c.id != null ? caseLink(c.id, c.kind) : esc(c.kind), who(c.offender) + (c.wrong ? ' <span class="tag bad">抓错</span>' : ''),
        c.victim ? who(c.victim) : c.firm ? who(c.firm) : c.bank ? esc(c.bank) : '', c.loss ? money(c.loss) : '', esc(c.motive), stageTag(c.stage ?? ''),
        c.lawyer ? who(c.lawyer) + (c.aid ? ' <span class="meta">援助</span>' : '') : c.aid ? '<span class="meta">指定</span>' : '',
        c.caught ? esc(c.verdict ?? '在审') : '<span class="meta">没抓到</span>'])));
  };
  /** 律所:律师多好、接过几场、赢了几场、手上积压的、上个月城里的官司挣了多少、打一场要多少。 */
  const lawPage = (j: J) => {
    if (!j?.lawyers) return '<div class="sb-empty">这个版本的引擎还没有</div>';
    const list = j.lawyers as J[];
    return list.length ? table(['律所', '律师好坏', '人', '接过', '赢了', '积压', '上月官司收入', '近一年民事', '轻案', '重案'], [...list].sort((a, b) => b.q - a.q).map((l) => [who(l.firm), dbar(l.q.toFixed(2), l.q / 1.2, 'var(--good)'), String(l.staff), String(l.cases), l.cases ? `${l.wins}(${pct(l.wins / l.cases)})` : '0',
        l.busy != null ? `${Math.round(l.busy)} 人天` : '', l.month != null ? money(l.month) : '', l.civil != null ? String(l.civil) : '', money(l.fee), money(l.feeHeavy)]))
      : '<div class="sb-empty">城里还没有律所</div>';
  };
  /** 民事官司:近一年多少件、和解、判原告赢、驳回、在执行的;按种类;每月;最近的(点开看小剧场)。 */
  const civilPage = (j: J) => {
    const c = j?.civil;
    if (!c) return '<div class="sb-empty">这个版本的引擎还没有</div>';
    const { xs, col } = series();
    const kinds = (c.kinds as J[]).filter((k) => k.n > 0).sort((a, b) => b.n - a.n);
    const recent = c.recent as J[];
    return grid(tile('近一年民事官司', `${num(c.n)} 件`), tile('调解和解', `${num(c.settled)} 件`, c.n ? pct(c.settled / c.n) : ''),
        tile('判原告赢', `${num(c.won)} 件`), tile('驳回', `${num(c.lost)} 件`), tile('原告请了律师', c.n ? pct(c.withLawyer / c.n) : '—'),
        tile('在强制执行', `${num(c.execs)} 件`, c.execs ? `还差 ${money(c.execLeft)}` : '', c.execs ? 'bad' : ''), tile('法院排着', `${j.civilSuits ?? 0} 件`), tile('诉讼费', money(c.fees), '近一年')) +
      (kinds.length ? sec('近一年按种类', table(['种类', '件数', '和解', '原告赢', '驳回', '要的', '拿到'], kinds.map((k) => [esc(k.kind), String(k.n), String(k.settled), String(k.won), String(k.lost), money(k.claim), money(k.award)]))) : '') +
      (col('suits').some((v) => v != null) ? sec('每月', lineChart(xs, [{ name: '立案', vals: col('suits') }, { name: '和解', vals: col('settled') }, { name: '判原告赢', vals: col('civilWon') }], { zero: true, key: 'so:civil', h: 120 })) : '') +
      sec('最近的官司', recent.length ? table(['哪天', '案子', '原告', '被告', '要', '拿到', '原告的律师', '被告的律师', '到哪一步'], recent.map((x) => [monthOf(x.day), caseLink(x.id, x.kind),
        (x.plaintiff ? who(x.plaintiff) : '') + (x.group > 1 ? ` <span class="meta">等 ${x.group} 户</span>` : ''), x.defendant ? who(x.defendant) : '', money(x.claim), x.award ? money(x.award) : '',
        x.lawyer ? who(x.lawyer) + (x.contingency ? ' <span class="meta">风险代理</span>' : '') : '<span class="meta">自己</span>', x.other ? who(x.other) : '<span class="meta">自己</span>', stageTag(x.stage)])) : '<div class="sb-empty">还没有</div>');
  };
  /** 关着的人:谁、拘留还是服刑、关在哪、为哪件案子(抓错了的标出来)、哪天进来、哪天出来。 */
  const inmateTable = (list: J[], place = true) =>
    list.length
      ? table(['人', '', ...(place ? ['关在哪'] : []), '案子', '进来', '出来'], list.map((x) => [
          avatar(x.who.id, x.age, '', 0.4, 20) + who(x.who) + ` <span class="meta">${x.age} 岁</span>`,
          stageTag(x.state),
          ...(place ? [x.plot != null ? plotLink(x.plot, x.at) : esc(x.at)] : []),
          x.case ? caseLink(x.case.id, x.case.kind) + (x.wrong ? ' <span class="tag bad">抓错</span>' : '') : '',
          x.since != null ? dateOf(x.since) : '',
          x.until != null ? dateOf(x.until) : '<span class="meta">等审</span>',
        ]))
      : '<div class="sb-empty">没有关着的人</div>';
  /** 监狱和拘留室:关着的人、每个派出所的拘留室、监狱关了多少。 */
  const jailPage = (j: J) => {
    if (!j?.custody) return '<div class="sb-empty">这个版本的引擎还没有</div>';
    const c = j.custody;
    const { xs, col } = series();
    return grid(tile('服刑', `${num(c.inmates)} 人`), tile('拘留着等审', `${num(c.detained)} 人`), tile('取保候审', `${num(c.bail)} 人`),
        tile('关在外地', `${num(c.away)} 人`, '', c.away ? 'bad' : '')) +
      (j.inmates ? sec(`关着的人 ${(j.inmates as J[]).length}`, inmateTable(j.inmates)) : '') +
      sec('关在哪', (j.jails as J[]).length ? table(['地方', '种类', '关着', '关得下'], (j.jails as J[]).map((x) => [plotLink(x.plot, x.name), esc(x.kind === '派出所' ? '派出所拘留室' : x.kind), x.held >= x.cells ? `<span class="bad">${x.held}</span>` : String(x.held), String(x.cells)])) : '<div class="sb-empty">还没有派出所和监狱</div>') +
      (col('inmates').some((v) => v != null) ? sec('每月', lineChart(xs, [{ name: '服刑', vals: col('inmates') }, { name: '拘留', vals: col('detainees') }, { name: '无罪释放', vals: col('acquitted') }], { zero: true, key: 'so:jail', h: 120 })) : '') +
      (col('wrongful').some((v) => v != null) ? sec('冤案', lineChart(xs, [{ name: '判了、其实抓错了人', vals: col('wrongful') }, { name: '平反', vals: col('exonerated') }, { name: '二审改判无罪', vals: col('reversed') }], { zero: true, key: 'so:wrong', h: 110 }), '上帝视角') : '');
  };
  /** 法院:检察院、法院排着的,坐牢的,被欠薪的,在罢工的。 */
  const courtPage = (j: J) => {
    if (!j) return '<div class="sb-empty">这个版本的引擎还没有</div>';
    return grid(tile('检察院排着', `${j.review} 件`, '审查起诉'), tile('法院排着的讨薪', `${j.wageSuits} 件`), tile('法院排着的刑事', `${j.crimeSuits} 件`),
        ...(j.civilSuits != null ? [tile('法院排着的民事', `${j.civilSuits} 件`)] : []),
        ...(j.appeals != null ? [tile('排着的二审', `${j.appeals} 件`)] : []),
        tile('在坐牢', `${j.jailed} 人`), tile('被欠薪', `${j.owedPeople} 人`, money(j.owed), j.owedPeople ? 'bad' : ''), tile('在罢工', `${j.striking.length} 家`, '', j.striking.length ? 'bad' : '')) +
      (j.striking.length ? sec('在罢工的公司', table(['公司', '到哪天'], (j.striking as J[]).map((x) => [who(x.firm), dateOf(x.until)]))) : '');
  };
  /** 工程质量:多收的钱、塌楼、追责;在盖的做法和料;监理;每月开裂返工退料扣款塌楼;安全度不够的在盖工程。 */
  const qualityPage = (q: J) => {
    if (!q) return '<div class="sb-empty">这个版本的引擎还没有</div>';
    const { xs, col } = series();
    return grid(tile('料钱差价', signed(q.skim), '在盖的,按图纸结的 − 实际花的'), tile('近一年塌楼', `${q.fell} 栋`, '', q.fell ? 'bad' : ''),
        tile('近一年压死', `${q.crushed} 人`, '住户;工人算工伤', q.crushed ? 'bad' : ''), tile('近一年追责', `${q.jailed} 起`, '重大责任事故'),
        tile('安全度不够', `${q.weak.length} 个`, '在盖的,一点二以下', q.weak.length ? 'bad' : '')) +
      `<div class="sb-2">${donut(q.levels.map((x: J) => ({ label: x.name, value: x.n })), (v) => `${v} 个`, '在盖的做法')}${donut(q.tiers.map((x: J) => ({ label: x.name, value: x.n })), (v) => `${v} 个`, '用的料')}</div>` +
      sec('监理', bars(q.supervisors.map((x: J) => ({ label: x.name, value: x.n })), (v) => `${v} 个`), '投资人请的') +
      (col('cracks').some((v) => v != null) ? sec('每月', lineChart(xs, [{ name: '开裂的层', vals: col('cracks') }, { name: '返工的层', vals: col('inspectFails') },
        { name: '退掉的料(批)', vals: col('rejected') }, { name: '被扣质量款', vals: col('penalties') }, { name: '塌楼', vals: col('collapsed') }], { zero: true, key: 'bd:q' })) : '') +
      sec('安全度不够的在盖工程', q.weak.length ? table(['工程', '建筑公司', '做法', '安全度'], q.weak.map((x: J) => [projLink(x.id, x.name), x.builder ? who(x.builder) : '', esc(x.method), `<span class="bad">${x.safety.toFixed(2)}</span>`])) : '<div class="sb-empty">没有</div>');
  };
  /** 政府发包和回扣。 */
  const tenderPage = (tenders: J[], gr: J) => {
    if (!tenders) return '<div class="sb-empty">这个版本的引擎还没有</div>';
    return sec('在发、在盖的政府工程', tenders.length ? table(['工程', '建筑公司', '上限', '合同', '进度'], tenders.map((p) => [p.id != null ? projLink(p.id, p.name) : plotLink(p.plot, p.name),
        p.builder ? who(p.builder) : `<span class="meta">${p.permit ? '在招标' : '等施工许可'}</span>`, money(p.ceiling), p.price ? money(p.price) : '', esc(projectProgressText(p))])) : '<div class="sb-empty">没有</div>') +
      sec('回扣', grid(tile('还没查出来的', `${gr.hidden} 笔`, '上帝视角', gr.hidden ? 'bad' : ''), tile('查出来的', `${gr.found.length} 笔`)) +
        (gr.found.length ? table(['查出', '怎么查出', '公司', '老板', '经办人', '工程', '回扣'], (gr.found as J[]).map((x) => [dateOf(x.day), esc(x.how), who(x.firm), who(x.boss), who(x.official), esc(x.project), money(x.amount)])) : ''));
  };
  /** 机关:人手、各机关的来件和排队。 */
  const agencyPage = (g: J) =>
    grid(tile('在岗', `${g.office.staff} 人`, `编制 ${g.office.need} 人`, g.office.staff < g.office.need ? 'bad' : ''), tile('攒下的工夫', `${g.office.hours.toFixed(0)} 工日`)) +
    table(['机关', '一月来件', '办了', '没办上', '人手', '排队', '平均等'], g.agencies.map((a: J) => [
      `${plotLink(a.plot, a.name)}<div class="meta">${esc(a.duty)} · 一件 ${a.cost} 工日${a.name === '法院' ? ',讨薪和小额民事 1 工日' : ''}</div>`, a.month.toFixed(0), a.done.toFixed(0),
      a.turned >= 0.5 ? `<span class="bad">${a.turned.toFixed(0)}</span>` : '0', a.fte.toFixed(1), String(a.queue), a.wait > 0 ? `${a.wait.toFixed(1)} 天` : '']));
  /** 部门:目标编制与实际在岗产生的服务分开显示。 */
  const deptPage = (g: J) =>
    '<p class="meta">目标标准决定编制;人手覆盖按实际在岗与一倍编制计算,机构质量另受负载影响。</p>' +
    table(['部门', '管什么', '目标标准', '编制', '在岗', '人手覆盖'], g.depts.map((d: J) => [esc(d.name), esc(d.role), d.basis ? esc(d.basis) : d.standard != null ? `${d.standard.toFixed(2)} 倍` : d.name === '市政' ? '按路况' : '按来件', String(d.need), String(d.have), d.cover < 1 ? `<span class="bad">${pct(d.cover)}</span>` : pct(d.cover)])) +
    // 医生在医院(运营公司请的),各医院有几个、按活该请几个
    ((g.depts as J[]).filter((d) => d.hospitals?.length).map((d: J) => `<p class="meta">医生在医院运营公司,不在公家:${(d.hospitals as J[]).map((h) => `${esc(h.name)} ${h.doctors} / ${h.target} 人`).join(' · ')}(有执业资格的 / 按看病的活该请的)</p>`).join('')) +
    donut(g.depts.map((d: J) => ({ label: d.name, value: d.have })), (v) => `${v} 人`, '在岗') + row('员工名单', `<a data-go="firm:${g.publicId}">打开</a>`);
  /** 机构:学校、医院、派出所的容量、负载、人手、好坏。 */
  /** 政府的运营:钱怎么分、每所学校医院派出所运营得怎样、路和地铁、公家的楼的维修(见 dash.rs 的 ops_json)。 */
  const opsPage = (o: J, g: J) => {
    if (!o) return '<div class="sb-empty">没有</div>';
    const b = o.budget;
    const tax = taxCard(g);
    const kinds = ['学校', '医院', '派出所'];
    return grid(tile('能动用', money(b.balance), `国库 ${money(b.funds)}`), tile('储备', money(b.reserve)), tile('发了包要出', money(b.tenders)),
        tile('欠地铁工程款', money(b.subway), o.metro?.trainOwed ? `其中列车 ${money(o.metro.trainOwed)}` : ''),
        tile('能拨给医院', money(b.opex ?? 0), o.opexNote ?? '', (b.opex ?? 0) <= 0 ? 'bad' : ''),
        tile('资金成本', `${(o.fundRate ?? 0).toFixed(1)}%`, `一年一块钱的好处,三十年折成今天 ${(o.capitalYears ?? 0).toFixed(1)} 块`),
        tile(`${taxKinds(g)}税费减免`, tax.value, tax.note, '', ' data-tab="govt|tax"'), tile('路况', o.roads.q.toFixed(2), `市政 ${o.roads.staff}/${o.roads.need} 人 · ${num(o.roads.cells)} 格`, o.roads.q < 0.8 ? 'bad' : ''),
        tile('公家的楼', `${num(o.civicArea)} 平方米`, `今天维修买到 ${o.maintDone.toFixed(0)}%`, o.maintDone < 50 ? 'bad' : '')) +
      sec('学校、医院、派出所', (o.facilities as J[]).length ? table(['机构', '类', '等级', '层', '用的 / 容量', '人手', '医生 / 编制', '好坏', '危险度', '修过', '运营账上', '缺口'],
        (o.facilities as J[]).map((f: J) => [plotLink(f.plot, f.name), kinds[f.kind] ?? '', String(f.tier), f.rooms != null ? `${f.floors} · 诊室 ${f.rooms}` : String(f.floors),
          `${f.load > f.cap ? `<span class="bad">${num(f.load)}</span>` : num(f.load)} / ${num(f.cap)}`, f.staff.toFixed(1),
          f.doctors != null ? `${f.doctors < f.target ? `<span class="bad">${f.doctors}</span>` : f.doctors} / ${f.target}` : '',
          f.q < 0.6 ? `<span class="bad">${f.q.toFixed(2)}</span>` : f.q.toFixed(2), `${f.danger > 30 ? `<span class="bad">${f.danger.toFixed(0)}%</span>` : `${f.danger.toFixed(0)}%`}`,
          String(f.repaired), f.cash != null ? money(f.cash) : '', f.shortfall ? `<span class="bad">${money(f.shortfall)}</span>` : ''])) : '<div class="sb-empty">还没有</div>') +
      (((o.metro?.jobs ?? []) as J[]).length ? sec('地铁土建', table(['线', '段', '中标的', '工日', '工钱和加价', '料', '通车'],
        (o.metro.jobs as J[]).map((j: J) => [esc(j.line), esc(j.what), j.firm != null ? `<a data-go="firm:${j.firm}">${esc(j.builder)}</a>` : '<span class="bad">没人接</span>',
          `${num(j.done)} / ${num(j.labor)}`, `${money(j.paid)} / ${money(j.price)}`, `${money(j.matPaid)} / ${money(j.mat)}${j.materialsMissing > 0 ? ` · 缺 ${j.materialsMissing} 类` : ''}`,
          j.finished != null ? `干完 ${esc(dateOf(j.finished))}` : esc(dateOf(j.opens))])), '按段招标，工钱按实际进度支付；料钱列实际支付 / 发包估价，价格变化会使两者不同。各类材料到齐且工日、付款完成才记完工。') : '') +
      sec('最近的决定', (g.mind as J[]).length ? `<ol class="sb-news">${(g.mind as J[]).slice(0, 20).map((x: J) => `<li><time>${esc(dateOf(x.day))}</time><p>${linkify(x.text)}</p></li>`).join('')}</ol>` : '<div class="sb-empty">还没做过决定</div>');
  };
  const instPage = (g: J) =>
    table(['机构', '等级', '层', '容量', '用的', '人手', '好坏'], g.institutions.map((i: J) => {
      const f = i.fac;
      if (!f) return [plotLink(i.plot, i.name), '', '', '', '', '', ''];
      const hospital = (g.medical?.hospitals ?? []).find((h: J) => h.hospital?.plot === i.plot)?.hospital;
      const school = (g.education?.schools ?? []).find((s: J) => s.school?.plot === i.plot)?.school;
      const name = hospital ? facilityLink(hospital, 'hospital') : school ? facilityLink(school, 'school') : plotLink(i.plot, i.name);
      const full = f.load > f.cap;
      return [name, esc(f.tier), String(f.floors), num(f.cap), full ? `<span class="bad">${num(f.load)}</span>` : num(f.load), f.staff.toFixed(1), f.q < 0.6 ? `<span class="bad">${f.q.toFixed(2)}</span>` : f.q.toFixed(2)];
    }));
  const facilityLink = (f: J, kind?: 'hospital' | 'school') => f
    ? kind && f.id != null ? `<a data-go="${kind}:${f.id}">${esc(f.name)}</a>` : plotLink(f.plot, f.name) : '—';
  const patientLink = (p: J) => p?.id != null ? `<a data-go="person:${p.id}" data-view="medical">${esc(p.name)}</a>` : who(p);
  const studentLink = (p: J) => p?.id != null ? `<a data-go="person:${p.id}" data-view="education">${esc(p.name)}</a>` : who(p);
  const hospitalSummary = (h: J): string => {
    if (!h?.enabled) return '<div class="sb-empty">此城市使用旧医疗规则</div>';
    if (!h.provider) return '<div class="sb-empty">医院尚无运营机构</div>';
    const c = h.capacity, w = h.ward, t = h.today ?? {};
    return '<h4>当前住院与可用资源</h4>' + grid(tile('实际占床', `${w?.occupied ?? 0} / ${c?.beds ?? 0}`, '当前占床 / 当班可用；受空间、设备与医生限制'),
      tile('当班医生', `${c?.doctors ?? 0} 人`, `专家 ${c?.specialists ?? 0} 人`),
      tile('剩余诊疗工时', `${num(c?.hoursLeft ?? 0, 1)} 小时`, `已用 ${num(t.hoursUsed ?? 0, 1)} 小时`),
      tile('待完成医嘱患者', `${w?.waiting ?? 0} 人`, `今日住院相关未完成尝试 ${w?.unpaid ?? 0} 次`),
      tile('检查剩余能力', `${c?.examinationsLeft ?? 0} 次`, '受设备和医生剩余工时共同限制'),
      tile('运营账户', money(h.cash), h.public ? '公立医院' : '民营医院')) +
      (h.public ? row('国库累计周转投入', money(h.workingCapital ?? 0)) + row('今日周转投入', money(h.dayCapital ?? 0)) +
        row('基本运转资金缺口', `<span class="${h.fundingShortfall > 0 ? 'bad' : ''}">${money(h.fundingShortfall ?? 0)}</span>`) : '') +
      row('运营机构', who(h.provider)) + row('医院', facilityLink(h.hospital, 'hospital')) +
      row('数据日期', h.day != null ? dateOf(h.day) : '—') +
      row('服务覆盖人口', num(h.servicePopulation)) + row('实际耗材库存', `${num(h.stock, 1)} 份`) +
      row('实际已购设备', `${num(h.kit ?? 0, 1)} 件`) + (h.quality != null ? row('设施服务质量', num(h.quality, 2)) : '') + hospitalActivityHTML(h);
  };
  const hospitalStaff = (h: J): string => sec('实际当班医务人员', (h.staff ?? []).length
    ? table(['医生', '资格', '登记执业进度', '登记培训进度', '培养与任职', '合同日工资'], h.staff.map((a: J) => [who(a.who), a.specialist ? '专家' : a.qualified ? '医生' : '受监督培训', a.practiceDays != null ? `${num(a.practiceDays)} 天` : '未记录', a.trainingDays != null ? `${num(a.trainingDays)} 天` : '未记录', clinicianCareerLinks(a.who), money(a.wage)]))
    : '<div class="sb-empty">当前没有可诊疗的当班人员</div>', '休养、住院和实际羁押者不提供诊疗工时；登记进度可能含既有资历，点“看医学培养”核对学历来源与本城到课。');
  const hospitalPatients = (h: J): string =>
    sec('当前住院患者', (h.patients ?? []).length ? table(['患者 · 点开病史', '当前病名 / 症状', '住院病程', '入院'], h.patients.map((a: J) => [patientLink(a.who), medicalConditionHTML(a.episodes, h.day), a.episode != null ? `#${a.episode}` : '未提供', dateOf(a.since)])) : '<div class="sb-empty">当前没有住院患者</div>', `已展示 ${(h.patients ?? []).length} / 实际占床 ${h.ward?.occupied ?? '总量未提供'} 人；名单最多显示 100 人`) +
    sec('待完成医嘱', (h.waitingPatients ?? []).length ? table(['患者 · 点开病史', '最近未完成原因'], h.waitingPatients.map((p: J) => [patientLink(p.who), esc(p.refusal ?? '等待接诊')])) : '<div class="sb-empty">当前没有待完成患者记录</div>', `已展示 ${(h.waitingPatients ?? []).length} / 实际待办 ${h.ward?.waiting ?? '总量未提供'} 人；名单最多显示 150 人`) +
    '<p class="meta">这里列当前住院与待诊者，不是医院所有历史患者。已住院者缺少当日付款或护理时仍保留占床；点击居民直接进入医疗页，查看当前诊断、既往病史和真实账单。</p>' + `<p>${hospitalHistoryLinkHTML(h)}</p>`;
  const hospitalFees = (h: J): string => sec('当前单次服务总价', (h.fees ?? []).length
    ? table(['服务', '总价'], h.fees.map((f: J) => [esc(f.name), f.total != null ? money(f.total) : '当前不可报价']))
    : '<div class="sb-empty">当前没有可用收费报价</div>', '家庭、雇主、基本医保和补充医保的分摊在就诊时核算') +
    '<p class="meta">报价不是已收款。实际服务还需要医生工时、设备、耗材和支付能力；居民医疗页列出已完成账单。</p>';
  const hospitalFinance = (h: J): string => {
    if (!h.provider) return '<div class="sb-empty">医院尚未开始经营</div>';
    const t = h.today ?? {}, p = h.payers ?? {}, months: J[] = [...(h.financialMonths ?? [])].sort((a, b) => a.day - b.day);
    return grid(tile('今日诊疗实收', money(h.dayReceived ?? 0), '已完成服务的四方实付'),
      tile('今日待命采购', money(h.dayStandby ?? 0), '公立实际值守成本扣除诊疗实收'),
      tile('累计诊疗实收', money(h.received ?? 0), '不含公立待命服务采购'), tile('累计待命采购', money(h.cumulativeStandby ?? 0))) +
      sec('本期经营流水', row('诊疗收款', money(t.medical ?? 0)) + row('保险机构直付', money(t.claims ?? 0)) +
        row('待命服务采购', money(t.standby ?? 0)) + row('已付医务工资', money(t.wages ?? 0)), '从本月开始累计，月末结账后翻页') +
      sec('累计诊疗付款来源', table(['付款来源', '实际支付'], [['家庭自付', money(p.patient ?? 0)], ['雇主代付', money(p.employer ?? 0)], ['补充或工伤保险直付', money(p.insurer ?? 0)], ['基本医保', money(p.public ?? 0)]])) +
      row('累计耗材采购支出', money(h.suppliesPurchased ?? 0)) +
      (h.public ? '<p class="meta">国库周转投入属于资本拨付，不计营业收入；公立基本工资、真实欠薪和耗材储备优先安排，仍受真实国库余额与采购供应约束。</p>' : '') +
      (months.length >= 2 ? sec('已结账月份的经营', lineChart(months.map((m) => m.day), [
        { name: '营业收入', vals: months.map((m) => m.revenue), fmt: money }, { name: '经营支出', vals: months.map((m) => m.expense), fmt: money },
        { name: '净利润', vals: months.map((m) => m.profit), fmt: money }], { key: `hp:fin${h.hospital?.id}`, fmt: money, zero: true })) : '') +
      sec('月度财报', months.length ? table(['结账', '营业收入', '经营支出', '设备折旧', '净利润', '期末现金'], [...months].reverse().map((m) => [dateOf(m.day), money(m.revenue), money(m.expense), money(m.wear), signed(m.profit), money(m.cash)])) : '<div class="sb-empty">尚无已结账的月度财报</div>') +
      `<p class="meta">财报收入包含已付待命采购；净利润扣除经营支出和设备折旧。完整流水见${who(h.provider)}。</p>`;
  };
  const hospitalPage = (h: J): string => hospitalSummary(h) + (h?.enabled && h.provider ? hospitalFinance(h) + hospitalStaff(h) + hospitalPatients(h) : '');
  const medicalSummary = (m: J): string => {
    if (!m?.enabled) return '<div class="sb-empty">此城市使用旧医疗规则</div>';
    const t = m.today, hs: J[] = m.hospitals ?? [];
    const sum = (key: string) => hs.reduce((n, h) => n + (h.ward?.[key] ?? 0), 0);
    return grid(tile('今日新病程', String(t.newCases)), tile('今日康复', String(t.recovered)), tile('住院人数', String(sum('occupied'))),
      tile('当前显现病程', m.caseCount != null ? num(m.caseCount) : '未提供', `已确诊 ${m.confirmedCaseCount ?? '未提供'} · 未确诊 ${m.unconfirmedCaseCount ?? '未提供'}`),
      tile('今日诊疗', String(t.consultations + t.examinations + t.treatments), `问诊 ${t.consultations} · 检查 ${t.examinations} · 治疗 ${t.treatments}`),
      tile('未完成医嘱患者', String(sum('waiting'))), tile('今日未完成尝试', String(t.refused ?? 0)),
      tile('可提供护理的病床', num(hs.reduce((n, h) => n + (h.capacity?.beds ?? 0), 0)), '空间、设备和当班医生共同约束'),
      tile('当班医生', `${num(hs.reduce((n, h) => n + (h.capacity?.doctors ?? 0), 0))} 人`, `专家 ${num(hs.reduce((n, h) => n + (h.capacity?.specialists ?? 0), 0))} 人`)) +
      row('数据日期', dateOf(m.day)) +
      '<p class="meta">疾病和传染参数是游戏模型。未完成检查的病程只显示症状；费用、床位和诊疗效果均在实际服务完成后登记。</p>';
  };
  const hospitalList = (m: J): string => sec('医院名单', (m?.hospitals ?? []).length ? table(['医院', '运营机构', '医生 / 专家', '设施质量', '占床 / 可用床', '待办患者', '耗材库存', '账户现金'], m.hospitals.map((h: J) => [facilityLink(h.hospital, 'hospital'), h.provider ? who(h.provider) : '尚未开诊', `${h.capacity?.doctors ?? 0} / ${h.capacity?.specialists ?? 0}`, h.quality != null ? num(h.quality, 2) : '—', `${h.ward?.occupied ?? 0} / ${h.capacity?.beds ?? 0}`, String(h.ward?.waiting ?? 0), num(h.stock ?? 0, 1), money(h.cash ?? 0)])) : '<div class="sb-empty">尚无医院</div>', '点击医院名称直接查看诊疗、医务人员、病床与财报');
  const medicalPatients = (m: J): string => sec('当前就诊动态', (m?.patients ?? []).length ? table(['居民 · 点开病史', '已确诊病名 / 当前症状', '严重程度', '住院医院', '当前安排 / 未完成原因'], m.patients.map((p: J) => [patientLink(p.who),
    medicalConditionHTML(p.episodes, m.day),
    (p.episodes ?? []).map((e: J) => `${e.severity} / 100`).join(' · ') || '—', facilityLink(p.hospital, 'hospital'),
    p.refusal ? `<span class="bad">${esc(p.refusal)}</span>` : p.hospital ? '住院中' : p.absent ? '医疗休养' : '未住院，可查看病历'])) : '<div class="sb-empty">当前没有显现病程的居民记录</div>', `${m?.day != null ? dateOf(m.day) + ' · ' : ''}已展示 ${(m?.patients ?? []).length} / ${m?.patientCount != null ? `实际 ${m.patientCount}` : '实际总量未提供'} 人，上限 ${m?.patientLimit ?? 150}；点击居民进入疾病与病史页`);
  const medicalBeds = (m: J): string => sec('当前住院与护理能力', table(['医院', '已占床', '可提供护理的床位', '床位余量', '今日入院', '今日出院', '待完成患者'], (m?.hospitals ?? []).map((h: J) => [
    facilityLink(h.hospital, 'hospital'), num(h.ward?.occupied ?? 0), num(h.capacity?.beds ?? 0), num(Math.max(0, (h.capacity?.beds ?? 0) - (h.ward?.occupied ?? 0))),
    num(h.ward?.admitted ?? 0), num(h.ward?.discharged ?? 0), num(h.ward?.waiting ?? 0)]))) +
    '<p class="meta">这是当前占床与当班护理能力。余量不代表已获准入院，还须具备当日工时、耗材和支付能力；能力下降时已有住院者仍占床。住院名单在医院详情中。</p>';
  const medicalInsurance = (m: J): string => {
    const i = m?.insurance;
    const f = m?.fund;
    return grid(tile('基本医保本期剩余授权', money(m?.fundLimit ?? 0), '国库内额度，不是独立现金账户'),
      ...(f ? [tile('医保基金结余', money(f.balance), '缴费进、报销出，在国库里单独记'), tile('缴费率', `${(f.rate * 100).toFixed(2)}%`, '占平常一个月收入；一年按报销定一次')] : [])) +
      (i ? grid(tile('基本医保有效覆盖', `${num(i.basicActive)} / ${num(i.basicEligible)} 人`, '有效参保 / 符合资格'), tile('有效补充医保', `${num(i.extraActive)} 人`)) +
        sec('累计实际资金', table(['项目', '金额'], [['基本医保实际缴费', money(i.contributions)], ['基本医保实际支付', money(i.publicPaid)], ['补充医保实际保费', money(i.premiums)], ['补充医保实际赔付', money(i.claims)]]))
        : '<div class="sb-empty">当前引擎尚未提供全城参保汇总</div>') +
      '<p class="meta">覆盖人数按当前在城居民统计；累计缴费和赔付包含历史居民。每笔基本医保支付仍须有真实国库资金；个人有效期、剩余额度与分摊账单见居民医疗页。</p>';
  };
  const medicalPage = (m: J): string => medicalSummary(m) + (m?.enabled ? hospitalList(m) + medicalPatients(m) + medicalInsurance(m) : '');
  const patientPage = (m: J, id: number, here: boolean): string => `<p><a data-go="person:${id}" data-view="medicalCases">按病程查案卷、诊疗时间线与收费 →</a></p>` + patientMedicalHTML(m, { personId: id, here, opened }, { hospital: f => facilityLink(f, 'hospital'), insurer: who }) +
    (m?.enabled ? medicalArchiveHTML(id, opened, historyView) : '');
  const schoolSummary = (s: J): string => {
    if (!s?.enabled) return '<div class="sb-empty">此城市使用旧教育规则</div>';
    if (s.shut) return '<div class="sb-empty">这所学校连着一年没有学生，已经关了（不排课、不修楼）；附近的学校坐不下了会重新开</div>' + row('学校', facilityLink(s.school, 'school'));
    return grid(tile('实际学位', num(s.seats)), tile('在岗教师', `${s.teachers.length} 人`, `可用 ${num(s.teacherHours, 1)} 小时`),
      tile('排课工时', `${num(s.reservedHours, 1)} 小时`, `实际授课 ${num(s.taughtHours ?? 0, 1)} 小时`), tile('就近课程需求', num(s.wanted), `候补 ${num(s.waiting)}${s.seatShort ? ` · 哪都坐不下 ${num(s.seatShort)}` : ''}`)) +
      row('学校', facilityLink(s.school, 'school')) + (s.level ? row('办学层次', esc(s.level)) + row('设施改造等级', String(s.grade)) : '') + row('数据日期', s.sampleDay != null ? dateOf(s.sampleDay) : '尚未排课');
  };
  const schoolCourses = (s: J): string => sec('实际课程', table(['阶段', '班级', '录取', '实际到课'], (s.courses ?? []).map((c: J) => [esc(c.name), num(c.classes), num(c.enrolled), num(c.present)])), s.sampleDay != null ? dateOf(s.sampleDay) : '尚未排课');
  const schoolStaffing = (s: J): string => {
    const f = s.staffing;
    if (!f) return '';
    return sec('师资编制', grid(tile('编制', `${num(f.target)} 人`, '全城同层次'), tile('缺额', `${num(f.short)} 人`),
      ...(s.level === '医学院' ? [tile('兼课', `${num(f.adjuncts)} 人`, '公立医院医生')] : []), tile('现行出价', money(f.offer), '日薪')) +
      (s.lackTeacher ? row('本校候课', `${num(s.lackTeacher)} 人,教师工时不足`) : '') + (f.reason ? row('缺额原因', esc(f.reason)) : ''));
  };
  const academicDate = (day: unknown): string => typeof day === 'number' && Number.isSafeInteger(day) && day >= 0 ? dateOf(day) : '未记录';
  const academicNumber = (value: unknown, precision = 0): string => typeof value === 'number' && Number.isFinite(value) && value >= 0 ? num(value, precision) : '未记录';
  const academicTeachingDays = (value: unknown): string => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? `${num(value)} 天` : '未记录';
  // 名单里直接用这一行带的人物编号,不另查每个人的学籍
  const teacherEducationLink = (person: J): string => {
    if (person?.kind !== 'person' || !Number.isSafeInteger(person.id) || person.id < 0 || person.id > 0xffffffff) return esc(person?.name ?? '—');
    return `${who(person)}<br><small><a data-go="person:${person.id}" data-view="education">学历与教学资历 →</a></small>`;
  };
  const schoolTeachers = (s: J): string => sec('实际到岗师资', s.faculty?.length ? table(['教师', '资格', '实际教学日', '合同日薪'], s.faculty.map((a: J) => [teacherEducationLink(a.who), [a.professor ? '教授授课资格' : '', a.medicalLecturer ? '临床讲师资格' : ''].filter(Boolean).join(' · ') || '普通教师', academicTeachingDays(a.academic?.teachingDays), money(a.wage)])) : (s.teachers ?? []).length ? s.teachers.map((a: J) => who(a)).join(' · ') : '<div class="sb-empty">没有专任教师</div>') +
    ((s.adjuncts ?? []).length ? sec('兼课医生', table(['医生', '任职单位', '实际教学日'], s.adjuncts.map((a: J) => [teacherEducationLink(a.who), esc(a.employer ?? '—'), academicTeachingDays(a.academic?.teachingDays)]))) : '') +
    '<p class="meta">名单只列本校实际到岗人员；资格不等于兼任另一岗位。每人每日共用真实工时、同一工资合同，课程预留与实际授课分别核算。</p>';
  const schoolPage = (s: J): string => schoolSummary(s) + (s?.enabled ? schoolStaffing(s) + schoolCourses(s) + schoolTeachers(s) : '');
  let schoolRoster = { id: -1, start: 0, stack: [] as number[] };
  const schoolStudents = (id: number): string => {
    if (schoolRoster.id !== id) schoolRoster = { id, start: 0, stack: [] };
    const roster = E.schoolstudents ? read(E.schoolstudents(id, schoolRoster.start)) : null;
    return schoolRosterHTML(roster, { student: studentLink }, schoolRoster.stack.length > 0);
  };
  const educationSummary = (e: J): string => {
    if (!e?.enabled) return '<div class="sb-empty">此城市使用旧教育规则</div>';
    return grid(tile('在读或申请课程', `${e.enrolledOrWaiting} 人`), tile('实际到课', `${e.present} 人`, e.sampleDay != null ? dateOf(e.sampleDay) : '尚未排课'), tile('候补或暂未到课', `${e.waiting} 人`), tile('学校', `${e.schools.length} 所`)) +
      (e.higher?.enabled ? grid(tile('登记大学学历', `${num(e.higher.universityDegrees)} 人`), tile('登记医学学历', `${num(e.higher.medicalDegrees)} 人`), tile('具有教授资格', `${num(e.higher.professorQualified)} 人`, `大学实际到岗 ${num(e.higher.professorPosts)} 人`), tile('临床讲师资格', `${num(e.higher.medicalLecturerQualified)} 人`, `医学院实际到岗 ${num(e.higher.medicalLecturerPosts)} 人`)) : '') +
      '<p class="meta">小学、大学、医学院按办学层次分开，楼宇改造等级不会自动变成大学。学习、毕业和资格随真实学历、到课与教学资历推进；教授和临床讲师须实际招聘、发工资并核课时。</p>';
  };
  const schoolList = (e: J): string => sec('学校名单', (e?.schools ?? []).length ? table(['学校', '办学层次', '学位', '教师', '录取', '实际到课', '候补', '排课 / 可用工时'], e.schools.map((s: J) => [facilityLink(s.school, 'school'), esc(s.level ?? '普通学校'), num(s.seats), String(s.teachers.length), String(s.courses.reduce((n: number, c: J) => n + c.enrolled, 0)), String(s.courses.reduce((n: number, c: J) => n + c.present, 0)), num(s.waiting), `${num(s.reservedHours, 1)} / ${num(s.teacherHours, 1)}`])) : '<div class="sb-empty">尚无学校</div>', '点击学校名称查看课程、教师与实际授课工时');
  const educationCourses = (e: J): string => {
    const schools: J[] = e?.schools ?? [];
    const total = (stage: string, key: string) => schools.reduce((n, s) => n + (s.courses ?? []).filter((c: J) => c.name === stage).reduce((k: number, c: J) => k + (c[key] ?? 0), 0), 0);
    return sec('实际课程与出勤', table(['阶段', '班级', '录取', '实际到课'], (e?.policy ?? []).map((p: J) => [esc(p.name), num(total(p.name, 'classes')), num(total(p.name, 'enrolled')), num(total(p.name, 'present'))])), e?.sampleDay != null ? dateOf(e.sampleDay) : '尚未排课') +
      sec('课程配置', table(['课程', '年龄范围', '每日小时', '班额', '所需学习日', '单日学费', '公共补助'], (e?.policy ?? []).map((p: J) => [esc(p.name) + (p.enabled ? '' : ' · 停开'), `${p.minAge}–${p.maxAge}`, num(p.hours, 1), num(p.classSize), p.required > 0 ? num(p.required) : '照护服务', money(p.fee), pct(p.subsidy)]))) +
      (e?.higher?.enabled ? '<p class="meta">中等课程的年龄范围用于常规自动安排。成年人可在人物「学籍与培训」中显式申请补修；教师、学位、工作、照护与往返时间仍须实际核验，报名不会直接取得学历。</p>' : '');
  };
  const educationWaiting = (e: J): string => {
    const students: J[] = e?.students ?? [];
    return sec('学校实际候补', table(['学校', '就近需求', '实际学位', '候补', '预留 / 完成授课工时'], (e?.schools ?? []).map((s: J) => [facilityLink(s.school, 'school'), num(s.wanted), num(s.seats), num(s.waiting), `${num(s.reservedHours, 1)} / ${num(s.taughtHours ?? 0, 1)}`]))) +
      (Array.isArray(e?.students) ? sec('居民出勤与候补安排', students.length ? table(['居民', '课程', '学校', '本次实际到课', '当前安排'], students.map((s: J) => [studentLink(s.who), esc(s.stage ?? '—'), facilityLink(s.school, 'school'), s.present ? `${num(s.hours ?? 0, 1)} 小时` : '未到课', esc(s.waiting || (s.present ? '已到课' : '本次未记录到课'))])) : '<div class="sb-empty">当前没有候补或出勤记录</div>', `最多显示 150 人${e.studentCount != null ? `，共 ${num(e.studentCount)} 人` : ''}`) : '') +
      '<p class="meta">录取人数与到课人数的差额不等同缺少学位。病休、费用、教师和课程安排等具体原因以居民学籍页为准。</p>';
  };
  const educationPage = (e: J): string => educationSummary(e) + (e?.enabled ? schoolList(e) + educationCourses(e) : '');
  const academicView = (a: J): string => {
    if (!a || typeof a !== 'object' || Array.isArray(a)) return sec('学历与教学资历', '<div class="sb-empty">没有学历记录</div>');
    const yes = (x: unknown, t: string, f: string) => x === true ? t : x === false ? f : '未记录';
    return sec('学历与教学资历', table(['学历', '真实来源', '登记 / 取得日', '学校', '本城实际到课', '累计学习日'], [a.secondary,a.university,a.medicine].filter(d => d && typeof d === 'object' && !Array.isArray(d)).map((d: J) => [typeof d.name === 'string' && d.name.trim() ? esc(d.name) : '未记录',typeof d.source === 'string' && d.source.trim() ? esc(d.source) : '未记录',academicDate(d.day),facilityLink(d.school,'school'),academicNumber(d.attended),academicNumber(d.learned,1)])) +
      row('城外学历登记', academicDate(a.backgroundRecorded)) + row('来城以前当过教授', yes(a.preCityProfessor, '是', '否')) +
      row('本城实际教学', academicTeachingDays(a.teachingDays)) + row('教授授课资格', yes(a.professorQualified, '具备', '尚未具备')) +
      row('教授资格取得', academicDate(a.professorSince)) + row('最近一次上课', academicDate(a.lastTaught)) +
      row('临床讲师资格', yes(a.medicalLecturerQualified, '已有真实专家资历', '尚未具备')) +
      '<p class="meta">城外学历的登记日不是本城毕业日，实际到课从零另记。大学毕业不等于教授；医学学业和真实院内带教共同决定新医务资格，既有医生资格保留。</p>') +
      clinicianCareerHTML(a, f => facilityLink(f, 'school'));
  };
  const studentPage = (s: J, id: number, here: boolean): string => {
    if (!s?.enabled) return '<div class="sb-empty">此城市使用旧教育规则</div>';
    return grid(tile('当前课程', esc(s.stage ?? '无在读课程')), tile('今日到课', s.present ? `${num(s.hours, 1)} 小时` : '未到课'),
      tile('家庭累计学费', money(s.familyPaid)), tile('公共累计补助', money(s.publicPaid))) +
      row('学校', facilityLink(s.school, 'school')) + (s.classNo != null ? row('班级', `本阶段第 ${s.classNo + 1} 班`) : '') + row('今日实付', `家庭 ${money(s.familyToday)} · 补助 ${money(s.publicToday)}`) + (s.waiting ? row('当前安排', esc(s.waiting)) : '') + (s.since != null ? row('入学', dateOf(s.since)) : '') +
      (s.trainingTrade ? row('正在培训', esc(s.trainingTrade)) : '') +
      secondaryCompletionHTML(s, { personId: id, here, paused: speed === 0, pending: actionPending != null }, { school: f => facilityLink(f, 'school') }) +
      academicView(s.academic) +
      sec('真实学习记录', table(['课程', '实际到课日', '累计学习日 / 所需', '完成'], s.courses.map((c: J) => [esc(c.name), num(c.attended), c.required > 0 ? `${num(c.learned, 1)} / ${num(c.required)}` : '照护服务', c.completed ? `已完成${c.graduated != null ? ` · ${dateOf(c.graduated)}` : ''}` : '—']))) +
      '<p class="meta">病休、住院、羁押、无学位、缺教师或费用未付均不计作到课。当前无在读课程不代表需要重新上学。</p>';
  };

  /** 交通:通勤、车、路况、怎么上班、地铁。 */
  const trafficPage = (g: J) => {
    const t = g.transport;
    const sub = t.sub;
    const sampled = sub?.sampleDay != null;
    const sampleNote = sampled ? `${dateOf(sub.sampleDay)}采样` : '尚未采样';
    return grid(tile('上下班平均来回', sampled ? `${Math.round(t.avg)} 分钟` : '—', sampleNote), tile('汽车', `${num(t.cars)} 辆`), tile('电动车', `${num(t.bikes)} 辆`), tile('最堵的一格', sampled ? `一天 ${Math.round(t.jam)} 辆` : '—', sampleNote),
      ...(t.roadQ != null ? [tile('路况', pct(t.roadQ), t.roadSlow > 1.01 ? `开车骑车慢 ${pct(t.roadSlow - 1)}` : '市政人手够', t.roadQ < 0.9 ? 'bad' : ''),
        tile('修了路的地', `${num(t.roads)} 块`, t.roadNew ? `这个月新修 ${num(t.roadNew)} 块` : '')] : [])) +
      sec('怎么上班', donut(t.modes.map((m: J) => ({ label: m.name, value: m.n })), (v) => `${num(v)} 人`, '当前选用')) +
      (sub && t.lines.length ? sec('地铁服务', `<p class="meta">${dateOf(t.day ?? city.day)}的服务状态</p>` + grid(
        tile('运营线路', `${sub.operatingLines} 条`, `登记 ${sub.registeredLines} 条`),
        tile('运营列车', `${sub.runningTrains} 列`, `登记配车 ${sub.assignedTrains} 列`),
        tile('仍有施工', `${sub.buildingLines} 条`, '包括分段运营中的线路'),
        tile('开放站点', `${sub.openStations} / ${sub.totalStations}`, '各线路分别计站')) +
        (sub.nextOpen != null ? row('下次开站', dateOf(sub.nextOpen)) : '') +
        (sub.operatingLines === 0 ? `<p class="meta">目前没有运营线路${sub.buildingLines > 0 ? '，登记配车仍在等线路开通' : '，尚未具备通车条件'}。</p>` : '') +
        row('客流采样', `${sampleNote}${sub.nextSample != null ? ` · 下次 ${dateOf(sub.nextSample)}` : ''}`) +
        '<p class="meta">开站后，客流在下一次选路采样时更新；线路施工与运营可以同时存在。</p>') +
        sec('采样客流', grid(tile('坐地铁上班', sampled ? `${num(sub.riders)} 人` : '—', sampled && sub.commuters ? `上班的 ${pct(sub.riders / sub.commuters, 1)}` : sampleNote),
          tile('要换乘', sampled && sub.riders ? pct(sub.xfer / sub.riders) : '—'), tile('骑车去站', sampled && sub.riders ? pct(sub.bike / sub.riders) : '—'),
          tile('来回', sampled && sub.riders ? `${Math.round(sub.min)} 分钟` : '—', '坐地铁的平均'))) : '') +
      sec('地铁线路', t.lines.length ? table(['线', '状态', '开放/总站', '长', '采样客流', '最挤', '运营/配车(列)', '班隔', '下次开站'], t.lines.map((l: J) => {
        const st: J[] = Array.isArray(l.stations) ? l.stations : [];
        return [lineChip(l.k, l.at != null ? plotLink(l.at, l.name) : esc(l.name)), esc(l.state ?? (l.open ? '运营中' : '施工中')),
          `${l.openStations ?? st.filter((s) => s.open).length} / ${l.totalStations ?? st.length}`, `${(l.len * 0.3).toFixed(1)} 公里`,
          l.sampleDay != null ? num(l.riders) : '—', l.open && l.sampleDay != null ? crowdCell(l.crowd) : '—', subwayFleet(l),
          l.headway != null ? `${l.headway.toFixed(1)} 分钟` : '—', l.nextOpen != null ? dateOf(l.nextOpen) : '—'];
      })) + (t.lines as J[]).map((l: J) => {
        const key = `subway:${l.k}`;
        const st: J[] = Array.isArray(l.stations) ? l.stations : [];
        return `<details data-key="${key}"${opened.has(key) ? ' open' : ''}><summary>${lineChip(l.k, esc(l.name))} · ${esc(l.state ?? (l.open ? '运营中' : '施工中'))} · 开放 ${l.openStations ?? st.filter((s) => s.open).length}/${st.length} 站</summary>` +
          table(['站', '开放', '换乘', '采样进出/日', '换进来', '到下一站'], st.map((s, k) => [plotLink(s.plot, s.name), s.open ? '已开放' : s.opens != null ? dateOf(s.opens) : '未开放',
            s.with.map((j: number) => `${j} 号线`).join('、'), sampled ? num(s.inout) : '—', sampled && s.xfer ? num(s.xfer) : '',
            k + 1 < st.length && s.open && st[k + 1].open && sampled ? crowdCell(l.load[k] ?? 0) : ''])) + '</details>';
      }).join('') +
        row('一趟', money(t.fare)) + (t.sub?.train ? row('一列车', money(t.sub.train)) : '') + (t.owed > 0 ? row('修地铁还欠', money(t.owed)) : '') : '<div class="sb-empty">还没有地铁</div>');
  };
  /** 开工登记的配车和真正载客的列车分开。 */
  const subwayFleet = (l: J) => `${l.runningTrains ?? (l.open ? l.trains : 0)} / ${l.assignedTrains ?? l.trains}`;
  /** 挤到几成(坐的人 ÷ 拉得下的;满了标红)。 */
  const crowdCell = (c: number) => (c >= 1 ? `<span class="bad">${pct(c)}</span>` : pct(c));
  /** 线的颜色小方块加名字。 */
  const lineChip = (k: number, label: string) => `<span class="sb-line"><i style="background:${lineColor(k)}"></i>${label}</span>`;
  /** 上班怎么走:坐地铁的写走(骑)到哪个站、坐几号线几站、在哪换乘、出站走几分钟;都写来回几分钟、一天花多少。 */
  const commuteText = (c: J) => {
    const r = c.route;
    const tail = `来回 ${Math.round(c.min)} 分钟${c.cash >= 0.01 ? ` · 一天 ${money(c.cash)}` : ''}`;
    if (!r) return `${esc(c.mode)} · ${tail} · 隔 ${Math.round(c.blocks)} 格`;
    const go = r.in.min < 0.5 ? `在${plotLink(r.in.plot, r.in.st)}上车` : `${r.in.bike ? '骑车' : '走'} ${Math.round(r.in.min)} 分钟到${plotLink(r.in.plot, r.in.st)}`;
    const legs = (r.legs as J[]).map((g, i) => `${i ? '换' : '坐'}${lineChip(g.line, `${g.line} 号线`)} ${g.stops} 站到${plotLink(g.toPlot, g.to)}`).join(' · ');
    return `${go} · ${legs}${r.out.min < 0.5 ? '' : ` · 出站走 ${Math.round(r.out.min)} 分钟`} · ${tail}`;
  };

  // ---------- GDP 和宏观数 ----------
  /** 全城口径统一的宏观数(见 dash.rs 的 macro_json):GDP、增长、物价、通胀、工钱、钱。各页要用的都从这里拿。 */
  const econ = (): J | null => (E.econ ? read(E.econ()) : null);
  /** 百分数(已经乘过 100 的),带正负号;没有的写 —。 */
  const pctS = (v: number | null | undefined) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`);
  /** 和再往前十二个月比涨了几成。 */
  const yoyCell = (v: number, p: number | null | undefined) => {
    if (p == null || !p || Math.sign(v) !== Math.sign(p)) return '<span class="meta">—</span>';
    const x = v / p - 1;
    return `<span class="${x < -0.005 ? 'down' : x > 0.005 ? 'up' : ''}">${x > 0 ? '+' : ''}${(x * 100).toFixed(1)}%</span>`;
  };
  /** 通胀(全国的,央行盯的)和基准利率:金融、政府、外地几页一个说法。 */
  const inflTile = (v: number) => tile('通胀', `${v.toFixed(1)}%`, '全国 · 目标 2%');
  /** 几行加成一行(合计、小计):近十二个月、占比、上一个十二个月、每年都加。 */
  const addRows = (name: string, rows: J[], group = '') => ({
    group, name, v: rows.reduce((a, r) => a + r.v, 0), share: rows.reduce((a, r) => a + r.share, 0),
    prev: rows.every((r) => r.prev != null) ? rows.reduce((a, r) => a + r.prev, 0) : null,
    years: (rows[0]?.years ?? []).map((_: number, i: number) => rows.reduce((a, r) => a + (r.years[i] ?? 0), 0)),
    out: rows.some((r) => r.out != null) ? rows.reduce((a, r) => a + (r.out ?? 0), 0) : undefined,
    ic: rows.some((r) => r.ic != null) ? rows.reduce((a, r) => a + (r.ic ?? 0), 0) : undefined,
  });
  /** GDP 的一张表:按大类先列小计、再列各项,最后统计误差和 GDP。prod 的多两列产出、中间投入。 */
  const gdpTable = (rows: J[], tail: J[], go: (r: J) => string, span: string, prod = false) => {
    const yrs = (r: J) => ((r.years as number[]).length >= 2 ? spark(r.years, 'var(--ink-3)', 60, 14) : '');
    // how:0 平常,1 小计和合计(粗),2 小计下面的一项(缩进)
    const name = (r: J, how: number) => {
      const g = go(r);
      const t = g ? `<a data-tab="${g}">${esc(r.name)}</a>` : esc(r.name);
      return how === 1 ? `<b>${t}</b>` : how === 2 ? `<span class="meta">　</span>${t}` : t;
    };
    const cell = (v: number | undefined) => (v == null ? '' : money(v));
    const line = (r: J, how: number) => [name(r, how), ...(prod ? [cell(r.out), cell(r.ic)] : []), how === 1 ? `<b>${money(r.v)}</b>` : money(r.v), `${r.share.toFixed(1)}%`, yoyCell(r.v, r.prev), yrs(r)];
    const out: string[][] = [];
    // 一直是零的行当(这座城没有的)不列
    rows = rows.filter((r) => r.v || r.out || r.ic || (r.years as number[]).some((x) => x));
    const groups = [...new Set(rows.map((r) => r.group as string))];
    for (const gname of groups) {
      const items = rows.filter((r) => r.group === gname);
      const sub = !!gname && items.length > 1;
      if (sub) out.push(line(addRows(gname, items, gname), 1));
      for (const r of items) out.push(line(r, sub ? 2 : 0));
    }
    tail.forEach((r, i) => out.push(line(r, i === tail.length - 1 ? 1 : 0)));
    return table(['', ...(prod ? ['产出', '中间投入', '增加值'] : [span]), '占 GDP', '比上一年', '每年'], out);
  };
  /** 概况的 GDP 页:近十二个月的 GDP、增长、人均;每月的曲线;支出法、收入法、生产法三张表;分项价格指数。 */
  /** 消费(docs/商业系统升级.md):成年人按上个月收入分组的收支、各档馆子;谁在买、钱花在哪。 */
  const spendPage = () => {
    if (!E.spend) return '<div class="sb-empty">这个版本的引擎还没有</div>';
    const d = read(E.spend());
    // 读档接着玩的城可能跑在老版本的引擎上(没有这一页的数据,引擎线程回 null)
    if (!d) return '<div class="sb-empty">这座城的引擎版本还没有这一页</div>';
    if (!d.adults) return '<div class="sb-empty">还没有成年人</div>';
    const rows = d.groups as J[];
    const top = rows[rows.length - 1];
    const head = grid(
      tile('成年人一天收入', money(d.income), `${num(d.adults)} 个成年人 · 上个月`),
      tile('花掉', pct(d.rate), `一天 ${money(d.spent)}(吃、下馆子、用品、房租、交通、看病上学保险……)`),
      tile('最高百分之一', pct(top.share), `拿全城收入的这么多 · 九成是分红的话多半是老板、股东`, '', ` title="分红占他们收入的 ${pct(top.div)},花掉 ${pct(top.rate)}"`),
      tile('最高百分之一花掉', pct(top.rate), `一天下馆子 ${num(top.fun, 0)} 元 · 用品 ${num(top.goods, 0)} 元`));
    const groups = sec('谁在买(成年人按上个月收入分组,元/天)', table(['组', '人', '收入', '占全城', '分红占', '吃', '下馆子', '用品', '房租', '别的', '花掉', '买房买股'],
      rows.map((g) => [esc(g.name), num(g.n), num(g.income, 0), pct(g.share), pct(g.div), num(g.food, 0), num(g.fun, 0), num(g.goods, 0), num(g.rent, 0), num(g.other, 0), pct(g.rate), num(g.invest, 0)])),
      '花掉 = 吃、下馆子、用品、房租、别的;买房买股不算花掉');
    const tiers = (d.tiers as J[]).length ? sec('馆子分档', table(['档次', '一份', '比大众贵', '一天几份', '馆子', '人', '买主心里贵几倍(中位 · 最高一成)'],
      (d.tiers as J[]).map((t) => [esc(t.name), num(t.price, 1), `${num(t.ratio, 2)} 倍`, num(t.volume, 1), num(t.shops), num(t.staff), t.mind ? `${num(t.mind[0], 1)} · ${num(t.mind[1], 1)}` : '—'])),
      d.own ? '中档、高档的价按买主各自心里的倍数出:钱多的人心里「好多少」值得多,供不应求时把价抬上去' : '中档、高档的出价跟着这一档昨天的价走') : '';
    return head + groups + tiers;
  };

  /** 商务区(见 dash.rs 的 cbd、eco 的 hotel.rs):外地客人(商务客、游客)来了几个、住了几晚、在本城花了多少;酒店几家几间、住满几成、房价;
   * 钱从哪到哪(邻城 → 酒店、馆子 → 工钱、房租);各座邻城;各家酒店;每月的曲线。 */
  const cbdPage = () => {
    if (!E.cbd) return '<div class="sb-empty">这个版本的引擎还没有</div>';
    const d = read(E.cbd());
    if (!d) return '<div class="sb-empty">这座城的引擎版本还没有这一页</div>';
    if (!d.on && !d.tour) return '<div class="sb-empty">这座城照原来的规则:没有外地商务客和酒店,游客的钱按公式分给饭馆</div>';
    const y = d.year, t = d.today;
    const span = y.months < 12 ? `近 ${y.months} 个月` : '近十二个月';
    const stays = y.stayBiz + y.stayTour;
    const head = grid(
      tile('商务客', `${num(y.biz, 1)} 人天/天`, `${span}平均 · 今天 ${num(t.biz, 1)}`, '', ' data-mode="35"'),
      tile('游客', `${num(y.tour, 1)} 人/天`, `今天 ${num(t.tour, 1)}`, '', ' data-mode="35"'),
      tile('住了几晚', `${num(stays, 1)} 间夜/天`, `商务客 ${num(y.stayBiz, 1)} · 今天 ${num(t.stay[0] + t.stay[1], 1)}`),
      tile('酒店', `${d.hotels.length} 家`, `${num(d.rooms)} 间客房`, '', ' data-mode="34"'),
      tile('住满', pct(y.occ), `卖掉的间夜 ÷ 在租的客房`),
      tile('房价', `${money(d.price)}/晚`, `一间的本钱 ${money(d.cost)}(人工、房租、设备)`),
      tile('外地客人一天在本城花', money(y.lodge + y.meal), `住 ${money(y.lodge)} · 吃 ${money(y.meal)}(${num(y.meals, 1)} 顿)`));
    const f = d.flow;
    const flow = sec('钱从哪到哪(上个月)', table(['从', '到', '多少'], [
      ['邻城的钱袋', '酒店(房费,含代收的交易税)', money(f.lodge)],
      ['邻城的钱袋', '馆子(饭钱,含代收的交易税)', money(f.meal)],
      ['酒店', '员工(工钱)', money(f.wage)],
      ['酒店', '房东(客房的房租)', money(f.rent)],
      ['酒店', '设备(床、空调、卫浴)', money(f.kit)],
      ['酒店', '国库(交易税、利润税)', money(f.tax)],
      ['酒店', '股东(分红)', money(f.div)],
    ]), '客人花的是邻城的钱:进了本城的酒店、馆子,再变成工钱、房租;GDP 里算出口「住宿餐饮」');
    const regions = sec('各座邻城(今天)', table(['邻城', '商务客', '游客', '住下', '工钱', '商务客住一晚最多出', '钱袋', '明天要来'], (d.regions as J[]).map((r) => [
      esc(r.name), num(r.biz, 1), num(r.tour, 1), num(r.stay, 1), money(r.wage), money(r.room), money(r.purse), `${num(r.next[0], 1)} · ${num(r.next[1], 1)}`])),
      '客人按那座邻城的工钱出价(四档,一档低一成五);钱袋不够客人花一百天的少来人');
    const hs = (d.hotels as J[]).slice().sort((a, b) => b.rooms - a.rooms);
    const hotels = sec(`酒店(${hs.length} 家)`, hs.length ? table(['酒店', '在哪', '客房', '人', '能卖', '卖掉', '要价', '客人觉得值', '房租/天', '一天挣'], hs.map((h) => [
      `<a data-go="firm:${h.id}">${esc(h.name)}</a>`, `<a data-go="plot:${h.plot}">(${h.at[0]}, ${h.at[1]})</a>`, `${num(h.rooms)} 间`, num(h.staff), num(h.can, 1), num(h.sold, 1), money(h.ask), pct(h.near), money(h.rent), money(h.profit)]))
      : '<div class="sb-empty">还没有酒店:外地客人住不上,当天回去,只吃一顿午饭</div>', '客人觉得值:离他们要办事的地方越远,一间值得越少(房价按就在跟前的算,实收打这个折)');
    const mo = d.months as number[][];
    const xs = mo.map((_, i) => d.day - (mo.length - 1 - i) * 30);
    const col = (i: number) => mo.map((r) => r[i]);
    const charts = mo.length >= 2 ? sec('每月(一天平均)', lineChart(xs, [{ name: '商务客', vals: col(0) }, { name: '游客', vals: col(1) }, { name: '住了几晚', vals: col(2).map((v, i) => v + col(3)[i]) }, { name: '在租的客房', vals: col(7) }], { zero: true, key: 'cbd:n', h: 130 })) +
      sec('外地客人一天在本城花的钱', lineChart(xs, [{ name: '住', vals: col(4), fmt: money }, { name: '吃', vals: col(5), fmt: money }], { fmt: money, zero: true, key: 'cbd:m', h: 120 })) : '';
    return head + `<p class="meta"><a data-mode="35">地图:外地客人</a> · <a data-mode="34">酒店</a> · <a data-mode="31">商务区(扎堆)</a></p>` + flow + regions + hotels + charts;
  };

  const gdpPage = () => {
    if (!E.gdp) return '<div class="sb-empty">这个版本的引擎还没有</div>';
    const d = read(E.gdp());
    const m = d.macro;
    if (!d.months) return '<div class="sb-empty">还不到一个月</div>';
    const span = d.months < 12 ? `近 ${d.months} 个月` : '近十二个月';
    const head = grid(
      tile('GDP', money(m.gdp), `${span} · 名义 ${pctS(m.nominalGrowth)}`),
      tile('实际增长', pctS(m.growth), `平减指数 ${pctS(m.deflator)}`, m.growth != null && m.growth < 0 ? 'bad' : ''),
      tile('人均 GDP', money(m.perCap), `实际 ${pctS(m.perCapGrowth)}`),
      // 实际 GDP 按今年的价(增长率不变);按开头那个月的价的数放在悬停里
      tile('实际 GDP', money(m.real * (m.toToday ?? 1)), `按今年的价 · 实际 ${pctS(m.growth)}`, '', ` title="按开头那个月的价是 ${money(m.real)}(本城物价跌了的,这个数比名义大)"`),
      tile('居民消费价格', m.cpi == null ? '—' : m.cpi.toFixed(1), `同比 ${pctS(m.cpiYoy)} · 开头 = 100 · 往邻城的价位收拢`, '',
        ` title="开局本城的工钱、物价按做服务卖给邻城能挣多少定,是邻城的两倍上下;人多了、工钱下来,本城的价往邻城的价位收拢。眼下本城一份日用 ${num(m.daily, 1)} 元,全国(本城加邻城按人口)${num(m.national, 1)} 元"`),
      tile('净出口', money(m.exports - m.imports), `出口 ${money(m.exports)} · 进口 ${money(m.imports)}`, '', ' data-tab="regions"'));
    // 每月的数加成近十二个月(一个月一个点);不满十三个月的画每月的
    const mo = d.monthly;
    const n = (mo.day as number[]).length;
    const roll = n >= 13;
    const sum12 = (v: number[]) => (roll ? v.slice(11).map((_, i) => v.slice(i, i + 12).reduce((a, b) => a + b, 0)) : v);
    const xs: number[] = roll ? mo.day.slice(11) : mo.day;
    const note = roll ? '近十二个月滚动' : '每月';
    const charts = n >= 2
      ? sec('GDP', lineChart(xs, [{ name: '名义', vals: sum12(mo.gdp), fmt: money }, { name: '实际(按今年的价)', vals: sum12(mo.real).map((v: number) => v * (m.toToday ?? 1)), fmt: money }], { fmt: money, key: 'gdp:t', h: 140 }), note) +
        sec('支出构成', lineChart(xs, [{ name: '居民消费', vals: sum12(mo.c), fmt: money }, { name: '投资', vals: sum12(mo.i), fmt: money }, { name: '政府', vals: sum12(mo.g), fmt: money },
          { name: '出口', vals: sum12(mo.x), fmt: money }, { name: '进口', vals: sum12(mo.m), fmt: money }], { fmt: money, zero: true, key: 'gdp:e', h: 150 }), note) +
        sec('价格指数', lineChart(mo.day, (d.px as string[]).map((k, i) => ({ name: k, vals: mo.px[i], fmt: (v: number) => v.toFixed(1) })), { fmt: (v) => v.toFixed(0), log: true, key: 'gdp:p', h: 130 }), '开头 = 100 · 对数刻度')
      : '';
    const expGo = (r: J) => ({ 吃: 'market', 餐饮娱乐: 'market', 用品: 'market', 房租: 'estate', 自有住房: 'estate', 建筑工程: 'build', 开发商销售增值: 'estate', 公家工资: 'govt|staff', 采购: 'govt', 公共工程: 'build|tender', 出口: 'regions', 进口: 'regions', 居民消费: 'people', 投资: 'build', 政府: 'govt', 净出口: 'regions' } as Record<string, string>)[String(r.name).replace(/[((].*$/, '')] ?? '';
    const incGo = (r: J) => ({ 工钱: 'market|labor', 公司利润: 'firms', 利息净额: 'finance', 金融业: 'finance', 房租: 'estate', 公家收费: 'govt|fin', 生产税净额: 'govt|fin' } as Record<string, string>)[r.name] ?? '';
    const prodGo = (r: J) => (r.group === '公共' ? 'govt' : r.group === '金融' ? 'finance' : r.name === '居民出租房屋' || r.name === '自有住房' ? 'estate' : r.name ? 'firms' : '');
    const total = { ...d.total, name: 'GDP' };
    const xm = [...d.xs, ...d.ms].filter((r: J) => r.v);
    return head + charts +
      sec('支出法', gdpTable(d.exp, [d.expDisc, total], expGo, span), '居民消费 + 投资 + 政府 + 出口 − 进口') +
      (xm.length ? sec('出口和进口', table(['', span, '占 GDP', '比上一年', '每年'], xm.map((r: J) => [`${esc(r.group)} · ${esc(r.name)}`, money(r.v), `${r.share.toFixed(1)}%`, yoyCell(r.v, r.prev), (r.years as number[]).length >= 2 ? spark(r.years, 'var(--ink-3)', 60, 14) : '']))) : '') +
      sec('收入法', gdpTable(d.inc, [d.incDisc, total], incGo, span), `公司利润含财报税前利润 ${money(d.profit)}`) +
      sec('生产法', gdpTable(d.prod, [d.ptax, total], prodGo, span, true), '增加值 = 产出 − 中间投入') +
      (d.dists?.length ? sec('片区', distTable(d, total, span), `${span} · 人均按住着的人`) : '');
  };
  /** GDP 页的片区表:各片区的 GDP、占比、比上一年、人均(按住着的人)、住着的人、上班的人、主要行业;分不到地块的单列。 */
  const distTable = (d: J, total: J, span: string) => {
    const yrs = (r: J) => ((r.years as number[]).length >= 2 ? spark(r.years, 'var(--ink-3)', 60, 14) : '');
    const adj = (v: number | undefined) => (v == null ? '' : `<span class="${v < 0 ? 'down' : v > 0 ? 'up' : ''}">${v > 0 ? '+' : ''}${money(v)}</span>`);
    const rows: string[][] = (d.dists as J[]).map((r) => [
      `<a data-mode="21">${esc(r.name)}</a>`, money(r.v), `${r.share.toFixed(1)}%`, yoyCell(r.v, r.prev), r.home == null ? '' : money(r.home), adj(r.adj),
      r.perCap == null ? '—' : money(r.perCap), num(r.pop), num(r.jobs),
      (r.top as J[]).slice(0, 2).map((x) => `<a data-tab="firms">${esc(String(x.name).replace(/[((].*$/, ''))}</a> ${x.share.toFixed(0)}%`).join(' · '), yrs(r)]);
    const un = d.distUnalloc;
    if (un && (un.v || (un.years as number[]).some((x) => x))) rows.push([esc(un.name), money(un.v), `${un.share.toFixed(1)}%`, yoyCell(un.v, un.prev), '', '', '', '', '', '', yrs(un)]);
    rows.push(['<b>GDP</b>', `<b>${money(total.v)}</b>`, '100.0%', yoyCell(total.v, total.prev), '', '', '', '', '', '', yrs(total)]);
    // 全城:GDP 到全城的初次收入(相当于 GNI),再按谁拿分
    const n = d.gni;
    const gni = n ? table(['全城', span], [
      ['GDP', money(n.gdp)],
      ...(n.from ? [['加:从邻城挣来的', money(n.from)]] : []),
      ...(n.to ? [['减:付给邻城的', money(n.to)]] : []),
      ['减:迁出者和外地业主的收入', money(n.away)],
      ['<b>全城初次收入(相当于 GNI)</b>', `<b>${money(n.gni)}</b>`],
      ['　住在城里的人', money(n.home + n.nohome) + (n.nohome ? ` <span class="meta">其中无住处 ${money(n.nohome)}</span>` : '')],
      ['　公司留存', money(n.corp)],
      ['　银行留存', money(n.bank)],
      ['　政府', money(n.gov)],
      ['　统计误差', money(n.gap)],
      ['另:低保、救济、社保', money(n.trans)],
    ]) : '';
    return `<p class="meta">GDP 按产出所在的地块计,居民收入按挣钱的人的住处计;两者之差为通勤调整。</p>` +
      table(['', 'GDP', '占 GDP', '比上一年', '居民收入', '通勤调整', '人均 GDP', '住着的人', '上班的人', '主要行业', '每年'], rows) + gni +
      `<p class="meta"><a data-mode="28">地图:地块 GDP</a> · <a data-mode="29">人均 GDP</a> · <a data-mode="32">居民收入</a> · <a data-mode="33">人均居民收入</a></p>`;
  };

  // ---------- 看板 ----------
  const overview = (): Tab[] => {
    const s = city.stats;
    const { xs, col } = series();
    /** 一个关键数:值、一年涨跌、近两年的小曲线;点了去那一页。worse 为真的涨了算坏事。 */
    const kpi = (label: string, value: string, hist: number[] | null, o: { worse?: boolean; bad?: boolean; note?: string; go?: string } = {}) =>
      tile(label, value, [o.note ? esc(o.note) : '', hist && hist.length > 1 ? trendNote(hist, o.worse) : '', hist && hist.length > 1 ? spark(hist.slice(-24), o.bad ? 'var(--bad)' : 'var(--ink-3)', 48, 12) : ''].filter(Boolean).join(' '),
        o.bad ? 'bad' : '', o.go ? ` data-tab="${o.go}"` : '');
    const last = (name: string) => {
      const v = col(name);
      return v.length ? v[v.length - 1] ?? 0 : 0;
    };
    const ratio = (a: string, b: string) => {
      const x = col(a), y = col(b);
      return x.map((v, i) => (y[i] ? v / y[i] : 0));
    };
    const yoy = (name: string) => {
      const v = col(name);
      const n = v.length;
      return n >= 13 && v[n - 13] ? v[n - 1] / v[n - 13] - 1 : 0;
    };
    return [
      ['health', '体检', () => {
        const so = E.society ? read(E.society()) : null;
        const co = E.construction ? read(E.construction()) : null;
        const adults = s.adults || 1;
        // 要注意的:按眼下的数挑出来,点了去那一页
        const warn: string[] = [];
        const add = (tag: string, text: string, go: string) => warn.push(`<li data-tab="${go}"><span class="tag bad">${esc(tag)}</span><span>${text}</span></li>`);
        if (s.idle / adults > 0.1) add('失业', `没活干的成年人占 ${pct(s.idle / adults, 1)}`, 'people');
        if (s.homeless > adults * 0.03) add('露宿', `${num(s.homeless)} 人没有住处`, 'people');
        const hungry = last('hungry');
        if (hungry >= 1) add('挨饿', `上个月平均每天 ${num(hungry)} 人挨饿`, 'people');
        // 物价按居民消费价格指数的同比(见 econ;引擎没有的照旧看日用一份)
        const ec = econ();
        const inf = ec?.cpiYoy != null ? ec.cpiYoy / 100 : yoy('price');
        if (Math.abs(inf) > 0.08) add(inf > 0 ? '涨价' : '跌价', `居民消费价格一年${inf > 0 ? '涨了' : '跌了'} ${pct(Math.abs(inf))}`, ec?.cpiYoy != null ? 'overview|gdp' : 'market');
        if (ec?.growth != null && ec.growth < -3) add('衰退', `实际 GDP 比上一年少了 ${pct(-ec.growth / 100)}`, 'overview|gdp');
        if (so) {
          const j = so.justice, m = so.mental;
          if (j.striking?.length) add('罢工', `${j.striking.slice(0, 3).map((x: J) => esc(x.firm.name)).join('、')}${j.striking.length > 3 ? ` 等 ${j.striking.length} 家` : ''}在罢工`, 'society');
          if (j.owedPeople > adults * 0.02) add('欠薪', `${num(j.owedPeople)} 人被欠工资,一共 ${money(j.owed)}`, 'society');
          if (m.adults && m.depressed / m.adults > 0.08) add('抑郁', `抑郁的成年人占 ${pct(m.depressed / m.adults, 1)}`, 'society');
        }
        if (co) {
          if (co.quality.weak.length) add('危楼', `${co.quality.weak.length} 个在盖的工程安全度不够`, 'build');
          if (co.workDeaths) add('工伤', `近三十天工地上 ${co.workDeaths} 人身亡`, 'build');
        }
        // 这个月的大事
        const news: J[] = read(E.news());
        const big = new Set(['命案', '倒塌', '银行倒闭', '暴雷', '抢劫', '罢工', '受贿', '曝光', '悲剧', '停贷', '判决']);
        const recent = news.filter((x) => big.has(x.kind) && city.day - x.day < 30).slice(0, 8);
        const item = (x: J) => {
          const go = x.plot != null ? `plot:${x.plot}` : x.firm != null ? `firm:${x.firm}` : x.person != null ? `person:${x.person}` : '';
          return `<li${go ? ` data-go="${go}"` : ''}><span class="tag k-${esc(x.kind)}">${esc(x.kind)}</span><time>${esc(dateOf(x.day))}${x.know != null ? ` · ${Math.round(x.know * 100)}% 知道` : ''}</time><p>${linkify(x.text)}</p></li>`;
        };
        const fam = so?.family, mn = so?.mental, jj = so?.justice;
        const crimes = jj ? (jj.kinds as J[]).filter((k) => k.kind !== '交通肇事').reduce((a, k) => a + k.n, 0) : 0;
        const q = co?.quality;
        return (warn.length ? sec('要注意的', `<ul class="sb-alerts">${warn.join('')}</ul>`) : '') +
          sec('经济', grid(
            ...(ec && ec.months ? [
              kpi('GDP', money(ec.gdp), null, { note: `${ec.months < 12 ? `近 ${ec.months} 个月` : '近十二个月'} · 实际 ${pctS(ec.growth)}`, bad: ec.growth != null && ec.growth < 0, go: 'overview|gdp' }),
              kpi('人均 GDP', money(ec.perCap), null, { note: `实际 ${pctS(ec.perCapGrowth)}`, go: 'overview|gdp' })] : []),
            kpi('人口', num(s.pop), col('pop'), { go: 'people' }),
            kpi('没活干', pct(s.idle / adults, 1), ratio('idle', 'adults'), { worse: true, bad: s.idle / adults > 0.1, go: 'people' }),
            kpi('一天工钱买几份日用', s.realWage.toFixed(2), col('realWage'), { go: 'market' }),
            kpi('日用一份', `${s.price.toFixed(1)} 元`, col('price'), { worse: true, go: 'market' }))) +
          sec('住房', grid(
            kpi('房价', money(s.house), col('house'), { note: '百平米', go: 'estate' }),
            kpi('房租', `${s.rent.toFixed(0)} 元/天`, col('rent'), { worse: true, go: 'estate' }),
            kpi('空房', pct(s.vacancy), ratio('vacant', 'units'), { go: 'estate' }),
            kpi('露宿', num(s.homeless), col('homeless'), { worse: true, bad: s.homeless > adults * 0.03, go: 'people' }))) +
          (fam && mn && jj ? sec('社会', grid(
            kpi('结了婚的', pct(fam.adults ? fam.married / fam.adults : 0), ratio('married', 'adults'), { note: '成年人', go: 'society' }),
            kpi('抑郁的', pct(mn.adults ? mn.depressed / mn.adults : 0, 1), ratio('depressed', 'adults'), { worse: true, go: 'society' }),
            kpi('近一年自杀', `${mn.year} 人`, null, { note: `每十万成年人 ${per100k(mn.year, mn.adults).toFixed(1)}`, go: 'society' }),
            kpi('近一年案子', `${num(crimes)} 件`, col('crimes'), { worse: true, note: `破案 ${pct(jj.clearance ?? 0)}`, go: 'justice|crime' }))) : '') +
          (q ? sec('建设', grid(
            kpi('在盖', `${s.building} 栋`, col('building'), { go: 'build' }),
            kpi('料钱差价', signed(q.skim), col('skim'), { note: '在盖的', go: 'build' }),
            kpi('近一年塌楼', `${q.fell} 栋`, null, { bad: q.fell > 0, note: `压死 ${q.crushed} 人`, go: 'build' }),
            kpi('安全度不够', `${q.weak.length} 个`, null, { bad: q.weak.length > 0, note: '在盖的', go: 'build' }))) : '') +
          sec('财政和钱', grid(
            kpi('国库', money(s.treasury), col('treasury'), { go: 'govt|fin' }),
            kpi('存款', money(s.deposits), col('deposits'), { go: 'finance' }),
            kpi('贷出', money(s.lent), col('lent'), { go: 'finance' }),
            kpi('贷款年息', `${(s.loanRate * 100).toFixed(1)}%`, col('loanRate'), { worse: true, go: 'finance' }))) +
          sec('这个月的大事', recent.length ? `<ol class="sb-news">${recent.map(item).join('')}</ol>` : '<div class="sb-empty">没有</div>');
      }],
      ['gdp', 'GDP', gdpPage],
      ['spend', '消费', spendPage],
      ['cbd', '商务区', cbdPage],
      ['sum', '人口和工作', () =>
        sec('人口', lineChart(xs, [{ name: '人口', vals: col('pop') }, { name: '成年人', vals: col('adults') }, { name: '露宿', vals: col('homeless') }], { zero: true, key: 'ov:pop' })) +
        sec('人都在干什么', areaChart(xs, [{ name: '打工', vals: col('employed') }, { name: '当老板', vals: col('owners') }, { name: '没活干', vals: col('idle') }], { key: 'ov:work' }))],
      ['price', '物价', () =>
        sec('物价和工钱', lineChart(xs, [{ name: '日工钱', vals: col('wage') }, { name: '日用一份', vals: col('price') }, { name: '房租一天', vals: col('rent') }], { log: true, key: 'ov:price' }), '对数刻度') +
        sec('房价和地价', lineChart(xs, [{ name: '房价(百平米)', vals: col('house'), fmt: money }, { name: '地价(一块)', vals: col('land'), fmt: money }], { log: true, fmt: money, key: 'ov:house' }), '对数刻度')],
      ['build', '盖楼和钱', () =>
        sec('盖楼', lineChart(xs, [{ name: '累计盖完', vals: col('built') }, { name: '在盖', vals: col('building') }, { name: '等人接', vals: col('tendering') }], { zero: true, key: 'ov:build' })) +
        sec('钱', areaChart(xs, [{ name: '存款', vals: col('deposits') }, { name: '国库', vals: col('treasury') }], { fmt: money, key: 'ov:money' }))],
      ['flows', '钱怎么流', () => {
        const fl = read(E.flows());
        const sect = table(['谁', '上个月进', '上个月出', '净', '眼下手上'], fl.sectors.map((x: J) => [
          esc(x.name), money(x.in), money(x.out), signed(x.in - x.out), x.hold == null ? '' : money(x.hold)]));
        const cells = table(['从', '到', '上个月', '主要是'], fl.cells.slice(0, 40).map((c: J) => [
          esc(c.from), esc(c.to), money(c.amt), c.top.map((t: J) => `${esc(t.tag)} ${money(t.amt)}`).join(' · ') + (c.kinds > c.top.length ? ` 等 ${c.kinds} 类` : '')]));
        const names: string[] = fl.sectors.map((x: J) => x.name);
        const links = fl.cells.map((c: J) => ({ from: names.indexOf(c.from), to: names.indexOf(c.to), amt: c.amt })).filter((l: J) => l.from >= 0 && l.to >= 0);
        return sec('上个月钱从谁流到谁', sankey(names, links, money), '不算存取款') +
          sec('每一大类', sect, '居民手上是现钱加存款') + sec('每一股(大的在前)', cells);
      }],
    ];
  };

  const people = (): Tab[] => {
    const p = read(E.people());
    const s = city.stats;
    const { xs, col } = series();
    const jobs = Object.entries(p.jobs as Record<string, number>).sort((a, b) => b[1] - a[1]);
    const ages = ['0-9', '10-19', '20-29', '30-39', '40-49', '50-59', '60-69', '70-79', '80+'];
    const found: J[] = peopleQ ? p.top.filter((a: J) => a.name.includes(peopleQ) || a.what.includes(peopleQ)) : p.top;
    return [
      ['sum', '概况', () => grid(tile('人口', num(s.pop)), tile('成年人', num(s.adults)), tile('孩子', num(p.kids)), tile('没活干', num(s.idle), pct(s.adults ? s.idle / s.adults : 0, 1)),
        tile('露宿', num(s.homeless)), tile('最富一成占', pct(p.top10Share), '全城身家')) +
        sec('大家在干什么', donut(jobs.map(([k, v]) => ({ label: k, value: v })), (v) => `${v} 人`, '成年人')) +
        sec('年龄', bars(ages.map((a, i) => ({ label: a, value: p.ages[i] ?? 0 })), (v) => `${v} 人`))],
      ['rich', '贫富', () =>
        sec('身家十等分', bars((p.deciles as number[]).map((v, i) => ({ label: `第 ${i + 1} 档上限`, value: v, color: 'var(--s2)' })), money), '成年人按身家从少到多分十档') +
        sec('日工钱分布', (p.wageQ as number[]).length ? grid(...['一成的人低于', '四分之一低于', '中位数', '四分之三低于', '九成低于'].map((k, i) => tile(k, `${Math.round(p.wageQ[i])} 元`))) : '<div class="sb-empty">没有人在打工</div>')],
      ['day', '生活方式', () => {
        const l = p.lifestyle;
        if (!l) return '<div class="sb-empty">还没有一日安排数据</div>';
        if (!l.sample) return '<div class="sb-empty">没有在城成年人的一日安排样本</div>';
        const hour = (v: number) => `${num(v, 2)} 小时`;
        return `<p class="meta">${dateOf(l.day)}，按在城的 ${num(l.sample)} 名成年人估算通常一天，包含无业、退休和羁押者；不是今天已完成的活动。小时数为每人每天平均。</p>` +
          grid(tile('平均可支配', hour(l.avgFree)), tile('平均工作', hour(l.avgWork)), tile('平均来回通勤', hour(l.avgCommute)), tile('平均家庭照料', hour(l.avgCare)),
            tile('安排超过一天', `${num(l.overloaded)} 人`, '固定事项超过 24 小时', l.overloaded ? 'bad' : ''), tile('通勤使用估计', `${num(l.estimatedCommute)} 人`, '没有适用样本，按当前步行距离估计')) +
          sec('可支配时间最少的居民', l.constrained.length ? table(['居民', '状态', '可支配', '工作', '来回通勤', '照料', '超额'], l.constrained.map((a: J) => [
            `<a data-go="person:${a.who.id}" data-view="day">${esc(a.who.name)}</a><br><small class="meta">${a.age} 岁</small>`, esc(a.state), hour(a.free), hour(a.work),
            `${hour(a.commute)}${a.commuteEstimated ? '<br><small class="meta">步行估计</small>' : a.commuteSample != null ? `<br><small class="meta">${dateOf(a.commuteSample)}样本</small>` : ''}`,
            hour(a.care), a.overload > 0 ? `<span class="bad">${hour(a.overload)}</span>` : '—',
          ])) : '<div class="sb-empty">没有成年人样本</div>', '最多 100 人；点击查看一日安排');
      }],
      ['family', '家庭与性别', () => {
        const f = p.familySystem;
        if (!f) return '<div class="sb-empty">没有家庭统计</div>';
        return grid(...f.genders.map((g: J) => tile(g.name, `${num(g.n)} 人`, `成年人 ${num(g.adults)} 人`)),
          tile('婚姻家庭', `${num(f.couples)} 对`, `同性伴侣 ${num(f.sameSexCouples)} 对`), tile('孕育中', `${f.pregnant.length} 人`),
          tile('今日家人接济', `${num(f.helpToday)} 笔`, '成功从给款者实际转给家人'), tile('暂缺可照料监护人', `${num(f.noGuardian)} 名孩子`, '不含机构照看的孩子', f.noGuardian ? 'bad' : '')) +
          sec('孕育中的家庭', f.pregnant.length ? table(['居民', '岁', '另一出生亲', '预产日期', '还需'], f.pregnant.slice(0, 100).map((a: J) => [
            `<a data-go="person:${a.who.id}" data-view="family">${esc(a.who.name)}</a>`, String(a.age), a.partner ? who(a.partner) : '未登记', dateOf(a.due), `${a.daysLeft} 天`,
          ])) : '<div class="sb-empty">当前没有孕育记录</div>', '最多100人，点击进入家庭') +
          '<p class="meta">婚配需要双方偏好相容并排除亲属；孕育经过270个游戏日。家庭保留出生双亲和当前监护人，每个孩子的口粮由唯一生活监护人负责。性别不改变工资或手艺。</p>';
      }],
      ['find', `找人 · 在城成年人 ${found.length}`, () => {
        const maxWorth = Math.max(1, ...found.map((a: J) => a.worth ?? 0));
        const cols: [string, (a: J) => number | string, (a: J) => string][] = [
          ['人', (a) => a.name, (a) => avatar(a.id, a.age, a.what ?? '', a.happy, 20) + who({ kind: 'person', id: a.id, name: a.name })],
          ['岁', (a) => a.age, (a) => String(a.age)],
          ['身家', (a) => a.worth, (a) => dbar(money(a.worth), a.worth / maxWorth, 'var(--good)')],
          ['资产 / 累计流入', (a) => a.from?.amt ?? 0, (a) => (a.from ? `<span title="${esc(a.from.scope ?? '')}">${esc(a.from.what)} ${money(a.from.amt)}</span>` : '')],
          ['估计日收入', (a) => a.income, (a) => `<span title="${esc(p.incomeScope ?? '预算估计，不是当天实际到账；实发见任职与工资。')}">${Math.round(a.income)}</span>`],
          ['现金存款', (a) => a.cash, (a) => money(a.cash)],
          ['欠银行', (a) => a.debt, (a) => (a.debt ? money(a.debt) : '')],
          ['公司', (a) => a.firms, (a) => (a.firms ? String(a.firms) : '')],
          ['房', (a) => a.units, (a) => (a.units ? `${a.units} 套` : '')],
          ['手艺', (a) => a.skill, (a) => a.skill.toFixed(2)],
          ['心情', (a) => a.happy, (a) => String(Math.round(a.happy * 100))],
          ['在干什么', (a) => a.what, (a) => esc(a.what)],
        ];
        const rows = sortRows('people', found, cols.map((c) => c[1])).slice(0, 200);
        return `<p class="meta">名单仅含当前在城成年人。身家与人物详情同口径；资产及累计流入不等于投资利润。显示 ${rows.length} / ${found.length} 人。</p><input class="sb-search" data-input="people" placeholder="名字或者公司" value="${esc(peopleQ)}">` +
          table(cols.map((c) => c[0]), rows.map((a: J) => cols.map((c) => c[2](a))), { sortKey: 'people', sortCol: sorts.people.col, sortDir: sorts.people.dir });
      }],
      ['chart', '曲线', () =>
        sec('人口', lineChart(xs, [{ name: '人口', vals: col('pop') }, { name: '打工', vals: col('employed') }, { name: '当老板', vals: col('owners') }, { name: '没活干', vals: col('idle') }], { zero: true, key: 'pp:pop' })) +
        sec('每月出生、去世、迁入、迁出', lineChart(xs, [{ name: '出生', vals: col('births') }, { name: '去世', vals: col('deaths') }, { name: '迁入', vals: col('arrived') }, { name: '迁出', vals: col('left') }], { zero: true, key: 'pp:flow' })) +
        sec('挨饿(一个月里平均每天)', lineChart(xs, [{ name: '挨饿', vals: col('hungry') }], { zero: true, key: 'pp:hungry' }))],
    ];
  };

  // 社会:家庭、心理健康(见 dash.rs 的 society;治安、法院在司法)
  const society = (): Tab[] => {
    if (!E.society) return [['none', '社会', () => '<div class="sb-empty">这个版本的引擎还没有</div>']];
    const so = read(E.society());
    const f = so.family, m = so.mental, j = so.justice;
    const { xs, col } = series();
    const adults = f.adults || 1;
    const crimes = (j.kinds as J[]).filter((k) => k.kind !== '交通肇事').reduce((a, k) => a + k.n, 0);
    return [
      ['sum', '概况', () => grid(
          tile('结了婚的', pct(f.married / adults), `${num(f.married)} 人`), tile('近一年结婚', `${num(f.marriages)} 对`), tile('近一年离婚', `${num(f.divorces)} 对`),
          tile('抑郁的', pct(m.adults ? m.depressed / m.adults : 0, 1), `${num(m.depressed)} 人`, m.adults && m.depressed / m.adults > 0.08 ? 'bad' : ''),
          tile('近一年自杀', `${m.year} 人`, `每十万成年人 ${per100k(m.year, m.adults).toFixed(1)}`, m.year ? 'bad' : ''),
          tile('近一年案子', `${num(crimes)} 件`, `每十万成年人 ${per100k(crimes, adults).toFixed(0)}`, '', ' data-tab="justice|crime"'), tile('破案率', pct(j.clearance ?? 0), '近半年', '', ' data-tab="justice|crime"'),
          tile('在坐牢', `${j.jailed} 人`, '', '', ' data-tab="justice|jail"'), tile('被欠薪', `${num(j.owedPeople)} 人`, money(j.owed), j.owedPeople ? 'bad' : ''), tile('在罢工', `${j.striking.length} 家`, '', j.striking.length ? 'bad' : '')) +
        (j.striking.length ? sec('在罢工的公司', table(['公司', '到哪天'], (j.striking as J[]).map((x) => [who(x.firm), dateOf(x.until)]))) : '') +
        sec('每月的结婚、离婚', lineChart(xs, [{ name: '结婚', vals: col('marriages') }, { name: '离婚', vals: col('divorces') }], { zero: true, key: 'so:fam', h: 120 })) +
        sec('每月的案子', lineChart(xs, [{ name: '案子', vals: col('crimes') }, { name: '命案', vals: col('homicides') }], { zero: true, key: 'so:crime2', h: 120 })) +
        sec('抑郁的人', lineChart(xs, [{ name: '抑郁的人', vals: col('depressed') }], { zero: true, key: 'so:dep2', area: true, h: 110 }))],
      ['family', '家庭', () => grid(tile('结了婚的', pct(f.married / adults), `${num(f.married)} 人`), tile('近一年结婚', `${num(f.marriages)} 对`), tile('近一年离婚', `${num(f.divorces)} 对`),
          tile('近一年出生', `${num(f.births)} 人`), tile('近一年家里接济', `${num(f.help)} 笔`, '夫妻之间、孩子给老人'), tile('身边没人的老人', `${num(f.lonelyOld)} 人`, '六十五岁以上,没有配偶、孩子在城里', f.lonelyOld ? 'bad' : '')) +
        sec('各年龄结了婚的', bars((f.byAge as J[]).map((x) => ({ label: x.name, value: x.n ? x.married / x.n : 0, sub: `${num(x.married)} / ${num(x.n)} 人` })), (v) => pct(v))) +
        sec('结了婚的人', lineChart(xs, [{ name: '结了婚的', vals: col('married') }], { zero: true, key: 'so:married', area: true, h: 120 })) +
        sec('每月的结婚、离婚', lineChart(xs, [{ name: '结婚', vals: col('marriages') }, { name: '离婚', vals: col('divorces') }], { zero: true, key: 'so:fam2', h: 120 }))],
      ['mind', '心理健康', () => mentalPage(m)],
    ];
  };

  // 建设:在盖的工程、建筑公司、工程质量、塌楼、政府发包、工地安全(见 dash.rs 的 construction)
  const building = (): Tab[] => {
    if (!E.construction) return [['none', '建设', () => '<div class="sb-empty">这个版本的引擎还没有</div>']];
    const c = read(E.construction());
    const q = c.quality;
    const live: J[] = c.live;
    const builders: J[] = c.builders;
    const { xs, col } = series();
    const sf = (v: number | null) => (v == null ? '' : v < 1.2 ? `<span class="bad">${v.toFixed(2)}</span>` : v.toFixed(2));
    return [
      ['sum', '概况', () => grid(
          tile('在盖', `${live.length} 个`, `政府的 ${live.filter((p) => p.gov).length} 个`), tile('料钱差价', signed(q.skim), '在盖的,按图纸结的 − 实际花的'),
          tile('安全度不够', `${q.weak.length} 个`, '一点二以下', q.weak.length ? 'bad' : ''), tile('近一年塌楼', `${q.fell} 栋`, `压死 ${q.crushed} 人`, q.fell ? 'bad' : ''),
          tile('近一年追责', `${q.jailed} 起`, '重大责任事故'), tile('近三十天工伤', `${c.injuries} 人`, `身亡 ${c.workDeaths} 人`, c.workDeaths ? 'bad' : ''),
          tile('建筑公司', `${builders.length} 家`, `不能投政府的标 ${builders.filter((b) => b.debarred).length} 家`)) +
        `<div class="sb-2">${donut(q.levels.map((x: J) => ({ label: x.name, value: x.n })), (v) => `${v} 个`, '在盖的做法')}${donut(q.tiers.map((x: J) => ({ label: x.name, value: x.n })), (v) => `${v} 个`, '用的料')}</div>` +
        sec('在盖、盖完', lineChart(xs, [{ name: '在盖', vals: col('building') }, { name: '等人接', vals: col('tendering') }], { zero: true, key: 'bd:n', h: 120 })) +
        sec('料钱差价(在盖的)', lineChart(xs, [{ name: '差价', vals: col('skim'), fmt: money }], { fmt: money, zero: true, key: 'bd:skim', h: 110 }), '按图纸结的 − 实际花的')],
      ['invest', '投资', () => {
        const iv = c.invest;
        const ms: number[][] = iv.months;
        const xsM = monthsX(ms.length);
        const part = (k: number) => ms.map((m) => m[k]);
        return grid(tile('今年签的工程', money(iv.thisYear), `去年 ${money(iv.lastYear)}`), tile('最近一个月', money(ms.length ? ms[ms.length - 1].reduce((a, b) => a + b, 0) : 0), '新签的合同价')) +
          sec('每月新签的工程(合同价)', lineChart(xsM, [{ name: '政府', vals: part(0), fmt: money }, { name: '开发商', vals: part(1), fmt: money }, { name: '个人', vals: part(2), fmt: money }, { name: '别的公司、银行', vals: part(3), fmt: money }], { fmt: money, zero: true, key: 'bd:inv', h: 140 })) +
          sec('每月买地', lineChart(xsM, [{ name: '地价', vals: (iv.land as number[]).map((v, i) => (iv.landFrom != null && i * 30 + 29 < iv.landFrom ? null : v)) as number[], fmt: money }], { fmt: money, zero: true, key: 'bd:land', h: 110 }), iv.landFrom != null ? `成交只留最近几百笔,从${dateOf(iv.landFrom)}起` : '') +
          sec('最近签的工程', table(['工程', '出钱的', '楼型', '层', '合同价', '签约', '建筑公司', ''], (iv.recent as J[]).map((p) => [projLink(p.id, p.name), who(p.investor), esc(p.type), String(p.floors), money(p.price), dateOf(p.signed), p.builder ? who(p.builder) : '', p.done ? '<span class="tag">盖完</span>' : ''])));
      }],
      ['live', `在盖 ${live.length}`, () => {
        const cols: [string, (p: J) => number | string, (p: J) => string][] = [
          ['工程', (p) => p.name, (p) => projLink(p.id, p.name) + (p.gov ? ' <span class="tag">政府</span>' : '')],
          ['建筑公司', (p) => p.builder?.name ?? '', (p) => (p.builder ? who(p.builder) : '')],
          ['层', (p) => p.floors, (p) => `${p.done}/${p.floors}`],
          ['进度', (p) => p.progress, (p) => esc(projectProgressText(p))],
          ['做法', (p) => p.level, (p) => (p.level ? `<span class="bad">${esc(p.method)}</span>` : esc(p.method))],
          ['料', (p) => p.black, (p) => `${esc(p.tier)}${p.black > 0.05 ? ` <span class="meta">黑 ${pct(p.black)}</span>` : ''}`],
          ['监理', (p) => p.supervisor, (p) => esc(p.supervisor)],
          ['料钱差价', (p) => p.skim, (p) => (p.skim ? signed(p.skim) : '')],
          ['安全度', (p) => p.safety ?? 99, (p) => sf(p.safety)],
        ];
        const rows = sortRows('live', live, cols.map((x) => x[1]));
        return `<p class="meta">显示 ${Math.min(rows.length, 200)} / ${rows.length} 个在盖工程；按当前排序最多列出 200 个。</p>` +
          table(cols.map((x) => x[0]), rows.slice(0, 200).map((p) => cols.map((x) => x[2](p))), { sortKey: 'live', sortCol: sorts.live?.col ?? 0, sortDir: sorts.live?.dir ?? -1 });
      }],
      ['builders', `建筑公司 ${builders.length}`, () => {
        const cols: [string, (b: J) => number | string, (b: J) => string][] = [
          ['公司', (b) => b.firm.name, (b) => badge(b.firm.name, 'builder', 18) + who(b.firm) + (b.debarred ? ' <span class="tag bad">禁投</span>' : '')],
          ['人', (b) => b.staff, (b) => String(b.staff)],
          ['手上', (b) => b.jobs, (b) => String(b.jobs)],
          ['交付', (b) => b.built, (b) => String(b.built)],
          ['品级', (b) => b.grade ?? -1, (b) => (b.grade == null ? '' : `${b.grade.toFixed(2)}`)],
          ['塌过', (b) => b.collapses, (b) => (b.collapses ? `<span class="bad">${b.collapses}</span>` : '')],
          ['偷了没事', (b) => b.skimOk, (b) => (b.skimOk ? String(b.skimOk) : '')],
          ['返工', (b) => b.reworks, (b) => (b.reworks ? String(b.reworks) : '')],
          ['评标底分', (b) => b.score, (b) => b.score.toFixed(1)],
          ['料钱差价', (b) => b.skim, (b) => (b.skim ? signed(b.skim) : '')],
          ['年净利润', (b) => b.profit, (b) => signed(b.profit)],
          ['老板胆子', (b) => b.risk, (b) => String(Math.round(b.risk * 100))],
          ['老板耐心', (b) => b.patience, (b) => String(Math.round(b.patience * 100))],
        ];
        const rows = sortRows('builders', builders, cols.map((x) => x[1]));
        return table(cols.map((x) => x[0]), rows.map((b) => cols.map((x) => x[2](b))), { sortKey: 'builders', sortCol: sorts.builders?.col ?? 0, sortDir: sorts.builders?.dir ?? -1 });
      }],
      ['quality', '工程质量', () => qualityPage(q)],
      ['fell', `塌楼 ${c.fell.length}`, () => grid(tile('近一年塌楼', `${q.fell} 栋`, '', q.fell ? 'bad' : ''), tile('近一年压死', `${q.crushed} 人`, '住户;工人算工伤', q.crushed ? 'bad' : ''), tile('近一年追责', `${q.jailed} 起`)) +
        sec('每月塌楼', lineChart(xs, [{ name: '塌楼', vals: col('collapsed') }, { name: '压死的住户', vals: col('collapseDeaths') }], { zero: true, key: 'bd:fell', h: 110 })) +
        sec('塌过的楼', c.fell.length ? `<ol class="sb-news">${(c.fell as J[]).map((x) => `<li${x.plot != null ? ` data-go="plot:${x.plot}"` : ''}><span class="tag k-倒塌">倒塌</span><time>${esc(dateOf(x.day))}</time><p>${linkify(x.text)}</p>${x.firm ? `<small class="meta">${who(x.firm)}</small>` : ''}</li>`).join('')}</ol>` : '<div class="sb-empty">还没有</div>')],
      ['tender', '政府发包', () => tenderPage(c.tenders, c.grafts)],
      ['safety', '工地安全', () => grid(tile('近三十天受重伤', `${c.injuries} 人`, '', c.injuries ? 'bad' : ''), tile('近三十天身亡', `${c.workDeaths} 人`, '', c.workDeaths ? 'bad' : '')) +
        sec('每月', lineChart(xs, [{ name: '受重伤', vals: col('injuries') }, { name: '身亡', vals: col('workDeaths') }], { zero: true, key: 'bd:safe', h: 120 }))],
    ];
  };

  const mrow = (m: J, key: number, extra: string[] = []) => [
    `<a data-go="market:${key}">${esc(m.name)}</a>`, num(m.price, m.price < 100 ? 1 : 0), num(m.trend, m.trend < 100 ? 1 : 0),
    `<span class="${m.price > m.trend * 1.03 ? 'up' : m.price < m.trend * 0.97 ? 'down' : ''}">${m.trend ? ((m.price / m.trend - 1) * 100).toFixed(0) : 0}%</span>`,
    num(m.volume, 1), num(m.bids, 1), num(m.asks, 1), m.dry ? `<span class="bad">${m.dry} 天</span>` : '', ...extra,
  ];
  const market = (): Tab[] => {
    const m = read(E.markets());
    const { xs, col } = series();
    const head = ['市场', '价', '两个月均价', '比均价', '今天成交', '买单', '卖单', '没成交'];
    return [
      ['goods', '日用和餐饮', () => table([...head, '店', '存货'], m.goods.map((g: J, i: number) => mrow(g, i, [String(g.sellers), num(g.stock, 0)]))) +
        (() => {
          const ec = econ();
          return grid(tile('全国物价', (ec?.national ?? m.outside).toFixed(1), '本城和邻城一份日用的价,按人口平均'),
            ec?.cpi != null ? tile('居民消费价格', ec.cpi.toFixed(1), `同比 ${pctS(ec.cpiYoy)} · 开头 = 100`, '', ' data-tab="overview|gdp"') : tile('城里物价(年均线)', m.cpi.toFixed(1)));
        })() +
        sec('日用和餐饮的价', lineChart(xs, [{ name: '日用', vals: col('price') }, { name: '餐饮娱乐', vals: col('funPrice') }], { log: true, key: 'mk:goods' }), '对数刻度')],
      ['supply', '进货和运输', () =>
        table([...head, '厂', '存货'], m.supply.map((g: J) => mrow(g, g.key, [String(g.sellers), num(g.stock, 0)])))],
      ['labor', '工钱', () => table(['工种', '日工钱', '比两个月均价', '在岗', '今天干活', '闲着', '当老板', '上月日均到手'], m.labor.map((g: J, i: number) => [
          `<a data-go="market:${100 + i}">${esc(g.name)}</a>`, num(g.price, 0),
          `<span class="${g.price > g.trend * 1.03 ? 'up' : g.price < g.trend * 0.97 ? 'down' : ''}">${g.trend ? ((g.price / g.trend - 1) * 100).toFixed(0) : 0}%</span>`,
          String(g.employed), g.worked != null ? String(g.worked) : '', String(g.idle), String(g.owners), num(g.earn, 0)])) +
        `<p class="meta">上月日均到手按这一行所有人算,闲着的算 0</p>` +
        sec('近三十天', table(['招', '辞退', '其中拿到补偿', '工地没活走', '嫌工钱低走', '公司关门散', '嫌远没去'], [[m.flows.hired, m.flows.fired, m.flows.severance ?? 0, ...m.flows.walked, m.flows.tooFar].map((x: number) => String(x))]) +
          table(['没活干改学建筑', '普工辞了去学', '工地收学徒', '外地人来干', '去外地招', '改行做普工'], [m.flows.crafts.map((x: number) => String(x))])) +
        sec('工钱和实际工钱', lineChart(xs, [{ name: '日工钱', vals: col('wage') }, { name: '买得起几份日用', vals: col('realWage') }], { log: true, key: 'mk:wage' }))],
      ['mat', '建材', () => `<p class="meta">邻城运来的只有正规厂档次</p>` +
        table([...head, '厂', '存货'], m.materials.map((g: J, i: number) => ({ g, i })).filter(({ g }: J) => g.volume > 0 || g.sellers > 0 || g.dry < 90).map(({ g, i }: J) => mrow(g, 2 + i, [String(g.sellers), num(g.stock, 0)]))) +
        sec('建材的价', lineChart(xs, [{ name: '混凝土一方', vals: col('concrete') }, { name: '钢筋一吨', vals: col('steel') }], { log: true, key: 'mk:mat' }), '对数刻度')],
      ['estate', '房、地、合同', () => table(head, [[m.rent, 200], [m.office, 201], [m.house, 202], [m.land, 203], [m.tender, 204]].map(([g, k]: J) => mrow(g, k)))],
    ];
  };

  const estate = (): Tab[] => {
    const e = read(E.estate());
    const d = read(E.deals());
    const { xs, col } = series();
    const types = Object.entries(e.types as Record<string, [number, number]>);
    const live = [...e.live].sort((a: J, b: J) => b.floors - a.floors);
    const stalled = live.filter((p: J) => p.waiting || p.stage === '等人接');
    return [
      ['sum', '概况', () => grid(tile('住房', `${e.homes} 套`, `空 ${e.vacant} 套`), tile('自住', `${e.ownerOcc} 套`, pct(e.homes ? e.ownerOcc / e.homes : 0)), tile('挂牌卖', `${e.listed} 套`),
        tile('写字楼', `${e.officesLet}/${e.offices} 套已租`, `还缺 ${Math.max(0, Math.round(e.officeShort))} 套`), tile('拆了重盖', `${e.demolished} 栋`), tile('塌了的废墟', `${e.collapsed} 块`, '', e.collapsed ? 'bad' : ''),
        ...(e.renos != null ? [tile('装修', `平均${newness(e.worn)}`, `旧过一半 ${e.shabby} 套 · 豪华 ${e.lux} 套`), tile('在翻新', `${e.renos} 套`, e.renoLux ? `翻成豪华 ${e.renoLux} 套` : '')] : [])) +
        sec('房子都是谁的', donut(Object.entries(e.byOwner as Record<string, number>).map(([k, v]) => ({ label: k, value: v })), (v) => `${v} 套`, '一共')) +
        sec('各种楼', table(['楼型', '栋', '套'], types.map(([k, [n, u]]) => [esc(k), String(n), String(u)])))],
      ['dist', '片区', () => table(['片区', '住房', '空置率', '房租(百平一天)', '估价(百平)', '估价是几年房租', '最近成交(百平,中位)', '成交几套', '装修', '豪华'],
        d.districts.filter((x: J) => x.homes).map((x: J) => [esc(x.name), String(x.homes), pct(x.vacant / x.homes), x.rent.toFixed(0), money(x.now), x.rent > 0 ? `${(x.now / (x.rent * 360)).toFixed(1)} 年` : '—', x.median != null ? money(x.median) : '—', String(x.deals), x.worn != null ? `平均${newness(x.worn)}` : '', x.lux != null ? `${x.lux} 套` : '']))],
      ['deals', '成交', () => `<p class="meta">分布使用当前保留的 ${d.list.length} 笔成交记录；下表展示最近 ${Math.min(d.list.length, 40)} 笔，最多 40 笔，不代表全部历史成交。</p>` +
        sec('成交价的分布(每平米)', histogram(d.list.map((x: J) => x.perM2), 12, (v) => `${Math.round(v)}`)) +
        sec('最近的成交', table(['哪天', '楼', '片区', '面积', '价', '每平米', '买家', '卖家'],
          d.list.slice(0, 40).map((x: J) => [monthOf(x.day), plotLink(x.plot, x.name) + (x.presale ? ' <span class="tag">期房</span>' : '') + (x.what && x.what !== '住房' ? ` <span class="tag">${esc(x.what)}</span>` : ''), esc(x.class), `${Math.round(x.area)}㎡`, money(x.price), `${Math.round(x.perM2)} 元`, who(x.buyer), who(x.seller)])))],
      ['land', `地块 ${(e.landDeals ?? []).length}`, () => (e.landDeals ? grid(tile('近一年地价', `${(e.landTrend ?? 0) >= 0 ? '+' : ''}${(e.landTrend ?? 0).toFixed(0)}%`, '按普通地段折算'), tile('挂牌的私地', `${e.landListed.length} 块`), tile('囤着的', `${e.landSpec} 块`)) +
          (e.tenderCount ? grid(tile('政府的地竞价', `${e.tenderCount[0]} 场`, `成交 ${e.tenderCount[1]} · 流拍 ${e.tenderCount[0] - e.tenderCount[1]} · 每场 ${(e.tenderCount[2] / Math.max(1, e.tenderCount[0])).toFixed(1)} 家`),
            tile('私人的地竞价', `${e.tenderCount[3]} 场`, `成交 ${e.tenderCount[4]} · 流拍 ${e.tenderCount[3] - e.tenderCount[4]} · 每场 ${(e.tenderCount[5] / Math.max(1, e.tenderCount[3])).toFixed(1)} 家`)) : '') +
          ((e.tenders as J[] | undefined)?.length ? sec('在竞价的地', tendersTable(e.tenders)) : '') +
          ((e.landSales as J[] | undefined)?.length ? sec('最近拿地的出价', landSalesTable(e.landSales, true)) : '') +
          sec('最近的地块成交', table(['哪天', '地', '片区', '价', '卖家', '买家', '怎么'], (e.landDeals as J[]).map((d) => [monthOf(d.day), plotLink(d.plot, `(${d.at[0]}, ${d.at[1]})`), esc(d.district), money(d.price), who(d.seller), who(d.buyer), esc(d.how)]))) +
          sec('挂牌的私地', (e.landListed as J[]).length ? table(['地', '片区', '要价', '主人', '当初买价', ''], (e.landListed as J[]).map((d) => [plotLink(d.plot, `(${d.at[0]}, ${d.at[1]})`), esc(d.district), money(d.ask), who(d.owner), money(d.paid), d.spec ? '<span class="tag">囤地</span>' : ''])) : '<div class="sb-empty">没有</div>')
        : '<div class="sb-empty">这个版本的引擎还没有</div>')],
      ['live', `工程 ${live.length}`, () => `<p class="meta">在盖和等人接的 ${live.length} 个,停着的 ${stalled.length} 个</p>` + table(['工程', '层', '阶段', '进度', '投资人', '建筑公司', '监管账户'],
        live.slice(0, 80).map((p: J) => [projLink(p.id, p.name), `${p.done}/${p.floors}`, `${esc(projectProgressStage(p))}${p.waiting ? ' <span class="bad">停工</span>' : ''}`, esc(projectProgressPercent(p.progress)), who(p.investor), p.builder ? who(p.builder) : '<span class="meta">没人接</span>', money(p.escrow)]))],
      ['cx', '小区', () => {
        // 小区分析:每栋住宅楼一个小区;贵贱差多少、什么收入的人住什么样的小区(见 eco 的 dash.rs 的 complexes)
        const c = read(E.complexes());
        const sp = c.spread;
        if (!sp.n) return '<div class="sb-empty">还没有盖好的住宅楼</div>';
        const gap = sp.p10 > 0 ? `九成的小区比一成的贵 ${(sp.p90 / sp.p10).toFixed(1)} 倍` : '';
        const head = grid(tile('小区', `${sp.n} 个`), tile('最便宜', money(sp.min), '估价,每百平'), tile('一成', money(sp.p10), '一成的房比它便宜'), tile('中间', money(sp.p50)),
          tile('九成', money(sp.p90), '九成的房比它便宜'), tile('最贵', money(sp.max), gap));
        const tierNames = ['收入最低的两成', '偏低', '中间', '偏高', '收入最高的两成'];
        const tiers = sec('什么人住什么小区(住户按收入分五档)', table(['住户', '一天收入', '住的小区估价(百平)', '租房的房租(百平一天)', '自住', '多半住在', '各片区'],
          (c.tiers as J[]).map((t, i) => [tierNames[i] ?? '', `${t.lo.toFixed(0)}~${t.hi.toFixed(0)} · 平均 ${t.income.toFixed(0)}`, money(t.v100), t.rent100 ? t.rent100.toFixed(0) : '—', pct(t.own),
            `${esc(t.district)}(${pct(t.districtShare)})`, (t.byDistrict as [string, number][]).map(([k, n]) => `${esc(k)} ${n}`).join(' · ')])) +
          bars((c.tiers as J[]).map((t, i) => ({ label: tierNames[i] ?? '', value: t.v100 })), money));
        const cols: [string, (x: J) => number | string, (x: J) => string][] = [
          ['小区', (x) => x.name, (x) => projLink(x.id, x.name) + (x.condemned ? ' <span class="bad">危楼</span>' : '')],
          ['片区', (x) => x.district, (x) => esc(x.district)],
          ['楼型', (x) => x.type, (x) => esc(x.type)],
          ['层', (x) => x.floors, (x) => String(x.floors)],
          ['套', (x) => x.units, (x) => String(x.units)],
          ['每套', (x) => x.area, (x) => `${Math.round(x.area)}㎡`],
          ['盖好几年', (x) => x.age ?? -1, (x) => (x.age != null ? x.age.toFixed(1) : '老房')],
          ['估价(百平)', (x) => x.v100, (x) => money(x.v100)],
          ['房租(百平一天)', (x) => x.rent100, (x) => (x.rent100 ? x.rent100.toFixed(0) : '—')],
          ['空', (x) => x.vacant / x.units, (x) => (x.vacant ? `${x.vacant} 套` : '')],
          ['豪华', (x) => x.lux, (x) => (x.lux ? pct(x.lux) : '')],
          ['装修', (x) => -x.worn, (x) => newness(x.worn)],
          ['住户收入', (x) => x.income, (x) => (x.live ? x.income.toFixed(0) : '—')],
          ['打工', (x) => x.work, (x) => (x.live ? pct(x.work) : '')],
          ['老板', (x) => x.boss, (x) => (x.boss ? pct(x.boss) : '')],
          ['退休', (x) => x.old, (x) => (x.old ? pct(x.old) : '')],
          ['没活干', (x) => x.idle, (x) => (x.idle ? `<span class="bad">${pct(x.idle)}</span>` : '')],
          ['自住', (x) => x.own, (x) => (x.live ? pct(x.own) : '')],
          ['危险度', (x) => x.danger, (x) => (x.danger > 0.05 ? `<span class="bad">${pct(x.danger)}</span>` : '')],
        ];
        const rows = sortRows('cx', c.list as J[], cols.map((x) => x[1]));
        return head + tiers + sec(`小区一览(${rows.length} 个,点列头排序)`, table(cols.map((x) => x[0]), rows.slice(0, 300).map((x) => cols.map((k) => k[2](x))), { sortKey: 'cx', sortCol: sorts.cx.col, sortDir: sorts.cx.dir }));
      }],
      ['tall', '最高的楼', () => table(['楼', '楼型', '层', '主人'], e.tall.map((t: J) => [plotLink(t.plot, t.name), esc(t.type), String(t.floors), who(t.owner)]))],
      ['chart', '曲线', () =>
        sec('房价和一年房租', lineChart(xs, [{ name: '房价', vals: col('house'), fmt: money }, { name: '一年房租', vals: col('rent').map((v) => v * 360), fmt: money }], { log: true, fmt: money, key: 'es:house' }), '对数刻度;房价是近来真成交的中位数,成交少的年份跳得厉害,各套房的估值比它稳') +
        sec('住房和空房', lineChart(xs, [{ name: '住房', vals: col('units') }, { name: '空房', vals: col('vacant') }, { name: '露宿', vals: col('homeless') }], { zero: true, key: 'es:units' })) +
        sec('写字楼', lineChart(xs, [{ name: '写字楼', vals: col('offices') }, { name: '租出去', vals: col('officesLet') }, { name: '租金', vals: col('officeRent') }], { zero: true, key: 'es:office' })) +
        sec('每月卖掉几套房', lineChart(xs, [{ name: '成交', vals: col('housesSold') }], { zero: true, key: 'es:sold' }))],
    ];
  };

  const firms = (): Tab[] => {
    const f = read(E.firms());
    const list: J[] = f.list;
    const kinds = Object.keys(KIND_CN).filter((k) => k !== 'public');
    const count = (k: string) => list.filter((x) => x.kind === k);
    const maxStaff = Math.max(1, ...list.map((x) => x.staff));
    const maxRev = Math.max(1, ...list.map((x) => x.rev12 ?? 0));
    const cols: [string, (x: J) => number | string, (x: J) => string][] = [
      ['公司', (x) => x.name, (x) => badge(x.name, x.kind, 18) + who({ kind: 'firm', id: x.id, name: x.name }) + (x.trouble ? ' <span class="tag bad">逾期</span>' : '')],
      ['行业', (x) => x.line, (x) => kindPill(x.kind, x.line)],
      ['人', (x) => x.staff, (x) => dbar(String(x.staff), x.staff / maxStaff)],
      ['年收入', (x) => x.rev12, (x) => dbar(money(x.rev12), (x.rev12 ?? 0) / maxRev, 'var(--good)')],
      ['年经营净利', (x) => x.profit12, (x) => signed(x.profit12)],
      ['净利率', (x) => x.margin ?? -1e9, (x) => (x.margin == null ? '' : `<span class="${x.margin < 0 ? 'bad' : ''}">${x.margin.toFixed(0)}%</span>`)],
      ['回报', (x) => x.roe ?? -1e9, (x) => (x.roe == null ? '' : `<span class="${x.roe < 0 ? 'bad' : ''}">${x.roe.toFixed(0)}%</span>`)],
      ['净资产', (x) => x.equity, (x) => signed(x.equity)],
      ['负债率', (x) => x.debtRatio ?? 0, (x) => (x.debtRatio ? `${x.debtRatio.toFixed(0)}%` : '')],
      ['年分红', (x) => x.div, (x) => (x.div ? money(x.div) : '')],
    ];
    let shown = list.filter((x) => x.kind !== 'public' && (!ft.kinds.size || ft.kinds.has(x.kind)) && x.staff >= ft.minStaff);
    if (ft.q) shown = shown.filter((x) => x.name.includes(ft.q) || x.owner.name?.includes(ft.q) || x.line.includes(ft.q));
    if (ft.profit === 'gain') shown = shown.filter((x) => x.profit12 > 0);
    if (ft.profit === 'loss') shown = shown.filter((x) => x.profit12 <= 0);
    if (ft.debt) shown = shown.filter((x) => x.debt > 0);
    if (ft.trouble) shown = shown.filter((x) => x.trouble);
    const [, key] = cols[ft.col];
    shown.sort((a, b) => {
      const va = key(a), vb = key(b);
      return (typeof va === 'string' ? String(va).localeCompare(String(vb), 'zh') : (va as number) - (vb as number)) * ft.dir;
    });
    const chip = (on: boolean, attr: string, t: string) => `<button class="chip${on ? ' on' : ''}" ${attr}>${t}</button>`;
    return [
      ['find', `找公司 ${shown.length}`, () =>
        `<input class="sb-search" data-input="firms" placeholder="公司名、总经理、行业" value="${esc(ft.q)}">` +
        `<div class="sb-chips"><span>行业</span>${chip(!ft.kinds.size, 'data-kind="all"', '全部')}${kinds.map((k) => chip(ft.kinds.has(k), `data-kind="${k}"`, KIND_CN[k])).join('')}</div>` +
        `<div class="sb-chips"><span>筛选</span>${chip(ft.profit === 'gain', 'data-filter="gain"', '赚钱的')}${chip(ft.profit === 'loss', 'data-filter="loss"', '亏钱的')}${chip(ft.debt, 'data-filter="debt"', '有贷款')}${chip(ft.trouble, 'data-filter="trouble"', '在逾期')}${chip(ft.minStaff >= 5, 'data-filter="big"', '五人以上')}${chip(ft.minStaff >= 20, 'data-filter="huge"', '二十人以上')}</div>` +
        '<p class="meta">年收入与年经营净利润取最近12个已结账月,新公司按已有月数;与公司财报同口径。经营现金收支另列,投资持仓重估不算经营收入。</p>' +
        table(cols.map((c) => c[0]), shown.slice(0, 150).map((x) => cols.map((c) => c[2](x))), { sortKey: 'firms', sortCol: ft.col, sortDir: ft.dir })],
      ['lines', '各行各业', () => sec('各行多少人在干', treemap(kinds.map((k) => ({ label: KIND_CN[k], value: count(k).reduce((a, x) => a + x.staff, 0), color: KIND_COLOR[k] ?? 'var(--s-other)', link: ` data-kind="${k}"` })), (v) => `${num(v)} 人`), '') +
        table(['行业', '家', '人', '上月经营收入', '账上的钱', '欠银行'], kinds.map((k) => {
          const c = count(k);
          return [`<a data-kind="${k}">${KIND_CN[k]}</a>`, String(c.length), String(c.reduce((a, x) => a + x.staff, 0)), money(c.reduce((a, x) => a + x.in, 0)), money(c.reduce((a, x) => a + x.cash, 0)), money(c.reduce((a, x) => a + x.debt, 0))];
        })) + `<p class="meta">关门了的一共 ${f.closed} 家</p>`],
      ['expand', '产能与扩产', () => {
        const candidates = shown.filter((x) => x.expansion).sort((a, b) => b.expansion.cost - a.expansion.cost);
        const planned = candidates.filter((x) => x.expansion.cost > 0);
        return `<p class="meta">沿用“找公司”的行业、姓名和财务筛选。按现有人员、场地、设备、实售和公开买单推算；预期需求不保证成交，采购也要实际买到货。</p>` +
          grid(tile('可查看', `${candidates.length} 家`), tile('有设备采购目标', `${planned.length} 家`, '实际能买多少还要看资金与供货'), tile('目标设备总价', money(planned.reduce((s, x) => s + x.expansion.cost, 0)), '含税报价，尚未支出')) +
          table(['公司', '行业', '主要限制', '日均产出 / 配齐设备产能', '设备够几人 / 人手', '目标添设备', '含税目标价', '下一步'], candidates.slice(0, 150).map((x) => {
            const e = x.expansion;
            return [`<a data-go="firm:${x.id}" data-view="expand">${esc(x.name)}</a>`, kindPill(x.kind, x.line), esc(e.limit), `${num(e.output, 1)} / ${num(e.full, 1)} ${esc(e.unit)}`, `${num(e.kitWorkers, 1)} / ${e.workers}`, e.cost > 0 ? (e.buySets > 0 ? `${num(e.buySets, 2)} 套` : '&lt;0.01 套') : '—', e.cost > 0 ? money(e.cost) : '—', esc(e.next)];
          })) + (candidates.length > 150 ? '<p class="meta">按目标设备金额显示前150家，可用“找公司”缩小范围。</p>' : '') +
          '<p class="meta">配齐设备产能按现有人手和场地算；原料、库存和销售仍可能压低实际产出。日均值反映近期生产，人员或场地变化后会有滞后。不同产业保留自己的单位，不相加。</p>';
      }],
      ['chart', '曲线', () => {
        const { xs, col } = series();
        return sec('公司数', lineChart(xs, [{ name: '公司', vals: col('firms') }, { name: '建筑公司', vals: col('builders') }, { name: '建材厂', vals: col('plants') }, { name: '开发商', vals: col('devs') }, { name: '服务业', vals: col('services') }], { zero: true, key: 'fm:n' }));
      }],
    ];
  };

  const finance = (): Tab[] => {
    const b = read(E.bank(0));
    const { xs, col } = series();
    const banks: J[] = b.banks;
    return [
      ['banks', `银行 ${banks.filter((x) => !x.closed).length}`, () => table(['银行', '谁的', '存户', '存款', '贷出', '国债', '家底', '存款年息', '贷款年息', '信任', '央行本金', '央行欠息', '总部', '分行'],
        banks.map((x) => [who({ kind: 'bank', id: x.id, name: x.name }) + (x.closed ? ' <span class="tag bad">倒闭</span>' : ''), x.holder ? `${who(x.holder.who)} ${x.holder.pct.toFixed(0)}%` : '市政府', String(x.depositors), money(x.deposits), money(x.lent), money(x.bonds ?? 0),
          `<span class="${x.equity < 0 ? 'bad' : ''}">${money(x.equity)}</span>`, `${x.depRate.toFixed(1)}%`, `${x.loanRate.toFixed(1)}%`, String(Math.round(x.trust * 100)), money(x.central), money(x.centralInterest ?? 0),
          x.hq ? plotLink(x.hq.plot, `${x.hq.floors} 层${x.hq.own ? '' : '(租)'}`) : '', String(x.branches ?? 0)]))],
      ['cb', '央行', () => {
        const c = b.cb ?? { bought: 0, sold: 0, boughtLast: 0, soldLast: 0, held: 0, debt: 0, bondRate: 0, parked: 0, parkedIn: 0, parkedOut: 0 };
        return grid(tile('基准利率', `${b.policy.toFixed(1)}%`), inflTile(b.inflation),
            tile('本月买进国债', money(c.bought), `上月 ${money(c.boughtLast)}`), tile('本月卖出国债', money(c.sold), `上月 ${money(c.soldLast)}`),
            tile('持有国债', money(c.held), `国债余额 ${money(c.debt)}`),
            tile('再贷款余额', money(c.relend ?? 0), `本月放出 ${money(c.relendOut ?? 0)} · 收回 ${money(c.relendBack ?? 0)}`),
            tile('银行救急借款', money(c.rescue ?? 0), '报价年息不低于各家贷款和基准利率'),
            tile('应收银行欠息', money(c.interestDue ?? 0), '已计息未实付,未计入国库现钱'),
            tile('国库暂不存银行', money(c.parked), `本月收回 ${money(c.parkedIn)} · 存回 ${money(c.parkedOut)}`)) +
          sec('国债', lineChart(xs, [{ name: '国债余额', vals: col('govDebt'), fmt: money }, { name: '央行持有', vals: col('cbBonds'), fmt: money }], { fmt: money, zero: true, key: 'fn:cb', h: 130 }));
      }],
      ['sum', '城市商业银行', () =>
        grid(tile('贷款年息', `${b.loanRate.toFixed(1)}%`), tile('存款年息', `${b.depRate.toFixed(1)}%`), tile('基准利率', `${b.policy.toFixed(1)}%`, `通胀 ${b.inflation.toFixed(1)}%(全国)`), tile('信任', `${Math.round(b.trust * 100)}`),
          tile('家底', money(b.equity), '', b.equity < 0 ? 'bad' : ''), tile('还能放', money(b.room)), tile('收过的楼', String(b.defaults)), tile('逾期', `${b.late.length} 笔`, '', b.late.length ? 'bad' : '')) +
        sec('贷款按种类', table(['种类', '笔数', '还欠', '平均年息', '逾期', '逾期的本金'], b.kinds.map((k: J) => [esc(k.kind), String(k.n), money(k.principal), `${k.rate.toFixed(1)}%`, k.late ? `<span class="bad">${k.late}</span>` : '0', money(k.latePrincipal)])) +
          donut(b.kinds.map((k: J) => ({ label: k.kind, value: k.principal })), money, '贷出'))],
      ['who', '借钱的', () =>
        sec('在逾期的', table(['借的人', '种类', '还欠', '拖了'], b.late.map((x: J) => [who(x.who), esc(x.kind), money(x.principal), `<span class="bad">${x.missed} 天</span>`]))) +
        sec('借得最多的', table(['借的人', '笔数', '还欠', '逾期'], b.borrowers.map((x: J) => [who(x.who), String(x.n), money(x.principal), x.missed ? `<span class="bad">${x.missed} 天</span>` : ''])), `显示 ${b.borrowers.length} 个借款客户，按未偿本金列出前 30 个，不是全部借款客户`)],
      ['insure', `保险 ${(b.insure?.list ?? []).filter((x: J) => !x.closed).length}`, () => {
        const v2 = E.insure ? read(E.insure()) : null;
        if (v2 && !v2.v1) {
          const open = (v2.list as J[]).filter((x) => x && !x.closed);
          const shut = (v2.list as J[]).filter((x) => x && x.closed).length;
          const sum = (k: string) => open.reduce((a, x) => a + (x[k] ?? 0), 0);
          const risk = open.reduce((a, x) => a + (x.lines as J[]).reduce((s, l) => s + l.prem, 0), 0);
          return grid(tile('保险公司', `${open.length} 家`, shut ? `关过 ${shut} 家` : ''), tile('保单', String(sum('policies'))), tile('一天收保费', money(sum('prem'))),
              tile('一天赔', money(sum('claim')), risk > 0 ? `赔付率 ${pct(sum('claim') / risk)}` : '')) +
            sec('险种', table(['险种', '全城一年损失', '全城平均', '一次平均', '能投保', '投了', '几成', '在卖', '最低价', '手续费(月)'],
              (v2.lines as J[]).map((l) => [esc(l.name), money(l.loss), esc(l.base), esc(l.size), `${l.holders} ${esc(l.what)}`, String(l.insured), l.holders ? pct(l.insured / l.holders) : '', `${l.sellers} 家`, l.low ? esc(l.low) : '—', money(l.fee)]))) +
            sec('保险公司', (v2.list as J[]).length ? table(['保险公司', '开张', '人', '本钱', '要的本钱', '偿付能力', '保单', '一天收', '一天赔', '赔付率', '近一年分红'],
              (v2.list as J[]).filter((x) => x).map((x) => [who(x.firm) + (x.closed ? ` <span class="tag bad">${yearOf(x.closedOn)}关门</span>` : ''), yearOf(x.opened), x.closed ? '' : `${x.staff} / ${x.desks.toFixed(1)}`,
                x.closed ? '' : money(x.capital), x.closed ? '' : money(x.need), x.closed ? '' : `<span class="${x.solvency < 1 ? 'bad' : ''}">${x.solvency.toFixed(2)}</span>`,
                x.closed ? '' : String(x.policies), x.closed ? '' : money(x.prem), x.closed ? '' : money(x.claim), x.closed || x.lr == null ? '' : `<span class="${x.lr > 100 ? 'bad' : ''}">${x.lr.toFixed(0)}%</span>`, money(x.divYear)]))
              : '<div class="sb-empty">城里还没有保险公司</div>') +
            open.map((x) => sec(x.firm.name, carrierLines(x))).join('') +
            ((v2.log as J[]).length ? sec('最近的理赔', claimTable((v2.log as J[]).slice(0, 60))) : '');
        }
        const ins = b.insure ?? { list: [], injury: 0, home: 0 };
        return grid(tile('工伤赔付', `${ins.injury.toFixed(2)}%`, '占建筑公司工钱'), tile('塌楼损失', `${ins.home.toFixed(3)}%`, '一年占房子值多少')) +
          (ins.list.length ? table(['保险公司', '工伤险费率', '房屋险费率(一年)', '投保的建筑公司', '投保的房', '一天收保费', '一天赔', '账上'],
            ins.list.map((x: J) => [who(x.firm) + (x.closed ? ' <span class="tag bad">关门</span>' : ''), `${x.rate.toFixed(2)}%`, `${x.homeRate.toFixed(3)}%`, String(x.builders), String(x.homes),
              money(x.prem), `<span class="${x.claim > x.prem ? 'bad' : ''}">${money(x.claim)}</span>`, money(x.cash)]))
            : '<div class="sb-empty">城里还没有保险公司</div>');
      }],
      ['chart', '曲线', () =>
        sec('存款和贷款', lineChart(xs, [{ name: '存款', vals: col('deposits'), fmt: money }, { name: '贷出', vals: col('lent'), fmt: money }, { name: '开发商欠', vals: col('devDebt'), fmt: money }], { fmt: money, zero: true, key: 'fn:dep' })) +
        sec('利率', lineChart(xs, [{ name: '贷款年息', vals: col('loanRate').map((v) => v * 100), fmt: (v) => `${v.toFixed(1)}%` }, { name: '存款年息', vals: col('depRate').map((v) => v * 100), fmt: (v) => `${v.toFixed(1)}%` }], { fmt: (v) => `${v.toFixed(0)}%`, zero: true, key: 'fn:rate' })) +
        sec('城里的钱', lineChart(xs, [{ name: '城里的钱', vals: col('money'), fmt: money }], { fmt: money, log: true, key: 'fn:money' }))],
    ];
  };

  // 司法的几页:全部案子、法院、检察院、公安(见 dash.rs 的 court、cases)
  /** 一件案子的两边:原告(民事原告、讨薪工人;刑事是检察院)告被告。 */
  const sides = (c: J) => `${c.p ? who(c.p) : ''} 告 ${c.d ? who(c.d) : ''}${c.v ? ` <small class="meta">受害 ${who(c.v)}</small>` : ''}${c.wrong ? ' <span class="tag bad">抓错</span>' : ''}`;
  const allCases = (): J[] => {
    if (!E.cases) return [];
    if (!caseCache || Math.abs(city.day - caseCache.day) >= 10) caseCache = { day: city.day, rows: read(E.cases()) };
    return caseCache.rows;
  };
  const OPEN = ['在逃', '拘留', '取保候审', '待开庭', '调解', '执行中', '服刑'];
  /** 全部案子:按种类、了结没有筛,按名字搜,点表头排序,一次两百条。 */
  const caseBrowser = () => {
    const rows = allCases();
    if (!rows.length) return '<div class="sb-empty">还没有案子</div>';
    const q = cb.q.trim();
    const name = (x: J) => (x ? x.name ?? '' : '');
    const hit = rows.filter((c) => (!cb.cat || c.cat === cb.cat) && (!cb.open || (cb.open === 'open') === OPEN.includes(c.stage)) &&
      (!q || [name(c.p), name(c.d), name(c.v), c.kind, name(c.lawyer), name(c.other), String(c.id)].some((t) => t.includes(q))));
    const n = (k: string) => rows.filter((c) => c.cat === k).length;
    const chip = (key: string, v: string, label: string) => `<button class="chip${cb[key] === v ? ' on' : ''}" data-cb="${key}:${v}">${label}</button>`;
    const cols: [string, (c: J) => number | string, (c: J) => string][] = [
      ['案号', (c) => c.id, (c) => caseLink(c.id, String(c.id))],
      ['哪天', (c) => c.day, (c) => monthOf(c.day)],
      ['案子', (c) => c.kind, (c) => caseLink(c.id, c.kind)],
      ['谁告谁', (c) => name(c.p) + name(c.d), (c) => sides(c)],
      ['金额', (c) => c.amount, (c) => (c.amount ? money(c.amount) : '')],
      ['结果', (c) => c.stage, (c) => `${stageTag(c.stage)}${c.verdict ? ` <small class="meta">${esc(c.verdict)}</small>` : ''}`],
      ['律师', (c) => name(c.lawyer) + name(c.other), (c) => [c.lawyer ? `${who(c.lawyer)}${c.aid ? ' <small class="meta">援助</small>' : ''}` : '', c.other ? `对面 ${who(c.other)}` : ''].filter(Boolean).join(' · ')],
    ];
    const sorted = sortRows('cases', hit, cols.map((x) => x[1]));
    return `<div class="sb-chips"><span>种类</span>${chip('cat', '', `全部 ${rows.length}`)}${chip('cat', '刑事', `刑事 ${n('刑事')}`)}${chip('cat', '民事', `民事 ${n('民事')}`)}${chip('cat', '讨薪', `讨薪 ${n('讨薪')}`)}</div>` +
      `<div class="sb-chips"><span>到哪一步</span>${chip('open', '', '全部')}${chip('open', 'open', '没了结')}${chip('open', 'done', '了结了')}</div>` +
      `<input class="sb-search" data-input="cases" placeholder="名字、公司、律所、案号" value="${esc(cb.q)}">` +
      `<p class="meta">${hit.length} 件${rows.length ? ` · 一共立过 ${num(Math.max(...rows.map((c) => c.id)) + 1)} 件,这里留着最近的 ${num(rows.length)} 件(更早的讨薪、民事案已归档)` : ''}</p>` +
      table(cols.map((x) => x[0]), sorted.slice(0, cbShow).map((c) => cols.map((x) => x[2](c))), { sortKey: 'cases', sortCol: sorts.cases?.col ?? 0, sortDir: sorts.cases?.dir ?? -1 }) +
      (sorted.length > cbShow ? `<button class="chip" data-cb="more:">再看 200 件(还有 ${sorted.length - cbShow} 件)</button>` : '');
  };
  /** 法院:人手、排着的每一件(谁告谁、等了几天)、最近审完的、审判员。 */
  const courtFull = (ct: J, j: J) => {
    if (!ct) return courtPage(j);
    const q = (k: string) => (ct.docket as J[]).filter((x) => x.queue === k).length;
    return grid(tile('排着', `${ct.docket.length} 件`, `讨薪 ${q('讨薪')} · 民事 ${q('民事')} · 刑事 ${q('刑事')} · 二审 ${q('二审')}`),
        tile('平均等了', `${Math.round(ct.avgWait)} 天`, '排着的案子从立案算', ct.avgWait > 60 ? 'bad' : ''),
        tile('近三十天判了', `${ct.verdicts} 件`, `调解 ${ct.settled} 件`), tile('近三十天立案(民事)', `${ct.suits} 件`),
        tile('法院的人手', `${ct.courtFte.toFixed(1)} 人`, `机关一共在岗 ${ct.officeStaff} / 编制 ${ct.officeNeed}`, ct.officeStaff < ct.officeNeed ? 'bad' : ''),
        tile('这个月没排上', `${Math.round(ct.turned)} 件`, '', ct.turned > 0 ? 'bad' : '')) +
      sec('排着的案子', (ct.docket as J[]).length ? table(['案子', '排的是', '谁告谁', '金额', '立案', '等了', '律师'], (ct.docket as J[]).slice(0, 200).map((c) => [
        caseLink(c.id, c.kind), esc(c.queue), sides(c), c.amount ? money(c.amount) : '', monthOf(c.filed), `${c.wait} 天`,
        [c.lawyer ? who(c.lawyer) : '', c.other ? `对面 ${who(c.other)}` : ''].filter(Boolean).join(' · ')])) : '<div class="sb-empty">没有排着的</div>') +
      sec('最近审完的', (ct.judged as J[]).length ? table(['哪天', '案子', '怎么了的', '谁告谁', '结果'], (ct.judged as J[]).map((c) => [
        monthOf(c.on), caseLink(c.id, c.kind), esc(c.act), sides(c), `${stageTag(c.stage)}${c.verdict ? ` <small class="meta">${esc(c.verdict)}</small>` : ''}`])) : '<div class="sb-empty">近一年没审过</div>') +
      sec(`机关的人 ${ct.judges.length}`, (ct.judges as J[]).length ? table(['人', '岁', '手艺'], (ct.judges as J[]).map((x) => [who(x.who), String(x.age), x.skill.toFixed(2)])) : '<div class="sb-empty">没有</div>', '法院、检察院和住建局、土地局、劳务市场共用这班人');
  };
  /** 检察院:排着审查的(抓进来的、关在哪、等了几天)。 */
  const prosPage = (ct: J) => {
    if (!ct) return '<div class="sb-empty">这个版本的引擎还没有</div>';
    return grid(tile('排着审查', `${ct.review.length} 件`), tile('检察院的人手', `${ct.prosFte.toFixed(1)} 人`)) +
      ((ct.review as J[]).length ? table(['案子', '被告', '受害', '抓进来', '等了', '关在哪'], (ct.review as J[]).map((c) => [
        caseLink(c.id, c.kind), (c.d ? who(c.d) : '') + (c.wrong ? ' <span class="tag bad">抓错</span>' : ''), c.v ? who(c.v) : '', monthOf(c.filed), `${c.wait} 天`, esc(c.held ?? '')]))
        : '<div class="sb-empty">没有排着的</div>');
  };
  /** 公安:破案率、警察、各派出所(管多少人、拘留室关了几个)、最近抓的。 */
  const policePage = (g: J, j: J) => {
    const d = (g.depts as J[]).find((x) => x.name === '警察');
    const stations = (g.institutions as J[]).filter((i) => i.fac?.kind === 2);
    const cells = new Map((j.jails as J[]).map((x) => [x.name, x]));
    const recent = allCases().filter((c) => c.cat === '刑事' && ['拘留', '取保候审'].includes(c.stage)).slice(0, 60);
    return grid(tile('破案率', pct(j.clearance ?? 0), '近半年'), ...(d ? [tile('警察', `${d.have} 人`, `编制 ${d.need}`, d.cover < 1 ? 'bad' : '')] : []),
        tile('拘留着', `${j.custody?.detained ?? 0} 人`), tile('取保候审', `${j.custody?.bail ?? 0} 人`)) +
      sec('派出所', stations.length ? table(['派出所', '管多少人', '人手', '好坏', '拘留室'], stations.map((i) => {
        const c = cells.get(i.name);
        return [plotLink(i.plot, i.name), num(i.fac.load), i.fac.staff.toFixed(1), i.fac.q < 0.6 ? `<span class="bad">${i.fac.q.toFixed(2)}</span>` : i.fac.q.toFixed(2), c ? plotLink(i.plot, `${c.held} / ${c.cells}`) : ''];
      })) : '<div class="sb-empty">还没有派出所</div>') +
      // 拘留室里关着的人(还没判、等审的;判了的在监狱,见「监狱」)
      sec(`关在拘留室里的人 ${((j.inmates ?? []) as J[]).filter((x) => x.state === '拘留').length}`, inmateTable(((j.inmates ?? []) as J[]).filter((x) => x.state === '拘留'))) +
      (() => {
        // 最近抓进去过的(案子里记着哪天拘留的):现在到哪一步了
        const held = allCases().filter((c) => c.cat === '刑事' && c.heldFrom != null).sort((a, b) => b.heldFrom - a.heldFrom).slice(0, 40);
        return sec('最近抓过的人', held.length ? table(['人', '案子', '哪天抓的', '现在'], held.map((c) => [(c.d ? who(c.d) : '') + (c.wrong ? ' <span class="tag bad">抓错</span>' : ''), caseLink(c.id, c.kind), dateOf(c.heldFrom), esc(c.stage)])) : '<div class="sb-empty">还没抓过人</div>');
      })() +
      sec('在审的案子(拘留、取保候审)', recent.length ? table(['案子', '被告', '受害', '哪天'], recent.map((c) => [caseLink(c.id, c.kind), (c.d ? who(c.d) : '') + (c.wrong ? ' <span class="tag bad">抓错</span>' : ''), c.v ? who(c.v) : '', monthOf(c.day)])) : '<div class="sb-empty">没有</div>');
  };
  /** 市政大楼:几套、机关自己用几套、租给了谁。 */
  const hallPage = (h: J) => h
    ? grid(tile('一共', `${h.units} 套`, `${h.floors} 层`), tile('机关自己用', `${h.own} 套`), tile('租出去', `${h.leased} 套`, `一天租金 ${money(h.rentDay)}`, 'good'), tile('空着', `${h.free} 套`, '', h.free ? 'bad' : '')) +
      row('在哪', plotLink(h.plot, '市政大楼')) +
      sec('租户', (h.tenants as J[]).length ? table(['哪套', '租户', '一天租金', '租了多久'], (h.tenants as J[]).map((x) => [esc(x.unit), x.who ? who(x.who) : '', money(x.rent), monthOf(x.since) + '起'])) : '<div class="sb-empty">还没租出去</div>')
    : '<div class="sb-empty">还没盖市政大楼</div>';
  // 政府:财政、人手、市政大楼、机构、交通、保交楼、政府的决定(发包在建设;见 dash.rs 的 gov)
  /** 政府的家当:地、公共建筑、房、路和地铁、投资过的工程,欠的钱(见 dash.rs 的 gov 里的 assets)。 */
  const govAssets = (g: J) => {
    const a = g.assets;
    const have = g.treasury + a.publicCash + a.land.value + a.civicCost + a.units.value + a.roads.value + a.subway.paid;
    const owe = a.debt + a.subway.owed + (a.bonds ?? 0);
    return grid(
        tile('家当合计', money(have - owe), `有 ${money(have)} · 欠 ${money(owe)}`),
        tile('国库', money(g.treasury), a.saving > 0 ? `其中攒着盖楼的 ${money(a.saving)}` : '', '', ' data-tab="govt|fin"'),
        tile('空地', `${a.land.n} 块`, `按附近成交值 ${money(a.land.value)} · 挂牌 ${a.land.onSale} 块`),
        tile('公共建筑', `${a.civic.length} 栋`, `盖的时候花了 ${money(a.civicCost)}`),
        tile('房', `${a.units.n} 套`, `租出 ${a.units.let} 套 · 值 ${money(a.units.value)}`),
        tile('路', `${num(a.roads.n)} 格`, `照眼下的工钱重修 ${money(a.roads.value)}`),
        tile('地铁运营', `${a.subway.operatingLines ?? a.subway.lines} 条`, `登记 ${a.subway.registeredLines ?? a.subway.lines} 条 · 仍有施工 ${a.subway.buildingLines ?? 0} 条`, '', ' data-tab="govt|traffic"'),
        tile('地铁工程款', money(a.subway.paid), `已经支付${a.subway.owed > 0 ? ` · 还欠 ${money(a.subway.owed)}` : ''}`, '', ' data-tab="govt|traffic"'),
        tile('欠银行', money(a.debt), '', a.debt > 0 ? 'bad' : ''),
        ...(a.bonds > 0 ? [tile('国债', money(a.bonds), '', '', ' data-tab="govt|debt"')] : [])) +
      sec(`公共建筑 ${a.civic.length}`, a.civic.length
        ? table(['名字', '是什么', '层', '盖的时候花了', '盖好'], a.civic.map((c: J) => [plotLink(c.plot, c.name), esc(c.kind ?? ''), c.floors ? String(c.floors) : '', c.cost != null ? money(c.cost) : '', c.built != null ? dateOf(c.built) : '']))
        : '<div class="sb-empty">还没有</div>') +
      sec(`政府出钱的工程 ${a.invested.length}`, a.invested.length
        ? table(['工程', '盖什么', '状态', '合同', '进度', '发包', '盖完'], a.invested.map((p: J) => [projLink(p.id, p.name), esc(p.what), stageTag(projectProgressStage(p)), money(p.price), esc(projectProgressPercent(p.progress)), dateOf(p.posted), p.finished != null ? dateOf(p.finished) : '']))
        : '<div class="sb-empty">还没有</div>') +
      (a.land.top.length ? sec('最值钱的空地', table(['地', '按附近成交值', ''], a.land.top.map((x: J) => [plotLink(x.plot, `(${x.at[0]}, ${x.at[1]})`), money(x.value), x.onSale ? '<span class="tag">挂牌</span>' : '']))) : '');
  };
  /** 国库的钱和国债:现钱、存在哪几家、国债余额和持有人、上个月的发行偿还和利息、一笔笔的国债(见 dash.rs 的 gov 里的 fisc)。 */
  const fiscPage = (f: J) => {
    if (!f) return '<div class="sb-empty">没有</div>';
    const l = f.last;
    return grid(
        tile('现钱', money(f.cash), f.parked > 0 ? `其中暂不存银行 ${money(f.parked)}` : ''), tile('存在银行', money(f.dep), `上月利息 ${money(l.depInterest)}`),
        tile('国债余额', money(f.debt), f.debt > 0 ? `银行 ${money(f.debt - f.cb)} · 央行 ${money(f.cb)}` : ''), tile('新发年息', `${f.rate.toFixed(1)}%`, '一年期'),
        tile('上月付息', money(l.interest), l.cbInterest > 0 ? `央行持有部分 ${money(l.cbInterest)},已交回` : ''),
        tile('上月发行', money(l.issued), `偿还 ${money(l.redeemed)}`)) +
      sec('国债持有人', (f.holders as J[]).length ? table(['持有人', '面值'], (f.holders as J[]).map((x) => [who(x.who), money(x.amt)])) : '<div class="sb-empty">没有</div>') +
      sec('存款', (f.deps as J[]).length ? table(['银行', '存款', '年息'], (f.deps as J[]).map((x) => [who(x.bank), money(x.amt), `${x.rate.toFixed(1)}%`])) : '<div class="sb-empty">没有</div>') +
      ((f.bonds as J[]).length ? sec(`国债 ${f.n} 笔`, table(['编号', '持有人', '面值', '年息', '发行', '到期'], (f.bonds as J[]).map((b) => [String(b.id), who(b.who), money(b.face), `${b.rate.toFixed(1)}%`, dateOf(b.issued), dateOf(b.due)]))) : '');
  };
  const deathDirectory = createDeathDirectory();
  const medicalDeathPage = () => {
    if (!E.medical_deaths) return recentDeathsHTML(null, false);
    const page = deathDirectory.get(gen, cursor => {
      const key = E.medical_deaths!(cursor.day, cursor.id);
      try { return read(key); } finally { cache.delete(key); used.delete(key); stale.delete(key); }
    });
    return recentDeathsHTML(page, deathDirectory.canPrevious(gen));
  };
  const medical = (): Tab[] => {
    let m: J;
    if (sub.get('d:medical') === 'deaths') {
      const key = E.gov(), entry = cache.get(key);
      let unavailable = !entry;
      if (entry) {
        try {
          m = JSON.parse(entry.raw)?.medical;
          unavailable = typeof m?.enabled !== 'boolean';
        } catch { unavailable = true; }
      }
      if (unavailable) {
        const waiting = inflight.has(key);
        const status = waiting ? '医疗概况正在读取' : entry ? '医疗概况暂不可用' : '医疗概况尚未读取';
        const notice = `<p class="${entry && !waiting ? 'bad' : 'meta'}" role="status">${status} <button class="chip" data-medical-summary-retry>${entry || waiting ? '重试' : '读取'}医疗概况</button></p>`;
        return [
          ['sum', '概况', () => medicalSummary(read(E.gov()).medical)],
          ['deaths', '近期死亡', () => notice + medicalDeathPage()],
        ];
      }
    } else m = read(E.gov()).medical;
    if (!m?.enabled) return [['sum', '概况', () => medicalSummary(m)], ['deaths', '近期死亡', medicalDeathPage]];
    return [
      ['sum', '概况', () => medicalSummary(m)],
      ['hospitals', '医院名单', () => hospitalList(m)],
      ['beds', '住院病床', () => medicalBeds(m)],
      ['patients', '就诊动态', () => medicalPatients(m)],
      ['insurance', '医保', () => medicalInsurance(m)],
      ['deaths', '近期死亡', medicalDeathPage],
    ];
  };
  const education = (): Tab[] => {
    const e = read(E.gov()).education;
    if (!e?.enabled) return [['sum', '概况', () => educationSummary(e)]];
    return [
      ['sum', '概况', () => educationSummary(e)],
      ['schools', '学校名单', () => schoolList(e)],
      ['courses', '课程与出勤', () => educationCourses(e)],
      ['waiting', '候补与安排', () => educationWaiting(e)],
    ];
  };
  /** 政府页的「市长」:这一届市长做了什么——每根杆拨在哪、照自动规则本来是多少,实际动了多少钱和人,多做的工程,
   * 各局服务标准,拨杆的记录;玩家是市长时同一页是政策台(见 engine/web/src/gov_officials.rs 的 term_json)。 */
  const mayorPage = (t: J) => {
    if (!t) return '<div class="sb-empty">眼下没有市长,政府照自动规则办</div>';
    const m = t.mayor;
    const head = `<p class="meta"><a data-go="person:${m.id}">${esc(m.name)}</a>${m.elected ? '' : '(代理)'} · ${m.since != null ? `${esc(dateOf(m.since))}上任` : ''}${t.plat ? ` · 政纲:${esc(t.plat)}` : ''}</p>`;
    const desk = t.desk && playMode;
    // 四列放得下窄面板:政纲写在杆下面,差写在现在下面
    const lever = (l: J) => [`${esc(l.name)}<br><small>${esc(l.dir)}</small>`, `${esc(l.now)}${l.diff ? `<br><small>${esc(l.diff)}</small>` : ''}`, esc(l.auto), l.rec != null ? pct(Math.max(0, l.rec)) : ''];
    // 政策台:每根杆一行档位(玩家自己拨,和 NPC 市长同一张表)
    const chips = desk ? (t.levers as J[]).map((l, k) => l.levels > 1 ? `<div class="sb-chips"><span>${esc(l.name)}</span>${(l.options as string[]).map((o, i) =>
      `<button class="chip${l.level === i ? ' on' : ''}" data-pcmd="299:${10 + k}:${i}"${pcmdPending != null ? ' disabled' : ''}>${esc(o)}</button>`).join('')}</div>` : '').join('') : '';
    const levers = (t.levers as J[]).length ? table(['', '现在', '照自动规则', '兑现'], (t.levers as J[]).map(lever)) + chips : '';
    const mo = t.money;
    const signedTax = mo.tax >= 0 ? `少收 ${money(mo.tax)}` : `多收 ${money(-mo.tax)}`;
    const st = t.staff;
    const moneyRows = table(['', '这一届'], [
      ['税', signedTax],
      ['学校', money(mo.school)], ['医院', money(mo.hospital)], ['地铁', money(mo.subway)], ['廉租房', money(mo.lowrent)],
      ['低保多发', money(mo.welfare)],
      ['卖地少收', `${money(mo.land)}${mo.landN ? `(${mo.landN} 块)` : ''}`],
      ['科长以上工钱省下', money(mo.paySaved)],
      ['公家', `${st.public} 人 · 科长 ${st.chiefs} 个 · 该有 ${st.want} 个 · 干满一年能提的 ${st.able} 人`],
    ]);
    const works = (t.works as J[]).length ? table(['日期', '', '工程', '造价'], (t.works as J[]).map((w) => [esc(dateOf(w.day)), esc(w.kind), esc(w.what), money(w.cost)])) : '<div class="sb-empty">没有</div>';
    const services = (t.services as J[]).length ? table(['局', '多调了', '压住没调', '多调那几回一年的工钱'], (t.services as J[]).map((x) => [esc(x.dept), `${x.up} 回`, `${x.held} 个月`, money(x.cost)])) : '<div class="sb-empty">没有</div>';
    const moves = (t.moves as J[]).length ? `<ol class="sb-news">${(t.moves as J[]).map((x) => `<li><time>${esc(dateOf(x.day))}</time><p>${linkify(x.text)}</p></li>`).join('')}</ol>` : '<div class="sb-empty">还没拨过</div>';
    return head + sec(desk ? '政策台' : '各项政策', levers) + sec('动了多少钱和人', moneyRows) + sec('照自动规则本来不做的工程', works) +
      sec('各局服务标准', services) + sec('拨杆的记录', moves);
  };
  /** 政府页的「官员」:市长、副市长、各局局长(本事、系数)和科长,级别工钱,任免记录(见 eco/src/officials.rs)。 */
  const officialsPage = (o: J) => {
    if (!o) return '<div class="sb-empty">官员层级关着(实验开关第 231 位)</div>';
    const pl = (p: J) => p ? `<a data-go="person:${p.id}">${esc(p.name)}</a>` : '<span class="meta">空着</span>';
    const since = (p: J) => p?.since != null ? dateOf(p.since) : '';
    const top = table(['职位', '谁', '本事', '在公家', '一天工钱', '上任'], [
      ['市长', o.mayor ? pl(o.mayor) : '<span class="meta">还没选(照自动规则代管)</span>', o.mayor ? num(o.mayor.ability, 2) : '', o.mayor ? `${num(o.mayor.years, 1)} 年` : '', o.mayor ? money(o.mayor.wage) : '', since(o.mayor)],
      ...(o.vice as J[]).map((v) => [`副市长 · ${esc(v.duty)}`, pl(v.who), v.who ? num(v.who.ability, 2) : '', v.who ? `${num(v.who.years, 1)} 年` : '', v.who ? money(v.who.wage) : '', since(v.who)]),
    ]);
    const bureaus = table(['局', '局长', '本事', '系数', '科长', '在岗'], (o.bureaus as J[]).map((b) => [
      esc(b.name),
      b.head ? `${pl(b.head)}<small>${esc(b.title)} · ${since(b.head)}上任</small>` : b.acting ? `<span class="meta">空着,</span>${pl(b.acting)}<small>资历最深的科长代管</small>` : '<span class="meta">空着</span>',
      b.head ? num(b.head.ability, 2) : b.acting ? num(b.acting.ability, 2) : '',
      `<span class="${b.factor < 1 ? 'bad' : b.factor > 1 ? 'good' : ''}">${num(b.factor, 2)}</span>`,
      (b.chiefs as J[]).length ? (b.chiefs as J[]).map(pl).join('、') : '<span class="meta">没有</span>',
      `${b.staff} 人`,
    ]));
    const pay = `<p class="meta">级别工钱(公家工资标准的倍数):${(o.pay as J[]).map((x) => `${esc(x.rank)} ${num(x.mult, 1)}`).join(' · ')};科长按每 ${num(o.perChief, 0)} 个人一个。全公家的平均本事 ${num(o.avg, 2)},系数 = 局长的本事 ÷ 平均。</p>`;
    const log = (o.log as J[]).length ? `<ol class="sb-news">${(o.log as J[]).map((x) => `<li><time>${esc(dateOf(x.day))}</time><p>${linkify(x.text)}</p></li>`).join('')}</ol>` : '<div class="sb-empty">还没有任免</div>';
    return sec('市长、副市长', top) + sec('各局', bureaus) + pay +
      '<p class="meta">有空缺时上一级挑人:看本事、交情、送过的礼、名声,各人看重的不一样;送礼会被检察院查。局长的本事管这个局干得怎样(学校、医院、派出所的覆盖,机关一天办的事)。</p>' +
      sec('任免记录', log);
  };
  /** 政府页的「选举」:这一届的候选人、政纲、竞选花的钱和捐款、民调,历届结果;玩家模式能投票(见 eco/src/election.rs)。 */
  const electionPage = (e: J) => {
    if (!e) return '<div class="sb-empty">选举关着(实验开关第 232 位)</div>';
    const phase = e.phase === 'campaign' ? `竞选中,${e.left} 天后投票` : e.phase === 'nominate' ? `报名中,${e.left} 天后投票` : `下一届 ${dateOf(e.voteDay)} 投票(投票前九十天开始报名)`;
    const pl = (p: J) => `<a data-go="person:${p.id}">${esc(p.name)}</a>`;
    const who = (c: J) => `${pl(c)}<small>${c.rank >= 2 ? ['', '科长', '局长', '副市长', '市长'][c.rank] : c.boss ? `${esc(c.boss)}的老板` : ''}${c.record ? ` · <span class="bad">判过 ${c.record} 回</span>` : ''}</small>`;
    const vote = (c: J) => playMode && !c.out && !c.player ? `<button class="sb-sd-btn${e.playerVote === c.id ? ' on' : ''}" data-pcmd="293:${c.id}:0">${e.playerVote === c.id ? '投了他' : '投他'}</button>` : '';
    const cands = (e.cands as J[]).length ? table(['候选人', '政纲', '民调', '竞选花了', '收到捐款', '听说过他', ''], (e.cands as J[]).map((c) => [
      who(c), esc(c.plat.text), c.out ? `<span class="bad">${esc(c.out)}</span>` : c.poll != null ? pct(c.poll) : '', money(c.spent), money(c.raised), pct(c.reach), vote(c),
    ])) : '<div class="sb-empty">这一届还没有人报名</div>';
    const xs = (e.polls as J[]).map((p) => dateOf(p.day));
    const chart = (e.polls as J[]).length > 1 ? lineChart(xs.map((_: string, i: number) => i), (e.cands as J[]).map((c, i) => ({ name: c.name, vals: (e.polls as J[]).map((p) => p.shares[i] ?? 0), fmt: pct })), { fmt: pct, zero: true, key: 'gv:poll', h: 120 }) : '';
    const dons = (e.donations as J[]).length ? table(['日子', '谁捐的', '捐给谁', '多少'], (e.donations as J[]).map((d) => [dateOf(d.day), esc(d.from), esc(d.to), money(d.amount)])) : '<div class="sb-empty">还没有人捐款</div>';
    const past = (e.past as J[]).length ? (e.past as J[]).map((t) => sec(`第 ${t.n} 届 · ${dateOf(t.day)}`, `<p class="meta">${t.winner ? `${pl(t.winner)}当选` : '没有人当选'} · ${t.adults} 个成年人里 ${t.voters} 个去投了票</p>` +
      table(['候选人', '票', '政纲'], (t.votes as J[]).map((v) => [pl(v), String(v.votes), esc(v.plat)])))).join('') : '<div class="sb-empty">还没选过</div>';
    return `<p class="meta">第 ${e.n || 1} 届市长选举 · ${phase}${playMode && e.playerVote == null && (e.cands as J[]).some((c: J) => !c.out) ? ' · 你还没投' : ''}</p>` +
      (e.promised ? `<p class="meta">现任市长当选时的政纲:${esc(e.promised.text)}</p>` : '') +
      (() => {
        const p = e.policy;
        if (!p || (p.spend == null && !p.tax && !p.staff)) return '<p class="meta">眼下没有市长拨政策,政府照自动规则办。</p>';
        // 新规则(开关 238 关着):引擎写好的一句话,加上选民记得他这一届兑现了政纲的几成
        if (p.text != null && p.rec) {
          const rec = ['税', '钱先花在哪', '房价', '公家的人'].map((n, i) => `${n} ${Math.round(p.rec[i] * 100)}%`).join('、');
          return `<p class="meta">市长眼下的政策:${esc(p.text)}。选民记得他这一届兑现了政纲的:${rec}(记性大概一年,越近的记得越清楚;投票时现任的政纲看这个,不看嘴上说的)。</p>`;
        }
        const parts = [p.tax > 0 ? `税在自动算的基础上多减 ${Math.round(p.tax * 100)} 个点` : p.tax < 0 ? `税少减 ${Math.round(-p.tax * 100)} 个点` : '税照自动算的',
          p.spend != null ? `${['学校', '医院', '地铁', '低保', '廉租房'][p.spend]}的好处乘 ${num(p.boost, 1)} 算` : '',
          p.housing === 0 ? '卖地底价打八五折' : p.housing === 1 ? '露宿的过半成就盖廉租房' : '',
          p.staff > 0 ? '各局多招(科长八个人一个)' : p.staff < 0 ? '精简(科长十五个人一个,科长以上工钱打九五折)' : ''].filter(Boolean);
        return `<p class="meta">市长眼下的政策:${parts.join(';')}。</p>`;
      })() +
      sec('候选人', cands) + (chart ? sec('民调', chart) : '') +
      '<p class="meta">选民按自己的账投:交的税、自己的行当得不得益、租房还是有房、现任这几年自己过得怎样、认不认识、名声;没听说过的候选人政纲和名声看得模糊(打七折)。民调十天一回,抽一千人。只在投票那天全城算一回。</p>' +
      sec('捐款(公开记账)', dons) + past;
  };
  const govt = (): Tab[] => {
    const g = read(E.gov());
    const { xs, col } = series();
    const decrees = (n: number) => ((g.mind as J[]).length ? `<ol class="sb-news">${(g.mind as J[]).slice(0, n).map((x) => `<li><time>${esc(dateOf(x.day))}</time><p>${linkify(x.text)}</p></li>`).join('')}</ol>` : '<div class="sb-empty">还没做过决定</div>');
    const need = (g.depts as J[]).reduce((a, d) => a + d.need, 0), have = (g.depts as J[]).reduce((a, d) => a + d.have, 0);
    const flow: number[][] = g.book.flow ?? [];
    const fy = g.fiscalYear;
    const budget = g.budget;
    const balance = budget?.balance ?? g.spare ?? 0;
    const tax = taxCard(g);
    return [
      ['sum', '概况', () => `<p class="meta"><a data-go="gov:0">打开市政府 →</a></p>` + grid(
          tile('国库', money(g.treasury), g.fisc && g.fisc.dep > 0 ? `现钱 ${money(g.fisc.cash)} · 存银行 ${money(g.fisc.dep)}` : '', '', ' data-tab="govt|debt"'),
          tile('储备与专款后余额', signed(balance), '查看储备、专款和待付款 →', balance < 0 ? 'bad' : '', ' data-tab="govt|plan"'),
          ...((g.assets?.subway?.owed ?? 0) > 0 ? [tile('待付地铁工程', money(g.assets.subway.owed), '已承诺,按施工进度付款', '', ' data-tab="govt|traffic"')] : []),
          ...(g.fisc && g.fisc.debt > 0 ? [tile('国债', money(g.fisc.debt), `银行 ${money(g.fisc.debt - g.fisc.cb)} · 央行 ${money(g.fisc.cb)}`, '', ' data-tab="govt|debt"')] : []),
          ...(fy ? [tile('近一年收入', money(fy.income), `${fy.months} 个月`, '', ' data-tab="govt|fin"'),
            tile('近一年支出', money(fy.spending), '不含存取款、借还本金', '', ' data-tab="govt|fin"'),
            tile('近一年结余', money(fy.balance), '', '', ' data-tab="govt|fin"')] : []),
          tile('公家员工', `${g.publicJobs} 人`, `编制 ${need} · 在岗 ${have}`, have < need ? 'bad' : '', ' data-tab="govt|staff"'),
          ...(g.officials ? [tile('市长', g.officials.mayor ? esc(g.officials.mayor.name) : '还没选', g.officials.mayor ? `${dateOf(g.officials.mayor.since)}上任` : '照自动规则代管 · 查看官员 →', '', ' data-tab="govt|officials"')] : []),
          tile('工资标准', `一天 ${Math.round(g.pay ?? 0)} 元`, `比普工高 ${(g.payPremium ?? 0).toFixed(0)}%`), tile('一天工钱', money(g.payroll)),
          inflTile(g.inflation), tile('基准利率', `${g.policy.toFixed(1)}%`),
          tile(`${taxKinds(g)}税费减免`, tax.value, `${tax.note} · 查看实际到款 →`, '', ' data-tab="govt|tax"'),
          tile('机关', `${g.office.staff} 人`, `编制 ${g.office.need}`, g.office.staff < g.office.need ? 'bad' : '', ' data-tab="govt|staff"'),
          ...(g.hall ? [tile('市政大楼', `租出 ${g.hall.leased} / ${g.hall.units} 套`, `一天租金 ${money(g.hall.rentDay)}`, '', ' data-tab="govt|hall"')] : [])) +
        sec('国库', lineChart(xs, [{ name: '国库', vals: col('treasury'), fmt: money }, { name: '存银行', vals: col('govDep'), fmt: money }, { name: '国债', vals: col('govDebt'), fmt: money }], { fmt: money, zero: true, key: 'gv:t', h: 120 })) +
        (fy ? `<div class="sb-2">${sec('近一年主要收入', table(['来源', '金额'], fy.in.slice(0, 5).map((x: J) => [esc(x.label), money(x.amount)])))}${sec('近一年主要支出', table(['用途', '金额'], fy.out.slice(0, 5).map((x: J) => [esc(x.label), money(x.amount)])))}</div>` : '') +
        sec('最近的决定', decrees(12))],
      ['tax', '税费减免', () => sec(`${taxKinds(g)}税费 · 当前比例与实际到款`, taxOverviewHTML(g)) + sec('最近的决定', decrees(12))],
      ['fin', '财政', () => grid(tile('一共造的钱', money(g.minted)), tile('一共销毁', money(g.burned)), tile('邻城付来 · 付给邻城', money(g.imported), `付出 ${money(g.exported)}`),
          tile('政府的房 · 空地', `${g.units} 套`, `${g.plots} 块`)) +
        `<div class="sb-2">${groupDonut(g.book, '收入', true, '上月收入')}${groupDonut(g.book, '支出', false, '上月支出')}</div>` +
        sec('每月进出', lineChart(monthsX(flow.length), [{ name: '进', vals: flow.map((f) => f[0]), fmt: money }, { name: '出', vals: flow.map((f) => f[1]), fmt: money }], { fmt: money, key: 'gv:flow', h: 130 })) +
        sec('公家员工', lineChart(xs, [{ name: '人数', vals: col('publicJobs') }], { zero: true, key: 'gv:jobs', h: 110 }))],
      ['plan', '资金安排', () => {
        const active = (g.assets?.invested ?? []).filter((p: J) => p.finished == null);
        return (budget ? sec('国库如何安排', table(['安排', '金额'], [
          ['国库合计 · 现钱与银行存款', money(budget.funds)],
          ['日常储备 · 全城成年人十天普工工钱', money(budget.reserve)],
          ['建设专款 · 为计划中的地标预留', money(budget.saving)],
          [tenderReserveLabel(), money(budget.tenders)],
          ['待付地铁工程 · 按施工进度付款', money(budget.subway)],
          ...(budget.health != null && budget.health > 0 ? [['医保基金结余 · 缴费攒下、留着报销', money(budget.health)]] : []),
          ['<b>扣除以上安排后的余额</b>', `<b class="${balance < 0 ? 'bad' : ''}">${signed(balance)}</b>`],
        ])) + '<p class="meta">预留款尚未花出，仍包含在国库里。已经支付的工程款记在财政收支中。</p>' : '') +
          sec('再增员的预估', table(['部门', '在岗 / 编制', '拟增人数', '一年服务价值', '一年工钱', '招聘情况'], (g.depts as J[]).filter((d) => d.next).map((d) => [
            esc(d.name), `${d.have} / ${d.need}`, `+${d.next.add}`, money(d.next.value), money(d.next.wage), d.have < d.need ? '<span class="bad">先补齐缺员</span>' : '编制已满',
          ]))) + '<p class="meta">按当前人口、设施和工资估算下一档增员。服务价值是教育、医疗等改善的折算，不是国库收入；增员还需满足招聘和储备条件。</p>' +
          sec('正在安排的公共工程', active.length ? table(['工程', '用途', '状态', '合同 / 预算', '监管账户余额', '进度'], active.map((p: J) => [
            projLink(p.id, p.name), esc(p.what), stageTag(projectProgressStage(p)), money(p.price || p.ceiling || 0), money(p.escrow ?? 0), esc(projectProgressPercent(p.progress)),
          ])) : '<div class="sb-empty">当前没有待签或在建工程</div>') +
          '<p class="meta">工程监管账户的钱独立于国库，随工程进度付给承包商。地铁建设见交通页。</p>';
      }],
      ['assets', '家当', () => govAssets(g)],
      ['debt', '国债和存款', () => fiscPage(g.fisc)],
      ['money', '流水', () => statement(g.book, 'gov')],
      ['staff', '人手', () => sec('部门', deptPage(g)) + sec('机关', agencyPage(g))],
      ...(g.election ? [['mayor', '市长', () => mayorPage(g.election.term)] as Tab] : []),
      ...(g.officials ? [['officials', '官员', () => officialsPage(g.officials)] as Tab] : []),
      ...(g.election ? [['election', '选举', () => electionPage(g.election)] as Tab] : []),
      ['hall', '市政大楼', () => hallPage(g.hall)],
      ['inst', '机构', () => instPage(g)],
      ['medical', '医疗', () => medicalPage(g.medical)],
      ['education', '教育', () => educationPage(g.education)],
      ['traffic', '交通', () => trafficPage(g)],
      ['rescue', `保交楼 ${g.rescued.length}`, () => (g.rescued.length ? table(['楼', '进度'], g.rescued.map((r: J) => [plotLink(r.plot, r.name), esc(projectProgressText(r))])) : '<div class="sb-empty">没有</div>')],
      ['ops', '运营', () => opsPage(g.ops, g)],
      ['decree', `决定 ${(g.mind as J[]).length}`, () => decrees(200)],
    ];
  };
  // 司法:治安、全部案子、法院、检察院、公安、监狱、民事官司、律所(见 dash.rs 的 gov、court、cases)
  const justice = (): Tab[] => {
    const g = read(E.gov());
    const ct = E.court ? read(E.court()) : null;
    const j = g.justice;
    return [
      ['crime', '治安', () => crimePage(j, city.stats.adults, g.stolen ?? 0)],
      ['cases', '案子', caseBrowser],
      ['court', `法院${ct ? ` ${ct.docket.length}` : ''}`, () => courtFull(ct, j)],
      ['pros', `检察院${ct ? ` ${ct.review.length}` : ''}`, () => prosPage(ct)],
      ['police', '公安', () => policePage(g, j)],
      ['jail', '监狱', () => jailPage(j)],
      ['civil', '官司', () => civilPage(j)],
      ['law', '律所', () => lawPage(j)],
    ];
  };

  const news = (): Tab[] => {
    const n: J[] = read(E.news());
    const m: J | null = E.media ? read(E.media()) : null;
    const page = (x: J) => `<li data-go="firm:${x.paper.id}"><span class="tag k-${esc(x.kind)}">${esc(x.kind)}</span><time>${esc(x.paper.name)}</time><p>${linkify(x.text)}</p>` +
      `<small class="meta">${dateOf(x.day)} · 报摊 ${x.street} 份 · 订户 ${x.subs}${x.scoop ? ' · 独家' : ''}</small></li>`;
    const mediaTabs: Tab[] = m ? [
      ['stand', `报摊 ${m.papers.length}`, () => m.papers.length
        ? grid(tile('每天看报的', `${num(m.readers)} 人`, `成年人 ${num(m.adults)}`), tile('头版热度', m.heat.toFixed(1), '最热那家的三十天平均'),
            tile('消息传多远', `× ${m.amp.toFixed(2)}`, '', m.amp > 1.2 ? 'bad' : '')) +
          sec('今天的头版', m.today.length ? `<ol class="sb-news">${m.today.map(page).join('')}</ol>` : '<div class="sb-empty">今天没出报</div>') +
          sec('各家报社', table(['报社', '路数', '信誉', '记者', '抓得到', '一天送出卖出', '订户', '一份', '广告千人价', '版面', '一天进账', '一天利润'], m.papers.map((p: J) => [
            who(p.firm), p.tabloid >= 0.6 ? '小报' : p.tabloid <= 0.3 ? '正经报' : '中间', p.trust.toFixed(2), String(p.staff), pct(p.cover), num(p.circ), num(p.subs), money(p.copy),
            money(p.cpm), `${p.adSold}/${p.slots ?? 8}`, money(p.rev), signed(p.profit)]))) +
          (series().col('papers').some((v) => v != null) ? sec('每天的报纸(按月平均)', lineChart(series().xs, [{ name: '份', vals: series().col('papers') }], { zero: true, key: 'nw:paper' })) : '') +
          sec('最近的头版', `<ol class="sb-news">${m.recent.slice(0, 60).map(page).join('')}</ol>`)
        : '<div class="sb-empty">城里还没有报社</div>'],
      ['ads', `广告 ${m.ads.length}`, () => m.ads.length
        ? table(['登广告的', '行业', '门前多来', '到哪天'], m.ads.map((a: J) => [who(a.firm), esc(a.line), pct(a.lift), dateOf(a.until)]))
        : '<div class="sb-empty">这个月没人登广告</div>'],
      ['expose', `曝光 ${m.exposed.length}`, () => m.exposed.length
        ? `<ol class="sb-news">${m.exposed.map((x: J) => `<li${x.firm ? ` data-go="firm:${x.firm.id}"` : ''}><span class="tag k-曝光">曝光</span><time>${esc(dateOf(x.day))}</time><p>${linkify(x.text)}</p></li>`).join('')}</ol>`
        : '<div class="sb-empty">还没有曝光</div>'],
    ] : [];
    // 按类筛:出现得多的类在前
    const counts = new Map<string, number>();
    for (const x of n) counts.set(x.kind, (counts.get(x.kind) ?? 0) + 1);
    const kindsList = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    const shown = newsKind ? n.filter((x) => x.kind === newsKind) : n;
    const chips = `<div class="sb-chips"><span>类</span><button class="chip${newsKind ? '' : ' on'}" data-nk="">全部 ${n.length}</button>${kindsList.map(([k, c]) => `<button class="chip${newsKind === k ? ' on' : ''}" data-nk="${esc(k)}">${esc(k)} ${c}</button>`).join('')}</div>`;
    return [['all', '大事', () => chips + (shown.length ? `<ol class="sb-news">${shown
      .slice(0, 200)
      .map((x) => {
        const go = x.plot != null ? `plot:${x.plot}` : x.firm != null ? `firm:${x.firm}` : x.person != null ? `person:${x.person}` : '';
        return `<li${go ? ` data-go="${go}"` : ''}><span class="tag k-${esc(x.kind)}">${esc(x.kind)}</span><time>${esc(dateOf(x.day))}${x.know != null ? ` · ${Math.round(x.know * 100)}% 知道` : ''}</time><p>${linkify(x.text)}</p></li>`;
      })
      .join('')}</ol>` : '<div class="sb-empty">还没有大事</div>')], ...mediaTabs];
  };

  // 外地:几座邻城(见 15-外地.md)
  const regions = (): Tab[] => {
    const R = read(E.regions());
    const list: J[] = R.list;
    const xs = monthsX(Math.max(0, ...list.map((r) => r.hist.pop.length)));
    const card = (r: J) => `<div class="sb-card">${sec(`${r.name} · ${r.km} 公里`, grid(
      tile('人口', num(r.pop)), tile('一天工钱', `${r.wage.toFixed(0)} 元`), tile('一份日用', `${r.cpi.toFixed(1)} 元`),
      tile('工钱买得起', `${r.real.toFixed(2)} 份`, `本城 ${R.city.real.toFixed(2)} 份`, r.real > R.city.real ? 'bad' : 'good'), tile('钱', money(r.money)),
    ) + bars(Object.entries(r.shares as Record<string, number>).map(([k, v]) => ({ label: k, value: v })), (v) => `${v.toFixed(0)}%`) + `<p class="meta"><a data-go="region:${r.id}">打开${esc(r.name)} →</a></p>`)}</div>`;
    const trade = (key: 'toCity' | 'fromCity') => list.map((r) => ({ name: r.name, vals: r.hist[key] as number[], fmt: money }));
    return [
      ['sum', '概况', () => grid(tile('全国物价', `${R.national.toFixed(1)} 元`, `通胀 ${R.inflation.toFixed(1)}%`), tile('技术水平', R.tech.toFixed(2), ''),
        // 出口、进口按 GDP 的口径(货和服务,见 econ);付来付去的钱还连着搬家带的、汇的
        ...((ec) => (ec && ec.months ? [tile('出口', money(ec.exports), `${ec.months < 12 ? `近 ${ec.months} 个月` : '近十二个月'} · 货和服务`, '', ' data-tab="overview|gdp"'),
          tile('进口', money(ec.imports), `${ec.months < 12 ? `近 ${ec.months} 个月` : '近十二个月'} · 货和服务`, '', ' data-tab="overview|gdp"')] : []))(econ()),
        tile('付给邻城的钱', money(R.exported), '累计,含搬迁、汇款'), tile('邻城付来的钱', money(R.imported), '累计,含搬迁'), tile('本城实际工钱', `${R.city.real.toFixed(2)} 份`, '平均日工钱折日用'),
        ...(R.sell ? [
          tile('服务卖到外地', `${money(R.sell.svc.got)} / 天`, `做了 ${num(R.sell.svc.made)} 份,做一份挣 ${R.sell.svc.rate.toFixed(1)} 元`),
          tile('制造品卖到外地', `${money(R.sell.manuf.got)} / 天`, `做得出 ${num(R.sell.manuf.made)} 份,卖掉 ${num(R.sell.manuf.sold)} 份`),
        ] : [])) +
        list.map(card).join('')],
      ['trade', '进出口', () => xs.length >= 2
        ? sec('本城从各邻城买的(每月,按那边的价)', lineChart(xs, trade('toCity'), { fmt: money, key: 'rg:to', zero: true })) +
          sec('各邻城从本城买的(每月)', lineChart(xs, trade('fromCity'), { fmt: money, key: 'rg:from', zero: true }))
        : '<div class="sb-empty">还不到两个月</div>'],
      ['move', '迁移', () => xs.length >= 2
        ? sec('每月从各邻城搬来本城的', lineChart(xs, list.map((r) => ({ name: r.name, vals: r.hist.movedOut })), { key: 'rg:in', zero: true })) +
          sec('每月从本城搬去各邻城的', lineChart(xs, list.map((r) => ({ name: r.name, vals: r.hist.movedIn })), { key: 'rg:out', zero: true }))
        : '<div class="sb-empty">还不到两个月</div>'],
      ['price', '物价和工钱', () => xs.length >= 2
        ? sec('一天工钱', lineChart(xs, list.map((r) => ({ name: r.name, vals: r.hist.wage })), { key: 'rg:wage' })) +
          sec('一份日用', lineChart(xs, list.map((r) => ({ name: r.name, vals: r.hist.cpi })), { key: 'rg:cpi' })) +
          sec('人口', lineChart(xs, list.map((r) => ({ name: r.name, vals: r.hist.pop })), { key: 'rg:pop' }))
        : '<div class="sb-empty">还不到两个月</div>'],
      ['goods', '各种货的价', () => table(['货', ...list.map((r) => `${r.name}(到本城)`), '本城'], (list[0]?.goods ?? []).map((g: J, i: number) =>
        [`${esc(g.name)}<small class="meta"> / ${esc(g.unit)}</small>`, ...list.map((r) => num(r.goods[i].landed, r.goods[i].landed < 100 ? 1 : 0)), g.city != null ? num(g.city, g.city < 100 ? 1 : 0) : '<span class="meta">—</span>']))],
    ];
  };

  // ---------- 关注 ----------
  // 点了星的人、公司:他们做了什么决定(在想什么)从下方弹出来;按城存在浏览器里(换一座城就是另一张单子)
  type Follow = { kind: 'person' | 'firm'; id: number; name: string };
  let follows: Follow[] = [];
  const followKey = () => `sb:follow:${seed}`;
  const loadFollows = () => {
    try {
      follows = JSON.parse(localStorage.getItem(followKey()) ?? '[]') ?? [];
    } catch {
      follows = [];
    }
  };
  const saveFollows = () => {
    try {
      localStorage.setItem(followKey(), JSON.stringify(follows));
    } catch {
      /* 存不了就只记在这一次 */
    }
  };
  const followed = (kind: string, id: number) => follows.some((x) => x.kind === kind && x.id === id);
  type FollowRead = { state: 'ready'; data: J } | { state: 'loading' | 'missing' | 'error' };
  const followData = (x: Follow): FollowRead => {
    try {
      const d = read(x.kind === 'person' ? E.person2(x.id) : E.firm2(x.id));
      if (d?.missing === true) return { state: 'missing' };
      return d && typeof d === 'object' ? { state: 'ready', data: d } : { state: 'error' };
    } catch (e) {
      return { state: e === PENDING ? 'loading' : 'error' };
    }
  };
  const followFace = (x: Follow, d: J, size: number) => (x.kind === 'person' ? avatar(x.id, d.age ?? 30, d.job?.trade ?? '', d.happy ?? 0.6, size) : badge(d.name ?? x.name, d.kind ?? '', size));
  const followSeen = new Map<string, string>(); // 'person:12' → 最新一条想法
  const checkFollows = guard(() => {
    const box = document.getElementById('toasts')!;
    for (const x of follows) {
      const result = followData(x);
      if (result.state !== 'ready') continue;
      const d = result.data;
      const list: J[] = d?.mind ?? [];
      if (!d || !list.length) continue;
      const key = `${x.kind}:${x.id}`;
      const newest = `${list[0].day}|${list[0].text}`;
      const seen = followSeen.get(key);
      followSeen.set(key, newest);
      if (seen == null || seen === newest) continue;
      const fresh: J[] = [];
      for (const m of list) {
        if (`${m.day}|${m.text}` === seen || fresh.length >= 2) break;
        fresh.push(m);
      }
      for (const m of fresh.reverse()) toast(box, `${followFace(x, d, 22)}<p><b>${esc(d.name ?? x.name)}</b>${esc(m.text)}</p><time>${esc(monthOf(m.day))}</time>`, key);
    }
  });
  const followPage = (): Tab[] => [['list', `关注 ${follows.length}`, () => {
    if (!follows.length) return '<div class="sb-empty">还没有关注的人和公司</div>';
    return table(['', '谁', '身家 · 估值', '最近在想'], follows.map((x) => {
      const result = followData(x);
      if (result.state !== 'ready') {
        const status = { loading: '读取中…', missing: '没有这一项', error: '资料暂时无法显示' }[result.state];
        return ['', esc(x.name || `${x.kind === 'person' ? '人物' : '公司'} #${x.id}`), '', `<span class="meta">${status}</span>`];
      }
      const d = result.data;
      const worth = x.kind === 'person' ? money(d.worth ?? 0) : money(d.value ?? 0);
      const last = d.mind?.[0] ? `${esc(d.mind[0].text)} <span class="meta">${esc(monthOf(d.mind[0].day))}</span>` : '';
      return [followFace(x, d, 22), who({ kind: x.kind, id: x.id, name: d.name ?? x.name }), worth, `<span style="white-space:normal">${last}</span>`];
    }));
  }]];

  // ---------- 年报 ----------
  // 新的一年开始:弹一条去年人口、工钱、房价、盖楼怎么变的
  let lastYear: number | null = null;
  const yearReport = guard(() => {
    const yr = Math.floor(city.day / 360);
    if (lastYear == null || yr < lastYear) {
      lastYear = yr;
      return;
    }
    if (yr === lastYear) return;
    lastYear = yr;
    const { col } = series();
    const span = (name: string) => {
      const v = col(name);
      const n = v.length;
      return n >= 13 ? { now: v[n - 1], ago: v[n - 13] } : null;
    };
    const d = (x: { now: number; ago: number }) => (x.ago ? `${x.now >= x.ago ? '+' : '−'}${Math.abs((x.now / x.ago - 1) * 100).toFixed(0)}%` : '');
    const pop = span('pop'), wage = span('wage'), house = span('house'), built = span('built');
    if (!pop) return;
    const parts = [
      `人口 ${num(pop.now)}(${d(pop)})`,
      wage ? `日工钱 ${Math.round(wage.now)} 元(${d(wage)})` : '',
      house ? `房价 ${money(house.now)}(${d(house)})` : '',
      built ? `盖完 ${num(built.now - built.ago)} 栋` : '',
    ].filter(Boolean);
    toast(document.getElementById('toasts')!, `<span class="tag">年报</span><p>第 ${yr} 年:${parts.join(' · ')}</p>`, '');
  });

  const contextKinds: Partial<Record<Kind, number>> = { plot: 0, project: 1, unit: 2, hospital: 3, school: 4, firm: 5 };
  let relatedAttempt: { identity: string; attempted: boolean; queued: boolean } | null = null;
  const relatedIdentity = (selected: Sel) => `${gen}:${queryEpoch}:${selected.kind}:${selected.id}`;
  const requestRelated = (selected: Sel) => {
    const kind = contextKinds[selected.kind];
    if (!E.context2 || kind == null) return;
    const identity = relatedIdentity(selected), key = E.context2(selected.id * 8 + kind);
    relatedAttempt = { identity, attempted: true, queued: false };
    let requestId: number | undefined;
    ask([key], () => {
      // A completed reply may omit this optional key. Keep its retry usable.
      // Do not alter an outstanding mandatory map route that shares this key.
      const mandatory = mapIntent != null && E.context2?.(mapIntent * 8) === key;
      if (!mandatory && requestId != null && inflight.get(key) === requestId && !asking.has(requestId)) inflight.delete(key);
      if (sel && relatedIdentity(sel) === identity && mapIntent == null) requestQueryPanels();
    });
    requestId = inflight.get(key);
  };
  const queueRelated = (selected: Sel) => {
    const kind = contextKinds[selected.kind];
    if (!E.context2 || kind == null) return;
    const identity = relatedIdentity(selected), contextKey = E.context2(selected.id * 8 + kind), attempt = relatedAttempt;
    if (!attempt || attempt.identity !== identity || attempt.attempted || cache.has(contextKey)) return;
    attempt.attempted = true; attempt.queued = true;
    // The whole primary view has rendered; its stale reads still send before this microtask.
    queueMicrotask(() => {
      if (relatedAttempt !== attempt) return;
      attempt.queued = false;
      if (!sel || relatedIdentity(sel) !== identity || mapIntent != null || cache.has(contextKey)) return;
      requestRelated(selected);
    });
  };
  const relatedPlaces = (selected: Sel): string => {
    const kind = contextKinds[selected.kind];
    if (!E.context2 || kind == null) { relatedAttempt = null; return ''; }
    const identity = relatedIdentity(selected), contextKey = E.context2(selected.id * 8 + kind);
    if (relatedAttempt?.identity !== identity) relatedAttempt = { identity, attempted: false, queued: false };
    const entry = cache.get(contextKey);
    const waiting = (!entry && !relatedAttempt.attempted) || relatedAttempt.queued || inflight.has(contextKey);
    const retry = (label = '重试') => `<button class="chip" data-related-retry="${identity}">${label}关联地点</button>`;
    const notice = (text: string) => `<nav class="sb-related" aria-label="此地点的相关面板"><p class="meta" role="status">${text} ${retry()}</p></nav>`;
    if (!entry) {
      used.delete(contextKey);
      return notice(waiting ? '关联地点正在读取' : '关联地点暂不可用');
    }
    let ctx: J;
    try {
      ctx = JSON.parse(entry.raw);
      const nodes = (xs: J, allowed: string[]) => Array.isArray(xs) && xs.every(x => x && typeof x === 'object' &&
        allowed.includes(x.kind) && Number.isSafeInteger(x.id) && x.id >= 0 && x.id <= 0xffff_ffff && typeof x.name === 'string');
      if (!ctx || typeof ctx !== 'object' || Array.isArray(ctx) || ctx.missing === true || 'queryError' in ctx || 'error' in ctx ||
        !nodes(ctx.places, ['plot', 'project', 'hospital', 'school']) || !nodes(ctx.units, ['unit']) ||
        !nodes(ctx.firms, ['firm']) || !nodes(ctx.people, ['person'])) throw new Error('关联地点资料不完整');
    } catch {
      used.delete(contextKey);
      return notice(waiting ? '关联地点正在读取' : '关联地点暂不可用');
    }
    // Navigation keeps its ordinary running refresh, but is never a primary panel dependency.
    used.add(contextKey);
    const active = (x: J) => x.kind === selected.kind && x.id === selected.id;
    const choices = (title: string, xs: J[]) => xs.length ? `<label>${title} ${xs.length}<select data-related-select aria-label="${title}"><option value="">选择${title}</option>${xs.map(x => `<option value="${x.kind}:${x.id}"${active(x) ? ' selected' : ''}>${esc(x.name)} · #${x.id}</option>`).join('')}</select></label>` : '';
    const pickers = choices('房屋', ctx.units ?? []) + choices('机构', ctx.firms ?? []) + choices('住户', ctx.people ?? []);
    const key = `placeChoices:${selected.kind}:${selected.id}`;
    const counts = [['房屋', ctx.units?.length], ['机构', ctx.firms?.length], ['住户', ctx.people?.length]].filter(([, n]) => n).map(([label, n]) => `${label} ${n}`).join(' · ');
    const html = `<nav class="sb-related" aria-label="此地点的相关面板"><div class="sb-related-places">${(ctx.places ?? []).map((x: J) => active(x) ? `<span class="on" aria-current="page">${esc(x.kind === 'project' ? '楼栋' : x.name)}</span>` : `<a data-go="${x.kind}:${x.id}" title="${esc(x.name)}">${esc(x.kind === 'project' ? '楼栋' : x.name)}</a>`).join('')}</div>${pickers ? `<details class="sb-related-choices" data-key="${key}"${opened.has(key) ? ' open' : ''}><summary>切换${counts}</summary><div class="sb-related-lists">${pickers}</div></details>` : ''}</nav>`;
    return html + (speed === 0 && entry.day !== engineDay
      ? `<p class="meta" role="status">关联地点资料截至${dateOf(entry.day)}。 ${retry('刷新')}</p>` : '');
  };
  const downloadControl = (x: Sel): string => {
    const names: Record<Kind, string> = { person:'完整人生', firm:'公司档案', hospital:'医院档案', school:'学校档案', plot:'地块档案', unit:'房屋档案', project:'楼栋档案', bank:'银行档案', gov:'政府档案', case:'案卷', region:'地区资料', market:'市场资料' };
    return `<span class="sb-download"><button data-export="${x.kind}:${x.id}:json" title="当前资料与本城已归档的全部相关记录；旧版未记录资料不会补造">下载${names[x.kind]}（JSON）</button><button data-export="${x.kind}:${x.id}:txt">${x.kind === 'person' ? '人生文本' : '档案文本'}（TXT）</button></span>`;
  };
  const readableExport = (x: J): string => x.kind === 'firm' ? investmentCompanyText(x) : lifeText(x);
  const deathLinks: DeathLinks = { entity: (kind, id, label) => `<a data-go="${kind}:${id}">${esc(label)}</a>` };
  updateArchive = () => {
    const el = document.getElementById('archiveAlert'); if (!el) return;
    el.hidden = archiveState.state !== 'blocked';
    if (!el.hidden) el.innerHTML = `<b>历史存档暂停，城市已停在完整日</b><span>${esc(archiveState.message ?? '')}</span><button data-archive="retry">重试保存</button><button data-archive="memory">放弃后续冷档，仅保留热窗口并继续</button>`;
  };
  const enginePage = (): Tab[] => [['status', '运行与存档', () => {
    const p = wk.parallel ?? {}, state: Record<string, string> = { unavailable: '不可用', idle: '待命', loading: '加载中', ready: '就绪', busy: '计算中', disabled: '已回退' };
    return grid(p.threads > 1 ? tile('线程池处理段', num(p.dispatched ?? 0), `帮手完成 ${num(p.completed ?? 0)} 份工作`) : tile('实际并行任务', num(p.dispatched ?? 0), `完成 ${num(p.completed ?? 0)} · 回退 ${num(p.fallbacks ?? 0)}`),
      tile('计算助手', state[p.state] ?? '尚未启动', p.reason ?? '达到计算阈值后启动'),
      tile('自开城累计日均', wk.stepDays ? `${num(wk.stepMs / wk.stepDays, 2)} ms` : '尚无样本', `累计 ${num(wk.stepDays)} 个完整日；含异步等待`),
      recentDayTimingCard(wk.timing),
      tile('已落盘历史', `${num(archiveState.stored ?? 0)} 条`, archiveState.state === 'ready' ? '事务提交成功后才确认' : archiveState.message ?? archiveState.state)) +
      // 换城、开新局以前的存档(最多三份):点了换回来,眼下这一局存进去
      (playMode ? sec('这局的规则', `<p class="meta">${esc(rulesLine({ rules: playMode.rules }))}</p><div class="sb-sd-acts">${newRulesHTML(playMode.rules)}</div>`) : '') +
      (prevSaves().length ? sec('恢复上一局', `<div class="sb-sd-acts">${prevSavesHTML()}</div>`) : '') +
      sec('网页多核心', row('引擎', p.threads > 1 ? `多线程版 · ${num(p.threads)} 个线程` : '单线程版') + row('为什么', esc(p.mode ?? '')) +
        (p.threads > 1 ? row('并行的段 / 分给线程的', `${num(p.runs ?? 0)} / ${num(p.dispatched ?? 0)}`) + row('帮手线程做了几份', num(p.completed ?? 0)) +
          '<p class="meta">多线程版:一天里能分开算的几段(地段、通勤、压力……)分给几个线程,结果按编号放回,和单线程版逐字一样(同一条历史)。桌面浏览器默认用(服务器要加 COOP/COEP 两个响应头);手机、核数少、不支持的浏览器用单线程版。</p>'
        : row('助手累计计算', `${num(p.workerMs ?? 0, 2)} ms`) + row('主模拟线程对应计算', `${num(p.mainMs ?? 0, 2)} ms`) +
          '<p class="meta">城市在模拟线程运行；达到阈值的银行客户报价拆给独立计算助手。界面绘制留在界面线程。任务数按真实派发计数，故障回到相同规则的串行计算；这些耗时不代表整座城市一定加速。</p>')) +
      sec('卡顿排查 · 近期完整日', recentDayTimingHTML(wk.timing)) +
      sec('卡顿排查 · 分阶段耗时', engineTimingHTML(wk.timing)) +
      sec('地图快照接收', row('接收 / 已刷新 / 合并 / 取消', `${num(cityPushPerf.received)} / ${num(cityPushPerf.applied)} / ${num(cityPushPerf.coalesced)} / ${num(cityPushPerf.cancelled)}`) +
        row('当前待显示', pendingCityPush ? '1 份，下一帧显示最新快照' : '0 份') + row('城市 JSON 解析', `${num(cityPushPerf.parsed)} 次${measureCityPushes ? ` · 累计 ${num(cityPushPerf.parseMs, 2)} ms · 最长 ${num(cityPushPerf.maxParseMs, 2)} ms` : ' · 网址加 perf=1 可计时'}`) +
        '<p class="meta">只合并同一帧前积压的地图显示；暂停时显示已收到的最后快照。模拟和完整历史照常逐日处理。这些计数不能换算成整城提速比例。</p>') +
      sec('归档写入分项', archiveWriteHTML(archiveState.write)) +
      (archiveState.state === 'off' ? sec('本城历史', row('状态', '未开启') +
        '<p class="meta">开启后，人物、公司等的每条记录从当天起存进本机浏览器，推演会明显变慢。</p>' +
        '<div class="sb-action-buttons"><button data-archive="enable">开启完整历史</button><button data-export="city:0:json">下载城市概况（JSON）</button><button data-export="city:0:txt">城市概况文本（TXT）</button></div>') :
      sec('本城历史', row('状态', esc(archiveState.message ?? archiveState.state)) + row('已写入条目', num(archiveState.stored ?? 0)) +
        row('等待提交条目', num(archiveState.queued ?? 0)) + row('其中开启前待补录', num(archiveState.backfill ?? 0)) +
        row('仅热模式放弃条目', num(archiveState.discarded ?? 0)) + row('最近确认日期', dateOf(archiveState.lastDay ?? 0)) +
        '<p class="meta">完整历史按事件增量写入此浏览器的本城档案，界面按页读取。它保存历史记录，不是可恢复整城运行状态的游戏存档；刷新重新建城会生成另一份档案。下载可带走当前资料和本城已归档记录。浏览器清理站点数据会移除本地档案。</p>' +
        `<div class="sb-action-buttons"><button data-export="city:0:json">下载城市概况（JSON）</button><button data-export="city:0:txt">城市概况文本（TXT）</button>${historyPref ? '<button data-archive="later">以后新城不记录</button>' : ''}</div>`));
  }]];
  const historySources = new Map<string, string>();
  const historyRecordSummary = (r: J, metadata: J, kind: string, entityId: number): string => {
    const d = r.data ?? {};
    if (r.source === 'death') return deathHistoryHTML({ observed: !!d.death, record: d.death, scope: historySourceNote('death') }, { kind, entityId, seq: r.seq, opened }, deathLinks);
    if (r.source === 'medical') return medicalRecordHTML(d, r.kind === 'person' ? r.entityId : undefined, { hospital: f => facilityLink(f, 'hospital'), insurer: who });
    if (r.source === 'employment') return employmentRecordText(d, r.day).map(line => `<p>${esc(line)}</p>`).join('');
    if (r.source === 'resident_period' && d.period?.balance) {
      const p = d.period, b = p.balance, tags: J[] = metadata?.tags ?? [];
      const values: [string, number][] = [['现金', b.cash / 100], ['存款', b.deposit / 100], ['房产', b.property], ['登记股份', b.shares], ['其他经营权益', b.business], ['土地', b.land], ['期房', b.presales], ['在建权益', b.construction], ['贷款本金', -b.debt / 100]];
      const flows = (side: string) => (d.flows?.[side] ?? []).map((f: J) => {
        const t = tags.find(t => t.id === f.tag);
        return [esc(t?.[side === 'in' ? 'inName' : 'outName'] ?? `类别 #${f.tag}`), money(f.amount / 100), f.internal ? '本人存取款，不计收入/消费' : '实际收付'];
      });
      return `<details><summary>本期流入 ${money(p.totalIn / 100)} · 流出 ${money(p.totalOut / 100)} · 期末身家 ${money(b.netWorth)}</summary>` +
        (d.opening?.kind === 'previous_closing' ? row('真实期初', `${dateOf(d.opening.day)} · 身家 ${money(d.opening.balance.netWorth)}`) : '<p class="meta">首期或旧资料没有可直接比较的整月期初资产；观察快照引用保留在原始记录中。</p>') +
        table(['期末资产 / 负债', '金额'], values.map(([n, v]) => [n, money(v)])) +
        (d.completeFlows ? sec('完整到账分类', table(['流水', '金额', '口径'], flows('in'))) + sec('完整支付分类', table(['流水', '金额', '口径'], flows('out'))) : '<p class="meta">此旧记录只有主要分类摘要，完整分类未知。</p>') +
        '<p class="meta">总流入和总流出排除本人存取款，包含资产买卖与借还；这不是工资与消费合计，也不是单件事的盈亏。</p></details>';
    }
    if (r.source === 'property_sale') return row('实际交割房屋', unitLink(d.unit, `房屋 #${d.unit}`)) +
      row('成交总价', money(d.gross / 100)) + row('交割清偿旧本金', money(d.principalRepaid / 100)) + row('卖家实际到账', money(d.netReceived / 100)) +
      row('实际广告费用', money(d.advertisingPaid / 100)) + row('卖家扣广告后现金净收', money(d.netAfterSaleCosts / 100)) +
      row('总价分流对账', d.settlementComplete ? '成交总价 = 清贷本金 + 卖家实际到账' : '<span class="bad">交割金额尚未对齐，查看原始记录</span>') +
      row('买家现金 / 存款', `${money(d.buyerBefore.cash / 100)} / ${money(d.buyerBefore.deposit / 100)} → ${money(d.buyerAfter.cash / 100)} / ${money(d.buyerAfter.deposit / 100)}`) +
      row('卖家现金 / 存款', `${money(d.sellerBefore.cash / 100)} / ${money(d.sellerBefore.deposit / 100)} → ${money(d.sellerAfter.cash / 100)} / ${money(d.sellerAfter.deposit / 100)}`);
    if (r.source === 'origin') {
      const kinds: Record<string, string> = { initial_city: '建城时已在城内', initial_abroad: '建城时登记在外地', external_arrival: '从外地真实迁入', city_birth: '在城里出生', external_heir_created: '在外地登记的继承人，从未入城', unknown_before_observation: '观察前来历未记录' };
      return `<p>${esc(d.nameAsOf)} · 人物 #${r.entityId} · ${esc(kinds[d.kind] ?? d.kind)}；记录时 ${num(d.ageAsOf ?? 0, 2)} 岁</p>` +
        (d.region ? row(d.regionRole === 'arrival_source' ? '迁入来源' : '登记地区', esc(d.region.name)) : '') +
        (d.cityEntryAge != null ? row('实际入城年龄', `${num(d.cityEntryAge, 2)} 岁`) : '') +
        (d.actualMoneyBrought != null ? row('实际带入资金', money(d.actualMoneyBrought / 100)) : '') +
        row('该节点现金与存款', `${money((d.cashAsOf ?? 0) / 100)} · ${money((d.depositAsOf ?? 0) / 100)}`) +
        row('登记双亲', (d.registeredParents ?? []).filter((id: J) => id != null).map((id: number) => who({kind:'person',id,name:`人物 #${id}`})).join(' · ') || '没有登记') +
        row('当时生活监护人', d.guardianAsOf != null ? who({kind:'person',id:d.guardianAsOf,name:`人物 #${d.guardianAsOf}`}) : '没有登记或无需监护') +
        row('当时登记住处', d.registeredHome != null ? unitLink(d.registeredHome, `房屋 #${d.registeredHome}`) : '未登记实际房号') +
        `<p class="meta">${esc(d.homeScope)}；建城时的登记身份不冒充更早的出生地，未知来历不反推。</p>`;
    }
    if (r.source === 'ordinary') return `<p>${esc(d.reason)}；${esc(d.result)} · 行动者 #${d.actor}，${num(d.actorHours ?? 0, 2)} 小时${d.target != null ? ` · 对象 #${d.target}，${num(d.targetHours ?? 0, 2)} 小时` : ''}</p>`;
    if (r.source === 'firm_month' && d.month) return row('本期经营', `收入 ${money(d.month.revenue)} · 支出 ${money(d.month.expense)} · 期末现金 ${money(d.month.cash)}`);
    return `<p>${linkify(d.detail ?? d.result ?? '')}</p>`;
  };

  const historyView = (kind: string, entityId: number, forcedSource?: string): string => {
    const source = forcedSource ?? historySources.get(`${kind}:${entityId}`) ?? '';
    const key = `${kind}:${entityId}:${source}`;
    let h = histories.get(key);
    if (!h) {
      h = { stack: [], page: null, pending: null }; histories.set(key, h);
      // 只留有限实体的当前80条页，不把浏览过的全城历史再复制进界面内存。
      while (histories.size > 64) histories.delete(histories.keys().next().value!);
    }
    if (archiveState.state === 'off') return '<div class="sb-empty">完整历史未开启</div><div class="sb-action-buttons"><button data-archive="enable">开启完整历史</button></div>';
    // 中途开启要先把引擎里还留着的旧记录写进本机(第十年约二十万条);写完之前不读,免得翻页要等它写完
    if (archiveState.state === 'opening' || archiveState.backfill > 0) return `<div class="sb-empty">正在保存开启前的记录${archiveState.backfill > 0 ? `，还剩 ${num(archiveState.backfill)} 条` : ''}</div>`;
    if (!h.page && h.pending == null) {
      const id = ++qid; h.pending = id; historyRequests.set(id, key);
      worker.postMessage({ t: 'history', gen, id, kind, entityId, source: source || undefined, before: h.before, limit: 80 });
    }
    const filters = kind === 'person' ? [['', '全部'], ['life', '大事记'], ['death', '死亡记录'], ['employment', '任职与工资'], ['ordinary', '日常'], ['medical', '病历'], ['resident_period', '月度账本'], ['property_sale', '房产交易'], ['origin', '人物来历']] : kind === 'firm' || kind === 'hospital' ? [['', '全部'], ['story', '大事记'], ['employment', '任职与工资'], ['firm_month', '月度财报'], ...(kind === 'hospital' ? [['medical', '医疗病历']] : [])] : [['', '全部']];
    const filter = forcedSource ? '' : `<nav class="sb-history-filter">${filters.map(([v, n]) => `<button class="${v === source ? 'on' : ''}" data-hsource="${v}">${n}</button>`).join('')}</nav>`;
    const note = (forcedSource === 'medical' ? `<p class="meta">每页最多 80 条，只读取${kind === 'hospital' ? '本院关联的' : '本人的'}已归档医疗记录。近期热记录与既往摘要有保留上限；较早已归档的检查、治疗、住院和结局可继续翻页。旧版未保存的诊断、账单与归档前已丢失的记录不能恢复，也不会凭症状倒猜。</p>` : '<p class="meta">每页最多 80 条，归档继续增长。月度快照覆盖整个结账期间，不把整月变化归因于单件事；旧版本已经丢失的数据不会补造。原始记录保留事件编号、实体编号和实际单位；人物与公司关联的同一编号是一条共同记录。</p>') +
      (source ? `<p class="meta">${esc(historySourceNote(source))}</p>` : '');
    if (!h.page) return filter + note + '<div class="sb-empty">正在读取这一页历史…</div>';
    const page = h.page;
    const coverage = page.coverage, start = coverage?.coverageStart ?? coverage?.metadata?.coverageStart;
    const hasStart = Number.isSafeInteger(start) && start >= 0;
    const coverageNote = hasStart ? `<p class="meta">本城归档观察从 ${dateOf(start)} 开始${Number.isSafeInteger(coverage.lastDay) && coverage.lastDay >= 0 ? `；归档队列最近处理日 ${dateOf(coverage.lastDay)}` : ''}${(coverage.retainedOnly ?? coverage.metadata?.retainedOnly) ? '；观察前仅导入当时仍保留的记录，不代表此前全部历史' : ''}。${coverage.state === 'memory' ? '最近处理日不代表这些记录已落盘。' : ''}</p>` : '';
    const rows = (page.records ?? []).map((r: J) => {
      const d = r.data ?? {};
      return `<li><time>${dateOf(r.day)}</time><span class="tag">${r.kind === 'person' && (kind !== 'person' || r.entityId !== entityId) ? `关联人物 #${r.entityId} · ` : ''}${esc(historySourceLabel(r.source))}${r.source !== 'employment' && (d.event || d.name) ? ` · ${esc(d.event ?? d.name)}` : ''}</span>${historyRecordSummary(r, page.coverage?.metadata, kind, entityId)}${historyRawHTML(kind, entityId, r, opened)}</li>`;
    }).join('');
    return filter + note + (page.error ? `<p class="bad">${esc(page.error)}</p>` : '') +
      (page.coverage?.state === 'memory' ? '<p class="bad">当前仅热数据模式：完整历史只含切换前实际落盘的记录。</p>' : '') +
      coverageNote +
      `<div class="sb-history-controls"><button data-hpage="prev" data-hkey="${esc(key)}"${h.stack.length ? '' : ' disabled'}>较新一页</button><span>第 ${h.stack.length + 1} 页</span><button data-hpage="next" data-hkey="${esc(key)}"${page.hasMore ? '' : ' disabled'}>更早一页</button><button data-hpage="refresh" data-hkey="${esc(key)}">刷新本页</button></div>` +
      (rows ? `<ol class="sb-life">${rows}</ol>` : '<div class="sb-empty">这个范围还没有已归档记录</div>');
  };
  const residentAudit = (a: J): string => {
    const x = a.audit;
    if (!x) return '<div class="sb-empty">此引擎尚未提供账本校验资料</div>';
    const flows = (xs: J[]) => table(['实际流水', '金额', '口径'], (xs ?? []).map(r => [esc(r.name), money(r.amount), r.internal ? '自己存取款，不计收入/支出' : '真实收付']));
    return `<p class="meta">${esc(x.scope)}</p>` +
      grid(tile('手头现钱', money(x.cashNow), '当前已经持有的现金'), tile('名下存款', money(x.deposit), '提款时仍需复核银行流动性'), tile('应收欠薪', money(x.wageReceivable), '尚未到账，与现金分开')) +
      row('欠薪雇主', who(x.wageDebtor)) + row('成年年龄', `${x.adultAge} 岁`) + row('当前可提供劳动', x.workAvailable === true ? '可按当前规则安排工作' : x.workAvailable === false ? '受健康、年龄、羁押或其他实际条件限制' : '未记录') +
      row('实际监护的未成年子女', `${x.householdDependents} 人`) + row('终身现金与存款账差', signed(x.liquidLifetimeDifference)) +
      `<p class="meta">${esc(x.liquidNote)} ${esc(x.receivableNote)} ${esc(x.repayScope)}</p>` +
      sec('已结账的月末资产负债', table(['日期', '现金', '存款', '房产', '登记股份', '经营权益', '土地', '期房 / 在建', '贷款本金', '净资产', '资产账差', '资金账差'], [...(x.months ?? [])].reverse().map((m: J) => [dateOf(m.day), money(m.cash), money(m.deposit), money(m.property), money(m.shares), money(m.business), money(m.land), `${money(m.presales)} / ${money(m.construction)}`, money(m.debt), money(m.netWorth), signed(m.balanceDifference), m.liquidDifference == null ? '缺期初' : signed(m.liquidDifference)])), '热窗口保留最近 24 期；较早月份从完整历史 → 月度账本分页读取，下载包含实际归档的月份') +
      sec('本月已到账', flows(x.currentIn)) + sec('本月已支付', flows(x.currentOut)) +
      sec('上期已到账', flows(x.lastIn)) + sec('上期已支付', flows(x.lastOut)) +
      `<p class="meta">${esc(x.consumptionScope)} 账差使用原始金额核算，展示金额经过舍入；资金账差非零会保留提示供排查，不强行塞入某件事。</p>` +
      sec('历史缺失与统计限制', `<ul>${(x.missing ?? []).map((n: string) => `<li>${esc(n)}</li>`).join('')}</ul>`);
  };

  const residentActions = (a: J, id: number): string => {
    if (!E.life2) return '<p class="meta">当前引擎没有日常行动接口</p>';
    const v = read(E.life2(id));
    const button = (label: string, code: number, target = 0xffff_ffff, value = 0, available = true) => `<button data-act="${id}:${code}:${target}:${value}"${speed !== 0 || actionPending != null || !available ? ' disabled' : ''}>${esc(label)}</button>`;
    const choices = (v.choices ?? []).map((c: J) => `<div class="sb-action">${button(c.name, c.code, c.target?.id, 0, c.available)}<span>${c.target ? who(c.target) + ' · ' : ''}${esc(c.reason)}${c.hours != null ? ` · ${num(c.hours, 2)} 小时` : ''}</span></div>`).join('');
    return `<p class="meta">先暂停，再以该居民尝试行动。使用城市现有条件、真实时间与支付规则；居民仍会自主生活。办理结果以回执和账单为准。</p>` +
      grid(tile('今天实际活动', `${num(v.activities ?? 0, 2)} 小时`), tile('实际餐饮娱乐', `${num(v.leisure ?? 0, 2)} 小时`), tile('剩余时间', `${num(v.free ?? 0, 2)} 小时`)) +
      sec('生活选择', choices) + sec('就医办理', `<div class="sb-action-buttons">${button('按病程就医', 1)}${button('申请住院', 2)}${button('按病情出院', 3)}</div><p class="meta">主动医疗每天最多办理一次。需要真实病程、医生、耗材、床位与支付能力。</p>`) +
      (a.adult ? sec('中等补修', secondaryCompletionActions(a.education, { personId: id, here: a.here === true, paused: speed === 0, pending: actionPending != null })) : '') +
      (a.adult && a.education?.higherEnabled ? sec('申请高等课程', `<div class="sb-action-buttons">${button('申请大学课程', 7, 0xffff_ffff, 4)}${button('申请医学院课程', 7, 0xffff_ffff, 5)}</div><p class="meta">大学需要真实中等学历，医学需要大学学历；报名后仍等待相应学院、合格教师、真实时间和付费。</p>`) : '') +
      (a.adult ? sec('申请职业课程', `<div class="sb-action-buttons">${['木工', '钢筋工', '混凝土工', '瓦工', '水电工', '装饰工', '防水工'].map((n, i) => button(n, 4, 0xffff_ffff, i + 1)).join('')}</div>`) : '') +
      ((a.familyDetails?.children ?? []).filter((c: J) => c.guardian?.id === id && c.here && !c.adult).length ? sec('为实际监护的孩子办理幼托', (a.familyDetails.children as J[]).filter((c: J) => c.guardian?.id === id && c.here && !c.adult).map((c: J) => `<div class="sb-action">${who(c.who ?? c)}${button('申请幼托', 5, c.who?.id ?? c.id)}${button('退托', 6, c.who?.id ?? c.id)}</div>`).join('')) : '') +
      sec('最近日常记录', (v.records ?? []).length ? table(['日期', '活动', '对象', '本人用时', '当时依据与结果'], v.records.map((r: J) => [dateOf(r.day), esc(r.name), r.target ? who(r.who?.id === id ? r.target : r.who) : '自己', `${num(r.hours, 2)} 小时`, `${esc(r.reason)}<br>${esc(r.result)}`])) : '<div class="sb-empty">尚无已完成的日常活动</div>', '这里保留最近 24 条；完整历史和下载保留本城已归档记录');
  };

  const dashboard = guard(() => {
    const el = document.getElementById('dash')!;
    if (tab === 'gov') tab = 'govt'; // 财政并进了政府(网址、浏览器记着的旧名字)
    document.querySelectorAll('#rail [data-tab]').forEach((b) => b.classList.toggle('on', (b as HTMLElement).dataset.tab === tab));
    if (!tab) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    const title = TABS.find(([k]) => k === tab)?.[1] ?? '';
    const pages: Record<string, () => Tab[]> = { overview, people, society, medical, education, govt, justice, market, estate, build: building, firms, finance, regions, news, follow: followPage, engine: enginePage };
    patch(el, paged(`d:${tab}`, `<div class="sb-head"><h3>${icon(tab, 18)}${esc(title)}</h3><button class="sb-x" data-closetab title="关闭(Esc)">${icon('close', 16)}</button></div>`, pages[tab]?.() ?? []));
  }, () => {
    if (!tab) return null;
    const key = `d:${tab}`, page = sub.get(key) ?? '';
    const label = [TABS.find(([id]) => id === tab)?.[1] ?? '看板', pageLabels.get(`${key}|${page}`)].filter(Boolean).join(' · ');
    return { host: document.getElementById('dash')!, context: { key: `${gen}|${key}|${page}`, label, close: 'dashboard' } };
  });

  // ---------- 查看器 ----------
  const go = (s: Sel) => {
    mapIntent = null;
    if (sel && (sel.kind !== s.kind || sel.id !== s.id)) trail.push(sel);
    sel = s;
    inspector();
    toTop('insp');
  };

  // 玩家模式的快捷:自己的人物页、自己的公司(再点一次回到原来看的那一页)
  let playerId: number | null = null;
  let playerFirms: number[] = [];
  const navTo = (kind: 'person' | 'firm', id: number) => {
    if (sel && sel.kind === kind && sel.id === id) {
      sel = trail.pop() ?? null;
      inspector();
      return;
    }
    go({ kind, id });
  };

  // 面板上「定位」按钮要去的地块:每一页算自己的(人是住处,公司是总部,工程是工地)
  let locate: number | null = null;

  /** 身家怎么来的:开户带的 + 一共挣的 − 一共花的 ± 搬家汇款、其他 + 每样资产(现在值的 − 净花进去的)= 现在的身家。 */
  const originView = (o: J, worth: number) => {
    const sum = (rows: J[], k: string) => rows.reduce((s: number, r: J) => s + r[k], 0);
    const inc = sum(o.income, 'gain') - sum(o.income, 'spend');
    const spend = sum(o.spend, 'spend') - sum(o.spend, 'gain');
    const move = sum(o.move, 'gain') - sum(o.move, 'spend');
    const other = sum(o.other, 'gain') - sum(o.other, 'spend');
    const gains = o.assets.reduce((s: number, x: J) => s + x.gain, 0);
    const total = o.start + inc - spend + move + other + gains;
    // 瀑布图:开户带的起,一项项加减到现在的身家(各项加起来就是身家)
    const items = [
      { label: '开户时登记资金', value: o.start, note: '实际资金期初，不含初始非现金资产' },
      { label: '一共挣的', value: inc, note: '工资、分红、房租、利息、社保、继承……' },
      { label: '一共花的', value: -spend, note: '吃、住、玩、用品、交通、税、利息……' },
      { label: '搬家、汇款', value: move },
      { label: '其他', value: other },
      ...o.assets.map((x: J) => ({ label: x.name, value: x.gain, note: x.name === '贷款'
        ? `借进 ${money(x.got)} · 还掉 ${money(x.paid)} · 还欠 ${money(-x.now)}`
        : `已记支付 ${money(x.paid)} · 已记收回 ${money(x.got)} · 当前估值 ${money(x.now)}` })),
    ];
    const detail = (title: string, list: J[], out: boolean) => list.length
      ? sec(title, table(['类', '一共'], list.filter((r: J) => (out ? r.spend : r.gain) > 0).map((r: J) => [esc(out ? r.out : r.in), money(out ? r.spend : r.gain)]))) : '';
    return sec('身家怎么来的', `<p class="meta">${esc(o.scope ?? '资产贡献用于对账，不等于投资收益；旧成本未记录时不能反推涨幅。')}</p>` + waterfall(items, money, '现在的身家') + (Math.abs(total - worth) >= 1 ? `<p class="bad">和身家差 ${money(total - worth)}</p>` : '')) +
      detail('挣的,按类', o.income, false) + detail('花的,按类', o.spend, true);
  };

  /** 持股账:每一家投了多少、眼下值多少、分到多少红、清算拿回多少、赚了还是亏了(关了门的也列)。 */
  const stakeTable = (list: J[]) => table(['公司', '成本', '眼下值', '分到', '盈亏'],
    list.map((x: J) => [
      `${who(x.firm)}${x.status ? ` <span class="tag${x.status === '关门' || x.status === '贷款逾期' ? ' bad' : ''}">${esc(x.status)}</span>` : ''}<br><small class="meta">${esc(x.line.split(' · ')[0])} · ${x.pct >= 0.01 ? `占 ${x.pct.toFixed(2)}%` : `${num(x.shares)} 股`}${x.since ? ` · ${monthOf(x.since)}起` : ''}</small>`,
      money(x.cost),
      x.closed ? '<span class="meta">关门了</span>' : money(x.value),
      `${money(x.divs + x.back)}${x.back ? `<br><small class="meta">清算拿回 ${money(x.back)}</small>` : x.yield ? `<br><small class="meta">一年 ${x.yield.toFixed(1)}%</small>` : ''}`,
      `${signed(x.gain)}${x.cost > 0 ? `<br><small class="meta">${x.gain >= 0 ? '+' : ''}${Math.round((x.gain / x.cost) * 100)}%</small>` : ''}`,
    ]));
  /** 通常一天的安排和市场共用的预算,不把想买的数量当成已经发生的消费。 */
  const personDay = (a: J) => {
    const x = a.lifestyle;
    if (!x) return '<div class="sb-empty">不在城里的居民没有当前一日安排</div>';
    const p = x.plan, b = x.leisure;
    const hours = (v: number) => `${v.toFixed(2).replace(/\.?0+$/, '')} 小时`;
    const times: [string, number][] = [
      ['睡眠', p.sleep], ['吃饭', p.meals], ['工作与经营', p.work], ['学习', p.study], ['幼托照护', p.supervision ?? 0], ['找工作', p.search],
      ['上下班来回', p.commute], ['已购买的餐饮娱乐', p.leisure ?? 0], ['已完成的日常活动', p.activities ?? 0], ['生活事务', p.chores], ['照顾孩子', p.care], ['养伤恢复', p.recovery], ['羁押等受限时间', p.restricted],
    ];
    const commute = p.commuteEstimated
      ? '<span class="tag">估计</span> 当前家与工作之间的步行距离；尚无适用的通勤样本'
      : p.commuteSample != null
        ? `${dateOf(p.commuteSample)}选路时的实际来回分钟，已换算成小时`
        : p.work > 0 ? '没有适用的住处或工作路线，这份安排未计入通勤' : '这份安排没有工作通勤';
    const children = p.youngKids + p.schoolKids;
    return `<p class="sb-lead">${dateOf(x.day)}，通常一天怎样安排。</p><p class="meta">按当前工作、学习、家庭照料和通勤估算时间去向；不是小时级行程，也不是今天已完成的日志。可支配时间还可以用于餐饮娱乐或休息。</p>` +
      grid(tile('必要安排', hours(p.fixed), '睡眠、吃饭及其他固定事项'), tile('可支配时间', hours(p.free), '一天 24 小时以内还能安排的时间', p.free > 0 ? 'good' : ''),
        tile('时间超额', hours(p.overload), p.overload > 0 ? '通常安排已超过一天，餐饮娱乐受时间限制' : '通常安排未超过一天', p.overload > 0 ? 'bad' : '')) +
      sec('时间去向', table(['安排', '一天小时'], times.map(([name, value]) => [esc(name), hours(value)])) +
        row('通勤依据', commute) +
        row('照料对象', children ? `同住幼儿 ${p.youngKids} 人 · 学龄孩子 ${p.schoolKids} 人` : '当前没有计入家庭照料的同住孩子') +
        (children ? row('配偶分担', p.sharedCare ? '与同住配偶平分照料时间；上表是本人承担的部分' : '这份照料时间没有与配偶分摊') : '')) +
      (b ? sec('先留多少钱', row('现金与存款合计', money(b.cash)) + row('日常生活储备目标', money(b.basicReserve)) +
        row('购房首付目标', money(b.homeSaving)) + row('扣除目标后的余钱', signed(b.spare)) +
        '<p class="meta">储备和首付目标仍包含在钱包里，是安排消费时希望留下的钱。</p>') +
        sec('餐饮娱乐预算', (x.legacy ? '<p class="meta">当前使用旧规则，消费数量只看钱和家庭人头；上面的时间安排仍可查看。</p>' : '') +
          row('每天数量上限', `${num(b.cap, 2)} 份`) + row(x.legacy ? '旧规则数量上限' : '按时间约束后的上限', `${num(b.timeCap, 2)} 份`) +
          row('当前想买', `${num(b.want, 2)} 份`) + row('本次愿花的预算', money(b.budget)) + row('安排理由', esc(b.reason)) +
          row('家庭同行系数', num(b.familyScale, 3)) + row('同行时间口径', `${num(b.want, 2)} 份 ÷ ${num(b.familyScale, 3)} × ${num(b.hoursPerFamilyServe, 2)} 小时`) +
          row('每份占本人时间', hours(b.hoursPerServe)) +
          '<p class="meta">餐饮娱乐合并计量，全家同时消费，数量按家庭系数折算同行时间。想买和预算都不是成交结果；有货并实际付款后才记消费，真实支出见「收支」。</p>') : '');
  };
  const personFamily = (a: J) => {
    const f = a.familyDetails;
    if (!f) return '<div class="sb-empty">没有家庭记录</div>';
    const state = (r: J) => !r.alive ? '已去世' : !r.here ? '在外地' : r.held ? '羁押中' : '在城';
    const relatives = [...(f.spouse ? [f.spouse] : []), ...f.parents, ...f.children];
    const guardian = f.guardian;
    return grid(tile('性别', esc(f.sex)), tile('婚姻', f.spouse ? who(f.spouse.who) : '未婚或当前无配偶', f.married != null ? `${dateOf(f.married)}结婚` : ''),
      tile('孩子', `${f.children.length} 人`, '出生子女及当前照料对象'), tile('登记同住', `${f.household.length} 人`, f.home == null ? '当前无登记住所' : '按实际同一套住房统计')) +
      (a.adult ? row('婚配偏好', esc(f.preference)) : '') +
      sec('孕育', f.pregnancy ? row('预产日期', `${dateOf(f.pregnancy.due)} · 还需 ${f.pregnancy.daysLeft} 天`) +
        row('另一出生亲', f.pregnancy.partner ? `${who(f.pregnancy.partner.who)} · ${state(f.pregnancy.partner)}` : '未登记') +
        '<p class="meta">孕期从受孕时记录，离婚或另一出生亲离世不会改写孩子的出生关系。</p>' : '<p class="meta">当前没有孕育记录。</p>') +
      sec('亲属与监护', (guardian ? row('当前生活监护人', `${who(guardian.who)} · ${state(guardian)}`) : !a.adult ? row('当前生活监护人', a.custody === 1 || a.custody === 2 ? '由所在机构照看' : '当前没有可照料的监护人') : '') +
        (relatives.length ? table(['关系', '人', '性别', '岁', '状态', '照料'], relatives.map((r: J) => [esc(r.rel), who(r.who), esc(r.sex), String(r.age), state(r),
          r.adult === false ? (r.guardian ? `口粮由${who(r.guardian)}负责` : r.held ? '机构照看' : '待安排监护') : ''])) : '<div class="sb-empty">没有已登记亲属</div>') +
        (a.siblings.length ? row('兄弟姐妹', a.siblings.map((r: J) => who(r.who)).join(' · ')) : '') +
        '<p class="meta">出生双亲保留血缘关系，当前监护人负责生活；换监护或再婚会保留出生关系。</p>') +
      sec('同住家人的日常账', f.household.length ? table(['人', '关系', '各自现金与存款', '日常收入估计/天', '上月收到赠与', '上月给出赠与'], f.household.map((r: J) => [who(r.who), esc(r.rel), money(r.cash), money(r.income), money(r.giftIn), money(r.giftOut)])) : '<div class="sb-empty">当前没有登记同住成员</div>') +
      sec('谁负责生活', row('本人及受照料孩子的口粮', f.foodBuyer ? `${who(f.foodBuyer)}采购${f.foodNeed > 0 ? ` · 当前共 ${num(f.foodNeed, 2)} 份/天` : ''}` : '当前没有在城采购责任人') +
        row('家人接济', f.helpDaily ? '每天检查实际需要；给款者仍须留够自己的家庭储备' : '按月检查') +
        '<p class="meta">各人的钱仍在各自账户。接济须真实转账，赠与也可能包含成年资助等；不会因为有家人就直接多出存款或免费食物。</p>');
  };
  const personView = (id: number, a: J = read(E.person2(id))): [string, Tab[]] => {
    const hasDeath = a.death?.observed === true && a.death?.record != null;
    const died = hasDeath || a.gone?.how === '去世';
    const deathView = () => deathHTML(a.death, { here: a.here, goneHow: a.gone?.how }, deathLinks);
    const deathEntry = died ? sec('死亡记录', (hasDeath ? row('实际触发源', esc(a.death.record.trigger?.name ?? '未知')) + row('实际死亡日', dateOf(a.death.record.day)) : '<p class="meta">旧资料未保留具体死亡快照，死因未知。</p>') + '<button data-sub="i:person|death">查看死亡细节、当时病历与关联案卷</button>') : '';
    locate = a.home?.plot ?? null;
    const b = a.balance;
    const assets = b.cash + b.deposit + b.houses + b.shares + b.business + b.land + b.presale + (b.wip ?? 0);
    const role = died ? '已去世' : !a.adult ? '孩子' : a.firms.some((f: J) => f.boss) ? '总经理' : a.job ? esc(a.job.trade) : a.age >= 68 ? '退休' : '没活干';
    // 玩家自己在失信名单上:人物页上带一个去失信页的按钮(为什么、怎么出来)
    const meNow = playMode && E.me ? read(E.me()) : null;
    const credit = meNow?.active && meNow.id === id && (meNow.credit?.listed || meNow.credit?.n > 0) ? ` · <span class="bad">失信</span> <button class="sb-sd-btn" data-credit="open">失信页</button>` : '';
    const head = `<div class="sb-who">${avatar(id, a.age, `${role} ${a.job?.line ?? ''}`, a.happy, 46)}<div><h3>${esc(a.name)}</h3><p class="meta">${esc(a.sex ?? '')} · ${Math.floor(a.age)} 岁 · ${role} · 手艺 ${a.skill.toFixed(2)}${died || a.here ? '' : ' · <span class="bad">不在城里了</span>'}${a.strike ? ' · <span class="bad">在停贷</span>' : ''}${credit}</p></div></div>`;
    // 上个月的收入、花销、结余:按账算(工资、分红、房租、利息、社保;吃、住、玩、交通、税、贷款利息)
    const inc = groupSum(a.book, '收入', true), spend = groupSum(a.book, '花销', false);
    // 去世、搬走的:账停在走之前的最后一个月底,「上月」是那个月
    const gone = a.gone as J | null;
    const lastM = gone ? `${gone.how}前一月` : '上月';
    const endM = gone ? monthOf(gone.booksEnd - 1) : '';
    const kpi = grid(
      tile('身家', money(a.worth), a.adult ? `全城第 ${a.rank} / ${a.of}` : ''),
      tile(`${lastM}收入`, money(inc), gone ? endM : '工资、分红、房租、利息、社保', 'good'), tile(`${lastM}花销`, money(spend), gone ? endM : '吃、住、玩、交通、税、利息', 'bad'),
      tile(`${lastM}结余`, signed(inc - spend)),
      tile('现金 · 存款', money(b.cash), `存款 ${money(b.deposit)}`), tile('房产', money(b.houses), `${a.houses.length} 套`),
      tile('公司股份', money(b.shares + b.business), `${a.firms.filter((f: J) => f.pct > 0 || f.boss).length} 家${a.firms.some((f: J) => f.boss) ? ' · 自己管 1 家' : ''}`), tile('欠银行', money(b.debt), `${a.loans.length} 笔`, b.debt ? 'bad' : ''),
    );
    // 生活:一句话、住处、今天、一天的钱、身体和心情、人(见 dash.rs 的 house_json、daily_json、body_json)
    const h = a.house, d = a.daily, bd = a.body;
    const others = (h?.people ?? []).filter((x: J) => x.who.id !== id);
    const since = (day: number) => {
      const n = city.day - day;
      return n >= 360 ? `${(n / 360).toFixed(1)} 年` : n >= 30 ? `${Math.floor(n / 30)} 个月` : `${n} 天`;
    };
    const summary = () => {
      const parts: string[] = [`${Math.floor(a.age)} 岁`];
      if (a.job) parts.push(`在${who(a.job.firm)}当${esc(a.job.trade)}`);
      else if (a.firms.some((f: J) => f.boss)) parts.push(`管着${a.firms.filter((f: J) => f.boss).map((f: J) => who(f.firm)).join('、')}`);
      else if (d?.doing) parts.push(esc(d.doing));
      const home = h ? `住在${esc(h.district)}的${h.unit != null ? unitLink(h.unit, h.addr || '一套房') : plotLink(h.plot, h.addr || '一套房')}(${h.floor} 楼,${Math.round(h.area)}㎡,${esc(h.how)}${h.how === '租的' ? `,一天 ${Math.round(h.rent)} 元` : ''})` + (others.length ? `,和${others.map((x: J) => `${esc(x.rel)}${who(x.who)}`).join('、')}一起住` : ',一个人住') : '<span class="bad">没有住处</span>';
      const go = a.commute.working && h ? `,每天${esc(a.commute.mode)}上班,来回 ${Math.round(a.commute.min)} 分钟` : '';
      return `<p class="sb-lead">${parts.join(',')};${home}${go}。</p>`;
    };
    const houseSec = () => {
      if (!h) return sec('住处', `<div class="sb-empty">没有住处${a.custody ? '(关着)' : ''}</div>`);
      const bld = h.building;
      const fac = (x: J, what: string, kind?: 'hospital' | 'school') => (x ? `${facilityLink(x, kind)} · 隔 ${x.blocks} 格${x.q != null ? ` · 好坏 ${x.q.toFixed(2)}` : ''}` : `<span class="meta">城里还没有${what}</span>`);
      return sec('住处', row('地址', `${h.unit != null ? unitLink(h.unit, h.addr || '一套房') : plotLink(h.plot, h.addr || '一套房')} · ${h.floor} 楼 ${h.room} 户 · ${Math.round(h.area)}㎡`) +
        row('片区', `${esc(h.district)} · (${h.at[0]}, ${h.at[1]})`) +
        row('房子', h.how === '租的'
          ? `租的 · 房东 ${who(h.owner)} · 一天 ${Math.round(h.rent)} 元${h.tenant && h.tenant.id !== id ? ` · ${who(h.tenant)}签的租约` : ''}${h.rentShare ? ` · 占全家收入 ${pct(h.rentShare)}` : ''}`
          : `${esc(h.how)} · 市值 ${money(h.value)}${h.bought ? ` · 当初 ${money(h.bought)} 买的` : ''}${h.loan ? ' · <span class="bad">在还按揭</span>' : ''}`, h.rentShare > 0.4 ? 'bad' : '') +
        (h.since ? row('住进来', `${dateOf(h.since)} · ${since(h.since)}`) : '') +
        row('一起住', h.people.length > 1 ? h.people.map((x: J) => `${avatar(x.who.id, x.age, '', 0.6, 18)}${x.who.id === id ? '<b>自己</b>' : `${esc(x.rel)} ${who(x.who)}`} ${x.age} 岁`).join(' · ') : '一个人住') +
        (bld ? row('这栋楼', `${plotLink(h.plot, bld.name)} · ${bld.floors} 层 ${bld.units} 套${bld.age != null ? ` · 盖好 ${bld.age.toFixed(1)} 年` : ''}${bld.builder ? ` · ${who(bld.builder)}盖的` : ''}${bld.grade ? ` · 品级 ${esc(bld.grade)}` : ''}`) : '') +
        (bld?.cracked ? row('', `<span class="bad">楼里裂了 ${bld.cracked} 层</span>`) : '') +
        (h.maintained ? '' : row('维修', '<span class="bad">房东今天没交维修费</span>')) +
        row('邻居', `比全城平均富 ${h.prestige.toFixed(2)} 倍 · 这片一百平米一天房租约 ${Math.round(h.areaRent)} 元`) +
        row('最近的学校', fac(h.school, '学校', 'school')) + row('最近的医院', fac(h.hospital, '医院', 'hospital')) + row('最近的派出所', fac(h.police, '派出所')) +
        (h.myHospital ? row('看病去', esc(h.myHospital)) : '') + (h.mySchool ? row('上学在', esc(h.mySchool)) : ''));
    };
    const todaySec = () => sec('今天', row('在干什么', esc(d?.doing ?? '')) +
      (a.adult ? row('工作', a.job ? `${esc(a.job.trade)} · ${who(a.job.firm)}${a.job.at ? ` · ${plotLink(a.job.at.plot, a.job.at.name)}` : ''} · 合同日薪报价 ${num(a.job.wage, 2)} 元 · 老板 ${who(a.job.boss)} · 同事 ${a.job.coworkers} 人 · ${yearOf(a.job.since)}入职；<a data-go="person:${id}" data-view="employment">查看实际应发、实发与欠薪 →</a>` : a.firms.some((f: J) => f.boss) ? '管自己的公司' : a.age >= 68 ? '退休' : `没活干${a.idle ? `(${a.idle} 天)` : ''}`) : '') +
      (a.commute.working && h ? row('上班', commuteText(a.commute)) : '') +
      (a.commute.car || a.commute.bike ? row('车', [a.commute.car ? '汽车' : '', a.commute.bike ? '电动车' : ''].filter(Boolean).join('、')) : '') +
      (!a.adult ? row('跟着谁', a.parent ? who(a.parent.who) : '—') : ''));
    const moneySec = () => d && (d.spend.length || d.earn.length)
      ? sec('一天的钱', `<div class="sb-2">${d.earn.length ? bars(d.earn.map((x: J) => ({ label: x.name, value: x.v, color: 'var(--good)' })), (v) => `${Math.round(v)} 元`) : '<div class="sb-empty">上个月没进账</div>'}${d.spend.length ? bars(d.spend.map((x: J) => ({ label: x.name, value: x.v, color: 'var(--bad)' })), (v) => `${Math.round(v)} 元`) : '<div class="sb-empty">上个月没花钱</div>'}</div>` +
          row('一天的基本开销', `${Math.round(d.living)} 元(一个人吃住的底)`) + row('家里存的日用', `${d.food.toFixed(1)} 份`), '上个月每天平均')
      : '';
    const bodySec = () => sec('身体和心情', row('心情', `${Math.round(bd.happy * 100)}`) + row('压力', dbar(String(Math.round(bd.stress * 100)), bd.stress, bd.stress > 0.5 ? 'var(--bad)' : 'var(--ink-3)')) +
      (bd.depressed ? row('', '<span class="bad">抑郁</span>') : '') + (bd.injured != null ? row('伤', `养到 ${dateOf(bd.injured)}`, 'bad') : '') +
      (bd.hunger > 0.3 ? row('饿着', `${bd.hunger.toFixed(1)} 天`, 'bad') : '') +
      (bd.grief != null ? row('失去家人', monthOf(bd.grief), 'bad') : '') + (bd.setback != null ? row('倒霉事', `${monthOf(bd.setback)}(公司倒了、被赶出来、出狱……)`, 'bad') : '') +
      (bd.sad != null ? row('身边的人出事', monthOf(bd.sad)) : ''));
    const peopleSec = () => sec('人', (a.spouse ? row('婚姻', `和 ${who(a.spouse.who)}${a.married != null ? ` · ${yearOf(a.married)}结婚` : ''}`) : a.adult ? row('婚姻', '单身') : '') +
      (a.paper ? row('订报', who(a.paper)) : '') +
      (a.jailed != null ? row('坐牢', `到 ${dateOf(a.jailed)}`, 'bad') : '') +
      (a.record ? row('前科', `${a.record} 次`, 'bad') : '') +
      (a.grudge ? row('记恨', `${who(a.grudge.who)}(${esc(a.grudge.why)},${monthOf(a.grudge.day)})`, 'bad') : '') +
      (a.custody ? row('眼下', `${custodyName(a.custody)}${a.custody !== 3 && a.heldAt ? ` · ${esc(a.heldAt)}` : ''}`, 'bad') : ''));
    // 一生:从哪来,一辈子挣的、花的、买卖房子、入股的总账,每个月底身家的走势
    const lifeSum = () => {
      const s = a.lifeSum;
      const life: J[] = a.life ?? [];
      if (!s) return '';
      const first = life.find(e => e.kind === '出生' || e.kind === '搬来');
      const parts = (xs: [string, number][]) => xs.filter(([, v]) => Math.abs(v) >= 1).map(([k, v]) => `${k} ${money(v)}`).join(' · ') || '<span class="meta">没有</span>';
      const flips = s.houseSold - s.houseBought;
      return sec('一生', (first ? row('来历', `${dateOf(first.day)} · ${linkify(first.text)}`) : row('来历', `<a data-origin="${id}">查看人物来历 →</a>；这里没有保留出生或迁入记录`)) +
          row('挣的', parts([['工钱', s.wage], ['从公司支取', s.draw], ['分红', s.dividend], ['收房租', s.rentIn], ['利息', s.interest], ['救济社保', s.welfare], ['继承和赠与', s.inherit]])) +
          row('花的', parts([['房租', s.rentOut], ['吃', s.food], ['下馆子', s.fun], ['用品', s.goods], ['税', s.tax]])) +
          row('房子', parts([['买房付出', s.houseBought], ['卖房净到账', s.houseSaleNetReceived ?? s.houseSold], ['借按揭', s.loanIn], ['归还本金', s.repaid], ['付利息', s.interestPaid], ['翻新', s.reno], ['维修', s.upkeep]]) +
            (s.houseBought || s.houseSold ? ` · <b>${flips >= 0 ? '累计售房净到账比购房支付多' : '累计购房支付比售房净到账多'} ${money(Math.abs(flips))}</b>,名下的房眼下值 ${money(a.balance.houses)}（这不是已实现买卖利润）` : '')) +
          row('入股', parts([['投进去', s.sharesBought], ['卖股拿回', s.sharesSold], ['分红', s.dividend]]) + (s.sharesBought ? ` · 手上的股份眼下值 ${money(a.balance.shares)}` : ''))) +
        ((s.worth as number[]).length >= 2 ? sec('身家(每个月底)', lineChart(monthsX(s.worth.length, a.gone?.booksEnd), [{ name: '身家', vals: s.worth, fmt: money }], { h: 120, fmt: money, key: `lw${id}`, area: true })) : '');
    };
    // 事件与月度财务上下文分开,同一期只显示一次快照。
    const bioView = () => {
      const ev: J[] = [...(a.life ?? [])].reverse();
      if (!ev.length) return deathView() + lifeSum() + '<div class="sb-empty">还没有记下什么事</div>';
      let year = -1, period: number | null = null;
      const ctx = (e: J) => {
        const label = `同期财务快照 · ${dateOf(e.periodStart)} → ${dateOf(e.periodEnd)}${e.periodOpen ? '（进行中）' : ''}`;
        const flows = e.period;
        const changes: J[] = [...(e.assets ?? [])].sort((a, b) => Math.abs(b.change) - Math.abs(a.change));
        const mainChange = changes.find((x) => Math.abs(x.change) >= 1);
        const list = (xs: J[]) => xs.length ? xs.map((x) => `${esc(x.name)} ${money(x.amount)}`).join(' · ') : '没有';
        return `<li class="period"><details><summary>${label}${mainChange ? ` · ${esc(mainChange.name)} ${signed(mainChange.change)}` : ''}</summary>` +
          row('同期身家', `${e.w0 != null ? money(e.w0) : '未留快照'} → ${e.w1 != null ? money(e.w1) : '未留快照'}`) +
          row('同期现金与存款', `${e.c0 != null ? money(e.c0) : '未留快照'} → ${e.c1 != null ? money(e.c1) : '未留快照'}`) +
          (flows ? row('同期全部进账', money(flows.totalIn)) + row('主要进账', list(flows.in)) + row('同期全部支出', money(flows.totalOut)) + row('主要支出', list(flows.out)) : '<p class="meta">本人的最近 24 期财务摘要中没有这个期间，无法还原分类进出。</p>') +
          (e.assets ? sec('资产分项变化 · 包含交易与重估', table(['项目', '期初', '期末', '净变化'], e.assets.map((x: J) => [esc(x.name), money(x.before), money(x.after), signed(x.change)]))) : '') +
          '<p class="meta">覆盖整个结账期间，不能视为单件事的盈亏。身家还包含房产、股份和债务的估值变化。</p></details></li>';
      };
      return deathView() + lifeSum() + '<p class="meta">按实际发生日期记录。展开每期财务快照可看同期主要进出；历史未记录的离职原因不会事后补写。</p>' + `<ol class="sb-life">${ev.map((e) => {
        const y = Math.floor(e.day / 360) + 1;
        const head = y !== year ? `<li class="yr">第 ${y} 年</li>` : '';
        year = y;
        const context = period !== e.periodStart ? ctx(e) : '';
        period = e.periodStart;
        return `${head}${context}<li><time>${Math.floor((e.day % 360) / 30) + 1}月${e.day % 30 + 1}日</time><span class="tag k-${esc(e.kind)}">${esc(e.kind)}</span><p>${linkify(e.text)}</p></li>`;
      }).join('')}</ol>`;
    };
    const financeKey = `personFinance:${id}`;
    const financeHead = `<div class="sb-person-total"><span>身家 <b>${money(a.worth)}</b></span><span>现金与存款 <b>${money(b.cash + b.deposit)}</b></span></div>` +
      `<details class="sb-person-finance" data-key="${financeKey}"${opened.has(financeKey) ? ' open' : ''}><summary>资产与${esc(lastM)}收支</summary>${kpi}</details>`;
    return [head + financeHead, [
      ['life', '生活', () => deathEntry + summary() + houseSec() + todaySec() + moneySec() + bodySec() + peopleSec() +
        sec('性子(打法)', bars([{ label: '胆子', value: a.traits.risk }, { label: '节俭', value: a.traits.thrift }, { label: '耐心', value: a.traits.patience }, { label: '跟风', value: a.traits.herd }].map((x) => ({ ...x })), (v) => `${Math.round(v * 100)}`) +
          `<p class="meta">${a.traits.risk > 0.6 ? '敢借钱、敢开公司;' : '不爱冒险;'}${a.traits.thrift > 0.6 ? '存钱多、花得少;' : '花钱大方;'}${a.traits.herd > 0.6 ? '房价涨了就追。' : '不跟风。'}</p>`)],
      ['day', '一日安排', () => personDay(a)],
      ['employment', '任职与工资', () => employmentHTML(a.employment, opened)],
      ...(E.life2 && a.here ? [['actions', '日常与行动', () => residentActions(a, id)] as Tab] : []),
      ['family', '家庭', () => personFamily(a)],
      ['medical', '疾病与病史', () => deathEntry + patientPage(a.medical, id, a.here)],
      ['medicalCases', '病程案卷', () => deathEntry + medicalCaseFilesHTML(a.medical, { personId: id, here: a.here, opened }, { hospital: f => facilityLink(f, 'hospital'), insurer: who })],
      ...(died ? [['death', '死亡记录', () => deathView() + '<p><button data-sub="i:person|medical">返回疾病与病史</button></p>'] as Tab] : []),
      ['education', '学籍与培训', () => studentPage(a.education, id, a.here === true)],
      ['bio', `${died ? '人物故事与死亡' : '近期大事记'} ${(a.life ?? []).length}`, bioView],
      ['history', '完整历史与月账', () => historyView('person', id)],
      ['think', '在想什么', () => mind(a.mind)],
      ['audit', '账本与口径', () => residentAudit(a)],
      ['money', '收支', () => {
        // 身家这个月怎么变的:攒下的(收入 − 花销,加搬家汇款、其他)和房子股票涨跌
        const w: number[] = a.book.worth ?? [];
        const net = (g: string) => groupSum(a.book, g, true) - groupSum(a.book, g, false);
        const saved = inc - spend + net('搬家和汇款') + net('其他') + (a.book.reconLast?.other ?? 0);
        const change = w.length >= 2 ? sec(gone ? `身家${lastM}怎么变的` : '身家这个月怎么变的', row('月初 → 月末', `${money(w[w.length - 2])} → ${money(w[w.length - 1])}`) +
          row('攒下的', signed(saved)) + row('资产、债务与估值等其余变动', signed(w[w.length - 1] - w[w.length - 2] - saved))) : '';
        // 自己的公司:按股份算挣了多少(留在公司里的不是这个人的现钱;分到手的分红算在收入里)
        const own = a.firms.filter((f: J) => f.pct > 0 && f.profit);
        const biz = own.length ? sec('名下公司上个月挣的(按股份)', table(['公司', '股份', '公司一天利润', '这一份一个月'], own.map((f: J) => [who(f.firm), `${f.pct.toFixed(1)}%`, money(f.profit), money(f.profit * 30 * f.pct / 100)]))) : '';
        const stop = gone ? `<p class="meta">账记到${endM}</p>` : '';
        return stop + `<div class="sb-2">${groupDonut(a.book, '收入', true, `${lastM}收入`)}${groupDonut(a.book, '花销', false, `${lastM}花销`)}</div>` + change + biz + statement(a.book, `p${id}`);
      }],
      ['assets', '身家', () => donut([{ label: '现金', value: b.cash }, { label: '存款', value: b.deposit }, { label: '房产', value: b.houses }, { label: '别家的股份', value: b.shares }, { label: '自己管的公司', value: b.business }, { label: '空地', value: b.land }, { label: '在盖的楼', value: b.wip ?? 0 }, { label: '期房', value: b.presale }], money, '资产') +
        (a.depositBank ? row('存款存在', who(a.depositBank)) : '') +
        row('资产合计', money(assets)) + row('欠银行', b.debt ? `<span class="bad">${money(b.debt)}</span>` : '0') + row('身家', money(assets - b.debt)) +
        sec('身家(每月月末)', history(a.book.worth, '身家', money, `pw${id}`, a.gone?.booksEnd)) + originView(a.origin, a.worth)],
      ...(a.firms.length || a.portfolio?.length ? [['firms', `公司 ${a.firms.length}`, () => (a.firms.length
        ? table(['公司', '行业', '身份', '股份', '市值', '近一年分到', '员工', '公司年净利润'], a.firms.map((f: J) => [who(f.firm), esc(f.line), [f.boss ? '总经理' : '', f.control ? '控股' : f.pct > 0 ? '股东' : ''].filter(Boolean).join(' · '), f.pct ? `${f.pct.toFixed(1)}%${playMode && meNow?.id === id ? ` <a data-stk="firm:${f.firm.id}">股市</a>` : ''}` : '', f.value ? money(f.value) : '', f.div ? money(f.div) : '', String(f.staff), signed(f.profit)]))
        : '<div class="sb-empty">没有</div>') + (a.portfolio?.length ? sec('持股账', stakeTable(a.portfolio)) : '')] as Tab] : []),
      ...(a.houses.length || a.presales.length ? [['houses', `房 ${a.houses.length}`, () => table(['楼', '面积', '市价', '买的时候', '租给', '租金/天'], a.houses.map((h: J) => [(h.id != null ? unitLink(h.id, `${h.building} · #${h.id}`) : plotLink(h.plot, h.building)) + (h.office ? ' <span class="tag">写字楼</span>' : h.works ? ' <span class="tag">铺面</span>' : ''), `${Math.round(h.area)}㎡`, money(h.value), h.bought ? money(h.bought) : '', h.self ? '自住' : h.tenant ? who(h.tenant) : '<span class="meta">空着</span>', h.self ? '' : h.rent.toFixed(0) + (h.forSale ? ' · 挂牌' : '') + (h.loan ? ' · 按揭' : '')])) +
        (a.presales.length ? sec('期房', table(['期房', '价', '哪天买的', '楼盖到', ''], a.presales.map((p: J) => [plotLink(p.plot, p.name), money(p.price), dateOf(p.day), esc(projectProgressText(p)), p.stalled ? '<span class="bad">停工</span>' : '']))) : '')] as Tab] : []),
      ...((a.cover ?? []).length ? [['cover', `保险 ${a.cover.length}`, () => coverTable(a.cover)] as Tab] : []),
      ...(a.loans.length ? [['loans', `贷款 ${a.loans.length}`, () => table(['种类', '押的', '还欠', '年息', '每天还', '拖了'], a.loans.map((l: J) => [esc(l.kind), plotLink(l.plot, l.what), money(l.principal), `${l.rate.toFixed(1)}%`, money(l.pay), l.missed ? `<span class="bad">${l.missed} 天</span>` : '']))] as Tab] : []),
      ...(a.cases?.length ? [['cases', `案子 ${a.cases.length}`, () => table(['哪天', '案子', '身份', '对方', '结果'],
        a.cases.map((x: J) => [monthOf(x.day), caseLink(x.id, x.kind), esc(x.role), x.other ? who(x.other) : '', `${stageTag(x.stage)}${x.verdict ? ` <small class="meta">${esc(x.verdict)}</small>` : ''}`]))] as Tab] : []),
    ]];
  };

  /** 公司的财报:几个比率、利润表(四列)、眼下的资产负债、公司的钱怎么来的、每个月的曲线。 */
  const finView = (x: J, id: number) => {
    const r = x.ratios;
    const pctOr = (v: number | null) => (v == null ? '—' : `${v.toFixed(1)}%`);
    const n = r.months;
    const monthScope = n > 0 ? `近 ${n} 个已结账月` : '月结';
    const tiles = grid(
      tile(`${monthScope}收入`, n > 0 ? money(r.revenue) : '—', '与公司列表、月度曲线同口径'), tile(`${monthScope}经营净利润`, n > 0 ? signed(r.profit) : '—'),
      tile('经营毛利率', pctOr(r.gross), r.grossNote ?? '收入减进货、原料、建材、运费'), tile('经营净利率', pctOr(r.net), '月结经营净利润 ÷ 月结收入'),
      tile('年化回报', pctOr(r.roe), '净利润 ÷ 平均净资产', r.roe != null && r.roe < 0 ? 'bad' : ''), tile('负债率', pctOr(r.leverage), '负债合计 ÷ 资产'),
    );
    const p = x.pnl;
    const col = (list: J[], c: number) => list.reduce((s: number, l: J) => s + l.v[c], 0);
    const cs = [0, 1, 2, 3];
    const net = cs.map((c) => col(p.income, c) - col(p.expense, c));
    const pnl = table(['经营现金明细', '这个月到今天', '上个月', `近 ${p.months ?? n} 个现金结账月`, '开业以来'], [
      ...p.income.map((l: J) => [esc(l.name), ...l.v.map((v: number) => money(v))]),
      ['<b>现金流入合计</b>', ...cs.map((c) => `<b>${money(col(p.income, c))}</b>`)],
      ...p.expense.map((l: J) => [esc(l.name), ...l.v.map((v: number) => money(-v))]),
      ['<b>经营现金净流入</b>', ...net.map((v) => `<b>${signed(v)}</b>`)],
    ]);
    const b = x.balance;
    const bal = table(['资产负债表(眼下)', ''], [
      ...([['现金', b.cash], ['建材存货', b.materials], ['商品存货', b.goods], ['设备', b.kit], ['空地', b.land], ['在盖的楼', b.wip], ['名下的房', b.units], ['别家公司的股份', b.holdings]] as [string, number][])
        .filter(([, v]) => v).map(([k, v]) => [k, money(v)]),
      ['<b>资产合计</b>', `<b>${money(b.assets)}</b>`], ['欠银行', b.debt ? `<span class="bad">${money(-b.debt)}</span>` : '0'],
      ...((b.presale ?? 0) !== 0 ? [['预收房款', `<span class="bad">${money(-b.presale)}</span>`]] : []),
      ['<b>负债合计</b>', `<b>${money(b.liabilities ?? b.debt)}</b>`],
      ['<b>净资产</b>', `<b>${signed(b.equity)}</b>`],
    ]);
    const o = x.origin;
    const parts: [string, number][] = [['开业时就有的', o.start], ['股东投进来的', o.owners], ['经营收支相抵', o.operating], ['分给股东的', o.dividends],
      ['向银行借的', o.borrowed], ['还给银行的', o.repaid], ['买卖资产(设备、地、房、股份)', o.invest], ['其他', o.other]];
    const total = parts.reduce((s, [, v]) => s + v, 0);
    // 公司的钱怎么来的(开业以来):开业带的起,一项项加减到现在账上的现金
    const origin = sec('公司的钱怎么来的(开业以来)', waterfall(parts.map(([k, v]) => ({ label: k, value: v })), money, '现在账上的现金') +
      (Math.abs(total - o.cash) >= 1 ? `<p class="bad">和账上差 ${money(total - o.cash)}</p>` : ''));
    // 近几个月的收入一项项扣到净利润
    const flow = sec(`近 ${p.months ?? n} 个现金结账月:经营现金收支`, waterfall([
      ...p.income.map((l: J) => ({ label: l.name, value: l.v[2] })),
      ...p.expense.map((l: J) => ({ label: l.name, value: -l.v[2] })),
    ], money, '经营现金净流入'), '折旧不走现金;它已计入月结经营净利润。代收交易税及投资、筹资等另列在现金流页。');
    const bridge = x.reconciliation;
    const reportBridge = !bridge ? '' : sec('经营现金与月结利润为什么不同', bridge.comparable && n > 0
      ? table(['同一结账窗口', '金额'], [
          ['经营现金净流入', signed(bridge.cashNet)],
          ['收入确认差异', signed(bridge.revenueDifference)],
          ['成本归集差异', signed(-bridge.expenseDifference)],
          ['设备折旧', signed(-bridge.wear)],
          ['月报精度与显示舍入差', signed(bridge.rounding)],
          ['<b>月结经营净利润</b>', `<b>${signed(bridge.profit)}</b>`],
        ])
      : `<p class="meta">${n === 0 ? '尚无已结账月报。' : `月报 ${n} 个月、现金分类 ${bridge.cashMonths} 个月,窗口不同,不强行对账。`}</p>`, bridge.note);
    const m: number[][] = x.months;
    const xs = monthsX(m.length, x.monthsEnd ?? undefined);
    const charts = m.length >= 2
      ? sec('每个月的收入和净利润', lineChart(xs, [{ name: '收入', vals: m.map((v) => v[0]), fmt: money }, { name: '净利润', vals: m.map((v) => v[1]), fmt: money }], { fmt: money, zero: true, key: `ff${id}` })) +
        sec('每个月末的净资产和欠银行', lineChart(xs, [{ name: '净资产', vals: m.map((v) => v[2]), fmt: money }, { name: '欠银行', vals: m.map((v) => v[3]), fmt: money }], { fmt: money, zero: true, key: `fb${id}` }))
      : '';
    return tiles + reportBridge + flow + pnl + bal + origin + charts;
  };

  const expansionPage = (e: J): string => {
    const daily = (v: number) => `${num(v, 1)} ${esc(e.unit)}/天`;
    return grid(tile('眼下主要限制', esc(e.limit), esc(e.next)), tile('日均实际产出', daily(e.output)),
      tile('现有设备产能', daily(e.equipped), '沿当前人员、手艺与场地推算'), tile('设备配齐的产能', daily(e.full), '人员、场地不变'),
      tile(e.salesIsProvision ? '日均服务提供量记录' : '近期日均售出', daily(e.sales), e.salesIsProvision ? '逐企提供量；成交折损已计入收益报价' : ''), tile('预期可售上限', daily(e.demand), '实售与公开买单，仍需竞争成交')) +
      sec('先解决什么', row('下一步', esc(e.next)) + row('人员与场地', `${e.workers} 人（含总经理） · ${e.places == null ? '无固定工位上限' : `${e.places} 个工位`}`) +
        row('设备', `够 ${num(e.kitWorkers, 2)} 人用 · 配齐现有人员还差 ${num(e.missingSets, 2)} 套`) +
        row('产能利用', `${num(e.utilization, 1)}%（日均实际产出 / 配齐设备产能）`) + row('现有库存', `${num(e.inventory, 1)} ${esc(e.unit)}`) +
        (e.rawCapacity == null ? '' : row('进货或原料支持', `${daily(e.rawCapacity)} · 按现货、已观察补货及可用进货款估算，尚未购入`))) +
      sec('设备采购的账', row('值得添的设备', `${e.cost > 0 && e.buySets === 0 ? '&lt;0.01' : num(e.buySets, 2)} 套 · 含税目标价 ${money(e.cost)}`) +
        row('须先留下', `${money(e.keep)} · 工资、进货或原料周转`) + row('预留后现钱', money(e.available)) +
        row('本次设备可贷', `${money(e.credit)} · 最终仍须银行实际放款`) +
        row('每日增量毛利估计', `${money(e.gain)} · 尚未扣新增设备资金成本`) + row('每日利息与折旧估计', money(e.carrying))) +
      '<p class="meta">与企业自动采购共用同一套判断。以上是当前估计；实购受现金、贷款、税价及市场供货约束，成交后才安装设备。添设备不会直接增加人员或创造订单。</p>';
  };

  let equityGeneration = -1;
  const equityStates = new Map<number, EquityHistoryState>();
  const equityState = (firm: number): EquityHistoryState => {
    if (equityGeneration !== gen) { equityStates.clear(); equityGeneration = gen; }
    let state = equityStates.get(firm);
    if (!state) { state = newEquityHistoryState(); equityStates.set(firm, state); }
    return state;
  };
  const ownershipSection = (firm: number, companyTotals: { day: number; marketValue: number | null; netAssets: number | null }[]): string => {
    if (!E.ownership_owners || !E.ownership) return '<div class="meta">此引擎未记录股东历史</div>';
    const state = equityState(firm);
    let directory = null, loading = false;
    try { directory = acceptEquityOwners(read(E.ownership_owners(firm, state.ownerOffset, EQUITY_OWNER_PAGE))); }
    catch (e) { if (e !== PENDING) throw e; loading = true; }
    if (directory?.firm === firm) rememberEquityOwners(state, directory);
    if (state.selectionMode === 'leaders' && state.ownerOffset !== 0) {
      try {
        const leading = acceptEquityOwners(read(E.ownership_owners(firm, 0, EQUITY_OWNER_PAGE)));
        if (leading?.firm === firm) rememberEquityOwners(state, leading);
      } catch (e) { if (e !== PENDING) throw e; loading = true; }
    }
    const histories = new Map<string, EquityHistoryPage | null>();
    if (state.view === 'rank') {
      state.rankUnsupported = !E.ownership_rank;
      if (E.ownership_rank) {
        const requested = state.timelineDay == null || state.timelineDay === state.rankPage?.currentDay ? EQUITY_RANK_CURRENT : state.timelineDay;
        const metric = EQUITY_METRICS.findIndex(([key]) => key === state.metric);
        const key = E.ownership_rank(firm, requested, metric, state.rankOffset, EQUITY_RANK_PAGE);
        // 日期播放只刷新眼下这一页，不把已播过的全部排行重新排进热查询。
        for (const old of used) if (old.startsWith('ownership_rank:') && old !== key) used.delete(old);
        try {
          const raw = read(key);
          const page = acceptEquityRank(raw, firm, requested, metric, state.rankOffset);
          if (page) {
            rememberEquityRank(state, page);
          } else if (raw?.error || raw?.missing) state.rankUnsupported = true;
        } catch (e) { if (e !== PENDING) throw e; loading = true; }
      }
    }
    for (const [key, owner] of state.view === 'rank' || state.view === 'company' ? [] : state.selected) {
      try {
        const page = acceptEquityHistory(read(E.ownership(firm, owner.kind, owner.id, state.historyOffset, EQUITY_HISTORY_PAGE)), firm, owner);
        if (page) rememberEquityHistory(state, key, page);
        histories.set(key, page);
      }
      catch (e) { if (e !== PENDING) throw e; histories.set(key, null); loading = true; }
    }
    return equityHistoryHTML(firm, state, directory, histories, loading, companyTotals);
  };
  const firmView = (id: number, f: J = read(E.firm2(id))): [string, Tab[]] => {
    locate = f.plot ?? null;
    const b = f.balance;
    const assets = b.cash + b.stock + b.inventory + b.land + b.wip + b.units + b.holdings + (b.kit ?? 0);
    const dev = f.kind === 'developer';
    const lastIn = groupSum(f.book, '经营', true);
    const lastOut = groupSum(f.book, '经营', false);
    const hq = f.hq;
    const head = `<div class="sb-who">${badge(f.name, f.kind, 42)}<div><h3>${esc(f.name)}</h3><p class="meta">${kindPill(f.kind, f.line)} · ${dev && !f.office ? '登记地址' : '总部'} ${plotLink(f.plot, hq.addr || `(${f.at[0]}, ${f.at[1]})`)}(${esc(hq.kind)}) · ${yearOf(f.opened)}成立${f.closed ? ` · <span class="bad">${yearOf(f.closedOn)}关门</span>` : ''}${f.trouble ? ' · <span class="bad">贷款逾期</span>' : ''}${f.exposed != null ? ` · <span class="bad">${monthOf(f.exposed)}被曝光</span>` : ''}${f.ad ? ` · 在登广告(到 ${dateOf(f.ad.until)})` : ''}</p></div></div>`;
    const workSpace = () => !hq.workforce ? '' : sec('员工在哪里办公',
      grid(tile('员工', `${hq.workforce.employees} 人`, '另预留 1 个管理席,不代表老板实际出勤'),
        tile('独立办公席位', `${hq.workforce.capacity} 席`, hq.workforce.unallocated ? `还有 ${hq.workforce.unallocated} 席未落实` : '已覆盖登记团队', hq.workforce.unallocated ? 'bad' : ''),
        tile('办公房租预算', `一天 ${money(hq.workforce.dailyBudget)}`, '经营收益与可动用现金共同约束')) +
      (f.office ? row('办公房间', unitLink(f.office.unit, f.office.building)) + row('面积', `${Math.round(f.office.area)}㎡`) : '<p class="meta">尚未登记独立办公室;旧资料没有逐人的实际办公房间,不能据此认定所有员工都在老板家。</p>') +
      `<p class="meta">项目 ${hq.workforce.roles[0]} · 销售 ${hq.workforce.roles[1]} · 物业 ${hq.workforce.roles[2]} · 财务 ${hq.workforce.roles[3]} 为当前业务所需岗位,尚未逐人登记专业分工。名下楼房是资产,只有登记为公司自用的房间才计办公席位。</p>`);
    const bk = f.bankInfo;
    // 估值一句话(和引擎 equity.rs 一个算法,拆开的几项在「股价」页,见 stocks.rs 的 valuation)
    const val = f.valuation;
    const valHow = !val || val.kind === 'bank' ? '' : `${val.kind === 'dev' ? '净资产' : '净资产 + 年利润估计 ÷ 要的回报'}${Math.abs(val.hold) >= 1 ? ' + 持有的股份' : ''}`;
    const valLink = val ? ' data-sub="i:firm|stock"' : '';
    const kpi = bk ? grid(
      tile('银行的家底', money(bk.equity), '全部资产 − 存款 − 央行本息', bk.equity < 0 ? 'bad' : ''), tile('柜上现金', money(bk.cash)), tile('存款', money(bk.deposits)), tile('贷出', money(bk.lent)),
      tile('欠央行本息', money(bk.central + (bk.centralInterest ?? 0)), '', bk.central > 0 || bk.centralInterest > 0 ? 'bad' : ''), tile('员工', `${f.staff.length} 人`, `按客户要 ${bk.desks} 张桌子`), tile('近一年分红', money(f.paidYear)),
    ) : grid(
      tile('月结收入', f.fin.ratios.months ? money(f.fin.ratios.revenue) : '—', `近 ${f.fin.ratios.months} 个已结账月 · 与公司列表同口径`), tile('月结经营净利润', f.fin.ratios.months ? signed(f.fin.ratios.profit) : '—', '月结收入 − 归集支出 − 设备折旧'),
      tile('净资产', signed(f.fin.balance.equity), '资产 − 负债合计'), tile('现金', money(b.cash)),
      tile('欠银行', money(b.debt), `${f.loans.length} 笔`, b.debt ? 'bad' : ''),
      ...((b.presale ?? 0) !== 0 ? [tile('预收房款', money(b.presale), '尚未交房')] : []), (f.marketPx != null
        ? tile('市值', money(f.marketPx * f.totalShares), `一股 ${f.marketPx.toFixed(2)} 元 · ${dateOf(f.marketDay)}成交 · 估值 ${money(f.value)}${val ? ' · 怎么算 →' : ''}`, '', valLink)
        : tile('估值', money(f.value), `一股 ${f.sharePrice.toFixed(2)} 元 · ${valHow}${val ? ' · 怎么算 →' : ''}`, '', valLink)),
      tile('人手', `${f.closed ? 0 : f.staff.length + (f.bossInTeam ? 1 : 0)} 人`, !f.bossInTeam || f.closed ? '' : '含老板'), tile('近一年分红', money(f.paidYear)),
    );
    // 投资公司:顶上看投出去多少、值多少、赚了多少;第一页是持仓(见 dash.rs 的 fund_json)
    const fu = f.fund;
    const fundKpi = fu ? grid(
      tile('股东投进来', money(fu.raised), `${fu.investors} 个股东`), tile('投出去', money(fu.invested), `${fu.live} 家还开着 · 一共 ${fu.list.length} 家`),
      tile('持仓估值', money(fu.value)), tile('估值与回款差额', signed(fu.gain), '估值 + 分到的红 + 清算拿回 − 成本；含未实现变化', fu.gain < 0 ? 'bad' : 'good'),
      tile('收到的分红', money(fu.divs), fu.back ? `清算拿回 ${money(fu.back)}` : ''), tile('账上现金', money(fu.cash)),
      tile('报价净值', money(fu.nav), `一股 ${fu.perShare.toFixed(2)} 元；无自动赎回`), tile('实际分给股东', money(fu.paid), `近一年派息 / 当前净值 ${fu.yield.toFixed(1)}%；不等同经营收益`),
    ) : '';
    const fundTabs: Tab[] = fu ? [
      ...(f.investmentDisclosure ? [['fundraising', '募集与投资', () => investmentHTML(f.investmentDisclosure, { firm: (id: number, name: string) => who({ kind: 'firm', id, name }) }) +
        `<div class="sb-action-buttons"><button data-export="firm:${id}:txt">下载募集披露与完整公司原始资料（TXT）</button></div>`] as Tab] : []),
      ['fund', `投资 ${fu.live}`, () => (fu.list.length
        ? donut(fu.list.filter((x: J) => x.value > 0).slice(0, 8).map((x: J) => ({ label: x.firm.name, value: x.value })), money, '持仓市值') + stakeTable(fu.list)
        : '<div class="sb-empty">还没投出去</div>')],
      ['deals', `入股记录 ${fu.deals.length}`, () => (fu.deals.length
        ? table(['哪天', '买了', '多少钱', '每股', '怎么买'], fu.deals.map((x: J) => [dateOf(x.day), who(x.firm), money(x.amt), `${x.price.toFixed(2)} 元`, esc(x.how)]))
        : '<div class="sb-empty">还没买过</div>')],
    ] : [];
    const bossSec = f.boss.who
      ? row('总经理', `${who(f.boss.who)} · ${f.boss.age} 岁 · 手艺 ${f.boss.skill.toFixed(2)} · 持股 ${f.boss.own.toFixed(1)}% · ${f.boss.pay ? `工资一天 ${Math.round(f.boss.pay)} 元` : '自己的公司,拿分红'}`)
      : row(f.clinic?.public ? '运营责任方' : '总经理', who(f.boss));
    const g = f.govern;
    // 公司治理:脾气、股东要的回报和公司挣的、最近一次留钱扩张的决定(见 governance.rs)
    const governSec = () => g.set
      ? sec('怎么分钱', row('脾气', `扩张心 ${Math.round(g.ambition * 100)} · 谨慎 ${Math.round(g.prudence * 100)}`) +
          row('一年挣本钱的', `<span class="${g.roe < g.hurdle ? 'bad' : 'good'}">${g.roe.toFixed(1)}%</span> · 股东平均要 ${g.hurdle.toFixed(1)}%`) +
          (g.plan ? row('最近的决定', esc(g.plan)) : '') +
          (g.overruled ? row('', `<span class="bad">连着 ${g.overruled} 个月想留钱扩张被股东否了</span>`) : '') +
          (g.lagging >= 6 ? row('', `<span class="bad">连着 ${g.lagging} 个月回报够不上股东要的</span>`) : ''))
      : '';
    const holdersSec = () => {
      // 老的引擎(wasm 还没重编)没有这两项:按没有处理
      const hsum = f.holderSum ?? { n: 0, kinds: [], yesPct: 0, restN: 0, restPct: 0, restValue: 0 };
      const d = f.dividends ?? { year: f.paidYear ?? 0, life: 0, months: [] };
      // 股价、K 线在股市页(玩家模式还能买卖)
      const stkLink = f.holders?.length && f.shareInfo ? ` · <a data-stk="firm:${id}">股市里看股价${playMode ? '、买卖' : ''}</a>` : '';
      const head = hsum.n ? `<p class="meta">股东 ${hsum.n} 个:${hsum.kinds.map((k: J) => `${k.name} ${k.n} 个占 ${k.pct.toFixed(1)}%`).join(',')} · 按股份 ${hsum.yesPct.toFixed(0)}% 赞成眼下的扩张${stkLink}</p>` : stkLink ? `<p class="meta">${stkLink.slice(3)}</p>` : '';
      const rows = f.holders.map((h: J, i: number) => [who(h.who) + (i === 0 ? ' <span class="tag">控股</span>' : ''), num(h.shares), `${h.pct.toFixed(2)}%`, money(h.value), h.cost != null ? money(h.cost) : '', h.divs ? money(h.divs) : '', money(d.year * h.pct / 100), h.hurdle != null ? `${h.hurdle.toFixed(1)}%` : '', h.yes ? '赞成' : '<span class="bad">不赞成</span>']);
      if (hsum.restN) rows.push([`其他 ${hsum.restN} 个股东`, '', `${hsum.restPct.toFixed(2)}%`, money(hsum.restValue), '', '', money(d.year * hsum.restPct / 100), '', '']);
      const pie = f.holders.length > 1 ? donut(f.holders.slice(0, 8).map((h: J) => ({ label: h.who.name, value: h.pct })).concat(hsum.n > 8 ? [{ label: '其他', value: 100 - f.holders.slice(0, 8).reduce((s: number, h: J) => s + h.pct, 0) }] : []), (v) => `${v.toFixed(1)}%`, '股份') : '';
      const divs = sec('分红', row('近一年', money(d.year)) + row('开业以来', money(d.life)) + (d.months.length >= 2 ? lineChart(monthsX(d.months.length), [{ name: '每月分红', vals: d.months, fmt: money }], { fmt: money, zero: true, key: `fd${id}`, h: 110 }) : ''));
      const xf = (f.xfers ?? []) as J[];
      const xfers = xf.length ? sec('股份转让(近一年)', table(['哪天', '卖家', '买家', '占', '每股', '多少钱'], xf.map((x) => [dateOf(x.day), who(x.seller), who(x.buyer), `${x.pct.toFixed(2)}%`, `${x.px.toFixed(2)} 元`, money(x.amt)]))) : '';
      const companyTotals = (f.fin?.assets ?? []).map((a: number[]) => ({ day: a[0], marketValue: a[8] ?? null,
        netAssets: a[1] + a[2] + a[3] + a[4] + a[5] + a[6] - a[7] - (a[9] ?? 0) }));
      return sec('股东历史', ownershipSection(id, companyTotals)) + head + table(['股东', '股', '占', '市值', '投了', '一共分到', '近一年分到', '要的回报', '扩张'], rows) + pie + xfers + divs;
    };
    const proj = (list: J[], asBuilder: boolean) =>
      table(asBuilder ? ['工程', '甲方', '位置', '阶段', '进度', '合同', '监管账户', '工钱已付', '料款 已付/进场', ''] : ['楼', '位置', '楼型', '层', '套', '期房', '状态', '进度', '监管账户', '开发贷', '建筑公司'],
        list.map((p: J) => asBuilder
          ? [projLink(p.id, p.name), who(p.investor), plotLink(p.plot, `(${p.at[0]}, ${p.at[1]})`), esc(projectProgressStage(p)), esc(projectProgressPercent(p.progress)), money(p.price), money(p.escrow), money(p.labPaid), `${money(p.matPaid)} / ${money(p.matGot)}`, p.waiting.length ? `<span class="bad">停工</span>` : '']
          : [projLink(p.id, p.name), plotLink(p.plot, `(${p.at[0]}, ${p.at[1]})`), esc(p.type), `${p.done}/${p.floors}`, String(p.units), p.presold ? `${p.presold} 套 · ${money(p.presoldAmt)}` : '', p.status === '停工' ? `<span class="bad">${esc(projectProgressStage(p))} · 停工 ${p.stalledDays ?? ''} 天</span>` : esc(projectProgressStage(p)), esc(projectProgressPercent(p.progress)), p.status === '完工' ? '' : money(p.escrow), p.loan ? money(p.loan) : '', p.builder ? who(p.builder) : '']));
    const trades = Object.entries(f.byTrade as Record<string, number>);
    // 资产每个月怎么变(fin.assets 和财报的月账一一对上:结账日、现金、存货、设备、地和房、在盖的楼、持有别家、欠银行、那天的市值)
    const assetMonths = (): string => {
      const am: number[][] = f.fin?.assets ?? [];
      if (am.length < 2) return '';
      const xs = am.map((a) => a[0]);
      const parts = ([['现金', 1], ['存货', 2], ['设备', 3], ['地和房', 4], ['在盖的楼', 5], ['持有别家', 6]] as [string, number][]).filter(([, k]) => am.some((a) => Math.abs(a[k]) >= 1));
      const eq = (a: number[]) => a[1] + a[2] + a[3] + a[4] + a[5] + a[6] - a[7] - (a[9] ?? 0);
      const rows = am.map((a, i) => [dateOf(a[0]), ...parts.map(([, k]) => money(a[k])), a[7] ? `<span class="bad">${money(a[7])}</span>` : '0', money(a[9] ?? 0), money(eq(a)), i > 0 ? signed(eq(a) - eq(am[i - 1])) : '']).reverse();
      const head = ['结账日', ...parts.map(([t]) => t), '欠银行', '预收房款', '净资产', '比上月'];
      const more = rows.length > 12 ? `<details data-key="fam:${id}"${opened.has(`fam:${id}`) ? ' open' : ''}><summary>更早的 ${rows.length - 12} 个月</summary>${table(head, rows.slice(12))}</details>` : '';
      const total = (a: number[]) => a[1] + a[2] + a[3] + a[4] + a[5] + a[6];
      const tipMore = (i: number): [string, string][] => [['资产合计', money(total(am[i]))], ['欠银行', money(am[i][7])], ['预收房款', money(am[i][9] ?? 0)], ['净资产', money(eq(am[i]))],
        ...(am[i][8] != null ? [['市值', money(am[i][8])] as [string, string]] : [])];
      return sec('每个月末的资产', areaChart(xs, parts.map(([name, k]) => ({ name, vals: am.map((a) => a[k]) })), { fmt: money, key: `fa${id}`, rows: tipMore })) +
        sec('每个月末的净资产和负债', lineChart(xs, [{ name: '净资产', vals: am.map(eq), fmt: money }, { name: '欠银行', vals: am.map((a) => a[7]), fmt: money }, { name: '预收房款', vals: am.map((a) => a[9] ?? 0), fmt: money }], { fmt: money, zero: true, key: `fe${id}`, h: 130,
          rows: (i) => [['资产合计', money(total(am[i]))]] })) +
        sec('资产一项项', table(head, rows.slice(0, 12)) + more);
    };
    // 股价:眼下的价、每月的市值和净资产、近一年每天的股价、估值怎么算、最近的买卖(见 stocks.rs 的 firm_stock、valuation)
    const stockTab = (): string => {
      const si = f.shareInfo;
      const am: number[][] = f.fin?.assets ?? [];
      const tiles = si ? grid(
        tile('每股', `${si.px.toFixed(2)} 元`, !si.listed ? `${dateOf(si.at)}的收盘` : si.traded != null ? `${dateOf(si.lastDay)}成交` : '近三个月没成交,按估值'),
        tile('市值', money(si.cap), `${num(si.shares)} 股`),
        tile('市盈率', si.pe == null ? '—' : si.pe.toFixed(1), `一年利润 ${money(si.profit)}`),
        tile('市净率', si.pb == null ? '—' : si.pb.toFixed(2), `净资产 ${money(si.book)}`),
        tile('分红率', pct(si.yield, 1), `一年分红 ${money(si.div)}`),
        tile('三十天', si.d30 == null ? '—' : `${si.d30 > 0 ? '+' : ''}${(si.d30 * 100).toFixed(1)}%`, si.listed ? '' : '已不能转让', si.d30 < 0 ? 'bad' : '')) : '';
      const eq = (a: number[]) => a[1] + a[2] + a[3] + a[4] + a[5] + a[6] - a[7] - (a[9] ?? 0);
      const capChart = am.length >= 2 && am.some((a) => a[8] != null)
        ? sec('每个月末的市值和净资产', lineChart(am.map((a) => a[0]), [{ name: '市值', vals: am.map((a) => a[8] ?? NaN), fmt: money }, { name: '净资产', vals: am.map(eq), fmt: money }], { fmt: money, zero: true, key: `fc${id}`,
          rows: (i) => [['市净率', am[i][8] != null && eq(am[i]) > 0 ? (am[i][8]! / eq(am[i])).toFixed(2) : '—'], ['欠银行', money(am[i][7])]], range: true })) : '';
      // 每天:[哪天, 收盘, 有没有成交, 收盘按最近的成交价, 每股估值, 市值, 成交额]
      const days: [number, number, boolean, number, number, number | null, number][] = si?.days ?? [];
      const pxFmt = (v: number) => `${v.toFixed(v < 100 ? 2 : 0)} 元`;
      const dayRows = (i: number): [string, string][] => {
        const d = days[i];
        return [[d[2] ? '成交' : '没成交', d[2] ? money(d[6]) : d[3] ? '按最近的成交价' : '按估值'], ...(d[5] != null ? [['市值', money(d[5])] as [string, string]] : []),
          ['估值(每股)', d[4] != null ? pxFmt(d[4]) : '—']];
      };
      const dayChart = days.length >= 2 ? sec('近一年每天的股价', lineChart(days.map((d) => d[0]), [{ name: '每股', vals: days.map((d) => d[1]), fmt: pxFmt }], { fmt: (v) => v.toFixed(v < 10 ? 2 : 0), key: `fp${id}`, h: 130, label: dateOf, rows: dayRows })) : '';
      const vk = `fval:${id}`;
      return tiles + capChart + dayChart + (f.valuation ? sec('估值怎么算', valuationHTML(f.valuation, vk, opened.has(vk))) : '') +
        (si ? sec('最近的买卖', tradesHTML(si.trades)) : '') + (si ? `<p class="meta"><a data-stk="firm:${id}">股市里看 K 线${playMode ? '、买卖' : ''} →</a></p>` : '');
    };
    // 保险公司的产品和理赔;投保的公司的保单(见 dash.rs 的 carrier_json、policy_json)
    const cr = f.carrier;
    const insTabs: Tab[] = [
      ...(cr ? [['carrier', `保险产品 ${(cr.lines as J[]).filter((l) => l.on).length}`, () => grid(tile('保单', String(cr.policies)), tile('一天收保费', money(cr.prem)), tile('一天赔', money(cr.claim), cr.lr != null ? `赔付率 ${cr.lr.toFixed(0)}%` : ''),
        tile('本钱', money(cr.capital), `账上 ${money(cr.cash)} · 股份 ${money(cr.holdings)}`), tile('要的本钱', money(cr.need)), tile('偿付能力', cr.solvency.toFixed(2), '', cr.solvency < 1 ? 'bad' : ''),
        tile('人手', `${cr.staff} 人`, `要 ${cr.desks.toFixed(1)}`)) + carrierLines(cr) + (cr.plan ? `<p class="meta">上个月:${esc(cr.plan)}</p>` : '') +
        ((f.carrierLog as J[]).length ? sec('理赔', claimTable(f.carrierLog)) : '')] as Tab] : []),
      ...((f.cover ?? []).length ? [['cover', `保单 ${f.cover.length}`, () => coverTable(f.cover)] as Tab] : []),
    ];
    const tabs: Tab[] = [
      ...((f.now ?? []).length ? [['now', '现状', () => `<ul class="sb-now">${(f.now as string[]).map((t) => `<li>${linkify(t)}</li>`).join('')}</ul>`] as Tab] : []),
      ...(f.clinic ? [['clinic', '医疗运营', () => hospitalPage(f.clinic)] as Tab] : []),
      ...insTabs,
      ...fundTabs,
      ['who', '谁的', () => sec('创立来源', companyOriginHTML(f.origin, row, who)) + bossSec + governSec() + holdersSec() + (f.holdings.length && !fu ? sec('持有别家公司', table(['公司', '行业', '占', '市值', '年分红(这一份)'], f.holdings.map((h: J) => [who(h.firm), esc(h.line), `${h.pct.toFixed(1)}%`, money(h.value), money(h.div)]))) : '')],
      ['place', `地方 ${1 + f.branches.length + f.buildings.length}`, () =>
        workSpace() + sec(dev && !f.office ? '登记地址' : '总部', row('在', plotLink(f.plot, hq.addr || `(${f.at[0]}, ${f.at[1]})`)) + row('是什么地方', esc(hq.kind)) + row('片区', esc(hq.district)) +
          (f.office || f.premises ? row('租金', f.office ? (f.office.rent ? `一天 ${money(f.office.rent)}` : '自己的,不交') : `一天 ${money(f.premises.rent)}`) : '') +
          (hq.near ? row('周围三格在写字楼上班的', `${Math.round(hq.near)} 人`) : '')) +
        (f.branches.length ? sec(`${f.kind === 'service' || f.kind === 'media' ? '分公司' : '分店'} ${f.branches.length} 家`, table(['在', '片区', '面积', '一天租金', '房东', '开了多久'],
          f.branches.map((b: J) => [plotLink(b.plot, b.addr || `(${b.at[0]}, ${b.at[1]})`), esc(b.district), `${Math.round(b.area)}㎡`, money(b.rent), who(b.landlord), `${Math.floor((city.day - b.since) / 30)} 个月`]))) : '') +
        (f.jobs.length ? sec(`工地 ${f.jobs.length} 个`, table(['工程', '位置', '阶段', '进度'], f.jobs.map((p: J) => [projLink(p.id, p.name), plotLink(p.plot, `(${p.at[0]}, ${p.at[1]})`), esc(projectProgressStage(p)), esc(projectProgressPercent(p.progress))]))) : '') +
        sec(`名下的楼 ${f.buildings.length} 栋`, table(['楼', '位置', '片区', '层', '名下几套', '在用', '一天租金', '市值'],
          f.buildings.map((b: J) => [plotLink(b.plot, b.name || '(这栋楼)'), `(${b.at[0]}, ${b.at[1]})`, esc(b.district), String(b.floors), String(b.units), String(b.used), money(b.rent), money(b.value)]))) +
        (f.unitList.length ? sec(`名下的房 ${f.unitsN} 套`, table(['门牌', '面积', '用途', '谁在用', '一天租金', '市值'],
          f.unitList.map((u: J) => [u.id != null ? unitLink(u.id, u.addr) : plotLink(u.plot, u.addr), `${Math.round(u.area)}㎡`, u.works ? '厂房' : u.office ? '写字楼' : '住宅', u.self ? '<b>自己用</b>' : u.tenant ? who(u.tenant) : '<span class="meta">空着</span>',
            u.rent ? money(u.rent) : '', money(u.value) + (u.forSale ? ' · 挂牌' : '')]))) : '') +
        (f.plots.length ? sec(`手上的空地 ${f.plots.length} 块`, table(['地', '买价', '在盖'], f.plots.map((p: J) => [plotLink(p.plot, `(${p.at[0]}, ${p.at[1]})`), money(p.price), esc(p.project ?? '')]))) : '')],
      ['story', `大事记 ${(f.story ?? []).length}`, () => {
        const ev: J[] = [...(f.story ?? [])].reverse();
        if (!ev.length) return '<div class="sb-empty">还没有记下什么事</div>';
        let year = -1;
        return `<ol class="sb-life">${ev.map((e) => {
          const y = Math.floor(e.day / 360) + 1;
          const head = y !== year ? `<li class="yr">第 ${y} 年</li>` : '';
          year = y;
          return `${head}<li><time>${Math.floor((e.day % 360) / 30) + 1} 月</time><span class="tag k-${esc(e.kind)}">${esc(e.kind)}</span><p>${linkify(e.text)}</p></li>`;
        }).join('')}</ol>`;
      }],
      ['think', '在想什么', () => mind(f.mind)],
      ...(f.paper ? [['paper', '报纸', () => {
        const p = f.paper;
        return grid(tile('路数', p.tabloid >= 0.6 ? '小报' : p.tabloid <= 0.3 ? '正经报' : '中间', `越大越像小报 ${Math.round(p.tabloid * 100)}`),
            tile('信誉', p.trust.toFixed(2), ''), tile('抓得到几成新闻', pct(p.cover), `记者 ${f.staff.length + 1} 人`),
            tile('一天送出、卖出', `${num(p.circ)} 份`, `订户 ${num(p.subs)}`),
            tile('卖到邻城', `${num(p.far ?? 0)} 份`, `一天印得出 ${num(p.print ?? 0)} 份${(p.short ?? 0) >= 1 ? `,少印 ${num(p.short)} 份` : ''}`), tile('一份', money(p.copy)), tile('广告千人价', money(p.cpm), `这个月卖出 ${p.adSold} / ${p.slots} 块`),
            tile('一天进账', money(p.rev), '卖报加广告,三十天平均')) +
          sec('最近的头版', p.stories.length ? `<ol class="sb-news">${p.stories.map((x: J) =>
            `<li><span class="tag k-${esc(x.kind)}">${esc(x.kind)}</span><time>${esc(dateOf(x.day))}</time><p>${linkify(x.text)}</p><small class="meta">报摊 ${x.street} 份 · 订户 ${x.subs}${x.scoop ? ' · 独家' : ''}</small></li>`).join('')}</ol>` : '<div class="sb-empty">还没出过报</div>');
      }] as Tab] : []),
      ...(f.tender ? [['tender', '投标', () => {
        const t = f.tender;
        return grid(tile('交付的工程', `${t.built} 个`), tile('品级平均', t.grade != null ? `${t.grade.toFixed(2)} / 4` : '还没交付过', 'S 四分、A 三分、B 两分、C 一分'),
            tile('塌过的楼', `${t.collapses} 栋`, '', t.collapses ? 'bad' : ''), tile('政府评标的底分', t.score.toFixed(1), '过往 30 + 人手 10 − 扣分'),
            ...(t.debarred != null ? [tile('不能投政府的标', `到 ${dateOf(t.debarred)}`, '回扣败露', 'bad')] : []),
            ...(t.skimOk != null ? [tile('偷了没出事', `${t.skimOk} 个工程`, ''), tile('隐蔽验收返工', `${t.reworks} 次`)] : [])) +
          (t.jobs?.length ? sec('手上的工程', table(['工程', '做法', '料', '料钱差价', '安全度'], t.jobs.map((j: J) => [projLink(j.id, j.name), esc(j.method), esc(j.tier), signed(j.skim),
            j.safety != null ? (j.safety < 1.2 ? `<span class="bad">${j.safety.toFixed(2)}</span>` : j.safety.toFixed(2)) : '']))) : '');
      }] as Tab] : []),
      ...(bk || f.kind === 'public' ? [] : [['fin', '财报', () => finView(f.fin, id)] as Tab]),
      ['money', '现金流', () => bk
        ? `<p class="meta">${esc(bk.name)}的账 · <a data-go="bank:${bk.id}">打开银行</a></p>` + statement(bk.book, `fb${id}`)
        : `<div class="sb-2">${groupDonut(f.book, '经营', true, '上月经营收入')}${groupDonut(f.book, '经营', false, '上月经营支出')}</div>` + statement(f.book, `f${id}`)],
      ['assets', '资产', () => donut([{ label: '现金', value: b.cash }, { label: '建材存货', value: b.stock }, { label: '商品存货', value: b.inventory }, { label: '地', value: b.land }, { label: '在盖的楼', value: b.wip }, { label: '名下的房', value: b.units }, { label: '持有别家', value: b.holdings }, { label: '设备', value: b.kit ?? 0 }], money, '资产') +
        row('资产合计', money(assets)) + row('欠银行', b.debt ? `<span class="bad">${money(b.debt)}</span>` : '0') + ((b.presale ?? 0) !== 0 ? row('预收房款', money(b.presale)) : '') + row('负债合计', money(b.liabilities ?? b.debt)) + row('净资产(账面)', signed(b.equity ?? (assets - b.debt - (b.presale ?? 0)))) +
        (f.unitsN ? row('名下的房', `${f.unitsN} 套 · 租出 ${f.rented} 套 · 挂牌 ${f.forSale} 套 · 一天租金 ${money(f.rentDay)}`) : '') +
        (f.stock.length ? sec('建材存货', table(['料', '数量', '值'], f.stock.map((s: J) => [esc(s.mat), `${s.qty.toFixed(1)} ${esc(s.unit)}`, money(s.value)]))) : '') +
        (f.loans.length ? sec(`贷款 ${f.loans.length} 笔`, table(['种类', '押的', '还欠', '年息', '每天还', '拖了'], f.loans.map((l: J) => [esc(l.kind), plotLink(l.plot, l.what), money(l.principal), `${l.rate.toFixed(1)}%`, money(l.pay), l.missed ? `<span class="bad">${l.missed} 天</span>` : '']))) : '') +
        assetMonths() + sec('现金(每月月末)', history(f.book.worth, '现金', money, `fw${id}`))],
      ...(f.valuation || f.shareInfo ? [['stock', '股价', stockTab] as Tab] : []),
    ];
    if (f.invested.length) tabs.push(['dev', `开发 ${f.invested.length}`, () => proj(f.invested, false)]);
    if (f.hotel) tabs.push(['hotel', '酒店', () => grid(tile('客房', `${num(f.hotel.rooms)} 间`, `配了设备 ${num(f.hotel.furnished, 1)} 间`, '', ' data-mode="34"'), tile('人手打理得过来', `${num(f.hotel.staffCap, 1)} 间`, '一间一晚要零点三个人工'),
      tile('一天能卖', `${num(f.hotel.can, 1)} 间`, '客房、设备、来上班的人取小的'), tile('一天卖掉', `${num(f.hotel.sold, 1)} 间`, f.hotel.reach != null ? `门口最多分得到 ${num(f.hotel.reach, 1)} 间` : ''),
      tile('要价', `${money(f.hotel.ask)}/晚`, `行情 ${money(f.hotel.market)}(就在跟前的一间)`), tile('客人觉得值', pct(f.hotel.near), '离要办事的地方越远越少'), tile('客房的房租', `${money(f.hotel.rent)}/天`))]);
    if (f.law) tabs.push(['law', '律所', () => grid(tile('律师好坏', f.law.q.toFixed(2)), tile('接过', `${f.law.cases} 场`, `赢了 ${f.law.wins}`), tile('积压', `${Math.round(f.law.busy)} 人天`),
      tile('上月官司收入', money(f.law.month)), tile('一个律师一天值', `${Math.round(f.law.day)} 元`), tile('轻案收费', money(f.law.fee)))]);
    if (f.cases?.length) tabs.push(['cases', `官司 ${f.cases.length}`, () => table(['哪天', '案子', '身份', '对方', '要', '拿到', '到哪一步'],
      f.cases.map((x: J) => [dateOf(x.day), caseLink(x.id, x.kind), esc(x.role), x.other ? who(x.other) : '', x.amount ? money(x.amount) : x.loss ? money(x.loss) : '', x.award ? money(x.award) : '', stageTag(x.stage)]))]);
    // 名声:在城里传的坏事、几成人知道(见 eco 的 buzz.rs)
    if (f.talk?.length) tabs.push(['name', '名声', () =>
      row('坏名声', f.infamy.toFixed(2), f.infamy >= 0.5 ? 'bad' : '') +
      (f.kind === 'builder' && f.distrust > 1.005 ? row('私人发包', `报价要低 ${Math.round((1 - 1 / f.distrust) * 100)}% 才比得过名声好的`) : '') +
      sec('在传的事', table(['哪天', '现在知道', '从报上看到', '事'], f.talk.map((x: J) => [dateOf(x.day), `${Math.round(x.know * 100)}%`, `${Math.round(x.read * 100)}%`, `<span class="tag k-${esc(x.kind)}">${esc(x.kind)}</span> ${linkify(x.text)}`])))]);
    if (f.jobs.length || f.built) tabs.push(['jobs', `承包 ${f.jobs.length}`, () => (f.jobs.length ? sec(`在盖 ${f.jobs.length}`, proj(f.jobs, true)) : '<div class="sb-empty">手上没有工程</div>') +
      (f.past?.length ? sec(`盖完 ${f.built} 栋${f.built > f.past.length ? `(最近 ${f.past.length} 栋)` : ''}`, table(['工程', '甲方', '位置', '楼型', '层', '合同', '盖完'], f.past.map((p: J) => [projLink(p.id, p.name), who(p.investor), plotLink(p.plot, `(${p.at[0]}, ${p.at[1]})`), esc(p.type), String(p.floors), money(p.price), p.finished != null ? dateOf(p.finished) : '']))) : '') + (f.clients.length ? sec('甲方', table(['甲方', '包给它几个'], f.clients.map((c: J) => [who(c.who), String(c.n)]))) : '')]);
    // 建筑公司工地上的人:员工里干了活的、挂着没派上活的(按天计酬的没活不拿钱),近一年的人天和一个干活的人天摊到多少工钱
    const crewRows = (c: J | null | undefined): string => {
      if (!c) return '';
      const [busy, slack, hurt] = c.today as number[];
      const idle = c.byDay ? '没派上活(按天计酬,不拿钱)' : '没派上活(拿七成待岗工钱)';
      const today = row('工地上的人', `昨天干活 ${busy} 人 · ${idle} ${slack} 人${hurt ? ` · 养伤 ${hurt} 人` : ''}`, slack > busy ? 'bad' : '');
      const [yw, yi] = c.year as number[];
      const year = yw + yi > 0 ? row(`近 ${c.months} 个月`, `干活 ${num(yw, 0)} 人天 · 没派上活 ${num(yi, 0)} 人天(${Math.round(yi * 100 / (yw + yi))}%)· 发工钱 ${money(c.paid)}${c.perDay != null ? `,摊到一个干活的人天 ${Math.round(c.perDay)} 元` : ''}`) : '';
      return today + year;
    };
    // 建筑公司:在盖什么,没活的写上一个盖完多久了
    const building = f.kind === 'builder'
      ? row('在盖', f.jobs.length
        ? f.jobs.map((p: J) => `${projLink(p.id, p.name)} <span class="meta">${p.done}/${p.floors} 层</span>`).join('、')
        : `<span class="meta">手上没有工程${f.past?.[0] ? `,上一个 ${projLink(f.past[0].id, f.past[0].name)} ${dateOf(f.past[0].finished)}盖完` : ''}</span>`)
      : '';
    const biz = building + (f.salePrice ? row('每天做 · 卖', `${f.out.toFixed(1)} · ${f.sold.toFixed(1)}`) + row('存货', f.inv.toFixed(0)) + row('要价 · 市价', `${f.ask.toFixed(1)} · ${f.salePrice.toFixed(1)}`) : '') +
      (f.reach != null ? row('门前人流', `一天 ${num(f.foot ?? 0)} 人 · 分到的客人最多买 ${num(f.reach, 1)} 份`, f.sold >= f.reach * 0.9 ? 'bad' : '') : '') +
      (f.wsStock != null ? row('进的货', `${num(f.wsStock, 0)} 份 · 进价 ${f.wsCost.toFixed(1)} 元`) + (f.selfHaul ? row('没雇到车,自己拉', `${num(f.selfHaul, 0)} 份·程`) : '') : '') +
      (f.kit?.need && !f.closed ? row('设备', `${f.kit.sets.toFixed(1)} 套 · 值 ${money(f.kit.value)} · 一个月用旧 ${money(f.kit.wear)}` + (f.kit.sets < f.kit.need - 0.05 ? ` <span class="bad">${f.kit.need} 个人只够 ${f.kit.sets.toFixed(1)} 套</span>` : '')) : '') +
      (f.hiring ? row('招人', esc(f.hiring)) : '') +
      (f.kind === 'builder' ? row('买料偏好', esc(f.tier)) + row('报价', `成本的 ${f.markup.toFixed(2)} 倍`) : '') + crewRows(f.crew) + row('每天工钱', money(f.wageBill)) + row('总经理日收益', `${Math.round(f.profit)} 元`);
    const place = (f.needsPremises ? sec('铺面', f.premises ? row('在', plotLink(f.premises.plot, f.premises.building)) + row('面积', `${Math.round(f.premises.area)}㎡`) + row('租金', `一天 ${money(f.premises.rent)}`) + row('房东', who(f.premises.landlord)) : '<p class="bad">没有铺面,在摆地摊:最多带一个帮手,产量打七折</p>') : '') +
      (f.office ? sec('写字楼', row('在', plotLink(f.office.plot, f.office.building)) + row('面积 · 桌子', `${Math.round(f.office.area)}㎡ · ${f.office.desks} 张`) + row('租金', `一天 ${money(f.office.rent)}`) + row('房东', who(f.office.landlord))) : '');
    tabs.push(['biz', '生意', () => biz + place]);
    if (f.expansion) tabs.push(['expand', '产能与扩产', () => expansionPage(f.expansion)]);
    const roleHead = f.kind === 'builder' ? '工种' : '岗位';
    tabs.push(['staff', `员工 ${f.staff.length}`, () => workSpace() + (trades.length > 1 ? donut(trades.map(([k, v]) => ({ label: k, value: v })), (v) => `${v} 人`, roleHead) : '') +
      table(['员工', roleHead, '日工钱', '手艺', '岁', '入职'], f.staff.map((s: J) => [avatar(s.id, s.age, s.trade ?? '', 0.6, 20) + who({ kind: 'person', id: s.id, name: s.name }), esc(s.trade), String(Math.round(s.wage)), s.skill.toFixed(2), String(s.age), yearOf(s.since)])) +
      sec('人数(每月月末)', history(f.book.staff, '人数', (v) => `${Math.round(v)} 人`, `fs${id}`))]);
    // 页签的顺序:投资公司的投资在最前,然后生意、员工、本行的几页,再是钱,最后谁的、在哪、在想什么、官司
    const order = ['biz', 'expand', 'staff', 'jobs', 'dev', 'tender', 'name', 'paper', 'law', 'fin', 'money', 'assets', 'stock', 'who', 'place', 'think', 'cases'];
    tabs.sort((x, y) => order.indexOf(x[0]) - order.indexOf(y[0]));
    const overviewKey = `companyOverview:${id}`;
    return [head + `<details class="sb-company-overview" data-key="${overviewKey}"${opened.has(overviewKey) ? ' open' : ''}><summary>财务与人数概况</summary>${fundKpi || kpi}</details>`, tabs];
  };

  // ---------- 一块地、一栋楼的交易、租住、房子(见 dash.rs 的 plotlog、plotrec、bldrec) ----------
  /** 交易、租住两页的数据:编号 × 4 + 筛哪一类(租住:0 全部、1 住宅、2 铺面、3 办公),× 2 + 哪一页(0 交易、1 租住)。 */
  const recOf = (bld: boolean, id: number, which: 0 | 1, f = 0): J => read((bld ? E.bldrec! : E.plotrec!)((id * 4 + f) * 2 + which));
  const chg = (d: number) => (Math.abs(d) < 0.005 ? '持平' : `${d > 0 ? '+' : '−'}${pct(Math.abs(d))}`);
  // 表里的日子写紧一点(一行要放得下价和人)
  const dayOf = (d: number) => `第${Math.floor(d / 360) + 1}年${Math.floor((d % 360) / 30) + 1}月${(d % 30) + 1}日`;
  // 哪一套:门牌(门牌里有楼名),楼拆了、塌了的标出来
  const roomCell = (r: J) => unitLink(r.unit, r.addr) + (r.bld?.gone ? ` <span class="meta">${esc(r.bld.gone)}</span>` : '');
  // 有成交价的买卖才算每平米、比上次(拆迁是补偿、断供记的是欠多少、继承和转手没有价)
  const PRICED = ['卖给自住的', '卖给收租的', '期房过户', '期房', '法拍'];
  const priceCell = (r: J) => {
    if (!r.price) return '—';
    const tag = r.what === '拆迁' ? '补偿 ' : /断供/.test(r.what) ? '欠 ' : r.what === '拆迁收购' ? '连楼 ' : '';
    return (tag ? `<span class="meta">${tag}</span>` : '') + money(r.price);
  };
  /** 交易一览:哪天(期房按签约那天)、哪一套、多少钱、每平米、比这一套上次成交、怎么成交的、谁卖给谁。brief 是摘要的几列。 */
  const dealTable = (rows: J[], brief = false) => {
    const unitCell = (r: J) => (r.land ? '<span class="tag">地</span>' : r.unit != null ? roomCell(r) : `<span class="meta">期房</span>${r.bld ? ` ${projLink(r.bld.id, r.bld.name)}` : ''}`);
    const how = (r: J) => esc(r.what) + (r.handed != null ? ` <span class="meta">${monthOf(r.handed)}过户</span>` : '');
    if (brief) return table(['哪天', '房', '价', '怎么', '买家'], rows.map((r) => [monthOf(r.day), unitCell(r), priceCell(r), how(r), r.b ? who(r.b) : '']));
    return table(['哪天', '房', '价', '每平米', '比上次', '怎么', '卖家', '买家'], rows.map((r) => [
      dayOf(r.day), unitCell(r), priceCell(r), PRICED.includes(r.what) && r.m2 ? money(r.m2) : '',
      r.prev && r.price && (PRICED.includes(r.what) || r.what === '拆迁') ? chg(r.price / r.prev - 1) : '', how(r), r.a ? who(r.a) : '', r.b ? who(r.b) : '']));
  };
  /** 租住一览:哪天、哪一套、什么事、一天的租、比这一套上一笔租金、谁(租客、公司、搬走的人)、房东。 */
  const letTable = (rows: J[]) => table(['哪天', '房', '什么事', '租金/天', '比上一笔', '谁', '房东'], rows.map((r) => [
    dayOf(r.day), roomCell(r), esc(r.what), r.rent != null ? money(r.rent) : '', r.prev && r.rent != null ? chg(r.rent / r.prev - 1) : '',
    r.who ? who(r.who) : '', r.owner ? who(r.owner) : '']));
  /** 成交价走势:房子每平米、地整块,按年或按季度;没有成交的年份跳过(见 dash.rs 的 trend_json)。 */
  const dealTrend = (t: J, key: string) => {
    if (!t) return '';
    const per = t.step === 360 ? '按年' : '按季度';
    const pick = (vals: (number | null)[]) => {
      const i = vals.map((v, k) => (v == null ? -1 : k)).filter((k) => k >= 0);
      return { xs: i.map((k) => t.xs[k] as number), vals: i.map((k) => vals[k] as number) };
    };
    const h = pick(t.home), l = pick(t.land);
    return (h.xs.length >= 2 ? sec('房子成交 · 每平米', lineChart(h.xs, [{ name: '均价', vals: h.vals, fmt: money }], { h: 120, fmt: money, key: `${key}h`, area: true }), per) : '') +
      (l.xs.length >= 3 ? sec('地成交 · 整块', lineChart(l.xs, [{ name: '均价', vals: l.vals, fmt: money }], { h: 100, fmt: money, key: `${key}l` }), per) : '');
  };
  /** 房子眼下怎样(一排数):几套、住着、空着(平均空了多久)、在卖、装修、危楼、租金比附近、最近一笔成交。 */
  const houseTiles = (s: J, last: J | null) => {
    if (!s?.n) return last ? grid(tile('最近成交', money(last.price), `${monthOf(last.day)} · ${esc(last.land ? `地 · ${last.what}` : last.what)}`)) : '';
    const kinds = [s.homes ? `住宅 ${s.homes}` : '', s.offices ? `写字楼 ${s.offices}` : '', s.works ? `厂房 ${s.works}` : ''].filter(Boolean).join(' · ');
    const r = s.rent;
    const diff = r.near > 0 ? r.paid / r.near - 1 : null;
    return grid(
      tile('房子', `${s.n} 套`, kinds),
      tile('住着', `${s.used} 套`, `自住 ${s.self} · 出租 ${s.let}`),
      tile('空着', `${s.free} 套`, s.free ? `平均空了 ${Math.round(s.vacDays ?? 0)} 天 · 最久 ${s.vacMax} 天` : '', s.free && (s.vacDays ?? 0) >= 90 ? 'bad' : ''),
      ...(s.sale ? [tile('在卖', `${s.sale} 套`)] : []),
      ...(s.homes ? [tile('装修', `平均${newness(s.worn ?? 0)}`, [s.lux ? `豪华 ${s.lux}` : '', s.fixing ? `在翻新 ${s.fixing}` : ''].filter(Boolean).join(' · '))] : []),
      ...(s.condemned ? [tile('危楼', `${s.condemned} 套`, '', 'bad')] : []),
      ...(diff != null ? [tile('租金', Math.abs(diff) < 0.005 ? '和附近持平' : `比附近${diff > 0 ? '高' : '低'} ${pct(Math.abs(diff))}`, `在租 ${r.n} 套 · 和附近同样的房新租比`)] : []),
      ...(last ? [tile('最近成交', money(last.price), `${monthOf(last.day)} · ${esc(last.land ? `地 · ${last.what}` : last.what)}`)] : []));
  };
  /** 一排筛选:key 是记在 sub 里的键(每块地、每栋楼各记各的),opts 是 [值, 名字]。 */
  const chips = (label: string, key: string, on: string, opts: [string, string][]) =>
    `<div class="sb-chips"><span>${esc(label)}</span>${opts.map(([v, t]) => `<button class="chip${v === on ? ' on' : ''}" data-sub="${key}|${v}">${esc(t)}</button>`).join('')}</div>`;
  /** 交易页(地块、楼一样):走势、按楼筛(地块上盖过不止一栋的)、一览。 */
  const dealsPage = (bld: boolean, id: number, blds: J[]) => {
    const fk = `${bld ? 'bld' : 'plot'}-db-${id}`;
    const b = Number(sub.get(fk) ?? -1);
    const one = !bld && blds.some((x) => x.id === b);
    const r = one ? recOf(true, b, 0) : recOf(bld, id, 0);
    return dealTrend(r.trend, `dt${one ? `b${b}` : `${bld ? 'b' : 'p'}${id}`}`) +
      (!bld && blds.length > 1 ? chips('楼', fk, one ? String(b) : '-1', [['-1', '全部'], ...blds.map((x: J): [string, string] => [String(x.id), `${x.name}${x.gone ? `(${x.gone})` : ''}`])]) : '') +
      (r.n > r.rows.length ? `<p class="meta">一共 ${r.n} 笔,列出最近 ${r.rows.length} 笔</p>` : '') +
      (r.rows.length ? dealTable(r.rows) : '<div class="sb-empty">还没有买卖</div>');
  };
  /** 租住页:按住宅、铺面、办公筛,地块上的还能按楼筛;一览。 */
  const letsPage = (bld: boolean, id: number, blds: J[]) => {
    const kk = `${bld ? 'bld' : 'plot'}-lk-${id}`, bk = `plot-lb-${id}`;
    const f = Number(sub.get(kk) ?? 0);
    const b = Number(sub.get(bk) ?? -1);
    const one = !bld && blds.some((x) => x.id === b);
    const r = one ? recOf(true, b, 1, f) : recOf(bld, id, 1, f);
    const k: number[] = r.kinds;
    const names = ['全部', '住宅', '铺面', '办公'];
    const kindOpts = names.map((t, i): [string, string] => [String(i), `${t} ${k[i]}`]).filter(([v]) => v === '0' || k[Number(v)] > 0);
    return (kindOpts.length > 2 ? chips('类', kk, String(f), kindOpts) : '') +
      (!bld && blds.length > 1 ? chips('楼', bk, one ? String(b) : '-1', [['-1', '全部'], ...blds.map((x: J): [string, string] => [String(x.id), `${x.name}${x.gone ? `(${x.gone})` : ''}`])]) : '') +
      (r.n > r.rows.length ? `<p class="meta">一共 ${r.n} 条,列出最近 ${r.rows.length} 条</p>` : '') +
      (r.rows.length ? letTable(r.rows) : '<div class="sb-empty">还没有租住的记录</div>');
  };
  /**
   * 房子一览(地块页、楼页一样):门牌(在卖、危楼标出来)、谁在用(空着的写空了几天)、租金、上次成交(多少、哪年)、
   * 卖过几次、租过几任(这套房的记录记满了写「几+」)、业主、值多少、装修、翻新要多少、面积;列头点了排序。
   */
  const houseTable = (list: J[], key: string) => {
    const home = list.some((u) => !u.office && !u.works);
    sorts[key] ??= { col: 0, dir: 1 };
    const fit = (u: J) => (u.office ? '写字楼' : u.works ? '厂房' : u.fixing ? '<span class="meta">在翻新</span>' : `${u.lux ? '豪华 · ' : ''}${newness(u.worn ?? 0)}`);
    const using = (u: J) => (u.user ? (u.self ? `<b>${u.user.kind === 'person' ? '自住' : '自用'}</b>` : who(u.user)) : u.condemned ? '<span class="meta">不能住</span>' : `<span class="meta">空着${u.vacant != null ? ` ${u.vacant} 天` : ''}</span>`);
    const leased = (u: J) => u.user && !u.self;
    const cols: [string, (u: J) => number | string, (u: J) => string][] = [
      ['门牌', (u) => u.id, (u) => unitLink(u.id, u.addr) + (u.condemned ? ' <span class="tag bad">危楼</span>' : u.forSale ? ' <span class="tag">在卖</span>' : '')],
      ['谁在用', (u) => (u.user ? (u.self ? 0 : 1) : 2 + (u.vacant ?? 0)), using],
      ['租金/天', (u) => (leased(u) ? u.rent : 0), (u) => (leased(u) ? money(u.rent) + (u.hot > u.rent * 1.05 ? ` <span class="meta">有人求租 ${money(u.hot)}</span>` : '') : '')],
      ['上次成交', (u) => u.lastDay ?? -1, (u) => (u.lastPrice ? `${money(u.lastPrice)} <span class="meta">${yearOf(u.lastDay)}</span>` : '')],
      ['卖过', (u) => u.sold ?? 0, (u) => (u.sold ? `${u.sold} 次` : '')],
      ['租过', (u) => u.leases ?? 0, (u) => (u.leases ? `${u.leases}${u.full ? '+' : ''} 任` : '')],
      ['业主', (u) => u.owner?.name ?? '', (u) => who(u.owner)],
      ['值多少', (u) => u.value ?? 0, (u) => money(u.value ?? 0)],
      ['装修', (u) => (u.office || u.works ? 2 : u.worn ?? 0), fit],
      ...(home ? [['翻新要', (u: J) => u.reno ?? 0, (u: J) => (u.reno ? money(u.reno) : '')] as [string, (u: J) => number, (u: J) => string]] : []),
      ['面积', (u) => u.area, (u) => `${Math.round(u.area ?? 0)}㎡`],
    ];
    const rows = sortRows(key, list, cols.map((c) => c[1]));
    return table(cols.map((c) => c[0]), rows.map((u) => cols.map((c) => c[2](u))), { sortKey: key, sortCol: sorts[key].col, sortDir: sorts[key].dir });
  };

  // 这块地眼下值多少、怎么算出来的(见 dash.rs 的 plot2、eco 的 landmkt.rs 的 plot_land)
  const landNowRows = (n: J) => n.built
    ? row('眼下值多少', `${money(n.value)} <span class="meta">楼上的房眼下估值合计 ${money(n.homes)},减掉按眼下造价重盖、按装修新旧折的楼钱 ${money(n.bld)}</span>`)
    : row('眼下值多少', n.plan
      ? `${money(n.value)} <span class="meta">照最划算的盖法:${n.plan.floors} 层${esc(n.plan.type)}${n.plan.lux ? '(豪华装修)' : ''},${n.plan.sell ? '盖了卖,按房价打九折' : `盖了收租,一年净租 ${money(n.rent)} 按 ${n.cap.toFixed(1)}% 的回报折`},盖完值 ${money(n.gross)};减掉造价、配套费、装修设备 ${money(n.cost)}${n.area > 0 ? `;按能盖的每平方米 ${money(n.value / n.area)}` : ''}</span>`
      : '<span class="meta">眼下盖什么都不划算</span>') + row('附近成交学到的', money(n.comps));
  // 拿地的出让记录:每一笔连同各家的出价(头一个是拿到的;见 eco 的 build.rs 的 investors、tender.rs):
  // 竞价的写谁申请的(卖家自己挂的写挂出来)、挂出来到截止几天、几家出价、出了几回价、流拍;各家出的是封顶
  const landSalesTable = (list: J[], wide: boolean) => table([...(wide ? ['地', '片区'] : []), '哪天', '卖家', '怎么卖', '底价', '成交价', '各家出价(心里值)', '借土地贷'],
    list.map((d: J) => [...(wide ? [plotLink(d.plot, `(${d.at[0]}, ${d.at[1]})`), esc(d.district)] : []), monthOf(d.day), who(d.seller),
      `${esc(d.how ?? '')}${d.applicant ? `<br><span class="meta">${who(d.applicant)}申请</span>` : ''}${d.opened != null && d.opened !== d.day ? `<br><span class="meta">挂了 ${d.day - d.opened} 天 · ${d.bidders ?? d.bids.length} 家 · 出价 ${d.raises ?? 0} 回</span>` : ''}`,
      money(d.ask), d.unsold ? '<b>流拍</b>' : money(d.price),
      (d.bids as J[]).map((b: J, i: number) => `${i === 0 && !d.unsold ? '<b>拿到</b> ' : `第 ${i + 1} 高 `}${who(b.who)} 出 ${money(b.bid)} <span class="meta">(心里值 ${money(b.worth)}${b.reach < b.worth ? `,钱只撑得起 ${money(b.reach)}` : ''})</span>`).join('<br>') || '<span class="meta">没人出价</span>',
      d.loan > 0 ? money(d.loan) : '']));
  // 眼下在竞价的地(见 dash.rs 的 land_tenders_json)
  const tendersTable = (list: J[]) => table(['地', '片区', '卖家', '谁申请的', '挂出来', '截止', '底价', '眼下', '领先', '几家'],
    list.map((t: J) => [plotLink(t.plot, `(${t.at[0]}, ${t.at[1]})`), esc(t.district), who(t.seller), t.applicant ? who(t.applicant) : `<span class="meta">${esc(t.why || '卖家自己挂')}</span>`,
      monthOf(t.opened), dateOf(t.close), money(t.floor), money(t.price), t.lead ? who(t.lead) : '', String(t.bidders)]));
  const plotView = (id: number, p: J = read(E.plot(id))): [string, Tab[]] => {
    locate = id;
    const x = read(E.plot2(id));
    // 交易、租住、房子情况(见 dash.rs 的 plotlog;老的引擎没有)
    const lg: J = E.plotlog ? read(E.plotlog(id)) : null;
    const b = p.building, pr = p.project;
    const hasUnits = p.units.some((u: J) => !u.gone);
    const nav = propertyNav([
      { label: '地块', current: true },
      ...(b ? [{ label: '楼栋', go: `project:${b.id}`, title: `查看${b.name}` }] : []),
      ...(pr ? [{ label: '在建工程', go: `project:${pr.id}`, title: `查看${pr.name}` }] : []),
      { label: '房屋', ...(hasUnits ? { ...(b ? { go: `project:${b.id}`, view: 'units' } : { page: 'i:plot|units' }), title: '查看房屋列表' } : { title: '当前没有已交付房屋' }) },
    ]);
    // 这里的价:都是真实成交(这块地上的;房租这里没人租看附近两格实际付的)
    const pr100 = (v: number | null, unit: string) => (v == null ? '—' : `${money(v)}${unit}`);
    const rentHere = x.prices.rent ?? x.prices.near;
    const head = `<h3>${esc(p.public ?? b?.name ?? (pr ? pr.name : '空地'))}</h3>${nav}` + grid(tile('片区', esc(p.class), `(${p.x}, ${p.y})`),
      tile('房租', pr100(rentHere, ' / 天'), x.prices.rent != null ? '这里每百平米实际付的' : x.prices.near != null ? '附近每百平米实际付的' : '附近没人租'),
      tile('房价', pr100(x.prices.live, ''), `${p.units.some((u: J) => !u.gone && !u.office && !u.works) ? '这里的房眼下值多少' : '这一带眼下的行情'},每百平米${x.prices.price != null ? ` · 各套上次成交平均 ${money(x.prices.price)}` : ''}`),
      ...(x.prices.office != null ? [tile('写字楼', pr100(x.prices.office, ' / 天'), '这里每百平米实际付的')] : []),
      // 地价:这块地眼下值多少(空地按剩余法、盖了楼的按房值减楼钱,见 dash.rs 的 plot2);挂价、拿地时付的另列。老的引擎没有 now,照原来
      ...(x.land?.now ? [tile('地价', money(x.land.now.value), x.land.now.built ? '眼下:房子值的减掉楼钱' : x.land.now.plan ? '眼下:盖了楼值的减掉成本' : '眼下盖什么都不划算'),
        ...(x.land.ask != null ? [tile(x.land.owner?.kind === 'gov' ? '政府挂价' : '挂牌要价', money(x.land.ask))] : []),
        ...(x.land.paid != null ? [tile('拿地时付', money(x.land.paid), x.land.since != null ? dateOf(x.land.since) : '')] : [])]
        : x.land && (x.land.ask != null || x.land.paid != null) ? [tile('地价', x.land.ask != null ? money(x.land.ask) : money(x.land.paid), x.land.ask != null ? (x.land.owner?.kind === 'gov' ? '政府挂价' : '挂牌转让') : '拿地时付的')] : []),
      tile('周围岗位', num(x.access, 1)),
      ...(x.foot != null ? [tile('人流', `${num(x.foot)} 人`, `连周围一圈 ${num(x.catch ?? 0)} 人 / 天`)] : []),
      ...(x.labor != null ? [tile('招人方便', `${x.labor.toFixed(2)} 倍`, '周围住的上班的人,比全城平均')] : []));
    let body = '';
    // 公家的地方点过去看哪一页:法院、检察院、派出所、监狱去司法,别的去政府
    const pub: string = p.public ?? '';
    const board = pub === '法院' ? 'justice|court' : pub === '检察院' ? 'justice|pros' : x.fac?.kind === 2 ? 'justice|police' : x.fac?.kind === 3 ? 'justice|jail'
      : pub === '市政大楼' ? 'govt|hall' : x.agency ? 'govt|staff' : 'govt|inst';
    const boardLink = row('', `<a data-tab="${board}">${board.startsWith('justice') ? '打开司法' : '打开政府'}</a>${pub === '市政大楼' ? ' · <a data-go="gov:0">市政府</a>' : ''}`);
    if (x.fac) {
      const f = x.fac;
      body += row('是什么', `${esc(f.tier)} · ${f.floors} 层`) + row('容得下', num(f.cap)) +
        row('用它的', f.load > f.cap ? `<span class="bad">${num(f.load)}(挤了${f.over ? ` ${f.over} 个月` : ''})</span>` : num(f.load)) +
        row('人手', f.staff.toFixed(1)) + row('好坏', f.q < 0.6 ? `<span class="bad">${f.q.toFixed(2)}</span>` : f.q.toFixed(2)) + boardLink;
    } else if (x.agency) {
      const a = x.agency;
      body += row('办什么', `${esc(a.duty)} · 一件 ${a.cost} 工日`) + row('近一个月', `来 ${a.month.toFixed(0)} 件${a.turned >= 0.5 ? ` · <span class="bad">没办上 ${a.turned.toFixed(0)} 件</span>` : ''}`) +
        (a.queue ? row('排队', `${a.queue} 件${a.wait > 0 ? ` · 平均等 ${a.wait.toFixed(1)} 天` : ''}`) : '') +
        row('人手', `折合 ${a.fte.toFixed(1)} 人 · 机关一共 ${a.staff}/${a.need} 人`) + boardLink;
    } else if (p.public) body += row('机构', esc(p.public)) + boardLink;
    if (x.medical) body += sec('医院诊疗与运营', hospitalSummary(x.medical));
    if (x.education) body += sec('学校与实际课堂', schoolPage(x.education));
    if (p.bank) body += row('银行', `<a data-go="bank:0">打开</a>`);
    if (b) {
      // 这栋楼一共值多少、一年收多少租、装修怎样(每套加起来,和下面住户表同一套数)
      const hs = (p.units as J[]).filter((u: J) => !u.gone);
      const worth = hs.reduce((s: number, u: J) => s + (u.value ?? 0), 0);
      const rentYear = hs.reduce((s: number, u: J) => s + (u.tenant ? u.rent : 0), 0) * 360;
      const homes = hs.filter((u: J) => !u.office && !u.works);
      const lux = homes.filter((u: J) => u.lux).length, fixing = homes.filter((u: J) => u.fixing).length;
      const worn = homes.length ? homes.reduce((s: number, u: J) => s + (u.worn ?? 0), 0) / homes.length : 0;
      body += sec(`${b.type} · ${b.floors} 层 · ${b.name}`, row('主人', who(b.investor)) + row('谁盖的', b.old ? '开局就有的老房子' : b.builder ? who({ kind: 'firm', ...b.builder }) : '—') +
        (b.finished && !b.old ? row('盖好', dateOf(b.finished)) : '') +
        (hs.length ? row('值多少', `${money(worth)} · ${hs.length} 套`) + row('一年租金', rentYear > 0 ? `${money(rentYear)}${worth > 0 ? `(值多少的 ${pct(rentYear / worth, 1)})` : ''}` : '<span class="meta">没租出去</span>') : '') +
        (homes.length ? row('装修', `平均${newness(worn)}${lux ? ` · 豪华 ${lux} 套` : ''}${fixing ? ` · 在翻新 ${fixing} 套` : ''}`) : '') +
        (b.loan ? row('欠银行', `${money(b.loan.principal)} · 年息 ${(b.loan.rate * 100).toFixed(1)}%${b.loan.missed ? ` · <span class="bad">拖了 ${b.loan.missed} 天</span>` : ''}`) : '') +
        (b.site?.cracks ? row('裂缝', `<span class="bad">${b.site.cracks} 处</span>`) : ''));
    }
    if (p.ruin != null && !pr) body += `<p class="bad">废墟 · ${dateOf(p.ruin)}倒塌</p>`;
    if (pr) body += sec(`在盖:${pr.name}`, row('工程', projLink(pr.id, `${pr.type} ${pr.floors} 层 · 打开工程`)) + row('投资人', who(pr.investor)) + row('建筑公司', pr.builder ? who({ kind: 'firm', ...pr.builder }) : '还没人接') +
      (pr.site ? row('阶段', esc(projectProgressText(pr.site))) : '') + (pr.site?.waiting.length ? row('停工', `<span class="bad">${pr.site.waiting.map(esc).join('、')}</span>`) : ''));
    if (!b && !pr && !p.public) body += row('主人', who(p.owner)) + (p.owner.kind === 'gov' ? row('政府挂价', money(p.ask)) : '');
    // 上半截:房子眼下怎样、成交价走势、最近几笔买卖(全的在「交易」「租住」「房子」几页)
    const brief = lg && (lg.sum?.n || lg.dealsN) ? houseTiles(lg.sum, lg.last) + dealTrend(lg.trend, `pt${id}`) +
      (lg.recent?.length ? sec('最近的买卖', dealTable(lg.recent, true) + (lg.dealsN > lg.recent.length ? `<p class="meta"><a data-sub="i:plot|deals">全部 ${lg.dealsN} 笔</a></p>` : '')) : '') : '';
    const tabs: Tab[] = [['sum', '这块地', () => brief + body]];
    // 地铁站:停哪几条线、换乘、每天进出、周围住的和上班的(见 dash.rs 的 plot2)
    const sv = x.station;
    if (sv) tabs.push(['station', '地铁站', () =>
      `<p class="sb-lead">${esc(sv.name)} · ${(sv.lines as J[]).map((l) => lineChip(l.k, esc(l.name))).join(' ')}</p>` +
      grid(tile('采样进出/日', sv.sampleDay != null ? `${num(sv.inout)} 人次` : '—', sv.sampleDay != null ? `${dateOf(sv.sampleDay)}采样` : '尚未采样'),
        tile('换乘', sv.sampleDay != null ? `${num(sv.xfer)} 人` : '—'), tile('周围住', `${num(sv.live)} 人`, '两格以内'),
        tile('在这上班', `${num(sv.jobs)} 人`, '两格以内'), tile('本站开放', dateOf(sv.opens), sv.opens > city.day ? '在修' : '已开放')) +
      sec('停的线', table(['线', '状态', '开放/总站', '采样客流', '最挤', '运营/配车(列)', '班隔', '下次开站'], (sv.lines as J[]).map((l) => [lineChip(l.k, esc(l.name)),
        esc(l.state ?? (l.open ? '运营中' : '施工中')), `${l.openStations} / ${l.totalStations}`, l.sampleDay != null ? num(l.riders) : '—',
        l.open && l.sampleDay != null ? crowdCell(l.crowd) : '—', subwayFleet(l), l.headway != null ? `${l.headway.toFixed(1)} 分钟` : '—', l.nextOpen != null ? dateOf(l.nextOpen) : '—']))) +
      ((sv.nextTo as J[]).length ? sec('隔一格换乘', (sv.nextTo as J[]).map((l) => lineChip(l.k, esc(l.name))).join(' ')) : '')]);
    if (lg?.dealsN) tabs.push(['deals', `交易 ${lg.dealsN}`, () => dealsPage(false, id, lg.blds)]);
    if (lg?.letsN) tabs.push(['lets', `租住 ${lg.letsN}`, () => letsPage(false, id, lg.blds)]);
    const units = p.units.filter((u: J) => !u.gone);
    if (units.length) {
      const used = units.filter((u: J) => u.tenant).length;
      tabs.push(['units', `房子 ${used}/${units.length}`, () => (lg ? houseTiles(lg.sum, null) : '') + houseTable(units.map((u: J) => ({ ...u, user: u.tenant })), 'house')]);
    }
    // 地:谁的、花多少买的、挂牌要多少、盖了楼值多少、这块地的成交(见 dash.rs 的 plot2、eco 的 landmkt.rs)
    const ld = x.land;
    if (ld && (ld.owner || ld.deals?.length)) tabs.push(['land', `地 ${ld.deals?.length ?? 0}`, () =>
      row('主人', who(ld.owner)) + (ld.now ? landNowRows(ld.now) : '') + (ld.paid != null ? row('拿地时付', `${money(ld.paid)}${ld.since != null ? ` · ${dateOf(ld.since)}` : ''}`) : '') +
      (ld.ask != null ? row(ld.owner?.kind === 'gov' ? '政府挂价' : '挂牌要价', `${money(ld.ask)}${ld.floor > 0 ? ` <span class="meta">· 不低于修门前路的钱 ${money(ld.floor)}</span>` : ''}`) : '') + (ld.spec ? row('', '<span class="tag">囤着等涨</span>') : '') +
      (!ld.now && ld.value != null ? row('行情价', money(ld.value)) : '') +
      (!ld.now && ld.worth != null ? row('盖了楼值多少', ld.worth > 0 ? money(ld.worth) : '<span class="meta">眼下盖什么都不划算</span>') : '') +
      (ld.tender ? sec('在竞价', row('卖家', who(ld.tender.seller)) + row('谁申请的', ld.tender.applicant ? who(ld.tender.applicant) : `<span class="meta">${esc(ld.tender.why || '卖家自己挂')}</span>`) +
        row('挂出来', dateOf(ld.tender.opened)) + row('截止', dateOf(ld.tender.close)) + row('底价', money(ld.tender.floor)) + row('眼下', `${money(ld.tender.price)}${ld.tender.lead ? ` · ${who(ld.tender.lead)}领先` : ''}`) +
        ((ld.tender.bids as J[]).length ? table(['谁', '封顶', '心里值', '钱撑得起'], (ld.tender.bids as J[]).slice().sort((a, b) => b.bid - a.bid).map((b: J) => [who(b.who), money(b.bid), money(b.worth), money(b.reach)])) : '<div class="sb-empty">还没人出价</div>')) : '') +
      (ld.sales?.length ? sec('出让记录', landSalesTable(ld.sales, false)) : '') +
      sec('这块地的过户', ld.deals?.length ? table(['哪天', '价', '卖家', '买家', '怎么'], ld.deals.map((d: J) => [dateOf(d.day), d.price ? (d.how === '拆迁收购' ? `<span class="meta">连楼</span> ${money(d.price)}` : money(d.price)) : '—', who(d.seller), who(d.buyer), esc(d.how)])) : '<div class="sb-empty">没有</div>')]);
    // 派出所的拘留室、监狱:这里关着的人
    if ((x.held as J[] | undefined)?.length) tabs.push(['held', `关着的人 ${x.held.length}`, () => inmateTable(x.held, false)]);
    if (p.firms.length) tabs.push(['firms', `公司 ${p.firms.length}`, () => table(['公司', '行业', '人', '总经理'], p.firms.map((f: J) => [who({ kind: 'firm', id: f.id, name: f.name }), esc(f.line), String(f.staff), who(f.owner)]))]);
    if (x.history.length) tabs.push(['hist', '盖过的楼', () => table(['楼', '楼型', '层', '投资人', '建筑公司', '盖好', '拆了'], x.history.map((h: J) => [h.id != null ? projLink(h.id, h.name) : esc(h.name), esc(h.type), String(h.floors), who(h.investor), h.builder ? who(h.builder) : '', h.finished != null ? yearOf(h.finished) : '', h.demolished != null ? yearOf(h.demolished) : '']))]);
    return [head, tabs];
  };

  const projectView = (id: number, p: J = read(E.project2(id))): [string, Tab[]] => {
    locate = p.plot ?? null;
    const nav = propertyNav([
      { label: '地块', go: `plot:${p.plot}`, title: '查看这栋楼所在的地块' },
      { label: p.finished != null ? '楼栋' : '在建工程', current: true },
      { label: '房屋', ...(p.unitList.length ? { page: 'i:project|units', title: '只查看这栋楼的房屋' }
        : { title: p.finished == null ? '工程尚未交付房屋' : '这栋楼当前没有可查看的房屋' }) },
    ]);
    const delivered: J[] = p.unitList;
    const inUse = delivered.filter((u) => u.user != null).length;
    const contractRent = delivered.reduce((s, u) => s + (u.user && !u.self ? u.rent : 0), 0);
    const kpi = p.finished != null ? grid(
      tile('状态', p.demolished != null ? '已拆除或倒塌' : '已交付', p.demolished != null ? dateOf(p.demolished) : dateOf(p.finished)),
      tile('楼层', `${p.floors} 层`), tile('现有房屋', `${delivered.length} 套`),
      tile('正在使用', `${inUse} / ${delivered.length} 套`, '含自住、自用和出租'),
      tile('市价估值', money(delivered.reduce((s, u) => s + u.value, 0)), '现有各套房屋估值合计'),
      tile('租约租金', `${money(contractRent)} / 天`, '当前出租租约合计，欠租可能未实收')) :
      grid(tile('施工计量', esc(projectProgressPercent(p.progress)), esc(projectProgressStage(p))), tile('主体', `${p.done} / ${p.floors} 层`), tile('合同价', money(p.price)), tile('监管账户', money(p.escrow)),
      tile('盖完还要', money(p.remaining)), tile('期房卖出', `${p.presold.length} / ${p.units} 套`, p.presold.length ? money(p.presold.reduce((s: number, x: J) => s + x.price, 0)) : ''),
      tile('停工', p.stalledDays != null ? `${p.stalledDays} 天` : '没停', p.waiting.length ? p.waiting.join('、') : '', p.stalledDays != null ? 'bad' : ''));
    const head = `<h3>${esc(p.name)}</h3>${nav}<p class="meta">${esc(p.type)} ${p.floors} 层 · ${p.units} 套 · ${p.product ? `套内 ${Math.round(p.unitArea)}㎡ · 每层 ${p.unitsPerFloor} 户 · 公区 ${Math.round(p.commonArea)}㎡` : `每套 ${Math.round(p.unitArea)}㎡`}${p.finished != null ? ` · ${dateOf(p.finished)}交付` : ''}</p>` + kpi;
    const tabs: Tab[] = [
      ['who', '谁', () => row('投资人', who(p.investor)) + row('建筑公司', p.builder ? who(p.builder) : '还没人接') + row('发包', dateOf(p.posted)) + (p.signed ? row('签约', dateOf(p.signed)) : '') + (p.loan ? row('开发贷', `${money(p.loan.principal)} · 年息 ${p.loan.rate.toFixed(1)}%${p.loan.missed ? ` · <span class="bad">拖了 ${p.loan.missed} 天</span>` : ''}`) : '')],
      ['money', '钱', () => (renderProjectSettlement(p.settlement, row) ?? (row('合同工钱及管理费', `已付 ${money(p.labPaid)} / ${money(p.labor)}`) + row('料款', `已付 ${money(p.matPaid)} · 进场计价 ${money(p.matGot)}`) + row('监管账户', money(p.escrow)))) +
        (p.fee ? row('城市配套费', `${money(p.fee)}${p.permit ? ',领许可时交了' : ',领许可时交'}`) : '') +
        (renderProjectDelivery(p.delivery, row, money, dateOf) ?? (p.fitout ? row('装修和设备', p.product ? `已实际支付 ${money(p.fitPaid ?? 0)} · 当前估算 ${money(p.fitout)}，含套内豪装与公区；买齐后交付` : `${money(p.fitout)}${p.finished != null ? ',交房前买了' : ',交房前从外地买'}`) : '')) + (p.policy ? sec('保险', coverTable([p.policy])) : '') + statement(p.book, `pr${id}`) + sec('监管账户(每月月末)', history(p.book.worth, '监管账户', money, `pe${id}`))],
    ];
    if (p.method) tabs.push(['method', '做法', () => {
      const m = p.method, s = m.spec, d = m.design;
      const diff = (label: string, a: string, b: string) => row(label, a === b ? esc(a) : `<span class="bad">${esc(a)}</span> <span class="meta">图纸 ${esc(b)}</span>`);
      return grid(tile('做法', esc(m.name), `料定的${esc(m.tier)}${m.black ? ` · 实际黑作坊 ${pct(m.black)}` : ''}`, m.level > 0 || m.tier !== '正规厂' ? 'bad' : ''),
          tile('监理', esc(m.supervisor), m.tested != null ? `竣工验收抽测 ${pct(m.tested || 1)}` : '投资人请的'),
          tile('料钱差价', signed(m.skim), '按图纸结的 − 实际花的'),
          tile('安全度', m.safety != null ? m.safety.toFixed(2) : '—', '上帝视角', m.safety != null && m.safety < 1.2 ? 'bad' : ''),
          ...(m.grade ? [tile('竣工品级', esc(m.grade))] : []),
          // 交付以后:裂了几层、危险度(租客买家按它压价)、危楼、房东们加固过几次、裂了没修几个月(见 eco 的 cracks.rs)
          ...(p.finished != null ? [tile('楼况', m.condemned ? '危楼' : m.cracked ? `裂了 ${m.cracked} 层` : '完好',
            `危险度 ${m.danger.toFixed(2)}${m.repaired ? ` · 加固过 ${m.repaired} 次` : ''}${m.skipped ? ` · 裂了没修 ${m.skipped} 个月` : ''}`, m.condemned || m.danger >= 0.3 ? 'bad' : '')] : [])) +
        sec('和图纸比', diff('混凝土', `C${s.concrete}`, `C${d.concrete}`) + diff('钢筋', `直径 ${s.dia} 间距 ${s.spacing}`, `直径 ${d.dia} 间距 ${d.spacing}`) +
          diff('养护', `${s.curing} 天`, `${d.curing} 天`) + diff('保护层', `${s.cover} mm`, `${d.cover} mm`) +
          (s.noVibrate ? row('振捣', '<span class="bad">不振捣</span>') : '') + (s.skipInspect ? row('隐蔽验收', '<span class="bad">不等验收就浇</span>') : '') +
          diff('保温', s.insulation === 'eps' ? '聚苯板' : '岩棉', '岩棉') + diff('电线', s.wire === 'cca' ? '铜包铝' : '铜', '铜') + diff('防水', `${s.waterproof} 道`, '2 道'));
    }]);
    if (p.floorsList.length) tabs.push(['floors', '每一层', () => `<div class="sb-floors">${p.floorsList.slice().reverse().map((f: J) => `<span class="fl ${esc(f.phase)}${f.cracked ? ' cracked' : ''}" title="${f.n} 层 · ${esc(f.phase)}${f.f ? ` · 承载 ${f.f.toFixed(2)}` : ''}${f.reworks ? ` · 返工 ${f.reworks} 次` : ''}">${f.n}</span>`).join('')}</div>`]);
    tabs.push(['presale', `期房 ${p.presold.length}`, () => (p.presaleAsk ? `<p class="meta">眼下要价(百平米)${money(p.presaleAsk)}</p>` : '') + table(['买家', '价', '哪天', '期房按揭'], p.presold.map((x: J) => [who(x.who), money(x.price), dateOf(x.day), x.loan != null ? money(x.loan) : '']))]);
    // 交付以后:每套房一览、每个月的账、这栋楼的大事(见 eco 的 unitlog.rs)
    if ((p.unitList as J[]).length) tabs.push(['units', `房子 ${p.unitList.length}`, () => houseTable(p.unitList, 'house')]);
    // 这栋楼所有房子的买卖、租住(和地块页、房页同一份记录,见 dash.rs 的 bldrec)
    if (p.dealsN && E.bldrec) tabs.push(['deals', `交易 ${p.dealsN}`, () => dealsPage(true, id, [])]);
    if (p.letsN && E.bldrec) tabs.push(['lets', `租住 ${p.letsN}`, () => letsPage(true, id, [])]);
    if ((p.months as J[]).length >= 2) tabs.push(['trend', '走势', () => {
      const ms: J[] = p.months, xs = ms.map((m) => m.day);
      return sec('全楼值多少', lineChart(xs, [{ name: '值多少', vals: ms.map((m) => m.value), fmt: money }], { h: 120, fmt: money, key: `bv${id}`, area: true })) +
        sec('一天收多少租', lineChart(xs, [{ name: '一天的租', vals: ms.map((m) => m.rent), fmt: money }], { h: 110, fmt: money, key: `br${id}`, area: true })) +
        sec('租出去几成', lineChart(xs, [{ name: '租出去', vals: ms.map((m) => (m.units ? m.let / m.units : 0)), fmt: (v: number) => pct(v) }], { h: 100, fmt: (v: number) => pct(v), key: `bo${id}`, zero: true })) +
        (ms.some((m) => m.danger > 0) ? sec('危险度', lineChart(xs, [{ name: '危险度', vals: ms.map((m) => m.danger), color: 'var(--bad)', fmt: (v: number) => pct(v) }], { h: 90, fmt: (v: number) => pct(v), key: `bd${id}`, zero: true })) : '');
    }]);
    if ((p.log as J[]).length) tabs.push(['log', `记录 ${p.log.length}`, () => table(['哪天', '什么事'], (p.log as J[]).slice().reverse().map((e) => [dateOf(e.day), esc(e.text)]))]);
    return [head, tabs];
  };

  // 一套房:值多少怎么算出来的、钱上的事、记录
  const unitView = (id: number, x: J = read(E.unit2(id))): [string, Tab[]] => {
    const b = x.brief;
    locate = x.plot;
    const nav = propertyNav([
      { label: '地块', go: `plot:${x.plot}`, title: '查看这套房所在的地块' },
      ...(x.building ? [{ label: x.building.finished != null ? '楼栋' : '在建工程', go: `project:${x.building.id}`, title: `查看${x.building.name}` }] : []),
      { label: '房屋', current: true },
    ]);
    const head = `<h3>${esc(b.addr || '一套房')}</h3>${nav}<p class="meta">${esc(x.district)} · ${b.product ? `套内 ${Math.round(b.area)}㎡ · 公区分摊 ${Math.round(b.commonArea)}㎡ · ${esc(b.product)}` : `${Math.round(b.area)}㎡ · ${b.office ? '写字楼' : b.works ? '厂房' : '住宅'}`}${b.lux ? ' · 豪华装修' : ''}${b.condemned ? ' · <span class="bad">危楼</span>' : ''}${b.gone ? ' · <span class="bad">没了</span>' : ''}</p>` +
      grid(tile('市价估值', money(b.value), x.home && x.holdValue != null ? `收租估值 ${money(x.holdValue)}` : ''),
        tile('业主', who(b.owner), x.bought ? `买的时候 ${money(x.bought)}` : ''),
        tile('谁在用', b.user ? (b.self ? (b.user.kind === 'person' ? '业主自住' : '业主自用') : who(b.user)) : '空着', b.user ? `${dateOf(x.since)}起` : x.vacant ? `空了 ${x.vacant} 天` : '', b.user ? '' : 'bad'),
        tile('租金', b.user && !b.self ? `${money(b.rent)} / 天` : '—', x.home && x.comparable ? `附近同样的新租 ${money(x.comparable)} / 天` : ''),
        ...(x.home ? [tile('装修', b.fixing ? '在翻新' : newness(b.worn), x.renoMats ? `翻新一次 ${money(x.renoMats + x.renoWork * x.renoPrice)}` : '')] : []),
        ...(b.forSale ? [tile('挂牌卖', money(x.saleAsk), `${dateOf(x.listed)}挂的`)] : []));
    const tabs: Tab[] = [];
    if (x.home) tabs.push(['worth', '估价', () => {
      const q = x.parts;
      const hp = x.holdParts;
      return sec('这套房一天能租多少', row('全城行情', `每一分「好」一天 ${money(x.rentIndex)}(新签的租约)`) +
          row('这套房的好坏', `${q.quality.toFixed(2)} 分 = 面积 ${q.area.toFixed(2)} × 邻居家底 ${q.prestige.toFixed(2)} × 豪华 ${q.lux.toFixed(2)} × 新旧 ${q.wear.toFixed(2)} × 楼况 ${q.danger.toFixed(2)}`) +
          row('这里的水平', `${x.lv.toFixed(2)} 倍全城平均(上班方便的地段按通勤推算 ${x.demand.toFixed(2)},周围在租的房多了按实付)`) +
          row('附近同样的房新租', `一天 ${money(x.comparable)} = ${money(x.rentIndex)} × ${q.quality.toFixed(2)} × ${x.lv.toFixed(2)}`)) +
        sec('市价估值', (x.appraise ? row('这套房估值', money(b.value)) +
            row('看哪些成交', `同一栋楼近来卖出去的 ${x.appraise.bldW.toFixed(1)} 笔、附近两格同类的 ${x.appraise.nearW.toFixed(1)} 笔(一笔一年分量减半、远的打折),按「好坏」折到这一套`) +
            row('按收益折', `${money(x.appraise.income)}(附近新租 × 附近有人住的几成 − 持有成本,一年 ÷ 折现率 ${pct(x.appraise.cap, 1)};无风险 ${pct(x.appraise.riskFree, 1)} 加从成交学到的风险溢价)`) +
            '<p class="meta">成交少的、成交价离按收益折的远的,多信按收益折的。银行按成交价和这个估值里小的放按揭。</p>' :
          row('全城成交指数', `${money(x.houseIndex)} / 标准百平米`) +
          row('这套房估值', `${money(b.value)}${x.marketFactor != null ? ` = ${money(x.houseIndex)} × 折算系数 ${x.marketFactor.toFixed(4)}` : '(全城房价 × 这套的好坏 × 这里的水平)'}`) +
          '<p class="meta">市价估值随成交指数变化；上次买价、房东挂牌价和收租估值是不同口径，均不保证能按这个价卖出。</p>') +
          (x.bought ? row('上次成交', money(x.bought)) : '') + (b.forSale ? row('挂牌价', money(x.saleAsk)) : '')) +
        sec('留作出租的估值', row('满租时的新租金', `${money(x.comparable)} / 天`) +
          (hp ? (hp.legacy ? '<p class="meta">旧规则按满租计算，不扣空置和持有成本。</p>' :
            row(hp.near ? '附近空置' : '全城空置', `${pct(hp.vacancy, 1)}，估计收得到 ${pct(1 - hp.vacancy, 1)} 的租`) +
            row('维修及持有成本', `${money(hp.upkeep)} / 天，空着也要承担`) +
            row('预计每日净租金', `${money(hp.net)} = 租金 × 入住比例 − 持有成本`)) +
            row('估值', `${money(x.holdValue)} = 年净租金 ${money(hp.annualNet)} ÷ ${pct(hp.rate, 1)}`) +
            (hp.net < 0 ? '<p class="meta">预计净租金为负，收租估值按零计；房屋实体和市价估值仍单独保留。</p>' : '') :
            row('收租估值', `${money(x.holdValue)}，按引擎当前出租收益口径计算`))) +
        sec('翻新', row('料钱', money(x.renoMats)) + row('工', `${x.renoWork.toFixed(0)} 个 × ${money(x.renoPrice)} = ${money(x.renoWork * x.renoPrice)}`) +
          (x.reno ? row('在翻', `还差 ${x.reno.left.toFixed(0)} / ${x.reno.total.toFixed(0)} 个工${x.reno.lux ? ',翻成豪华' : ''} · ${dateOf(x.reno.start)}开工 · 已花 ${money(x.reno.cost)}`) : ''));
    }]);
    if (x.loan || x.ins || x.policy) tabs.push(['money', '按揭和保险', () => (x.loan ? row('按揭', `欠 ${money(x.loan.principal)} · 年息 ${(x.loan.rate * 100).toFixed(1)}%${x.loan.missed ? ` · <span class="bad">拖了 ${x.loan.missed} 天</span>` : ''}`) : '') +
      (x.ins ? row('房屋险', `${who(x.ins.by)} · 保到 ${dateOf(x.ins.until)}`) : '') + (x.policy ? sec('保险', coverTable([x.policy])) : '')]);
    tabs.push(['log', `记录 ${x.log.length}`, () => x.log.length ? table(['哪天', '什么事', '钱', '谁', '对方'], (x.log as J[]).map((e) => [dateOf(e.day), esc(e.what),
      e.amount ? (/^交房/.test(e.what) ? `造价 ${money(e.amount)}` : /租/.test(e.what) && !/拆|塌/.test(e.what) ? `${money(e.amount)} / 天` : /断供/.test(e.what) ? `欠 ${money(e.amount)}` : money(e.amount)) : '', e.a ? who(e.a) : '', e.b ? who(e.b) : ''])) : '<div class="sb-empty">还没有记录</div>']);
    return [head, tabs];
  };

  const marketView = (k: number): [string, Tab[]] => {
    const m = read(E.market2(k));
    const h: number[] = m.hist;
    const total = m.sellers.reduce((s: number, x: J) => s + x.sold, 0);
    const head = `<h3>${esc(m.name)}</h3>` +
      grid(tile('价', num(m.price, m.price < 100 ? 1 : 0)), tile('两个月均价', num(m.trend, 1)), tile('最后成交', num(m.last, 1)), tile('今天成交', num(m.volume, 1), money(m.value)),
        tile('买单 · 卖单', `${num(m.bids, 1)} · ${num(m.asks, 1)}`, m.bids > m.asks ? '买的多' : '卖的多'), tile('没成交', m.dry ? `${m.dry} 天` : '天天有', '', m.dry > 30 ? 'bad' : ''));
    const tabs: Tab[] = [['price', '价格', () => sec('价格(每月月末)', history(h, '价', (v) => num(v, v < 100 ? 1 : 0), `mk${k}`))]];
    // 餐饮娱乐分档(没分档的城没有这一页):各档一份的价、一天卖了多少、挂着的买卖、想去的(连这一档没馆子只好去便宜的)、几家
    if (m.tiers) tabs.push(['tiers', '档次', () => table(['档次', '一份', '比大众贵', '今天成交', '买单 · 卖单', '想去的(一天)', '馆子', '人', '买主心里贵几倍(中位 · 最高一成)'], m.tiers.map((t: J) => [esc(t.name), num(t.price, 1), t.ratio == null ? '—' : `${num(t.ratio, 2)} 倍`, num(t.volume, 1), `${num(t.bids, 1)} · ${num(t.asks, 1)}`, num(t.wanted, 1), String(t.shops), t.staff == null ? '—' : String(t.staff), t.minds ? `${num(t.minds.mid, 1)} · ${num(t.minds.top, 1)}` : '—'])) +
      '<p class="meta">同样吃一顿、玩一回的时间,好馆子一份要的人手、食材、装修都多;钱多的人同样的时间往贵的走。卖的要价按各档自己的行情;买的按各人心里这一档值大众的几倍出(钱多的心里值得多)。</p>']);
    if (m.sellers.length) tabs.push(['sellers', `卖家 ${m.sellers.length}`, () => donut(m.sellers.slice(0, 10).map((x: J) => ({ label: x.firm.name, value: x.sold })), (v) => v.toFixed(1), '每天卖') +
      table(['卖家', ...(m.tiers ? ['档次'] : []), '要价', '存货', '每天做', '每天卖', '份额', '人'], m.sellers.map((x: J) => [who(x.firm), ...(m.tiers ? [esc(x.tier ?? '')] : []), x.ask.toFixed(1), x.stock.toFixed(0), x.made.toFixed(1), x.sold.toFixed(1), total ? pct(x.sold / total) : '', String(x.staff)]))]);
    return [head, tabs];
  };

  // 银行是谁的:市政府控股的是市属,别的是私营
  const bankOwnerLine = (b: J): string => {
    const c = b.company;
    if (!c) return '城市的商业银行';
    const kind = c.gov > 0.5 ? '市属银行' : '私营银行';
    return `${kind} · 行长 ${who(c.boss)}`;
  };

  // 大事记:按年分组,新的在上面(ev 已经是新的在前)
  const eventList = (ev: J[]) => {
    if (!ev.length) return '<div class="sb-empty">还没有记下什么事</div>';
    let year = -1;
    return `<ol class="sb-life">${ev.map((e) => {
      const y = Math.floor(e.day / 360) + 1;
      const head = y !== year ? `<li class="yr">第 ${y} 年</li>` : '';
      year = y;
      return `${head}<li><time>${Math.floor((e.day % 360) / 30) + 1} 月</time><span class="tag k-${esc(e.kind)}">${esc(e.kind)}</span><p>${linkify(e.text)}</p></li>`;
    }).join('')}</ol>`;
  };

  // 银行的地方:总部、在盖的、分行、名下的楼、这几年怎么变的(见 dash.rs 的 bank_premises_json)
  const bankPlace = (pm: J, id: number): string => {
    const at = (x: J) => plotLink(x.plot, x.name || `(${x.at[0]}, ${x.at[1]})`);
    const h = pm.hq;
    const t = pm.tower;
    const head = row('总部', h ? `${at(h)} · ${esc(h.district)} · ${h.floors} 层 · ${h.own ? '自己的楼' : `租的,一天 ${money(h.rent)}`}` : '还没有地方') +
      row('坐人', `用 ${Math.round(pm.used)}㎡ · 要 ${pm.desks} 张桌子 · 员工 ${pm.staff} 人`) +
      (pm.staffNeed != null ? row('业务量', `一天存取 ${Math.round(pm.tx)} 笔 · 新放贷款 ${pm.newLoans.toFixed(1)} 笔 · 要 ${pm.staffNeed} 个人`) : '') +
      (t ? row('在盖', `${plotLink(t.plot, t.name)} · 盖到 ${t.done}/${t.floors} 层 · ${t.builder ? who(t.builder) : '还没人接'}`) : '') +
      row('自己盖的楼', pm.estate ? `账面 ${money(pm.estate)},算进家底` : '没有') +
      row('算账', `股东要 ${pm.hurdle.toFixed(1)}% · 多一块存款一年多挣 ${pm.margin.toFixed(1)}%`);
    const place = (list: J[]) => table(['在哪', '片区', '面积', '房租/天', '房东', '开张'],
      list.map((x: J) => [unitLink(x.unit, x.addr || `(${x.at[0]}, ${x.at[1]})`), esc(x.district), `${Math.round(x.area)}㎡`, money(x.rent), who(x.landlord), yearOf(x.since)]));
    const branches = sec(`分行 ${pm.branches.length}`, pm.branches.length ? place(pm.branches) : '<div class="sb-empty">没有分行</div>');
    const extra = pm.extra.length ? sec(`总部坐不下,租在别处 ${pm.extra.length}`, place(pm.extra)) : '';
    const blds = pm.buildings.length ? sec(`名下的楼 ${pm.buildings.length}`, table(['楼', '片区', '层', '套', '自用', '租出', '一天租金', '值多少'],
      pm.buildings.map((x: J) => [at(x), esc(x.district), String(x.floors), String(x.units), String(x.used), String(x.let), money(x.rent), money(x.value)]))) : '';
    // 每个月一笔:[哪天, 总部几层, 自己的楼, 分行, 桌子, 员工, 名下的楼值多少, 账面];每年取最后一个月
    const s: J[] = pm.sites;
    const years = s.filter((r, i) => i === s.length - 1 || Math.floor(s[i + 1][0] / 360) !== Math.floor(r[0] / 360)).reverse();
    const hist = years.length ? sec('这几年', table(['年', '总部', '分行', '桌子', '员工', '名下的楼值'],
      years.map((r) => [`第 ${Math.floor(r[0] / 360) + 1} 年`, r[1] ? `${r[1]} 层 · ${r[2] ? '自己的' : '租的'}` : '没有', String(r[3]), String(r[4]), String(r[5]), money(r[6])]))) : '';
    const chart = s.length >= 2 ? sec('名下的楼(每月)', lineChart(s.map((r) => r[0]), [{ name: '市值', vals: s.map((r) => r[6]), fmt: money }, { name: '账面', vals: s.map((r) => r[7]), fmt: money }], { h: 120, fmt: money, zero: true, key: `bk${id}` })) : '';
    return head + branches + extra + blds + hist + chart;
  };

  // 收回来的房、地、楼:手上有什么、账面价,一共收进来、勾销、卖掉多少(见 dash.rs 的 bank_seized_json)
  const seizedPage = (sz: J): string => {
    const mk = (m: J, what: string) => (m ? `${what}卖价是当时行情价的 ${m.q.toFixed(0)}%,收进来平均 ${Math.round(m.days)} 天卖掉` : `${what}还没卖过(按挂的价估)`);
    const link = (x: J) => (x.unit != null ? unitLink(x.unit, x.name) : x.project != null ? projLink(x.project, x.name) : plotLink(x.plot, x.name));
    return row('手上', `${sz.n} 件 · 账面 ${money(sz.value)},算进家底`) +
      row('接收与核销', `勾销本金 ${money(sz.off)} · 接收时估值 ${money(sz.in)}`) +
      row('累计重估', `${signed(sz.revalue ?? 0)} · 估值变动没有带来现金`) +
      row('出售与处置', sz.sold || sz.gain ? `实际收到 ${money(sz.sold)} · 处置净损益 ${signed(sz.gain)}` : '还没有处置记录') +
      row('全城的银行', `${mk(sz.home, '住房')};${mk(sz.land, '空地')}`) +
      sec(`手上的 ${sz.n}`, sz.list.length ? table(['什么', '在哪', '账面价', '收进来'], sz.list.map((x: J) => [esc(x.kind), link(x), money(x.mark), monthOf(x.since)])) : '<div class="sb-empty">没有</div>');
  };

  const bankView = (id: number): [string, Tab[]] => {
    const b = read(E.bank(id));
    const estate = b.premises?.estate ?? 0;
    const sz = b.seized;
    const seized = sz?.value ?? 0;
    const assets = b.cash + b.lent + (b.bonds ?? 0) + estate + seized;
    const interestDue = b.centralInterest ?? 0;
    const liabilities = b.deposits + b.central + interestDue;
    const head = `<h3>${esc(b.name)}${b.closed ? ' <span class="tag bad">倒闭</span>' : ''}</h3><p class="meta">${bankOwnerLine(b)}</p>` +
      grid(tile('家底', money(b.equity), '', b.equity < 0 ? 'bad' : ''), tile('柜上现金', money(b.cash)), tile('存款', money(b.deposits), `${b.nDepositors} 户${b.govDep > 0 ? ` · 国库 ${money(b.govDep)}` : ''}`), tile('贷出', money(b.lent)),
        ...(b.bonds > 0 ? [tile('国债', money(b.bonds))] : []), tile('央行借款本金', money(b.central), `再贷款 ${money(b.relend ?? 0)} · 救急款 ${money(b.rescue ?? b.central)}`), tile('央行欠息', money(interestDue), '已计息未实付,已从家底扣除'), tile('贷款年息', `${b.loanRate.toFixed(1)}%`), tile('存款年息', `${b.depRate.toFixed(1)}%`), tile('信任', `${Math.round(b.trust * 100)}`, '', b.trust < 0.5 ? 'bad' : ''));
    const pm = b.premises;
    return [head, [
      ['think', '在想什么', () => mind(b.mind)],
      ...(pm ? [['place', `地方${pm.branches.length ? ` · 分行 ${pm.branches.length}` : ''}`, () => bankPlace(pm, id)] as Tab,
        ['story', `大事记 ${pm.story.length}`, () => eventList(pm.story)] as Tab] : []),
      ['sheet', '资产负债', () => `<div class="sb-2">${donut([{ label: '柜上现金', value: b.cash }, { label: '国债', value: b.bonds ?? 0 }, ...b.kinds.map((k: J) => ({ label: k.kind, value: k.principal })), ...(estate ? [{ label: '自己的楼', value: estate }] : []), ...(seized ? [{ label: '收回的抵押资产', value: seized }] : [])], money, '资产')}${donut([{ label: '存款', value: b.deposits }, { label: '央行本金', value: b.central }, { label: '央行欠息', value: interestDue }, { label: '家底', value: Math.max(0, b.equity) }], money, '负债和家底')}</div>` +
        row('资产合计', money(assets)) + row('负债', money(liabilities)) +
        row('央行再贷款', `${money(b.relend ?? 0)} · 按基准年息 ${b.policy.toFixed(1)}%`) +
        row('央行救急款', `${money(b.rescue ?? b.central)} · 报价年息 ${(b.rescueRate ?? b.policy).toFixed(1)}% · 有余钱先偿还`) +
        row('央行欠息', `${money(interestDue)} · 有余钱先付息再还救急本金,不计复利`) +
        row('准备金率', pct(b.reserve)) + row('还能放', money(b.room))],
      ...(sz && (sz.n || sz.off || sz.sold) ? [['seized', `收回来的 ${sz.n}`, () => seizedPage(sz)] as Tab] : []),
      ['loans', '贷款', () => table(['种类', '笔数', '还欠', '平均年息', '逾期', '逾期的本金'], b.kinds.map((k: J) => [esc(k.kind), String(k.n), money(k.principal), `${k.rate.toFixed(1)}%`, k.late ? `<span class="bad">${k.late}</span>` : '0', money(k.latePrincipal)])) +
        sec('在逾期的', table(['借的人', '种类', '还欠', '拖了'], b.late.map((x: J) => [who(x.who), esc(x.kind), money(x.principal), `<span class="bad">${x.missed} 天</span>`])))],
      ['money', '收支', () => grid(tile('日均息差与处置', signed(b.opIncome ?? 0), '已扣资金成本，未扣人工'), tile('员工与行长日薪', money(b.staffCost ?? 0))) +
        '<p class="meta">借款只计实收利息，存款按实际入账息费；国债按合同日息估算，央行利息按应计。资产重估不计入这项收益。</p>' + statement(b.book, 'bank')],
      ...(b.company ? [['staff', '股东和员工', () => {
        const c = b.company;
        return row('背后的公司', who(c.firm)) + row('行长', `${who(c.boss)} · 一天 ${money(c.bossPay)}`) + row('员工', `${c.staff.length} 人 · 按客户要 ${c.desks} 张桌子`) +
          sec(`股东 ${c.nHolders}`, table(['股东', '占股'], c.holders.map((h: J) => [who(h.who), `${h.pct.toFixed(1)}%`]))) +
          sec('员工', table(['人', '一天工钱', '手艺'], c.staff.map((x: J) => [who(x.who), money(x.wage), x.skill.toFixed(2)])));
      }] as Tab] : []),
      ['people', '客户', () => sec('借得最多的', table(['借的人', '笔数', '还欠', '逾期'], b.borrowers.map((x: J) => [who(x.who), String(x.n), money(x.principal), x.missed ? `<span class="bad">${x.missed} 天</span>` : ''])), `显示 ${b.borrowers.length} 个借款客户，按未偿本金列出前 30 个，不是全部借款客户`) +
        sec('存得最多的', table(['储户', '存款'], b.depositors.map((x: J) => [who(x.who), money(x.deposit)])), `显示 ${b.depositors.length} 名在城个人储户，按存款列出前 20 名，不是全部存款客户`)],
    ]];
  };

  const hospitalView = (id: number, detail?: J): [string, Tab[]] => {
    if (!E.hospital2) return ['<h3>医院</h3>', [['none', '概况', () => '<div class="sb-empty">当前引擎尚未提供独立医院详情</div>']]];
    const h = detail === undefined ? read(E.hospital2(id)) : detail;
    if (!h?.hospital || h.missing) return ['<h3>医院</h3>', [['none', '概况', () => '<div class="sb-empty">没有这所医院</div>']]];
    locate = h.hospital.plot ?? null;
    const head = `<h3>${esc(h.hospital.name)}</h3><p class="meta">${h.public === false ? '民营' : h.provider ? '公立' : '尚未开诊'}医院${h.day != null ? ` · ${dateOf(h.day)}` : ''}</p>` +
      `<nav class="sb-facility-nav" aria-label="医院导航"><a data-tab="medical|hospitals">全部医院 →</a></nav>`;
    if (!h.enabled || !h.provider) return [head, [
      ['sum', '概况', () => hospitalSummary(h)],
      ...(h.enabled === true ? [['records', '诊疗历史', () => historyView('hospital', id, 'medical')] as Tab] : []),
    ]];
    return [head, [
      ['sum', '诊疗概况', () => hospitalSummary(h)],
      ['staff', '医生专家', () => hospitalStaff(h)],
      ['patients', '住院与待诊', () => hospitalPatients(h)],
      ['records', '诊疗历史', () => historyView('hospital', id, 'medical')],
      ['fees', '服务与费用', () => hospitalFees(h)],
      ['fin', '医院财报', () => hospitalFinance(h)],
    ]];
  };
  const schoolView = (id: number, detail?: J): [string, Tab[]] => {
    if (!E.school2) return ['<h3>学校</h3>', [['none', '概况', () => '<div class="sb-empty">当前引擎尚未提供独立学校详情</div>']]];
    const s = detail === undefined ? read(E.school2(id)) : detail;
    if (!s?.school || s.missing) return ['<h3>学校</h3>', [['none', '概况', () => '<div class="sb-empty">没有这所学校</div>']]];
    locate = s.school.plot ?? null;
    const head = `<h3>${esc(s.school.name)}</h3><p class="meta">${s.sampleDay != null ? `${dateOf(s.sampleDay)}的排课与实际到课` : '尚未排课'}</p>` +
      `<nav class="sb-facility-nav" aria-label="学校导航"><a data-tab="education|schools">全部学校 →</a></nav>`;
    return [head, [
      ['sum', '概况', () => schoolSummary(s)],
      ...(s.enabled ? [['students', '学生名单', () => schoolStudents(id)], ['courses', '课程与到课', () => schoolCourses(s)], ['teachers', '教师工时', () => schoolTeachers(s)]] as Tab[] : []),
    ]];
  };

  const govView = (): [string, Tab[]] => {
    const g = read(E.gov());
    const head = `<h3>市政府</h3>` +
      grid(tile('国库', money(g.treasury), '', '', ' data-tab="govt|fin"'), tile('公家员工', `${g.publicJobs} 人`, `一天工钱 ${money(g.payroll)}`), inflTile(g.inflation), tile('基准利率', `${g.policy.toFixed(1)}%`));
    return [head, [
      ['think', '在想什么', () => mind(g.mind)],
      ['money', '收支', () => statement(g.book, 'gov')],
      ['staff', '人手', () => sec('部门', deptPage(g)) + sec('机关', agencyPage(g))],
      ['inst', '机构', () => instPage(g)],
      ['medical', '医疗', () => medicalPage(g.medical)],
      ['education', '教育', () => educationPage(g.education)],
      ['traffic', '交通', () => trafficPage(g)],
      ['tender', '发包', () => tenderPage(g.tenders, g.grafts)],
      ['rescue', `保交楼 ${g.rescued.length}`, () => (g.rescued.length ? table(['楼', '进度'], g.rescued.map((r: J) => [plotLink(r.plot, r.name), esc(projectProgressText(r))])) : '<div class="sb-empty">没有</div>')],
    ]];
  };

  // 一个案子:小剧场(按幕演)、案卷、当事人(见 dash.rs 的 case2、eco 的 story.rs)
  const caseView = (id: number): [string, Tab[]] => {
    const none = (t: string): [string, Tab[]] => ['<h3>案子</h3>', [['none', '案子', () => `<div class="sb-empty">${t}</div>`]]];
    if (!E.case2) return none('这个版本的引擎还没有');
    const c = read(E.case2(id));
    if (c.missing) return none('这个案子太老,案卷没留下');
    locate = c.plot ?? null;
    const wage = c.kind === '讨薪';
    const acts: J[] = c.events ?? [];
    const caseNames = withCaseNames(textNames(), c);
    const caseText = (text: string) => linkify(text, caseNames);
    const open = ['拘留', '取保候审', '待开庭', '服刑', '调解', '执行中'].includes(c.stage);
    const play = () => `<div class="sb-play"><div class="scene why"><h5>起因</h5><p>${caseText(c.why)}</p></div>` +
      acts.map((e, i) => `<div class="scene"><h5><b>第 ${i + 1} 幕</b> ${esc(e.act)}<time>${esc(dateOf(e.day))}</time></h5><p>${caseText(e.text)}</p></div>`).join('') +
      (open ? `<div class="scene next"><p>未完待续……</p></div>` : '') + `</div>`;
    const file = () => table(['哪天', '这一步', '经过'], acts.map((e) => [dateOf(e.day), esc(e.act), `<span style="white-space:normal">${caseText(e.text)}</span>`]));
    const others = () => ((c.others as J[]).length ? sec('同一个人的别的案子', table(['哪天', '案子', '到哪一步'], (c.others as J[]).map((x) => [dateOf(x.day), caseLink(x.id, x.kind), stageTag(x.stage)]))) : '');
    if (c.civil) {
      // 民事案:原告告被告(个人、公司、银行、政府)
      const civilHead = `<h3>${esc(c.kind)}案 <small class="meta">案号 ${c.id}</small></h3>` +
        `<p class="meta">${stageTag(c.stage)} ${esc(dateOf(c.day))} · ${plotLink(c.plot, c.place)} · ${c.plaintiff ? who(c.plaintiff) : ''}${c.group > 1 ? ` 等 ${c.group} 户` : ''} 告 ${c.defendant ? who(c.defendant) : ''}</p>` +
        grid(tile('要赔', money(c.amount), `诉讼费 ${money(c.fee)}`),
          tile('原告的律师', c.lawyer ? who(c.lawyer) : '自己上庭', c.lawyer ? `${c.contingency ? '风险代理 · ' : ''}好坏 ${c.lawyerQ.toFixed(2)}` : ''),
          tile('被告的律师', c.other ? who(c.other) : '没请', c.other ? `好坏 ${c.otherQ.toFixed(2)}` : ''),
          tile('原告占几分理', pct(c.merit), '上帝视角'),
          tile('结果', esc(c.verdict ?? '还没了'), c.award ? `拿到 ${money(c.award)}${c.execLeft != null ? ` · 还差 ${money(c.execLeft)}` : ''}` : ''));
      const sides = () => sec('原告', row('谁', c.plaintiff ? who(c.plaintiff) : '') + (c.group > 1 ? row('一起告的', `${c.group} 户`) : '')) +
        sec('被告', row('谁', c.defendant ? who(c.defendant) : '')) + sec('起因', `<p>${caseText(c.why)}</p>`) + others();
      return [civilHead, [['play', '小剧场', play], ['file', `案卷 ${acts.length}`, file], ['who', '当事人', sides]]];
    }
    const head = `<h3>${esc(c.kind)}案 <small class="meta">案号 ${c.id}</small></h3>` +
      `<p class="meta">${stageTag(c.stage)} ${esc(dateOf(c.day))} · ${plotLink(c.plot, c.place)} · ${wage
        ? `原告 ${c.victim ? who(c.victim) : ''} 告 ${c.firm ? who(c.firm) : ''}(老板 ${who(c.offender)})`
        : `${who(c.offender)}${c.real ? ' <span class="tag bad">抓错了人</span>' : ''}${c.victim ? ` → ${who(c.victim)}` : c.firm ? ` → ${who(c.firm)}` : c.bank ? ` → ${esc(c.bank)}` : ''}`}</p>` +
      grid(tile(wage ? '原诉请工资本金' : '损失', wage ? money(c.amount) : c.loss ? money(c.loss) : '—', wage ? '登记诉请，不是已判金额或当前余欠' : esc(c.motive)),
        tile(wage ? '工人的律师' : '辩护律师', c.lawyer ? who(c.lawyer) : c.aid ? '法院指定' : '没请', c.lawyer ? `${c.aid ? '法律援助 · ' : ''}好坏 ${c.lawyerQ.toFixed(2)}` : ''),
        ...(c.other ? [tile('公司的律师', who(c.other), `好坏 ${c.otherQ.toFixed(2)}`)] : []),
        tile(wage ? '追偿进度' : '结果', wage ? esc(c.wageRecovery?.statusName ?? c.stage) : esc(c.verdict ?? (c.caught ? '在审' : '没抓到')), wage ? '判决与实际到账分开，见追偿明细' : ['拘留', '服刑'].includes(c.stage) && c.held ? `关在${esc(c.held)}` : ''));
    const people = () => (wage && c.victim ? sec('原告(讨薪的工人)', row('人', who(c.victim))) : '') +
      sec(wage ? '被告(欠薪的公司)' : '嫌犯', (wage && c.firm ? row('公司', who(c.firm)) : '') + row(wage ? '老板' : '人', who(c.offender)) + row('年纪', `${c.age} 岁`) + (c.record ? row('前科', `${c.record} 次`, 'bad') : '') +
        (c.custody ? row('眼下', `${custodyName(c.custody)}${c.custody !== 3 ? ` · ${esc(c.heldAt)}` : ''}`, 'bad') : '') + (c.here ? '' : row('', '<span class="meta">不在城里了</span>'))) +
      (c.real ? sec('真凶', row('人', who(c.real)) + row('', `<span class="meta">上帝视角${c.cleared ? ' · 已平反' : ''}</span>`)) : '') +
      (c.victim && !wage ? sec('受害人', row('人', who(c.victim))) : '') +
      sec('起因', `<p>${caseText(c.why)}</p>`) + others();
    return [head, [
      ...(wage ? [['recovery', '追偿与实际到账', () => wageRecoveryHTML(c.wageRecovery)] as Tab] : []),
      ['play', '小剧场', play],
      ['file', `案卷 ${acts.length}`, file],
      ['who', '当事人', people],
    ]];
  };

  const regionView = (id: number): [string, Tab[]] => {
    const R = read(E.regions());
    const r: J = R.list[id];
    const xs = monthsX(r.hist.pop.length);
    const head = `<h3>${esc(r.name)}</h3><p class="meta">邻城 · 离本城 ${r.km} 公里</p>` + grid(
      tile('人口', num(r.pop)), tile('一天工钱', `${r.wage.toFixed(0)} 元`), tile('一份日用', `${r.cpi.toFixed(1)} 元`),
      tile('工钱买得起', `${r.real.toFixed(2)} 份`, `本城 ${R.city.real.toFixed(2)} 份`), tile('钱', money(r.money)));
    return [head, [
      ['goods', '货和价', () => table(['货', '价', '运到本城', '本城的价', '今天做', '存货', '今天卖给本城', '今天从本城买', '人手'],
        r.goods.map((g: J) => [`${esc(g.name)}<small class="meta"> / ${esc(g.unit)}</small>`, num(g.price, g.price < 100 ? 1 : 0), num(g.landed, g.landed < 100 ? 1 : 0),
          g.city != null ? num(g.city, g.city < 100 ? 1 : 0) : '', num(g.made, 0), num(g.stock, 0), g.toCity ? num(g.toCity, 1) : '', g.fromCity ? num(g.fromCity, 1) : '', `${g.labor.toFixed(1)}%`]))],
      ['work', '干什么', () => bars(Object.entries(r.shares as Record<string, number>).map(([k, v]) => ({ label: k, value: v })), (v) => `${v.toFixed(1)}%`)],
      ['hist', '历史', () => xs.length >= 2
        ? sec('工钱和一份日用', lineChart(xs, [{ name: '一天工钱', vals: r.hist.wage }, { name: '一份日用', vals: r.hist.cpi }], { key: `rv:p${id}` })) +
          sec('和本城的买卖(每月)', lineChart(xs, [{ name: '卖给本城', vals: r.hist.toCity, fmt: money }, { name: '从本城买', vals: r.hist.fromCity, fmt: money }], { fmt: money, key: `rv:t${id}`, zero: true })) +
          sec('搬来搬去(每月)', lineChart(xs, [{ name: '从本城搬来', vals: r.hist.movedIn }, { name: '搬去本城', vals: r.hist.movedOut }], { key: `rv:m${id}`, zero: true }))
        : '<div class="sb-empty">还不到两个月</div>'],
    ]];
  };

  const inspector = guard(() => {
    const el = document.getElementById('insp')!;
    if (!sel) {
      relatedAttempt = null;
      el.hidden = true;
      return;
    }
    el.hidden = false;
    if (mapIntent != null && E.context2) {
      const intent = mapIntent; const ctx = read(E.context2(intent * 8));
      if (mapIntent === intent) {
        const preferred = (ctx.places ?? []).find((p: J) => p.kind === 'hospital' || p.kind === 'school') ?? (ctx.places ?? []).find((p: J) => p.kind === 'project');
        if (preferred) sel = { kind: preferred.kind, id: preferred.id };
        mapIntent = null;
      }
    }
    const related = relatedPlaces(sel);
    const back = trail.length ? `<a class="sb-back" data-back>${icon('back', 14)}返回</a>` : '';
    const views: Record<Kind, (id: number, detail?: J) => [string, Tab[]]> = { plot: plotView, person: personView, firm: firmView, project: projectView, unit: unitView, hospital: hospitalView, school: schoolView, market: marketView, bank: bankView, gov: govView, region: regionView, case: caseView };
    locate = null;
    // 编号在这座城里已经没有了(换了城、旧链接):引擎给 {missing:true},各页读字段会出错
    const probe: Partial<Record<Kind, (id: number) => string>> = { plot: (i) => E.plot(i), person: (i) => E.person2(i), firm: (i) => E.firm2(i), project: (i) => E.project2(i), unit: (i) => E.unit2(i),
      ...(E.hospital2 ? { hospital: (i: number) => E.hospital2!(i) } : {}), ...(E.school2 ? { school: (i: number) => E.school2!(i) } : {}) };
    let view: [string, Tab[]];
    try {
      const p = probe[sel.kind];
      const detail = p ? read(p(sel.id)) : undefined;
      view = p && detail.missing === true ? [`<h3>没有这一项</h3>`, []] : views[sel.kind](sel.id, detail);
    } catch (e) {
      if (e === PENDING) throw e;
      console.error('实体资料显示失败', { kind: sel.kind, id: sel.id }, e);
      view = [`<h3>资料暂时无法显示</h3><p class="meta">当前对象 #${sel.id}；请稍后重试。</p>`, []];
    }
    const [head, list] = view;
    if (['firm', 'hospital', 'school', 'gov', 'plot', 'project', 'unit', 'bank', 'case', 'region'].includes(sel.kind)) list.push(['history', '完整历史', () => historyView(sel!.kind, sel!.id)]);
    const loc = (locate != null ? `<button class="sb-loc" data-locate="${locate}" title="在地图上找到">${icon('locate', 14)}定位</button>` : '')
      + (playMode && playerId != null && !(sel.kind === 'person' && sel.id === playerId) ? `<button class="sb-loc" data-nav="person:${playerId}" title="回到自己的人物页(P)">回到我</button>` : '')
      + (playMode && playerId != null && sel.kind === 'person' && sel.id !== playerId ? `<button class="sb-loc sb-who-btn" data-who="${sel.id}">对这个人做什么</button>` : '');
    const canFollow = (sel.kind === 'person' || sel.kind === 'firm') && list.length > 0;
    const on = canFollow && followed(sel.kind, sel.id);
    const star = canFollow ? `<button class="sb-star${on ? ' on' : ''}" data-follow="${sel.kind}:${sel.id}">${icon('star', 14)}${on ? '已关注' : '关注'}</button>` : '';
    // 标题栏第一行:左边返回、下载,右边定位、关注、关闭(一排排开,不叠在一起)
    patch(el, paged(`i:${sel.kind}`, `<div class="sb-win-bar">${back}${downloadControl(sel)}<span class="sb-win-acts">${loc}${star}<button class="sb-x" data-close title="关闭(Esc)">${icon('close', 16)}</button></span></div>${related}<div class="sb-entity-head">${head}</div>`, list));
    queueRelated(sel);
  }, () => {
    if (!sel) return null;
    const names: Record<Kind, string> = { person: '人物', firm: '公司', hospital: '医院', school: '学校', plot: '地块', project: '楼栋', unit: '房屋', bank: '银行', gov: '政府', case: '案件', region: '地区', market: '市场' };
    const key = `i:${sel.kind}`, page = sub.get(key) ?? '';
    const label = [`${names[sel.kind]} #${sel.id}`, pageLabels.get(`${key}|${page}`)].filter(Boolean).join(' · ');
    return { host: document.getElementById('insp')!, context: { key: `${gen}|${key}:${sel.id}|${page}`, label, close: 'entity' } };
  });

  // ---------- 地图模式 ----------
  // 地图上每块地眼下的颜色(鼠标停上去的说明里用)
  let modeColors: (string | null)[] | null = null;
  const modes = guard(() => {
    const m = MODES.find((x) => x.k === mode) ?? MODES[0];
    let legend = '';
    if (mode >= 0) {
      if (!modeData) modeData = read(E.mapmode(mode));
      const v: (number | null)[] = modeData.v;
      const colors: (string | null)[] = new Array(v.length).fill(null);
      if (m.cat) {
        const cats: string[] = modeData.cats;
        v.forEach((x, i) => (colors[i] = x == null ? null : PALETTE[x % PALETTE.length]));
        legend = `<div class="sb-legend maplegend">${cats.map((c, i) => `<span><i style="background:${PALETTE[i % PALETTE.length]}"></i>${esc(c)}</span>`).join('')}</div>`;
      } else {
        const vals = v.filter((x): x is number => x != null).sort((a, b) => a - b);
        if (vals.length) {
          // 连续取色:按数值(差得多的按对数)或按排名,见 mapScale
          const sc = mapScale(vals, byRank);
          // 越高越差的(生活成本、失业、通勤、楼龄、裂缝、空置)用黄到红,有正有负的用红蓝两头,别的用黄绿到深蓝
          const ramp = sc.div ? DIV : m.bad ? SEQ_BAD : SEQ_VAL;
          v.forEach((x, i) => {
            if (x != null) colors[i] = rampAt(ramp, sc.at(x));
          });
          const grad = Array.from({ length: 17 }, (_, j) => rampAt(ramp, j / 16)).join(',');
          // 刻度:两头的贴边,中间的挤不下就不标(按字数估宽度,色条约 440 像素宽)
          const marks = sc.ticks.map((t) => {
            const text = m.fmt(t), p = Math.round(sc.at(t) * 1000) / 10;
            const w = [...text].reduce((a, ch) => a + (ch.charCodeAt(0) < 128 ? 6 : 10.5), 0) / 4.4;
            const [l, r] = p < 8 ? [p, p + w] : p > 92 ? [p - w, p] : [p - w / 2, p + w / 2];
            return { text, p, l, r, tr: p < 8 ? '0' : p > 92 ? '-100%' : '-50%' };
          });
          const last = marks[marks.length - 1];
          const kept = marks.filter((k, i) => i === 0 || i === marks.length - 1);
          for (const k of marks.slice(1, -1)) {
            const prev = kept[kept.length - 2] ?? kept[0];
            if (k.l >= prev.r + 2 && k.r <= last.l - 2) kept.splice(kept.length - 1, 0, k);
          }
          if (kept.length === 2 && kept[0].r > kept[1].l - 2) kept.pop();
          const ticks = kept.map((k) => `<span style="left:${k.p}%;transform:translateX(${k.tr})">${esc(k.text)}</span>`);
          legend = `<div class="sb-scale"><span>色阶</span>${['按数值', '按排名'].map((t, i) => `<button class="chip${(i === 1) === byRank ? ' on' : ''}" data-scale="${i}">${t}</button>`).join('')}${sc.note && !byRank ? `<em>${sc.note}</em>` : ''}</div>` +
            `<div class="sb-grad" style="background:linear-gradient(90deg,${grad})"></div><div class="sb-ticks">${ticks.join('')}</div>`;
        }
      }
      modeColors = colors;
      map.setOverlay(colors);
    } else {
      modeColors = null;
      map.setOverlay(huntLayer); // 玩家开着找房图层时照样画它
    }
    const groups = [...new Set(MODES.map((x) => x.group))];
    const box = document.getElementById('modes')!;
    box.hidden = !modesOpen;
    document.querySelector('#rail [data-maps]')?.classList.toggle('on', modesOpen || mode >= 0 || transit === 1 || relief > 0);
    map.setTransit(transit);
    map.setRelief(relief);
    // 地铁图层、楼高各一行;开着地铁图的列出每条线的颜色
    const subRow = `<div><span>地铁</span>${TRANSIT.map((t, i) => `<button class="chip${i === transit ? ' on' : ''}" data-transit="${i}" title="T">${t}</button>`).join('')}</div>` +
      `<div><span>楼高</span>${RELIEF.map((t, i) => `<button class="chip${i === relief ? ' on' : ''}" data-relief="${i}" title="H">${t}</button>`).join('')}</div>`;
    const lines = city?.lines ?? [];
    const subLegend = transit === 1
      ? `<div class="sb-legend maplegend">${lines.length ? lines.map((l: J, i) => `<span><i style="background:${lineColor(l.k ?? i + 1)}"></i>${esc(l.name)} · ${esc(l.state ?? (l.open ? '运营中' : '施工中'))}</span>`).join('') : '<span>还没有地铁</span>'}</div>`
      : '';
    patch(box, `<div class="sb-head"><h3>地图模式${mode >= 0 ? ` · ${esc(m.name)}` : transit === 1 ? ' · 地铁图' : ''}</h3><button class="sb-x" data-maps title="收起(M)">${icon('close', 15)}</button></div><div class="sb-mode-list">${subRow}${groups
      .map((g) => `<div>${g ? `<span>${g}</span>` : ''}${MODES.filter((x) => x.group === g).map((x) => `<button class="chip${x.k === mode ? ' on' : ''}" data-mode="${x.k}">${x.name}</button>`).join('')}</div>`)
      .join('')}</div>${legend}${subLegend}`);
  }, undefined, true);

  // 鼠标停在地图上:这块地是什么、谁的;开着地图模式就加上这一格的数
  const tipEl = document.getElementById('maptip')!;
  let tipAt: { x: number; y: number } | null = null;
  const mapTip = () => {
    const p = tipAt ? map.hoveredPlot() : null;
    if (!p || !tipAt) {
      tipEl.hidden = true;
      return;
    }
    let mv: { name: string; value: string; color: string | null } | undefined;
    // 站:叫什么、停哪几条线(开着地铁图、没开别的地图模式的时候)
    const st = stationIdx.get(`${p.x},${p.y}`);
    if (st && (transit === 1 || mode < 0)) mv = { name: '地铁站', value: `${st.name} · ${st.ks.join('、')} 号线`, color: lineColor(st.ks[0]) };
    else if (playMode && assetSpots.has(p.id)) {
      const x = assetSpots.get(p.id)!;
      mv = { name: '你的', value: `${x.lines.slice(0, 3).join(';')}${x.lines.length > 3 ? ` 等 ${x.lines.length} 样` : ''} · 值 ${money(x.value)}${x.income > 0 ? ` · 一天进 ${Math.round(x.income)} 元` : ''}`, color: null };
    }
    else if (mode >= 0 && modeData) {
      const m = MODES.find((x) => x.k === mode)!;
      const v = modeData.v[p.id];
      if (v != null) mv = { name: m.name, value: m.cat ? String(modeData.cats?.[v] ?? '') : m.fmt(v), color: modeColors?.[p.id] ?? null };
    }
    tipEl.innerHTML = plotTip(p, mv);
    tipEl.hidden = false;
    placeTip(tipEl, tipAt.x, tipAt.y);
  };
  map.onHover = (p, x, y) => {
    tipAt = p ? { x, y } : null;
    mapTip();
  };

  // 新出的大事在屏幕下方弹一条(暂停的时候不弹);换城以后从头记
  let newsSeen: string | null = null;
  const checkNews = guard(() => {
    const n: J[] = read(E.news());
    if (!n.length) return;
    const key = (x: J) => `${x.day}|${x.text}`;
    if (newsSeen == null) {
      newsSeen = key(n[0]);
      return;
    }
    const fresh: J[] = [];
    for (const x of n) {
      if (key(x) === newsSeen || fresh.length >= 3) break;
      fresh.push(x);
    }
    newsSeen = key(n[0]);
    const box = document.getElementById('toasts')!;
    for (const x of fresh.reverse()) {
      const go = x.plot != null ? `plot:${x.plot}` : x.firm != null ? `firm:${x.firm}` : x.person != null ? `person:${x.person}` : '';
      toast(box, `<span class="tag k-${esc(x.kind)}">${esc(x.kind)}</span><p>${linkify(x.text)}</p><time>${esc(monthOf(x.day))}</time>`, go);
    }
  });

  // ---------- K 线、深度图、曲线的手势(见 sbkline.ts、sbdepth.ts、sbchart.ts) ----------
  // K 线:悬停、长按拖选区间、拖动平移、滚轮和捏合缩放、区间滑条、范围按钮;重画股市页一帧最多一回
  installKline(() => stk, () => mePanel());
  installDepth();
  // 曲线:长按拖选一段看区间(每月末的市值和净资产那几张);重画以后照原样画回去
  installChartRange();
  // 手机上点一下(手指、笔):曲线出浮框,点别处收起(鼠标的照下面 mousemove 走;K 线、深度图的各自装了)
  document.body.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse') return;
    const el = (e.target as Element).closest?.('.sb-cw') as HTMLElement | null;
    document.querySelectorAll('.sb-cw').forEach((c) => { if (c !== el) hoverChart(c as HTMLElement, null); });
    const i = el ? hoverChart(el, e.clientX) : null;
    hover = el && i != null ? { id: el.dataset.chart!, i } : null;
  });
  // 从股市页点公司、人进去:查看器挪到股市页上面(两个窗口默认都在右边)
  document.getElementById('stocks')!.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('[data-go]')) raise(document.getElementById('insp')!);
  });

  // ---------- 点、键 ----------
  document.body.addEventListener('click', (e) => {
    const t = (e.target as HTMLElement).closest('details[data-key^="equity-owners:"] > summary,[data-ipo],[data-ipo-go],[data-go],[data-locate],[data-back],[data-close],[data-tab],[data-closetab],[data-kind],[data-sort],[data-filter],[data-speed],[data-reseed],[data-sky],[data-mode],[data-transit],[data-relief],[data-scale],[data-maps],[data-sub],[data-help],[data-follow],[data-nk],[data-cb],[data-act],[data-export],[data-hpage],[data-hsource],[data-archive],[data-origin],[data-medical-archive],[data-students],[data-medical-deaths],[data-pcmd],[data-me],[data-pstop],[data-psave],[data-here],[data-hunt],[data-amt-go],[data-nav],[data-who],[data-date],[data-lend],[data-site],[data-biz],[data-land],[data-credit],[data-govp],[data-govk],[data-knob],[data-fknob],[data-stk],[data-stk-go],[data-trend-retry],[data-medical-summary-retry],[data-related-retry],[data-equity-metric],[data-equity-owner],[data-equity-page],[data-equity-window],[data-equity-view],[data-equity-follow],[data-equity-time],[data-equity-play],[data-equity-step],[data-equity-rank-page]') as HTMLElement | null;
    if (!t) return;
    // 原生toggle稍后才派发；先同步本次展开，紧跟着调速/刷新不会用旧状态关回去。
    if (t.tagName === 'SUMMARY' && sel?.kind === 'firm') {
      const details = t.parentElement;
      if (details instanceof HTMLDetailsElement && details.firstElementChild === t && details.dataset.key === `equity-owners:${sel.id}`) {
        e.preventDefault(); details.open = !details.open;
        setEquityOwnersOpen(equityState(sel.id), details.open);
        return;
      }
    }
    const d = t.dataset;
    if (d.equityFirm !== undefined) {
      const firm = Number(d.equityFirm);
      if (sel?.kind !== 'firm' || sel.id !== firm) return;
      const state = equityState(firm);
      if (d.equityTime !== undefined && t.tagName === 'INPUT') return;
      if (d.equityView && ['rank', 'stack', 'lines', 'company'].includes(d.equityView)) {
        state.view = d.equityView as typeof state.view; state.playing = false;
      }
      else if (d.equityFollow !== undefined) followEquityLeaders(state);
      else if (d.equityPlay !== undefined) toggleEquityPlayback(state);
      else if (d.equityStep !== undefined) {
        const direction = Number(d.equityStep);
        if (direction !== -1 && direction !== 1) return;
        state.playing = false;
        stepEquityTimeline(state, direction);
      }
      else if (d.equityTime !== undefined) setEquityTimelineDay(state, Number(d.equityTime));
      else if (d.equityMetric && EQUITY_METRICS.some(([key]) => key === d.equityMetric)) { state.metric = d.equityMetric as typeof state.metric; state.playing = false; state.rankOffset = 0; }
      else if (d.equityRankPage !== undefined) {
        const offset = Number(d.equityRankPage);
        if (!Number.isSafeInteger(offset) || offset < 0 || offset > 0xffffffff) return;
        state.rankOffset = offset; state.playing = false;
      }
      else if (d.equityOwner) toggleEquityOwner(state, d.equityOwner);
      else {
        const value = Number(d.equityPage ?? d.equityWindow);
        if (!Number.isSafeInteger(value) || value < 0 || value > 0xffffffff) return;
        if (d.equityPage !== undefined) state.ownerOffset = value;
        else state.historyOffset = value;
      }
      return inspector();
    }
    if (d.relatedRetry !== undefined) {
      if (!sel || d.relatedRetry !== relatedIdentity(sel)) return;
      requestRelated(sel);
      return inspector();
    }
    if (d.medicalSummaryRetry !== undefined) {
      ask([E.gov()], requestQueryPanels);
      return dashboard();
    }
    if (d.trendRetry !== undefined) {
      seriesCache = null;
      ask([E.series()], requestQueryPanels);
      return top();
    }
    if (d.nav) {
      const [k, v] = d.nav.split(':');
      return navTo(k as 'person' | 'firm', Number(v));
    }
    if (d.stk) {
      // 股市页:玩家模式开着就停(要拍板),看城市照走;行情、每家公司的详情各自记着滚到哪
      const [k, v] = d.stk.split(':');
      const pause = (on: boolean) => { if (playMode) hold(on); };
      const pane = document.getElementById('stocks')!;
      const page = () => (stk.tab === 'firm' ? `firm:${stk.firm}` : stk.tab);
      const was = page();
      if (stocksOpen) stocksScroll.set(was, pane.scrollTop);
      if (k === 'open') { stocksOpen = true; pause(true); }
      else if (k === 'toggle') { stocksOpen = !stocksOpen; pause(stocksOpen); }
      else if (k === 'close') { stocksOpen = false; pause(false); }
      else if (k === 'tab') { stk.tab = v as StockState['tab']; stocksOpen = true; pause(true); }
      else if (k === 'firm') { if (stk.firm !== Number(v)) { stk.kz = 0; stk.ko = 0; } stk.firm = Number(v); stk.tab = 'firm'; stocksOpen = true; pause(true); }
      else if (k === 'sort') { if (stk.sort === v) stk.dir = -stk.dir; else { stk.sort = v; stk.dir = -1; } }
      else if (k === 'range') { stk.range = v === 'month' ? 'month' : v === 'week' ? 'week' : 'day'; stk.kz = 0; stk.ko = 0; }
      else if (k === 'redup') { stk.redUp = v === '1'; saveRedUp(stk.redUp); }
      else if (k === 'kreset') { stk.kz = 0; stk.ko = 0; }
      mePanel();
      if (page() !== was || k === 'open' || k === 'toggle') pane.scrollTop = stocksScroll.get(page()) ?? 0;
      return;
    }
    if (d.ipo) {
      // 过往招股按哪一列排:点同一列正着倒着换
      const [k, v] = d.ipo.split(':');
      if (k === 'sort') { if (stk.ipo.sort === v) stk.ipo.dir = -stk.ipo.dir; else { stk.ipo.sort = v; stk.ipo.dir = -1; } }
      return mePanel();
    }
    if (d.ipoGo) {
      // 认购(250 招股编号、元);发起(252 公司、要多少元,再 253 最少几成 | 期限 << 8 | 用途 << 16、每股多少分)
      const [k, vs] = d.ipoGo.split(':'); const id = Number(vs);
      if (k === 'buy') {
        const box = document.querySelector<HTMLInputElement>(`input[data-ipo-amt="${id}"]`);
        const v = Math.floor(Number(box?.value ?? 0));
        if (!Number.isFinite(v) || v <= 0) { box?.focus(); return; }
        return sendCmd(250, id, v, `认购 ${v} 元`);
      }
      const form = document.querySelector<HTMLElement>(`[data-ipo-form="${id}"]`);
      if (!form) return;
      const f = ipoForm(form);
      if (!(f.want > 0)) { form.querySelector<HTMLInputElement>('[data-ipo-in="want"]')?.focus(); return; }
      const minPct = Math.max(0, Math.min(100, Math.round(f.min / f.want * 100)));
      cmdQueue = [[253, (minPct | (Math.min(255, f.days) << 8) | (f.use << 16)) >>> 0, Math.round(f.price * 100), '发起招股']];
      return sendCmd(252, id, f.want, '招股要多少');
    }
    if (d.stkGo) {
      // 挂单:先定股数(186),再挂价(184 求购 / 64 卖);股数都得填。按股数买(188)从要价低的一路买到够
      const [k, fs] = d.stkGo.split(':'); const f = Number(fs);
      const val = (n: string) => Number(document.querySelector<HTMLInputElement>(`input[data-stk-in="${n}:${f}"]`)?.value ?? 0);
      const box = (n: string) => document.querySelector<HTMLInputElement>(`input[data-stk-in="${n}:${f}"]`);
      const need = (n: string, v: number) => { if (v > 0) return true; box(n)?.focus(); return false; };
      if (k === 'bidmkt') {
        // 按股数买:从要价低的股东一路买上去,买够或者钱花完(见 eco/src/bourse.rs 的 player_buy_walk)
        const q = Math.floor(val('mkt'));
        if (!need('mkt', q)) return;
        return sendCmd(188, f, q, `买 ${q} 股`);
      }
      // 卖一成、一半、全卖:股数按手上的算,每股要价得先填
      const part = /^askpart(\d+)$/.exec(k);
      const mine = Number(d.mine ?? 0);
      const isAsk = k === 'ask' || !!part;
      const p = val(isAsk ? 'ask' : 'bid');
      const q = part ? mine * Number(part[1]) / 1000 : Math.floor(val(isAsk ? 'askq' : 'bidq'));
      if (!need(isAsk ? 'ask' : 'bid', p) || !need(isAsk ? 'askq' : 'bidq', q)) return;
      // 卖的股数按百分之一股定(194;散户手上常常不到一股),全卖的定 0(不限,手上有多少卖多少);求购的按整股(186)
      const qty: [number, number, number, string] = isAsk ? [194, f, part?.[1] === '1000' ? 0 : Math.max(1, Math.round(Math.min(q, mine) * 100)), '定股数'] : [186, f, Math.max(1, Math.ceil(q)), '定股数'];
      cmdQueue = [qty, [isAsk ? 64 : 184, f, Math.round(p * 100), isAsk ? '挂单卖' : '挂单求购']];
      const [c, t, v, l] = cmdQueue.shift()!;
      return sendCmd(c, t, v, l);
    }
    if (d.site) {
      if (d.site === 'close') { siteId = null; hold(false); }
      else { if (siteId !== Number(d.site)) siteFloor = null; siteId = Number(d.site); whoId = null; herePlot = null; hold(true); }
      return mePanel();
    }
    if (d.biz) {
      if (d.biz === 'close') { bizOpen = false; hold(false); }
      else { bizOpen = true; landOpen = false; siteId = null; whoId = null; herePlot = null; hold(true); }
      return mePanel();
    }
    if (d.govp) {
      if (d.govp === 'close') { govOpen = false; hold(false); }
      else { govOpen = true; creditOpen = false; bizOpen = false; landOpen = false; siteId = null; whoId = null; herePlot = null; hold(true); }
      return mePanel();
    }
    if (d.govk) {
      // 报名前选政纲:点着换,报名的时候一起报
      const [k, v] = d.govk.split(':');
      govState[k as keyof GovState] = Number(v);
      return mePanel();
    }
    if (d.credit) {
      if (d.credit === 'close') { creditOpen = false; hold(false); }
      else { creditOpen = true; bizOpen = false; landOpen = false; siteId = null; whoId = null; herePlot = null; hold(true); }
      return mePanel();
    }
    if (d.land) {
      if (d.land === 'close') { landOpen = false; hold(false); }
      else { landOpen = true; bizOpen = false; siteId = null; whoId = null; herePlot = null; hold(true); }
      return mePanel();
    }
    if (d.fknob) {
      // 一层的一样做法:「第几层 × 1000000 + 哪一样 × 10000 + 数」(见 eco/src/player_site.rs 的 174)
      const [pid, n, field] = d.fknob.split(':').map(Number);
      const v = Math.floor(Number(document.querySelector<HTMLInputElement>(`input[data-fknob-in="${d.fknob}"]`)?.value ?? 0));
      if (!Number.isFinite(v) || v <= 0 || v >= 10000) return;
      return sendCmd(174, pid, n * 1_000_000 + field * 10000 + v, `第 ${n} 层改做法 ${v}`);
    }
    if (d.knob) {
      // 工地的一样做法:读旁边那一格的数,编成「哪一样 × 10000 + 数」(见 eco/src/player.rs 的 172)
      const [pid, field] = d.knob.split(':').map(Number);
      const v = Math.floor(Number(document.querySelector<HTMLInputElement>(`input[data-knob-in="${d.knob}"]`)?.value ?? 0));
      if (!Number.isFinite(v) || v <= 0 || v >= 10000) return;
      return sendCmd(172, pid, field * 10000 + v, `改做法 ${v}`);
    }
    if (d.who) {
      if (d.who === 'close') { whoId = null; hold(false); }
      else { whoId = Number(d.who); herePlot = null; hold(true); }
      return mePanel();
    }
    if (d.date) {
      const [k, v] = d.date.split(':');
      if (k === 'sort') dates.sort = v as DateState['sort'];
      else if (k === 'only') dates.only = v as DateState['only'];
      return mePanel();
    }
    if (d.lend) {
      // 借钱给人:金额、年息、期限(年息放进 target 的高八位,见 eco/src/social.rs)
      const [id, k] = d.lend.split(':').map(Number);
      const v = Math.floor(Number(document.querySelector<HTMLInputElement>(`input[data-amt="lend:${id}"]`)?.value ?? 0));
      const rate = Number(document.querySelector<HTMLSelectElement>(`select[data-rate="${id}"]`)?.value ?? 6);
      if (!Number.isFinite(v) || v <= 0) return;
      return sendCmd(144 + k, (id | (rate << 24)) >>> 0, v, `借出 ${v} 元`);
    }
    if (d.pcmd) {
      if (runtimeFailed || pcmdPending != null || !playMode) return;
      // 一个按钮办几条的用分号隔开(比如失信页的「全部还清」):先办第一条,办成了接着办下一条
      const label = (t.innerText || '').split('\n')[0].slice(0, 24);
      const [first, ...rest] = d.pcmd.split(';').map((c) => c.split(':').map(Number));
      cmdQueue = rest.map(([c, tg, v]) => [c, tg, v, label] as [number, number, number, string]);
      const [code, target, value] = first;
      sendCmd(code, target, value, label);
      // 暂停卡上选了:办成了、卡上也没有别的要选的,当作看过了,接着走(见 pcmded)
      if (t.closest('#stop')) resumeAfter = ![...document.querySelectorAll('#stop .sb-stop-item.ask')].some((x) => !x.contains(t));
      return;
    }
    if (d.amtGo) {
      // 填金额的:读旁边那一格,一下办完
      const box = document.querySelector<HTMLInputElement>(`input[data-amt="${d.amtSrc ?? d.amtGo}"]`);
      const v = Math.floor(Number(box?.value ?? 0) * Number(d.mul ?? 1) + 1e-6);
      if (!Number.isFinite(v) || v <= 0) { box?.focus(); return; }
      const [code, target] = d.amtGo.split(':').map(Number);
      // 价钱按分的(data-mul)写回填的那个数
      const shown = d.mul ? Number(box?.value ?? 0) : v;
      sendCmd(code, target, v, `${d.label ?? ''} ${shown} 元`);
      if (t.closest('#stop')) resumeAfter = ![...document.querySelectorAll('#stop .sb-stop-item.ask')].some((x) => !x.contains(t));
      return;
    }
    if (d.me) {
      const [k, v] = [d.me.slice(0, 1), d.me.slice(2)];
      if (k === 't') { meTab = v; meList = null; }
      else if (k === 'l') { meList = meList === v ? null : v; }
      return mePanel();
    }
    if (d.hunt) {
      const [k, v] = d.hunt.split(':');
      if (k === 'toggle') { hunting = !hunting; hold(hunting); }
      else if (k === 'close') { hunting = false; hold(false); }
      else if (k === 'help') hunt.help = !hunt.help;
      else if (k === 'show') hunt.show = v as HuntState['show'];
      else if (k === 'sort') hunt.sort = v as HuntState['sort'];
      else if (k === 'max') hunt.max = Number(v);
      return mePanel();
    }
    if (d.here) {
      if (d.here === 'close') { herePlot = null; hold(false); }
      else if (d.here === 'more') hereFull = !hereFull;
      else if (d.here.startsWith('bt:')) { hereType = d.here.slice(3) || null; hereFull = true; }
      else if (d.here.startsWith('open:')) { firmPlace = Number(d.here.slice(5)); meTab = 'biz'; meList = 'lines'; herePlot = null; hold(false); }
      else if (d.here.startsWith('plot:')) { herePlot = Number(d.here.slice(5)); hereFull = true; hold(true); map.select(herePlot); map.focus(herePlot); }
      return mePanel();
    }
    if (d.pstop) {
      if (d.pstop === 'ok') { resumeFromStop(); return mePanel(); }
      ackSeq = Math.max(stopSeq, lastAlertSeq);
      worker.postMessage({ t: 'pack', gen, seq: stopSeq });
      if (d.pstop === 'look') meTab = 'log'; else { speed = lastSpeed || 1; top(); }
      return mePanel();
    }
    if (d.psave === 'export') {
      savePlay();
      const save = loadSave();
      if (!save) return;
      const a = downloadPanel.show(new Blob([JSON.stringify(save, null, 1)], { type: 'application/json;charset=utf-8' }), `存档-${save.name}-种子${save.seed}-第${save.day}天.json`, 'json');
      try { a.click(); } catch { /* 链接还在 */ }
      return;
    }
    if (d.psave === 'quit') { savePlay(); location.href = location.pathname; return; }
    if (d.students !== undefined) {
      if (t instanceof HTMLButtonElement && t.disabled) return;
      const [rawId, action, cursor] = d.students.split(':'); const id = Number(rawId);
      if (sel?.kind !== 'school' || sel.id !== id || schoolRoster.id !== id) return;
      if (action === 'prev' && schoolRoster.stack.length) schoolRoster.start = schoolRoster.stack.pop()!;
      else if (action === 'next' && cursor !== '' && Number.isSafeInteger(Number(cursor)) && Number(cursor) > schoolRoster.start && Number(cursor) <= 0xffff_ffff) {
        schoolRoster.stack.push(schoolRoster.start); schoolRoster.start = Number(cursor);
      } else if (action === 'first') { schoolRoster.start = 0; schoolRoster.stack = []; }
      else return;
      return inspector();
    }
    if (d.medicalDeaths !== undefined) {
      if (deathDirectory.navigate(gen, d.medicalDeaths)) {
        const c = deathDirectory.cursor(gen); if (E.medical_deaths) cache.delete(E.medical_deaths(c.day, c.id));
        return dashboard();
      }
      return;
    }
    if (d.medicalArchive !== undefined) {
      const personId = Number(d.medicalArchive);
      if (sel?.kind !== 'person' || sel.id !== personId) { e.preventDefault(); return; }
      opened.add(medicalArchiveKey(personId)); inspector(); return;
    }
    if (d.archive === 'enable') { setHistoryPref(true); worker.postMessage({ t: 'archiveEnable', gen }); return; }
    if (d.archive === 'later') { setHistoryPref(false); dashboard(); return; }
    if (d.archive) { worker.postMessage({ t: d.archive === 'retry' ? 'archiveRetry' : 'archiveMemory', gen }); return; }
    if (d.export) {
      if (runtimeFailed) { toast(document.getElementById('toasts')!, '<p>引擎已停止，无法再读取完整档案；已生成的下载文件仍可保存。</p>', ''); return; }
      const [kind, id, format] = d.export.split(':'); const request = ++qid;
      if (!exportDownloads!.begin({ id: request, gen, kind, entityId: Number(id), format: format || 'json' })) return;
      worker.postMessage({ t: 'export', gen, id: request, kind, entityId: Number(id) });
      toast(document.getElementById('toasts')!, archiveState.state === 'off' ? '<p>正在准备下载：完整历史未开启，只含当前资料</p>' : '<p>正在准备下载：按完整游戏日读取当前资料和本城已归档历史</p>', ''); return;
    }
    if (d.act) {
      if (runtimeFailed || speed !== 0 || actionPending != null) return;
      if (actionSeq >= 0xffff_ffff) { toast(document.getElementById('toasts')!, '<p>本城操作编号已用尽，请先导出进度。</p>', ''); return; }
      flushCityPush();
      const [actor, action, target, value] = d.act.split(':').map(Number);
      const id = ++actionSeq; actionPending = id;
      worker.postMessage({ t: 'act', gen, id, day: engineDay, actor, action, target: Number.isFinite(target) ? target : 0xffff_ffff, value: value || 0 });
      return inspector();
    }
    if (d.origin !== undefined) {
      historySources.set(`person:${Number(d.origin)}`, 'origin'); sub.set('i:person', 'history'); return inspector();
    }
    if (d.hsource !== undefined) {
      if (!sel) return; historySources.set(`${sel.kind}:${sel.id}`, d.hsource); return inspector();
    }
    if (d.hpage) {
      const h = histories.get(d.hkey!); if (!h || h.pending != null) return;
      if (d.hpage === 'next' && h.page?.nextBefore) { h.stack.push(h.before); h.before = h.page.nextBefore; }
      else if (d.hpage === 'prev' && h.stack.length) h.before = h.stack.pop();
      else if (d.hpage !== 'refresh') return;
      h.page = null; return inspector();
    }
    if (d.nk !== undefined) { newsKind = d.nk; return dashboard(); }
    if (d.cb !== undefined) {
      const [k, v] = d.cb.split(':');
      if (k === 'more') cbShow += 200;
      else { cb[k] = v ?? ''; cbShow = 200; }
      return dashboard();
    }
    if (d.speed !== undefined) { if (runtimeFailed) return; speed = Number(d.speed); if (speed) lastSpeed = speed; else flushCityPush(); top(); return inspector(); }
    if (d.reseed !== undefined) {
      // 不弹框:眼下这一局先存进「恢复上一局」(见 sbplayer.ts 的 pushPrevSave)
      if (playMode) { savePlay(); pushPrevSave(loadSave()); }
      seed = 1 + Math.floor(Math.random() * 1e6); sel = null; trail.length = 0; newsSeen = null;
      if (playMode) playMode = { kind: playMode.kind, log: [], name: '', enter: playMode.enter, until: playMode.enter, rules: RULES, bits: [] };
      inspector(); return start();
    }
    if (d.sky !== undefined) { skyIdx = (skyIdx + 1) % SKIES.length; return top(); }
    if (d.help !== undefined) { const h = document.getElementById('help')!; h.hidden = !h.hidden; return; }
    if (d.follow) {
      const [k, v] = d.follow.split(':');
      const id = Number(v);
      if (followed(k, id)) {
        follows = follows.filter((x) => !(x.kind === k && x.id === id));
      } else {
        const x: Follow = { kind: k as Follow['kind'], id, name: '' };
        const result = followData(x);
        x.name = result.state === 'ready' ? result.data.name ?? '' : '';
        follows.push(x);
        followSeen.delete(`${k}:${id}`);
      }
      saveFollows();
      inspector();
      return tab === 'follow' ? dashboard() : undefined;
    }
    if (d.close !== undefined) { sel = null; trail.length = 0; map.select(null); return inspector(); }
    if (d.locate !== undefined) return map.focus(Number(d.locate));
    if (d.back !== undefined) { sel = trail.pop() ?? null; inspector(); return toTop('insp'); }
    if (d.closetab !== undefined) { tab = null; return dashboard(); }
    if (d.tab) {
      // data-tab="面板|页":打开面板,停在那一页
      const [tb, pg] = d.tab.split('|');
      if (pg) sub.set(`d:${tb}`, pg);
      tab = tab === tb && t.closest('#rail') ? null : tb;
      dashboard();
      return toTop('dash');
    }
    if (d.mode !== undefined) { mode = Number(d.mode); modeData = null; return modes(); }
    if (d.maps !== undefined) { modesOpen = !modesOpen; return modes(); }
    if (d.transit !== undefined) { transit = Number(d.transit); return modes(); }
    if (d.relief !== undefined) { relief = Number(d.relief); return modes(); }
    if (d.scale !== undefined) { byRank = d.scale === '1'; saveUI(); return modes(); }
    if (d.sub) {
      // 换一页:记下这一页滚到哪,回来的时候接着看
      const [key, id] = d.sub.split('|');
      const panel = t.closest('#dash,#insp') as HTMLElement;
      const body = panel.querySelector('.sb-body') as HTMLElement | null;
      if (body) scrolls.set(body.dataset.page!, body.scrollTop);
      sub.set(key, id);
      if (panel.id === 'dash') dashboard();
      else inspector();
      const nb = panel.querySelector('.sb-body') as HTMLElement | null;
      if (nb) nb.scrollTop = scrolls.get(nb.dataset.page!) ?? 0;
      return;
    }
    if (d.kind) {
      if (d.kind === 'all') ft.kinds.clear();
      else if (ft.kinds.has(d.kind)) ft.kinds.delete(d.kind);
      else ft.kinds.add(d.kind);
      tab = 'firms';
      return dashboard();
    }
    if (d.filter) {
      if (d.filter === 'gain' || d.filter === 'loss') ft.profit = ft.profit === d.filter ? 'all' : d.filter;
      if (d.filter === 'debt') ft.debt = !ft.debt;
      if (d.filter === 'trouble') ft.trouble = !ft.trouble;
      if (d.filter === 'big') ft.minStaff = ft.minStaff === 5 ? 1 : 5;
      if (d.filter === 'huge') ft.minStaff = ft.minStaff === 20 ? 1 : 20;
      return dashboard();
    }
    if (d.sort) {
      const [key, c] = d.sort.split(':');
      const col = Number(c);
      const st = key === 'firms' ? ft : (sorts[key] ??= { col, dir: -1 });
      if (st.col === col) st.dir = -st.dir;
      else { st.col = col; st.dir = -1; }
      dashboard();
      return inspector();
    }
    if (d.go) {
      const [k, v] = d.go.split(':');
      if (d.view) sub.set(`i:${k}`, d.view);
      if (k === 'plot') map.focus(Number(v));
      return go({ kind: k as Kind, id: Number(v) });
    }
  });
  document.body.addEventListener('change', (e) => {
    const el = e.target as HTMLSelectElement;
    if (!el.matches('[data-related-select]') || !el.value) return;
    const [kind, id] = el.value.split(':'); go({ kind: kind as Kind, id: Number(id) });
  });
  // 展开、收起的明细记下来(刷新以后还是那样)
  document.body.addEventListener('toggle', (e) => {
    const el = e.target as HTMLDetailsElement;
    const k = el.dataset?.key;
    if (!k) return;
    if (k.startsWith('equity-owners:')) {
      if (!el.isConnected || sel?.kind !== 'firm' || k !== `equity-owners:${sel.id}`) return;
      setEquityOwnersOpen(equityState(sel.id), el.open);
    }
    const rawHistory = el.dataset.historyRaw ?? el.dataset.historyDetail;
    if (rawHistory && (!el.isConnected || !sel || rawHistory !== `${sel.kind}:${sel.id}`)) return;
    // 换人后旧节点可能迟到触发toggle，不能清掉原人物记住的展开状态。
    if (k.startsWith('personFinance:') && (!el.isConnected || sel?.kind !== 'person' || k !== `personFinance:${sel.id}`)) return;
    if (k.startsWith('companyOverview:') && (!el.isConnected || sel?.kind !== 'firm' || k !== `companyOverview:${sel.id}`)) return;
    if (k.startsWith('placeChoices:') && (!el.isConnected || !sel || k !== `placeChoices:${sel.kind}:${sel.id}`)) return;
    const medicalArchive = k.startsWith('medicalArchive:');
    if (medicalArchive && (!el.isConnected || sel?.kind !== 'person' || k !== medicalArchiveKey(sel.id))) return;
    const medicalCase = /^medicalCase:(\d+):(\d+)$/.exec(k);
    if (k.startsWith('medicalCase:') && (!medicalCase || !el.isConnected || sel?.kind !== 'person' || Number(medicalCase[1]) !== sel.id)) return;
    const wasOpen = opened.has(k);
    if (el.open) opened.add(k);
    else opened.delete(k);
    if ((medicalArchive || medicalCase || rawHistory) && wasOpen !== el.open) inspector();
  }, true);
  // 搜索框:打字时只刷新表,光标不丢
  document.body.addEventListener('input', (e) => {
    const el = e.target as HTMLInputElement;
    if (el.dataset.equityTime !== undefined) {
      const firm = Number(el.dataset.equityFirm);
      if (sel?.kind !== 'firm' || sel.id !== firm) return;
      const state = equityState(firm); state.playing = false;
      setEquityTimelineDay(state, Number(el.value));
      return inspector();
    }
    // 招股的格子:按钮上的字跟着改
    if (el.dataset.ipoAmt !== undefined || el.dataset.ipoIn !== undefined) { const root = document.getElementById('stocks'); if (root) syncIpo(root); return; }
    const k = el.dataset.input;
    if (!k) return;
    if (k === 'firms') ft.q = el.value;
    if (k === 'people') peopleQ = el.value;
    if (k === 'cases') { cb.q = el.value; cbShow = 200; }
    const pos = el.selectionStart;
    dashboard();
    const again = document.querySelector(`[data-input="${k}"]`) as HTMLInputElement | null;
    if (again) { again.focus(); again.setSelectionRange(pos, pos); }
  });
  // 图上看数:竖线和数值框
  document.body.addEventListener('mousemove', (e) => {
    const el = (e.target as HTMLElement).closest('.sb-cw') as HTMLElement | null;
    document.querySelectorAll('.sb-cw').forEach((c) => {
      if (c !== el) hoverChart(c as HTMLElement, null);
    });
    if (!el) { hover = null; return; }
    const i = hoverChart(el, e.clientX);
    hover = i == null ? null : { id: el.dataset.chart!, i };
  });
  document.body.addEventListener('change', (e) => {
    const t = e.target as HTMLInputElement;
    if ((t as HTMLElement).dataset.pplace !== undefined) { firmPlace = Number((t as unknown as HTMLSelectElement).value); mePanel(); return; }
    if (t.dataset.huntLayer !== undefined) {
      hunt.layer = t.checked;
      if (hunt.layer && mode >= 0) { mode = -1; modes(); }
      if (!hunt.layer) { huntLayer = null; if (mode < 0) map.setOverlay(null); }
      mePanel(); return;
    }
    if (t.dataset.stkLine !== undefined) { stk.line = (t as unknown as HTMLSelectElement).value; mePanel(); return; }
    if (t.dataset.stkDist !== undefined) { stk.district = (t as unknown as HTMLSelectElement).value; mePanel(); return; }
    if (t.dataset.stkMine !== undefined) { stk.mineOnly = t.checked; mePanel(); return; }
    if (t.dataset.stkAll !== undefined) { stk.allFirms = t.checked; mePanel(); return; }
    if (t.dataset.ipoFirm !== undefined) { stk.ipo.firm = Number((t as unknown as HTMLSelectElement).value); mePanel(); return; }
    if (t.dataset.assetLayer !== undefined) {
      assetLayer = t.checked;
      if (assetLayer) { hunt.layer = false; huntLayer = null; if (mode >= 0) { mode = -1; modes(); } }
      else if (mode < 0) map.setOverlay(null);
      mePanel(); return;
    }
    if (t.dataset.pfirmauto !== undefined && playMode && pcmdPending == null && !runtimeFailed) {
      sendCmd(30, 0, t.checked ? 1 : 0, t.checked ? '托管给经理' : '自己管公司'); return;
    }
    if (t.dataset.pauto === undefined || !playMode || pcmdPending != null || runtimeFailed) return;
    sendCmd(5, 0, t.checked ? 1 : 0, t.checked ? '开自动买日用' : '关自动买日用');
  });
  document.addEventListener('keydown', (e) => {
    if (['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON', 'SUMMARY'].includes((e.target as HTMLElement).tagName)) return;
    if (runtimeFailed && (e.code === 'Space' || /^Digit[1-4]$/.test(e.code))) { e.preventDefault(); return; }
    // 玩家模式:P 打开自己的人物页,Shift+1–9 打开第几家公司(1–4 留给倍速)
    if (playMode && playerId != null && e.code === 'KeyP' && !e.metaKey && !e.ctrlKey) { navTo('person', playerId); return; }
    if (playMode && e.shiftKey && /^Digit[1-9]$/.test(e.code)) { const f = playerFirms[Number(e.code.slice(5)) - 1]; if (f != null) navTo('firm', f); return; }
    if (e.code === 'Space') { e.preventDefault(); if (speed) { lastSpeed = speed; speed = 0; flushCityPush(); } else speed = lastSpeed || 1; top(); return inspector(); }
    if (/^Digit[1-4]$/.test(e.code)) { speed = speeds[Number(e.code.slice(5))]; lastSpeed = speed; top(); return inspector(); }
    if (e.code === 'KeyM') { modesOpen = !modesOpen; return modes(); }
    if (e.code === 'KeyT') { transit = (transit + 1) % 3; return modes(); }
    if (e.code === 'KeyH') { relief = (relief + 1) % 3; return modes(); }
    const help = document.getElementById('help')!;
    if (e.key === '?') { help.hidden = !help.hidden; return; }
    if (e.code === 'Escape') {
      if (playMode && stocksOpen) { stocksOpen = false; hold(false); mePanel(); return; }
      if (!playMode && stocksOpen) { stocksOpen = false; mePanel(); return; }
      if (playMode && siteId != null) { siteId = null; hold(false); mePanel(); return; }
      if (playMode && bizOpen) { bizOpen = false; hold(false); mePanel(); return; }
      if (playMode && landOpen) { landOpen = false; hold(false); mePanel(); return; }
      if (playMode && creditOpen) { creditOpen = false; hold(false); mePanel(); return; }
      if (playMode && govOpen) { govOpen = false; hold(false); mePanel(); return; }
      if (playMode && whoId != null) { whoId = null; hold(false); mePanel(); return; }
      if (playMode && herePlot != null) { herePlot = null; hold(false); mePanel(); return; }
      if (playMode && hunting) { hunting = false; hold(false); mePanel(); return; }
      if (!help.hidden) help.hidden = true;
      else if (sel) { sel = null; trail.length = 0; map.select(null); inspector(); } else if (tab) { tab = null; dashboard(); }
    }
  });

  const refresh = guard(() => {
    const c = read(E.city());
    c.hour = skyHour;
    c.date = dateOf(c.day);
    c.weather = 'sunny';
    city = c;
    map.set(city);
    stationIdx = new Map();
    (c.lines ?? []).forEach((l: J, i: number) => (l.st as [number, number][]).forEach(([x, y], k) => {
      const at = `${x},${y}`;
      const v = stationIdx.get(at) ?? { name: l.sn?.[k] ?? '', ks: [] as number[] };
      v.ks.push(l.k ?? i + 1);
      stationIdx.set(at, v);
    }));
  }, undefined, true);
  /** 面板整体重画:看板、查看器、顶栏;鼠标停在图上的那个位置接着画。 */
  // 「我」的面板、要停下来的事、地图上的家和单位(玩家模式)
  let lastAlertSeq = 0;
  /** 股市页:看城市、玩家两种模式都在 mePanel 里画;看城市只看行情和公司详情。 */
  const stocksBody = () => {
    const stocksEl = document.getElementById('stocks')!;
    stocksEl.hidden = !stocksOpen;
    document.querySelector('#rail [data-stk]')?.classList.toggle('on', stocksOpen);
    if (!stocksOpen) return;
    const play = !!playMode;
    // 一次一次地招股没开(照原来一直增发):不出「招股」页签;看城市没有招股、持仓
    const ipoOn = play && E.meipo_on ? read(E.meipo_on()) === true : false;
    if ((!ipoOn && stk.tab === 'ipo') || (!play && stk.tab === 'mine')) stk.tab = 'market';
    const rows = stk.tab === 'market' && E.mestocks ? read(E.mestocks()) : null;
    const mkt = stk.tab === 'market' && E.mestockmarket ? read(E.mestockmarket()) : null;
    const firm = stk.tab === 'firm' && stk.firm != null && E.mestock ? read(E.mestock(stk.firm)) : null;
    const hold = play && stk.tab === 'mine' && E.meholdings ? read(E.meholdings()) : null;
    const ipo = ipoOn && stk.tab === 'ipo' && E.meipos ? read(E.meipos()) : null;
    patch(stocksEl, stocksHTML(stk, rows, firm, hold, pcmdPending != null, ipo, ipoOn, play, opened, mkt));
    restoreChartRange();
    // 招股:按钮上的字跟着格子里填的数(配到约几股、募满剩几成),刷新以后再对一遍
    if (stk.tab === 'ipo') syncIpo(stocksEl);
  };
  const mePanel = guard(() => {
    const el = document.getElementById('me')!;
    const stopEl = document.getElementById('stop')!;
    // 看城市:没有「我」的面板,股市页照样能开(只看)
    if (!playMode || !E.me) { el.hidden = true; stopEl.hidden = true; map.setMarks([]); stocksBody(); return; }
    const me = read(E.me());
    playerId = me.active ? me.id : null;
    playerFirms = (me.firms as J[] ?? []).map((f) => f.id);
    const jobs = me.active && me.here && meTab === 'work' && E.mejobs ? read(E.mejobs()) : null;
    const sale = me.active && meTab === 'live' && meList === 'sale' && E.mesale ? read(E.mesale()) : null;
    const cands = me.active && meTab === 'biz' && me.runs && !me.runs.auto && meList === 'cands' && E.mecandidates ? read(E.mecandidates()) : null;
    const tenders = me.active && meTab === 'biz' && me.runs?.build && !me.runs.auto && E.metenders ? read(E.metenders()) : null;
    const singles = me.active && meTab === 'family' && meList === 'dates' && E.mesingles ? read(E.mesingles()) : null;
    const ceos = me.active && meTab === 'biz' && meList === 'ceos' && E.meceos ? read(E.meceos()) : null;
    const cases = me.active && meTab === 'log' && E.mecases ? read(E.mecases()) : null;
    const civic = me.active && (meTab === 'world' || (meTab === 'biz' && me.runs?.media)) && E.mecivic ? read(E.mecivic()) : null;
    const premises = me.active && meTab === 'biz' && meList === 'premises' && E.mepremises ? read(E.mepremises()) : null;
    // 找房面板和图层
    const huntEl = document.getElementById('hunt')!;
    const rent = me.active && me.here && (hunting || hunt.layer || meTab === 'live') && E.merent ? read(E.merent()) : null;
    huntEl.hidden = !hunting;
    if (hunting) patch(huntEl, huntHTML(rent, hunt, pcmdPending != null));
    const layer = hunt.layer && rent ? huntColors(rent, city?.plots.length ?? 0) : null;
    if (JSON.stringify(layer) !== JSON.stringify(huntLayer)) { huntLayer = layer; if (mode < 0) map.setOverlay(huntLayer); }
    const lines = me.active && meTab === 'biz' && meList === 'lines' && !me.runs && E.melines ? read(E.melines()) : null;
    const places: { value: number; label: string }[] = [];
    const homePlot = me.home?.plot ?? me.dwelling;
    if (homePlot != null) places.push({ value: homePlot, label: `住的那块地(${me.home?.place ?? '家里'})` });
    if (firmPlace !== ANY_PLOT && firmPlace !== homePlot) places.push({ value: firmPlace, label: `地图上点的地块 ${firmPlace}` });
    el.hidden = !me.active;
    document.body.classList.toggle('sb-playing', !!me.active);
    patch(el, sideHTML(me, { tab: meTab, list: meList, busy: pcmdPending != null, place: firmPlace, places, hunt: hunting, link: plink, rules: playMode?.rules, assetLayer, ceos, cases, civic, premises, singles: singles ? singlesHTML(singles, dates, pcmdPending != null) : null, queued: pcmdPending != null ? `${speed > 0 ? '已排上,下一天开始前办' : '正在办'}:${pcmdLabel}` : '' }, jobs, rent, lines, sale, null, cands, tenders));
    // 股市页
    stocksBody();
    // 工地页
    const siteEl = document.getElementById('site')!;
    const site = siteId != null && E.mesite ? read(E.mesite(siteId)) : null;
    siteEl.hidden = !site;
    // 大窗口开着:提示挪到下面,不挡窗口的标题(见 sandbox.css 的 sb-pane-open;和 player-v2 同一个办法)
    document.body.classList.toggle('sb-pane-open', stocksOpen || !siteEl.hidden);
    if (site) {
      const sel = siteFloor ?? siteDefaultFloor(site);
      patch(siteEl, siteHTML(site, pcmdPending != null, sel));
      // 剖面:画布第一次出现时接上;每次刷新把新的样子给它(它自己按帧画、新的一天长出来)
      const cv = siteEl.querySelector<HTMLCanvasElement>('canvas.sb-section');
      if (cv && cv !== siteCanvas) {
        siteSec?.destroy();
        siteSec = new Section(cv);
        siteSec.pickFuture = true;
        siteSec.onFloor = (n) => { siteFloor = n; mePanel(); };
        siteCanvas = cv;
      }
      if (siteSec && site.view) {
        const all = site.all as { n: number }[];
        // 天色和城市地图同一个钟点(见 skyHour)
        siteSec.set({ ...site.view, hour: site.view.day * 24 + Math.floor(skyHour) } as unknown as Project, 'sunny');
        siteSec.select(sel);
        siteSec.notes = (n) => siteNote(all[n - 1]);
      }
    } else if (siteSec) {
      siteSec.destroy();
      siteSec = null;
      siteCanvas = null;
    }
    // 生意页
    const bizEl = document.getElementById('biz')!;
    const biz = bizOpen && E.mebiz ? read(E.mebiz()) : null;
    bizEl.hidden = !bizOpen;
    if (bizOpen) patch(bizEl, bizHTML(biz, pcmdPending != null));
    // 房东页
    const landEl = document.getElementById('land')!;
    const land = landOpen && E.meland ? read(E.meland()) : null;
    landEl.hidden = !landOpen;
    if (landOpen) patch(landEl, landHTML(land, pcmdPending != null));
    // 失信页
    const creditEl = document.getElementById('credit')!;
    const credit = creditOpen && E.mecredit ? read(E.mecredit()) : null;
    creditEl.hidden = !creditOpen;
    if (creditOpen) patch(creditEl, creditHTML(credit, pcmdPending != null));
    // 官职页
    const govEl = document.getElementById('govp')!;
    const gov = govOpen && E.megov ? read(E.megov()) : null;
    govEl.hidden = !govOpen;
    if (govOpen) patch(govEl, govHTML(gov, pcmdPending != null));
    // 大窗口开着:提示挪到下面,不挡窗口的标题(见 sandbox.css 的 sb-pane-open)
    document.body.classList.toggle('sb-pane-open', stocksOpen || bizOpen || landOpen || creditOpen || govOpen || !siteEl.hidden);
    // 对这个人做什么
    const whoEl = document.getElementById('who')!;
    const who = whoId != null && E.mewho ? read(E.mewho(whoId)) : null;
    whoEl.hidden = !who?.active;
    if (who?.active) patch(whoEl, whoHTML(who, pcmdPending != null));
    // 地图上点的那块地:在这里能做什么
    const hereEl = document.getElementById('here')!;
    const here = herePlot != null && me.here && E.mehere ? read(E.mehere(herePlot)) : null;
    hereEl.hidden = !here?.active;
    if (here?.active) {
      patch(hereEl, hereCardHTML(here, pcmdPending != null, hereFull, hereType));
      // 就地弹:点的位置右边一点,出不了屏幕;玩家拖到别处摆过的就待在那里
      if (hereAt && !placed(hereEl)) {
        const w = hereEl.offsetWidth || 300, h = hereEl.offsetHeight || 200;
        hereEl.style.left = `${Math.max(8, Math.min(window.innerWidth - w - 8, hereAt.x + 18))}px`;
        hereEl.style.top = `${Math.max(70, Math.min(window.innerHeight - h - 8, hereAt.y - 40))}px`;
      }
    }

    lastAlertSeq = (me.alerts as J[])[0]?.seq ?? 0;
    // 名下的一切标在地图上(房、地、公司、持股、挂牌的,家、上班、有人想买);「我的资产」图层按值上色
    assetSpots = me.here ? assetsOf(me) : new Map();
    map.setMarks(me.here ? assetMarks(me, assetSpots) : []);
    if (assetLayer && !hunt.layer && mode < 0) map.setOverlay(assetColors(assetSpots, city?.plots.length ?? 0));
    const unseen = stopSeq > ackSeq ? (me.alerts as J[]).filter((a) => a.seq > ackSeq) : [];
    stopEl.hidden = !unseen.length;
    if (unseen.length) patch(stopEl, stopHTML(unseen, me, pcmdPending != null, plink));
  });
  // 玩家模式:鼠标停在能点的名字上(人、公司、地块、房、楼),一行字说是什么、值多少
  const peekEl = document.createElement('div');
  peekEl.className = 'sb-peek'; peekEl.hidden = true;
  document.body.appendChild(peekEl);
  const PEEK: Record<string, number> = { person: 1, firm: 2, plot: 3, unit: 4, project: 5 };
  let peekAt: { kind: number; id: number; x: number; y: number } | null = null;
  const peekDraw = guard(() => {
    if (!peekAt || !E.peek) { peekEl.hidden = true; return; }
    const r = read(E.peek(peekAt.kind, peekAt.id));
    if (!r.t) { peekEl.hidden = true; return; }
    peekEl.textContent = r.t;
    peekEl.hidden = false;
    const w = peekEl.offsetWidth || 260;
    peekEl.style.left = `${Math.max(8, Math.min(window.innerWidth - w - 8, peekAt.x + 12))}px`;
    peekEl.style.top = `${Math.max(8, peekAt.y + 16)}px`;
  });
  document.body.addEventListener('mouseover', (e) => {
    if (!playMode) return;
    const a = (e.target as HTMLElement).closest('[data-go]') as HTMLElement | null;
    const [k, v] = (a?.dataset.go ?? '').split(':');
    if (!a || !PEEK[k] || !Number.isSafeInteger(Number(v))) { if (peekAt) { peekAt = null; peekEl.hidden = true; } return; }
    if (peekAt && peekAt.kind === PEEK[k] && peekAt.id === Number(v)) return;
    peekAt = { kind: PEEK[k], id: Number(v), x: e.clientX, y: e.clientY };
    peekDraw();
  });
  const panels = guard(() => {
    // 首份城市快照到达前，归档等回信也可能排入面板重画。
    if (first) return;
    // 先清空曲线的登记再画:股市页、侧栏在 mePanel 里画,画的曲线也要登记上(停上去才有浮框)
    resetCharts();
    mePanel();
    seriesCache = null;
    top();
    dashboard();
    inspector();
    checkNews();
    checkFollows();
    yearReport();
    mapTip();
    if (hover && CHARTS.has(hover.id)) {
      const el = document.querySelector(`.sb-cw[data-chart="${CSS.escape(hover.id)}"]`) as HTMLElement | null;
      if (el) drawHover(el, hover.i);
    }
    restoreChartRange();
  });
  drawQueryPanels = panels;
  // 地图上的车和行人:按模拟里每块地真实的车流、人流画(引擎老的没有人流,就只有车流)
  const activity = guard(() => {
    try {
      const cars = read(E.mapmode(24)).v as (number | null)[];
      const people = read(E.mapmode(25)).v as (number | null)[];
      map.setActivity(cars, people.some((x) => x != null) ? people : null);
    } catch (e) {
      if (e === PENDING) throw e;
      map.setActivity(null, null);
    }
  }, undefined, true);
  // 开一座城(引擎线程里生成、快进);第一份地图数据到了再画面板、撤开场画面
  let first = true;
  const start = () => {
    const cityUrl = new URL(location.href);
    cityUrl.searchParams.set('seed', String(seed));
    window.history.replaceState(window.history.state, '', cityUrl);
    gen++;
    exportDownloads?.reset();
    clearCityPushes(); cityNeedsImmediate = true;
    Object.assign(cityPushPerf, { received: 0, applied: 0, coalesced: 0, cancelled: 0, parsed: 0, parseMs: 0, maxParseMs: 0 });
    clearQueryRedraws();
    downloadPanel.clear();
    if (splashTimer != null) { clearTimeout(splashTimer); splashTimer = null; }
    if (splash && !splash.isConnected) document.body.appendChild(splash);
    splash?.classList.remove('gone');
    splash?.querySelector<HTMLElement>('.sb-load')?.removeAttribute('hidden');
    bootFinished = bootFailed = stopRequested = false;
    runtimeFailed = false;
    document.getElementById('engineAlert')!.hidden = true;
    bootDone = 0;
    bootTarget = pendingReplay?.completeDay ?? (playMode ? Math.max(playMode.until ?? 0, playMode.enter ?? 0) : skip * 360);
    stopSkip.disabled = false;
    stopSkip.textContent = '先玩到这里';
    bootNote.textContent = skip > 0 ? '长年份预热在本页计算。关闭后重开长年份地址会从头推演；下方入口可不预热重新开局，这不是已存档。' : '';
    bootUpdate(0, bootTarget);
    if (!validYears) bootDetail.textContent = '预热年份无效，已改为从开局生成城市。';
    cache.clear();
    inflight.clear();
    asking.clear();
    histories.clear(); historyRequests.clear(); historySources.clear();
    schoolRoster = { id: -1, start: 0, stack: [] };
    actionPending = null; mapIntent = null;
    archiveState = historyPref ? { state: 'opening', stored: 0, message: '正在打开新城市历史存档' } : { state: 'off', stored: 0, message: '完整历史未开启' }; updateArchive();
    wk.parallel = wk.timing = null; wk.stepDays = wk.stepMs = 0;
    used.clear();
    loadFollows();
    followSeen.clear();
    lastYear = null;
    modeData = null;
    first = true;
    pcmdPending = null; stopSeq = ackSeq = 0; saveDay = -1;
    // replay:这一回开城要先把以前的日子推一遍(读档、读回进度、网址给了年份),加载时间分开看
    monitorStart({ gen, seed, mode: playMode ? 'player' : 'city', ...(playMode ? { playerKind: playMode.kind, enterDay: playMode.enter ?? 0 } : {}),
      replay: pendingReplay != null || (playMode != null && (playMode.until ?? 0) > (playMode.enter ?? 0)) || (!playMode && skip > 0) });
    const replayReceipt = pendingReplay; pendingReplay = null;
    worker.postMessage(replayReceipt ? { t: 'init', gen, seed, pop: 80, skip: 0, history: historyPref, buildId: REPLAY_BUILD_ID, replayReceipt, fast: q.get('replayfast') !== '0' }
      : playMode ? { t: 'init', gen, seed, pop: 80, skip: 0, history: historyPref, buildId: REPLAY_BUILD_ID, player: playMode.kind, log: playMode.log, until: Math.max(playMode.until ?? 0, playMode.enter ?? 0), enter: playMode.enter ?? 0, fast: q.get('replayfast') !== '0', rules: playMode.rules, bits: playMode.bits }
      : { t: 'init', gen, seed, pop: 80, skip, history: historyPref, buildId: REPLAY_BUILD_ID });
  };
  // 界面记得住:开着哪一页、各面板停在哪一页、天色、地图模式(网址里带了的听网址的;浏览器存不了就算了)
  const UI_KEY = 'sb:ui';
  try {
    const ui = JSON.parse(localStorage.getItem(UI_KEY) ?? '{}') ?? {};
    if (!q.has('tab') && ui.tab !== undefined) tab = ui.tab;
    if (!q.has('sub') && Array.isArray(ui.sub)) for (const [k, v] of ui.sub) sub.set(k, v);
    // 天色存在 sky2(加了「自动」,原来的 sky 的档位对不上了,不读)
    if (fixHour == null && typeof ui.sky2 === 'number') {
      skyIdx = ui.sky2 % SKIES.length;
      skyHour = SKIES[skyIdx][0] === 'auto' ? autoHour(Date.now()) : SKIES[skyIdx][2];
    }
    if (!q.has('mode') && typeof ui.mode === 'number') {
      mode = ui.mode;
      modesOpen = !!ui.modesOpen;
    }
    if (!q.has('transit') && typeof ui.transit === 'number') transit = ui.transit % 3;
    if (!q.has('relief') && typeof ui.relief === 'number') relief = ui.relief % 3;
    if (!q.has('scale') && typeof ui.byRank === 'boolean') byRank = ui.byRank;
  } catch {
    /* 没存过或者存不了 */
  }
  const saveUI = () => {
    try {
      localStorage.setItem(UI_KEY, JSON.stringify({ tab, sub: [...sub.entries()], sky2: skyIdx, mode, modesOpen, transit, relief, byRank }));
    } catch {
      /* 存不了就算了 */
    }
  };
  // 点完、按完键以后存一下(在上面的处理之后跑)
  document.body.addEventListener('click', saveUI);
  document.addEventListener('keydown', saveUI);
  const sq = q.get('select');
  if (sq) {
    const [k, v] = sq.split(':');
    sel = { kind: k as Kind, id: Number(v) };
  }
  // 查性能:网址带 perf=1 时每两秒在控制台打一次:帧数、引擎线程模拟了几天用了多久、交地图数据、画地图、重画面板各用了多久(这个线程的)
  const perf = q.has('perf');
  measureCityPushes = perf;
  map.setDrawTiming(perf);
  const pf = { refresh: 0, panels: 0, frames: 0, t0: performance.now(), stepMs: 0, stepDays: 0 };
  const timed = (k: 'refresh' | 'panels', f: () => void) => {
    if (!perf) return f();
    const t0 = performance.now();
    f();
    pf[k] += performance.now() - t0;
  };
  onCity = () => {
    timed('refresh', refresh);
    if (first && city) {
      first = false;
      panels();
      modes();
      activity();
      // 网址里带了快进几年的(截图、分享):直接撤掉开场画面,不等它淡出
      bootFinished = true;
      if (skip) splash?.remove();
      splash?.classList.add('gone');
      splashTimer = setTimeout(() => { splash?.remove(); splashTimer = null; }, 600);
    } else if (speed === 0) {
      // 暂停下来的那一天:面板也换成这一天的
      panels();
    }
  };
  // 开始页:选模式(看城市 / 做一个人)、选开局身份、继续上次的存档。网址带 years 或 mode=city 的直接看城市(截图、分享的链接)
  const begin = (mode: 'city' | 'person', kind = 0, save: PlayerSave | null = null) => {
    document.getElementById('startPage')?.remove();
    loadT0 = performance.now();
    if (mode === 'person') {
      if (save) {
        seed = save.seed; playMode = { kind: save.kind, log: [...save.log], until: save.day, name: save.name, enter: save.enter ?? 0, rules: saveRules(save), bits: saveBits(save) };
        verifyLoad = save.print ? { day: save.day, print: save.print } : null;
        speed = 0; // 读回来先停着
      }
      else playMode = { kind, log: [], name: '', enter: enterYears * 360, until: enterYears * 360, rules: RULES, bits: [] };
    }
    const url = new URL(location.href);
    url.searchParams.set('mode', mode);
    window.history.replaceState(window.history.state, '', url);
    start();
  };
  const startPage = (step: 'mode' | 'who' = 'mode') => {
    let el = document.getElementById('startPage');
    if (!el) { el = document.createElement('div'); el.id = 'startPage'; el.className = 'sb-start'; document.body.appendChild(el); }
    el.innerHTML = startPageHTML(loadSave(), step, enterYears);
    el.querySelector('.sb-st-in')?.appendChild(gameMonitor.settingsEntry('设置'));
  };
  let oldSave: PlayerSave | null = null; // 等玩家选「新开一局」还是「仍然读取」的旧版本存档
  const openSave = (s: PlayerSave) => {
    // 旧版本的存档:这一版改了城的规矩,照旧指令重放出来的城和存档时不一样;让玩家选新开一局还是仍然读取
    if (!SAME_ENGINE.includes(s.engine)) {
      oldSave = s;
      let el = document.getElementById('startPage');
      if (!el) { el = document.createElement('div'); el.id = 'startPage'; el.className = 'sb-start'; document.body.appendChild(el); }
      el.innerHTML = oldSaveHTML(s);
      return;
    }
    begin('person', s.kind, s);
  };
  /** 仍然读取:原来的存档另存备份,按新版本重放(读完照新版本号重新存一份,见 ready)。 */
  const forceOpen = (s: PlayerSave) => {
    backupSave(s);
    forceLoad = { from: s.engine, print: s.print, day: s.day };
    begin('person', s.kind, { ...s, print: undefined });
  };
  document.body.addEventListener('click', (e) => {
    const t = (e.target as HTMLElement).closest('[data-pstart]') as HTMLElement | null;
    if (!t) return;
    const v = t.dataset.pstart!;
    if (v === 'city') begin('city');
    else if (v === 'person') startPage('who');
    else if (v === 'back') startPage('mode');
    else if (v === 'continue') { const s = loadSave(); if (s) openSave(s); }
    else if (v === 'newrules') {
      // 换成最新规则:原来这一局先存进「恢复上一局」,再按最新规则从头重放(城会变样)
      if (playMode) savePlay();
      const s = loadSave();
      if (!s) return;
      pushPrevSave(s);
      const fresh = { ...s, rules: RULES, bits: [], print: undefined };
      writeSave(fresh);
      openSave(fresh);
    }
    else if (v.startsWith('restore:')) {
      // 恢复上一局:眼下这一局(玩着的先存一下)换进备份,拿出来的那份当存档读
      if (playMode) savePlay();
      const s = takePrevSave(Number(v.slice(8)));
      if (!s) return;
      pushPrevSave(loadSave());
      writeSave(s);
      openSave(s);
    }
    else if (v === 'force') { if (oldSave) forceOpen(oldSave); oldSave = null; }
    else if (v === 'loaded') document.querySelector('.sb-load-wrap')?.remove();
    else if (v.startsWith('when:')) { enterYears = Number(v.slice(5)); startPage('who'); }
    else if (v.startsWith('kind:')) {
      // 不弹框:原来的存档先存进「恢复上一局」
      pushPrevSave(loadSave());
      begin('person', Number(v.slice(5)));
    }
  });
  document.body.addEventListener('change', (e) => {
    const t = e.target as HTMLInputElement;
    if (t.dataset.pimport === undefined || !t.files?.[0]) return;
    void t.files[0].text().then((text) => {
      const s = JSON.parse(text);
      if (!(s && s.v === 1 && Number.isSafeInteger(s.seed) && Array.isArray(s.log) && Number.isSafeInteger(s.day) && Number.isSafeInteger(s.kind))) throw new Error('不是存档');
      writeSave(s);
      openSave(s);
    }).catch(() => window.alert('这个文件不是存档。'));
  });
  const gameMonitor = installGameMonitor({ send: message => worker.postMessage(message),
    restore: receipt => {
      // 读回的是玩家模式的进度,之后会写进玩家存档:眼下这一局(玩着的先存一下)收进「恢复上一局」
      if (receipt.init.mode === 'player') { if (playMode) savePlay(); pushPrevSave(loadSave()); }
      speed = 0; worker.postMessage({ t: 'speed', gen, v: 0 });
      seed = receipt.init.seed; pendingReplay = receipt;
      // Original citizen nonce receipts survive replay; the next real action must use a fresh u32 ID.
      for (const mutation of receipt.mutations) if (mutation.kind === 'citizen_act') actionSeq = Math.max(actionSeq, mutation.requestId);
      setHistoryPref(receipt.history.enabled);
      // 规则版本、开关照回执记的(引擎线程照同一套置开关重放,见 sbcheckpoint.ts 的 replayBits);没记的是同一个构建的回执,照最新规则
      playMode = receipt.init.mode === 'player' ? { kind: receipt.init.playerKind!, name: '', enter: receipt.init.enterDay, until: receipt.completeDay,
        rules: receipt.init.rules ?? RULES, bits: replayBits(receipt.init),
        log: receipt.mutations.filter(m => m.kind === 'pcmd').map(m => ({ day: m.executedDay, code: m.code, target: m.target, value: m.value })) } : null;
      forceLoad = null; verifyLoad = null; loadT0 = performance.now();
      document.getElementById('startPage')?.remove(); start();
    },
    download: (blob, filename) => { const a = downloadPanel.show(blob, filename, 'json'); try { a.click(); } catch { /* Persistent real link remains. */ } },
    // 倍速;玩家模式里自己的钱(现钱加存款)和身家:用「我」那一页最近一次的数,没看过就不带
    snapshot: () => {
      const out: { speed: number; cash?: number; worth?: number } = { speed };
      const me = playMode ? cache.get('me')?.raw : null;
      if (me) { try { const x = JSON.parse(me); if (typeof x.cash === 'number') out.cash = x.cash + (typeof x.deposit === 'number' ? x.deposit : 0); if (typeof x.worth === 'number') out.worth = x.worth; } catch { /* 读不了就不带 */ } }
      return out;
    },
  });
  monitorMessage = gameMonitor.message; monitorStart = gameMonitor.start;
  // 设置对话框也和别的窗口一样按住标题栏拖(见 sbwin.ts);它是模态的,总在最上面
  { const s = document.getElementById('gameSettings'); if (s) makeWin(s, { kind: 'settings', handle: '.sb-head', top: true }); }
  worker.addEventListener('error', gameMonitor.workerError);
  if (q.has('years') || q.get('mode') === 'city') start();
  else startPage();
  let last = performance.now();
  let lastPanel = 0;
  let lastMode = 0;
  let lastActivity = 0;
  let sentSpeed = -1;
  let lastEquityPlayback = 0;
  let playingEquity: EquityHistoryState | null = null;
  const frame = (now: number) => {
    requestAnimationFrame(frame); // 先排下一帧:这一帧哪里出错也不会停
    const dt = Math.min(0.25, (now - last) / 1000);
    last = now;
    // 倍速交给引擎线程(暂停、调速都在这里发)
    if (speed !== sentSpeed) {
      if (speed <= 0) flushCityPush();
      if (speed > 0 && stopSeq > ackSeq) { ackSeq = Math.max(stopSeq, lastAlertSeq); worker.postMessage({ t: 'pack', gen, seq: stopSeq }); mePanel(); }
      sentSpeed = speed;
      worker.postMessage({ t: 'speed', gen, v: speed });
      if (city) inspector();
    }
    if (!city) return;
    map.setMotion(speed > 0); // 控制状态仍同步；后台只省去不可见绘制和周期面板查询。
    if (document.hidden) return;
    const equityPlayback = sel?.kind === 'firm' && sub.get('i:firm') === 'who' ? equityState(sel.id) : null;
    if (playingEquity && playingEquity !== equityPlayback) playingEquity.playing = false;
    playingEquity = equityPlayback;
    if (equityPlayback?.playing && now - lastEquityPlayback >= 600) {
      lastEquityPlayback = now;
      advanceEquityTimeline(equityPlayback);
      inspector();
    }
    // 游戏在走:画布少填一些像素;暂停:按满密度画
    map.setQuality(speed > 0 ? 1.5 : Infinity);
    if (perf) {
      pf.frames++;
      if (now - pf.t0 > 2000) {
        const s = (now - pf.t0) / 1000;
        const days = wk.stepDays - pf.stepDays, ms = wk.stepMs - pf.stepMs;
        const paint = map.takeDrawStats();
        console.log(`[perf] 每秒:帧 ${(pf.frames / s).toFixed(0)} 模拟 ${(days / s).toFixed(0)} 天(推进合计含等待/归档 ${(ms / s).toFixed(0)}ms,一天 ${days ? (ms / days).toFixed(1) : '-'}ms) 交地图 ${(pf.refresh / s).toFixed(0)}ms 画地图 ${(map.drawMs / s).toFixed(0)}ms(${(map.draws / s).toFixed(0)} 张) 面板 ${(pf.panels / s).toFixed(0)}ms 回信重画 ${(queryPerf.ms / s).toFixed(0)}ms(${queryPerf.draws} 次,最长 ${queryPerf.maxMs.toFixed(1)}ms) 人 ${city.stats.pop}`);
        console.log(`[map-perf] 每秒:地面江面 ${(paint.groundMs / s).toFixed(1)}ms 阴影 ${(paint.shadowMs / s).toFixed(1)}ms 建筑树木(含塔吊烟灯) ${(paint.buildingsMs / s).toFixed(1)}ms 车人 ${(paint.dynamicMs / s).toFixed(1)}ms 其他 ${(paint.otherMs / s).toFixed(1)}ms; ${paint.frames}张细分实测,阴影路径复用${paint.shadowHits}/重建${paint.shadowBuilds}。地面含喷泉,细分计时仅perf启用。`);
        console.log(`[city-push] 本城累计:接收${cityPushPerf.received} 合并${cityPushPerf.coalesced} 刷新${cityPushPerf.applied} 取消${cityPushPerf.cancelled} city解析${cityPushPerf.parsed}次/${cityPushPerf.parseMs.toFixed(1)}ms(最长${cityPushPerf.maxParseMs.toFixed(1)}ms) 待显示${pendingCityPush ? 1 : 0};仅运行中地图显示合并,不合并模拟日/冷档/查询。`);
        pf.refresh = pf.panels = pf.frames = 0;
        queryPerf.ms = queryPerf.draws = queryPerf.maxMs = 0;
        pf.stepMs = wk.stepMs;
        pf.stepDays = wk.stepDays;
        map.drawMs = map.draws = 0;
        pf.t0 = now;
      }
    }
    // 换天色:往前转到挑的钟点,一秒转八个钟头
    const auto = fixHour == null && SKIES[skyIdx][0] === 'auto';
    const target = fixHour ?? (auto ? autoHour(Date.now()) : SKIES[skyIdx][2]);
    const gap = (((target - skyHour) % 24) + 24) % 24;
    // 自动转的时候攒够三分钟的钟点(现实一秒左右)才换一次天色:每换一次地图要整张重画
    if (gap > (auto && gap < 1 ? 0.05 : 0.01)) {
      skyHour = (skyHour + Math.min(gap, dt * 8)) % 24;
      city.hour = skyHour;
      map.set(city);
    }
    // 面板一秒刷新一次(暂停的时候不用):上一秒画面用到的数据整批向引擎要新的,到了再画;地图模式两秒、车流三秒一次
    // 地图(city)不在这里要:走着的时候引擎线程每过一天就推一份(最多一秒十次),再要一遍是白算一整座城。
    // 已经是这一天的也不再要:城一天只变一次(玩家办了事会清掉缓存),城大了一天要一两秒,一秒一刷就把同一天的面板算好几遍
    if (speed > 0 && now - lastPanel > 1000) {
      lastPanel = now;
      const keys = [...used].filter((k) => k !== 'city' && cache.get(k)?.day !== engineDay);
      used.clear();
      if (keys.length) ask(keys, requestQueryPanels);
      else timed('panels', panels);
    }
    if (speed > 0 && now - lastActivity > 3000) {
      lastActivity = now;
      activity();
    }
    if (speed > 0 && (mode >= 0 || transit === 1) && now - lastMode > 2000) {
      lastMode = now;
      modeData = null;
      modes();
    }
  };
  requestAnimationFrame(frame);
}

main();
